import * as React from 'react';
import React__default, { useCallback, useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useHistory, generatePath, useLocation, Link as Link$1 } from 'react-router-dom';
import makeSlug from 'slugify';
import qs from 'query-string';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import ResizeObserver from 'resize-observer-polyfill';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import ReactSelect, { components } from 'react-select';
import 'react-calendar/dist/Calendar.css';
import DateTimePicker from 'react-datetime-picker/dist/entry.nostyle';
import { parseISO, set, isValid, format, parse } from 'date-fns';
import Calendar from 'react-calendar';
import { useDropzone } from 'react-dropzone';
import ReactModal from 'react-modal';
import Popover from 'react-popover';
import { createPortal } from 'react-dom';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, isValidNumberForRegion } from 'libphonenumber-js';

function useAction(actionCreator, ...rest) {
  const dispatch = useDispatch();
  return useCallback((...args) => {
    // Hack, that allows making checks based on store state in sagas.
    // This timeout leaves execution window to commit changes in redux store before the next action call.
    // Without it, action calls will be batched by the react scheduler, so all of the triggered sagas will rely on the same state snapshot.
    // For example, if you want to make a request debounce in a saga based on some flag in the store
    // all of the 'yield select()' sagas batched by the scheduler will point to the same store snapshot.
    setTimeout(() => dispatch(actionCreator(...rest, ...args)), 0);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [actionCreator, dispatch, ...rest]);
}

function useBoolean(initialValue) {
  const [bool, setBool] = useState(initialValue);
  const update = useCallback(newValue => {
    if (typeof newValue === 'boolean') {
      setBool(newValue);
    } else {
      setBool(true);
    }
  }, []);
  const setToFalse = useCallback(() => update(false), [update]);
  return [bool, update, setToFalse];
}

// countTo is a unix timestamp (seconds) that represents time in the future
// interval is an interval of time which passes between counter changes
// hook will call setCount with new value in the end of every interval

function useCountdownTimer(countTo, interval) {
  const [count, setCount] = useState(0); // counter initializes on mount and if countTo or interval values are changed

  useEffect(() => {
    let timerId = 0; // Variable to store timer identifier

    const countDown = () => setCount(c => {
      if (c > 0) {
        timerId = window.setTimeout(countDown, interval);
        return c - 1;
      } else {
        return c;
      }
    });

    const initCount = countTo ? countTo - Math.floor(Date.now() / 1000) : 0;
    setCount(initCount);
    timerId = window.setTimeout(countDown, interval); // Clear timer on unmount

    return () => clearTimeout(timerId);
  }, [countTo, interval]);
  return count;
}

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const useDidMount = cb => {
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return cb();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
};

const useFileObjectUrl = file => {
  const [url, setUrl] = useState(typeof file === 'string' ? file : undefined);
  useEffect(() => {
    let url;

    if (file instanceof File) {
      url = URL.createObjectURL(file);
      setUrl(url);
    } else if (typeof file === 'string') {
      setUrl(file);
      return;
    } else {
      setUrl(undefined);
      return;
    }

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [file]);
  return {
    url
  };
};

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function nonNullable(value) {
  return value !== null && typeof value !== 'undefined';
}

function nonEmptyFields(obj) {
  const result = {};

  for (const [key, val] of Object.entries(obj)) {
    if (typeof val !== undefined && val !== '') {
      result[key] = val;
    }
  }

  return result;
}

function pairs(obj) {
  return Object.entries(obj);
}
const helpersObject = {
  nonNullable,
  nonEmptyFields,
  pairs
};
const isPlainObject = obj => {
  return obj !== null && Object.prototype.toString.call(obj) === '[object Object]';
};
/**
 * Creates key:value representation of given nested object. Where "key" is lodash like object path.
 * Example input:
 * const obj = {
 *   a: { b: { c: 1 } },
 *   v: [[ 1 ]],
 *   d: [{ x: 6, s: [1, { z: { c: { b: 'hello' } } }] }]
 * }
 * Example output:
 * {
 *   'a.b.c': 1,
 *   'v[0][0]': 1,
 *   'd[0].x': 6,
 *   'd[0].s[0]': 1,
 *   'd[0].s[1].z.c.b': 'hello'
 * }
 */

const flat = obj => {
  if (!isPlainObject(obj) && !Array.isArray(obj)) {
    return {};
  }

  const result = {};

  const walk = (obj, path = '') => {
    if (isPlainObject(obj)) {
      return Object.keys(obj).forEach(key => {
        walk(obj[key], path.length ? `${path}.${key}` : key);
      });
    }

    if (Array.isArray(obj)) {
      return obj.forEach((obj, i) => {
        walk(obj, `${path}[${i}]`);
      });
    }

    result[path] = obj;
  };

  walk(obj, '');
  return result;
};
const unflat = obj => {
  // Captives order:
  // [1] dot (skipping)
  // [2] regular key
  // [3] array key
  // [4] unexpected token
  const rxToken = /(\.)|([\w\-_]+)|(\[\d+\])|([^\w\[\]]|\[\D+\]?)/y;
  const rxIndex = /\[(\d)\]/;

  const tokenize = key => {
    rxToken.lastIndex = 0;
    const result = [];
    let captives;

    while (captives = rxToken.exec(key)) {
      if (captives[4]) {
        console.warn(`Failed to parse key "${key}": unexpected token "${captives[4]}" at ${rxToken.lastIndex}`);
        return [];
      }

      if (captives[3]) {
        const [, index] = rxIndex.exec(captives[3]);
        result.push({
          type: 'arrayKey',
          key: parseInt(index, 10)
        });
      }

      if (captives[2]) {
        result.push({
          type: 'key',
          key: captives[2]
        });
      }
    }

    return result;
  };

  let root;

  for (const [key, value] of Object.entries(obj)) {
    const tokens = tokenize(key);

    if (!root && tokens[0]) {
      switch (tokens[0].type) {
        case 'key':
          root = {};
          break;

        case 'arrayKey':
          root = [];
          break;
      }
    }

    let pointer = root;
    const lastIndex = tokens.length - 1;
    tokens.forEach((token, index) => {
      if (index === lastIndex) {
        pointer[token.key] = value;
      } else if (!pointer[token.key]) {
        switch (tokens[index + 1].type) {
          case 'key':
            pointer[token.key] = {};
            break;

          case 'arrayKey':
            pointer[token.key] = [];
            break;
        }
      }

      pointer = pointer[token.key];
    });
  }

  return root;
};

function ownKeys$f(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$f(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$f(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$f(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function useFormManager() {
  const slots = useRef({});
  const [dirty, setDirty] = useState(false);
  const [valid, setValid] = useState(true);
  const bindCbs = useRef({}); // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const register = useCallback((slotName, instance) => {
    const runValidation = () => {
      const list = Object.values(slots.current);
      setValid(list.every(formik => formik.isValid));
      setDirty(list.some(formik => formik.dirty));
    }; // eslint-disable-next-line react-hooks/rules-of-hooks


    useEffect(() => {
      slots.current[slotName] = instance;
      runValidation(); // eslint-disable-next-line
    }, [instance]); // eslint-disable-next-line react-hooks/rules-of-hooks

    useEffect(() => {
      return () => {
        delete slots.current[slotName];
        delete bindCbs.current[slotName];
        runValidation();
      }; // eslint-disable-next-line
    }, []);
  }, []);
  const bind = useCallback(slotName => {
    if (!bindCbs.current[slotName]) {
      bindCbs.current[slotName] = config => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const formikInstance = useFormik(_objectSpread$f(_objectSpread$f({}, config), {}, {
          validateOnMount: true
        }));
        register(slotName, formikInstance);
        return formikInstance;
      };
    }

    return bindCbs.current[slotName];
  }, [register]);
  const map = useCallback(cb => {
    return Object.values(slots.current).map(cb);
  }, [slots]);
  const isAllValid = useCallback(() => map(item => item.isValid).every(valid => valid), [map]);
  const submitAll = useCallback(cb => {
    return Promise.all(map(formik => {
      return formik.submitForm();
    })).then(() => {
      const result = {};
      Object.entries(slots.current).forEach(([key, instance]) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        result[key] = instance.values;
        Object.keys(flat(instance.errors)).forEach(path => {
          instance.setFieldTouched(path, true);
        });
      });
      const valid = isAllValid();
      cb && cb(valid, result);
      return [valid, result];
    });
  }, [isAllValid, map]);
  const getValues = useCallback(slotName => {
    return slots.current[slotName] ? slots.current[slotName].values : null;
  }, []);
  const reset = useCallback(form => {
    if (form) {
      slots.current[form].resetForm();
    } else {
      map(instance => {
        instance.resetForm();
      });
    }
  }, [map]);
  return {
    slots: slots.current,
    map,
    isAllValid,
    valid,
    dirty,
    submitAll,
    getValues,
    bind,
    register,
    reset
  };
}

const HelmetContext = /*#__PURE__*/React__default.createContext({
  helmet: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateHelmet: () => {}
});
const useHelmet = ({
  title,
  description,
  favicons
}) => {
  const {
    updateHelmet
  } = useContext(HelmetContext);
  useEffect(() => {
    updateHelmet({
      title,
      description,
      favicons
    });
  }, [title, description, favicons, updateHelmet]);
};

// color: full HEX, opacity: 0-1
const alphaColor = (color, opacity = 1) => {
  const hexOpacity = opacity ? Math.trunc(Math.min(opacity, 1) * 255).toString(16) : '00';
  return `${color}${hexOpacity.length > 1 ? hexOpacity : `0${hexOpacity}`}`;
};

const helpersColor = {
  alphaColor
};

const downloadUrl = (url, headers = {}, fileName) => {
  var _headers$contentDisp, _headers$contentDisp$, _headers$contentDisp$2, _headers$contentType, _headers$contentType2;

  if (!window || !document) return;
  const fileNameNormalized = fileName || ((_headers$contentDisp = headers['content-disposition']) === null || _headers$contentDisp === void 0 ? void 0 : (_headers$contentDisp$ = _headers$contentDisp.match(/filename="(.*)"/)) === null || _headers$contentDisp$ === void 0 ? void 0 : (_headers$contentDisp$2 = _headers$contentDisp$.groups) === null || _headers$contentDisp$2 === void 0 ? void 0 : _headers$contentDisp$2[1]) || document.title;
  const mimeType = (_headers$contentType = (_headers$contentType2 = headers['content-type']) === null || _headers$contentType2 === void 0 ? void 0 : _headers$contentType2.split(';')[0]) !== null && _headers$contentType !== void 0 ? _headers$contentType : '';
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileNameNormalized);
  link.setAttribute('type', mimeType);
  link.click();
  window.URL.revokeObjectURL(url);
};

const downloadBlob = (data, headers = {}, fileName) => {
  if (!window || !document) return;
  const url = window.URL.createObjectURL(data);
  downloadUrl(url, headers, fileName);
};

const helpersDownload = {
  downloadUrl,
  downloadBlob
};

var ValidationError;

(function (ValidationError) {
  ValidationError[ValidationError["FILE_TOO_LARGE"] = 1] = "FILE_TOO_LARGE";
  ValidationError[ValidationError["MAX_WIDTH_EXCEEDED"] = 2] = "MAX_WIDTH_EXCEEDED";
  ValidationError[ValidationError["MAX_HEIGHT_EXCEEDED"] = 3] = "MAX_HEIGHT_EXCEEDED";
  ValidationError[ValidationError["UNKNOWN"] = 4] = "UNKNOWN";
})(ValidationError || (ValidationError = {}));

const imageFileValidator = async (file, maxWidth = 500, maxHeight = 500, maxFileSize = 3e6) => {
  const read = () => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return new Promise((resolve, reject) => {
      reader.onload = e => {
        var _e$target;

        resolve((_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.result);
      };

      reader.onerror = () => reject(new Error('File read error'));

      reader.onabort = () => reject(new Error('File read cancelled'));
    });
  };

  const validate = src => {
    const image = new Image();
    image.src = src;
    return new Promise((resolve, reject) => {
      image.onload = () => {
        if (image.width > maxWidth) {
          resolve(ValidationError.MAX_WIDTH_EXCEEDED);
          return;
        }

        if (image.height > maxHeight) {
          resolve(ValidationError.MAX_HEIGHT_EXCEEDED);
          return;
        }

        resolve(null);
      };

      image.onerror = () => reject(new Error('Image read error'));

      image.onabort = () => reject(new Error('Image read cancelled'));
    });
  };

  try {
    if (file.size > maxFileSize) {
      return ValidationError.FILE_TOO_LARGE;
    }

    const url = await read();
    return validate(url);
  } catch (err) {
    console.error(err);
    return ValidationError.UNKNOWN;
  }
};

const helpersImageFileValidator = {
  ValidationError,
  imageFileValidator
};

const inactiveBrowserTabLock = () => new Promise(resolve => {
  if (!document.hidden) {
    resolve();
    return;
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      resolve();
    }
  });
});

const helpersBrowser = {
  inactiveBrowserTabLock
};

const pwgen = (length = 12) => {
  const chars = '!@#$%^&*()~0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  return Array.from(crypto.getRandomValues(new Uint32Array(length))).map(rnd => chars[rnd % chars.length]).join('');
};

const helpersPassword = {
  pwgen
};

const slug = value => makeSlug(value, {
  remove: /[*+~.()'"!:@]/g,
  lower: true,
  strict: true
});

const helpersSlug = {
  slugify: slug
};

const fixParamType = param => {
  return String(param);
};

const isNonEmpty = v => {
  return typeof v !== 'undefined';
};

const stringifyQueryParams = record => {
  const result = {};

  for (const [key, val] of Object.entries(record)) {
    if (Array.isArray(val)) {
      result[key] = val.filter(isNonEmpty).map(fixParamType);
    } else {
      if (isNonEmpty(val)) {
        result[key] = fixParamType(val);
      }
    }
  }

  return result;
};

const stringifyUrl = options => {
  return qs.stringifyUrl({
    url: options.url,
    query: options.query ? stringifyQueryParams(options.query) : {}
  });
};

const helpersUrl = {
  stringifyQueryParams,
  stringifyUrl
};

function ownKeys$e(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$e(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$e(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$e(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

const makeRefreshTokenAndRetry = ({
  getRefreshToken,
  refreshTokenPair,
  saveRefreshToken,
  saveAccessToken,
  clearAccessToken,
  clearRefreshToken,
  onFailedRetry,
  client
}) => {
  let invalidToken = null;
  let tokenPromise = null;

  const retryRequest = (errorResponse, accessToken) => {
    const config = _objectSpread$e({}, errorResponse.config);

    config.headers.Authorization = `Token ${accessToken}`;
    return client(errorResponse.config);
  };

  return async error => {
    const {
      response: errorResponse
    } = error;
    const refreshToken = getRefreshToken();
    if (!refreshToken || !(errorResponse !== null && errorResponse !== void 0 && errorResponse.config) || !errorResponse.config.headers.Authorization) throw error;
    const currentToken = errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.config.headers.Authorization.split(' ')[1];
    if (tokenPromise && invalidToken === currentToken) try {
      const response = await tokenPromise;
      return retryRequest(errorResponse, response.accessToken);
    } catch (e) {
      throw e;
    }
    if (!invalidToken || invalidToken !== currentToken) try {
      invalidToken = currentToken;
      tokenPromise = refreshTokenPair(refreshToken);
      const response = await tokenPromise;
      saveRefreshToken(response.refreshToken);
      saveAccessToken(response.accessToken);
      return retryRequest(errorResponse, response.accessToken);
    } catch (e) {
      tokenPromise = null;
      invalidToken = null;
      clearAccessToken();
      clearRefreshToken();
      onFailedRetry === null || onFailedRetry === void 0 ? void 0 : onFailedRetry(e);
      throw e;
    }
    throw error;
  };
};

function useHistoryPush() {
  const history = useHistory();
  return useCallback(config => {
    const url = generatePath(config.route, config.params);
    const path = helpersUrl.stringifyUrl({
      url,
      query: config.query
    });
    history.push(path);
  }, [history]);
}

let id = 0;

const generateId = () => `id-${++id}`;

function useId(initialId) {
  return useMemo(() => initialId || generateId(), [initialId]);
}

const useInfiniteScroll = (handler, hasMore, isLoading, delta = 200) => {
  const wrapperRef = useRef(null);
  const anchorRef = useRef(null);
  const anchor = hasMore ? jsx("div", {
    style: {
      visibility: 'hidden'
    },
    ref: anchorRef
  }, void 0) : null;
  const handleIntersection = useCallback(entries => {
    if (entries[0].isIntersecting && !isLoading && hasMore) {
      handler();
    }
  }, [handler, isLoading, hasMore]);
  useEffect(() => {
    if (anchorRef.current) {
      const observer = new IntersectionObserver(handleIntersection, {
        root: wrapperRef.current,
        rootMargin: `${delta}px`
      });
      observer.observe(anchorRef.current);
      return () => {
        observer.disconnect();
      };
    }
  }, [wrapperRef, anchorRef, delta, handleIntersection]);
  return {
    wrapperRef,
    anchor,
    isLoading
  };
};

function useKey(ref, event, handler, key) {
  useEffect(() => {
    const node = ref.current;

    const handleKeyUp = e => {
      if (e.target === node) {
        if (key && e.key === key) {
          handler(e.key, e);
        } else if (!key) {
          handler(e.key, e);
        }
      }
    };

    if (node) {
      document.addEventListener(event, handleKeyUp);
    }

    return () => {
      if (node) {
        document.removeEventListener(event, handleKeyUp);
      }
    };
  }, [ref, handler, event, key]);
  return ref;
}

const useLockedBodyScroll = lock => {
  useEffect(() => {
    const handler = e => e.preventDefault();

    const oldBodyOverscroll = document.body.style.overflowY;
    const oldDocumentOverscroll = document.documentElement.style.overflowY;

    const apply = () => {
      document.documentElement.style.overflowY = 'hidden';
      document.body.style.overflowY = 'hidden';
      document.documentElement.addEventListener('touchmove', handler);
      document.body.addEventListener('touchmove', handler);
    };

    const clear = () => {
      document.documentElement.style.overflowY = oldBodyOverscroll;
      document.body.style.overflowY = oldDocumentOverscroll;
      document.documentElement.removeEventListener('touchmove', handler);
      document.body.removeEventListener('touchmove', handler);
    };

    if (lock) {
      const htmlOffsetY = document.documentElement.scrollTop;
      const bodyOffsetY = document.body.scrollTop;
      const root = document.getElementById('root');
      apply();
      const oldRootPosition = root.style.position;
      root.style.position = 'relative';
      root.style.top = `-${htmlOffsetY}px`;
      return () => {
        clear();
        root.style.position = oldRootPosition;
        root.style.top = '';
        document.documentElement.scrollTo({
          top: htmlOffsetY
        });
        document.body.scrollTo({
          top: bodyOffsetY
        });
      };
    }
  }, [lock]);
};

function useMatchMedia(media) {
  const [isMatches, setIsMatches] = useState(typeof window !== 'undefined' ? window.matchMedia(media).matches : false);
  useEffect(() => {
    let timeout;

    const handleWindowResize = () => {
      clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        setIsMatches(window.matchMedia(media).matches);
      }, 200);
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [media]);
  return isMatches;
}

const useIsMounted = () => {
  const ref = useRef(true);
  useDidMount(() => {
    return () => {
      ref.current = false;
    };
  });
  return ref.current;
};

function useOnClickOutside(ref, handler, openerRef) {
  useEffect(() => {
    const listener = event => {
      if (openerRef && openerRef.current) {
        const el = event.target;

        if (el === openerRef.current || openerRef.current.contains(el)) {
          return;
        }
      }

      return !ref.current || ref.current.contains(event.target) ? null : handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, openerRef]);
}

const useQuery = () => {
  const location = useLocation();
  return new URLSearchParams(location.search);
};

const usePageQuery = () => {
  const page = useQuery().get('page');
  const parsedPageNumber = page ? parseInt(page) : null;

  if (parsedPageNumber && parsedPageNumber < 1) {
    return 1;
  }

  return page ? parseInt(page) : null;
};

const usePageVisibilityChange = (onVisible, onHidden) => {
  const handleChange = useCallback(() => {
    const visibility = document.visibilityState;

    if (visibility === 'hidden') {
      onHidden();
    } else if (visibility === 'visible') {
      onVisible();
    }
  }, [onHidden, onVisible]);
  useEffect(() => {
    document.addEventListener('visibilitychange', handleChange);
    return () => {
      document.removeEventListener('visibilitychange', handleChange);
    };
  }, [handleChange]);
};

function ownKeys$d(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$d(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$d(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$d(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function useParentOffset() {
  const [parentRect, setParentRect] = useState(null);
  const [childRect, setChildRect] = useState(null);
  const [offset, setOffset] = useState(null);
  const [childNode, setChildNode] = useState();
  const [parentNode, setParentNode] = useState();
  const childNodeRef = useRef();
  const parentNodeRef = useRef();
  const resizeObserverRef = useRef();
  useEffect(() => {
    if (resizeObserverRef.current) {
      return;
    } // TODO we must create only one ResizeObserver instance for entire application


    const observer = new ResizeObserver(() => {
      if (parentNodeRef.current) {
        const node = parentNodeRef.current;
        const rect = node.getBoundingClientRect();
        const borderWidth = parseInt(getComputedStyle(node).getPropertyValue('border-left-width'), 10) || 0;
        setParentRect(_objectSpread$d(_objectSpread$d({}, rect), {}, {
          left: rect.left + borderWidth
        }));
      }

      if (childNodeRef.current) {
        setChildRect(childNodeRef.current.getBoundingClientRect());
      }
    });
    resizeObserverRef.current = observer;
    return () => {
      observer.disconnect();
    };
  }, []);
  const parentRef = useCallback(node => {
    if (node !== null) {
      const rect = node.getBoundingClientRect();
      const borderWidth = parseInt(getComputedStyle(node).getPropertyValue('border-left-width'), 10) || 0;
      setParentRect(_objectSpread$d(_objectSpread$d({}, rect), {}, {
        left: rect.left + borderWidth
      }));
      setParentNode(node);
      parentNodeRef.current = node;
    }
  }, []);
  const childRef = useCallback(node => {
    if (node !== null) {
      setChildRect(node.getBoundingClientRect());
      setChildNode(node);
      childNodeRef.current = node;
    }
  }, []);
  useEffect(() => {
    const cnode = childNode;
    const pnode = parentNode;
    const observer = resizeObserverRef.current;

    if (cnode && pnode && observer) {
      observer.observe(cnode);
      observer.observe(pnode);
      return () => {
        observer.unobserve(cnode);
        observer.unobserve(pnode);
      };
    }
  }, [childNode, parentNode]);
  useEffect(() => {
    if (parentRect && childRect) {
      setOffset({
        left: childRect.left - parentRect.left,
        top: childRect.top - parentRect.top,
        width: childRect.width,
        height: childRect.height
      });
    } else {
      setOffset(null);
    }
  }, [parentRect, childRect]);
  return [parentRef, childRef, offset];
}

const store = new Map();
const usePersistentScroll = (id, isMounted = true) => {
  const elementRef = useRef(null);
  const restoreScroll = useCallback(target => {
    const pos = store.get(id);

    if (pos) {
      target.scrollTo(...pos);
    } else {
      target.scrollTo(0, 0);
    }
  }, [id]);
  const storeScroll = useCallback(target => {
    store.set(id, [target.scrollX || target.scrollLeft || 0, target.scrollY || target.scrollTop || 0]);
  }, [id]);
  const scrollRef = useCallback(element => {
    if (element) {
      restoreScroll(element);
      elementRef.current = element;
    }
  }, [restoreScroll]);
  useEffect(() => {
    if (!isMounted) return;
    const target = elementRef.current || window;

    const handler = () => storeScroll(target);

    restoreScroll(elementRef.current || window);
    target.addEventListener('scroll', handler);
    return () => {
      target.removeEventListener('scroll', handler);
    };
  }, [isMounted, restoreScroll, storeScroll]);
  return {
    scrollRef
  };
};

const createObserver = (cb, threshold = 0.5) => {
  if ('IntersectionObserver' in window) {
    return new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        cb === null || cb === void 0 ? void 0 : cb();
      }
    }, {
      threshold
    });
  }

  return null;
};

const useInView = (cb, threshold = 0.5) => {
  const [ref, setRef] = useState(null);
  const observer = useRef(null);
  useEffect(() => {
    if (ref && cb) {
      const instance = observer.current;

      if (instance) {
        instance.observe(ref);
        return () => instance.unobserve(ref);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [ref]);
  useEffect(() => {
    if (cb) {
      const instance = createObserver(cb, threshold);
      observer.current = instance;

      if (ref && instance) {
        instance.observe(ref);
        return () => instance.disconnect();
      }
    } else if (observer.current && !cb) {
      observer.current.disconnect();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [cb, threshold]);
  return {
    ref: setRef,
    disconnect: () => {
      var _observer$current;

      return (_observer$current = observer.current) === null || _observer$current === void 0 ? void 0 : _observer$current.disconnect();
    },
    unobserve: () => {
      var _observer$current2;

      return ref && ((_observer$current2 = observer.current) === null || _observer$current2 === void 0 ? void 0 : _observer$current2.unobserve(ref));
    }
  };
};

const useScrollToTop = () => {
  useDidMount(() => {
    window.scrollTo(0, 0);
  });
};

const useScrollTrigger = (enterHandler, leaveHandler, delta = 0) => {
  const wrapperRef = useRef(null);
  const anchorRef = useRef(null);
  const anchor = useMemo(() => jsx("div", {
    style: {
      visibility: 'hidden'
    },
    ref: anchorRef
  }, void 0), [anchorRef]);
  const handleIntersection = useCallback(entries => {
    if (entries[0].isIntersecting) {
      enterHandler();
    } else if (entries[0].intersectionRatio !== 1) {
      leaveHandler();
    }
  }, [enterHandler, leaveHandler]);
  useEffect(() => {
    if (anchorRef.current) {
      const observer = new IntersectionObserver(handleIntersection, {
        root: wrapperRef.current,
        rootMargin: `${delta}px`
      });
      observer.observe(anchorRef.current);
      return () => {
        observer.disconnect();
      };
    }
  }, [wrapperRef, anchorRef, delta, handleIntersection]);
  return {
    wrapperRef,
    anchor
  };
};

const useTempBoolean = (value, timeout = 1000, lock = false) => {
  const workValue = value || lock;
  const timeoutRef = useRef();
  const [tempValue, setTempValue] = useState(workValue);
  useEffect(() => {
    if (workValue && !lock) {
      timeoutRef.current = window.setTimeout(() => {
        setTempValue(false);
      }, timeout);
    }

    setTempValue(workValue);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [workValue, lock, timeout]);
  return tempValue;
};

const useWindowResize = () => {
  const initialSize = typeof window !== 'undefined' ? [window.innerWidth, window.innerHeight] : [0, 0];
  const [size, setSize] = useState(initialSize);
  useEffect(() => {
    const handler = () => {
      setSize([window.innerWidth, window.innerHeight]);
    };

    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);
  return size;
};

function useThrottle(fn, timeout = 300) {
  const [ready, setReady] = useState(true);
  const timerRef = useRef(null);

  if (!fn || typeof fn !== 'function') {
    throw new Error('As a first argument, you need to pass a function to useThrottle hook.');
  }

  const throttledFn = useCallback((...args) => {
    if (!ready) {
      return;
    }

    setReady(false);
    fn(...args);
  }, [ready, fn]);
  useEffect(() => {
    if (!ready) {
      timerRef.current = window.setTimeout(() => {
        setReady(true);
      }, timeout);
      return () => clearTimeout(timerRef.current);
    }
  }, [ready, timeout]);
  return [throttledFn, ready];
}

const useGTM = (gtmId, dataLayer = []) => {
  useEffect(() => {
    if (!gtmId) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    } // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore


    window.dataLayer = dataLayer;
    const script = document.createElement('script');
    script.innerHTML = `
      ;(function(w, d, s, l, i) {
        w[l] = w[l] || []
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : ''
        j.async = true
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
        f.parentNode.insertBefore(j, f)
      })(window, document, 'script', 'dataLayer', '${gtmId}')
      `;
    document.head.insertBefore(script, document.head.childNodes[0]); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const useIntercom = intercomAppId => {
  const isInitialized = useRef(false);
  useEffect(() => {
    if (!intercomAppId || isInitialized.current || typeof window === 'undefined') {
      return;
    } // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore


    const script = document.createElement('script');
    script.innerHTML = `
      (function(){
        const APP_ID = '${intercomAppId}'
        window.intercomSettings = {
          app_id: APP_ID
        };
        (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + APP_ID;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
      })()
      `;
    const lastHeadNode = document.head.childNodes.length - 1;
    document.head.insertBefore(script, document.head.childNodes[lastHeadNode]);
    isInitialized.current = true;
  }, [intercomAppId]);
};

var Progress;

(function (Progress) {
  Progress["IDLE"] = "IDLE";
  Progress["WORK"] = "WORK";
  Progress["SUCCESS"] = "SUCCESS";
  Progress["ERROR"] = "ERROR";
})(Progress || (Progress = {}));

var Currency;

(function (Currency) {
  Currency["CAD"] = "CAD";
  Currency["EUR"] = "EUR";
  Currency["RUB"] = "RUB";
  Currency["USD"] = "USD";
  Currency["UAH"] = "UAH";
})(Currency || (Currency = {}));

const useDidMountWithProgress = (cb, progress) => {
  const [isMounted, setMounted] = useState(false);
  useEffect(() => {
    if (!isMounted) {
      cb(); // this timeout added just for compatibility with useAction hook.
      // see useAction hook for details.

      setTimeout(() => setMounted(true), 0);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isMounted]);
  return isMounted ? progress : Progress.WORK;
};

const updateOpts = options => {
  var _window$$zopim;

  const chat = (_window$$zopim = window.$zopim) === null || _window$$zopim === void 0 ? void 0 : _window$$zopim.livechat;
  if (!chat || !options) return;
  options.language && chat.setLanguage(options.language);
  options.email && chat.setEmail(options.email);
  options.phone && chat.setPhone(options.phone);
  options.name && chat.setName(options.name);
};
const useZendeskWidget = (key, options) => {
  var _window;

  const [isZendeskMounted, setIsZendeskMounted] = useState(typeof window === 'undefined' ? false : !!((_window = window) !== null && _window !== void 0 && _window.$zopim));
  useEffect(() => {
    if (!isZendeskMounted || !options) return;
    updateOpts(options); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options === null || options === void 0 ? void 0 : options.email, options === null || options === void 0 ? void 0 : options.phone, options === null || options === void 0 ? void 0 : options.name, options === null || options === void 0 ? void 0 : options.language]);
  useEffect(() => {
    var _window$$zopim2, _window3;

    if (!options) return;

    if (!isZendeskMounted) {
      const interval = setInterval(() => {
        var _window2;

        const current = typeof window === 'undefined' ? false : !!((_window2 = window) !== null && _window2 !== void 0 && _window2.$zopim);

        if (current) {
          setIsZendeskMounted(current);
          clearInterval(interval);
        }
      }, 100);
      return () => {
        clearInterval(interval);
      };
    }

    (_window$$zopim2 = (_window3 = window).$zopim) === null || _window$$zopim2 === void 0 ? void 0 : _window$$zopim2.call(_window3, () => {
      updateOpts(options);
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZendeskMounted]);
  useEffect(() => {
    if (typeof window === 'undefined' || !key) return;
    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
    document.head.insertBefore(script, document.head.childNodes[0]); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var _path$1b;

function _extends$1d() {
  _extends$1d = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1d.apply(this, arguments);
}

function SvgAdd(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1d({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1b || (_path$1b = /*#__PURE__*/React.createElement("path", {
    d: "M12.667 8.667h-4v4H7.334v-4h-4V7.333h4v-4h1.333v4h4v1.334z"
  })));
}

var _path$1a;

function _extends$1c() {
  _extends$1c = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1c.apply(this, arguments);
}

function SvgArrowBack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1c({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1a || (_path$1a = /*#__PURE__*/React.createElement("path", {
    d: "M17.67 3.87L15.9 2.1 6 12l9.9 9.9 1.77-1.77L9.54 12l8.13-8.13z"
  })));
}

var _path$19;

function _extends$1b() {
  _extends$1b = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1b.apply(this, arguments);
}

function SvgArrowDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1b({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$19 || (_path$19 = /*#__PURE__*/React.createElement("path", {
    d: "M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z",
    fill: "#037BFF"
  })));
}

var _path$18;

function _extends$1a() {
  _extends$1a = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1a.apply(this, arguments);
}

function SvgArrowLeftBold(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1a({
    width: 8,
    height: 12,
    viewBox: "0 0 8 12",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$18 || (_path$18 = /*#__PURE__*/React.createElement("path", {
    d: "M8 10.59L3.42 6 8 1.41 6.59 0l-6 6 6 6L8 10.59z"
  })));
}

var _path$17;

function _extends$19() {
  _extends$19 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$19.apply(this, arguments);
}

function SvgArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$19({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$17 || (_path$17 = /*#__PURE__*/React.createElement("path", {
    d: "M8 0L6.59 1.41 12.17 7H0v2h12.17l-5.58 5.59L8 16l8-8-8-8z"
  })));
}

var _path$16;

function _extends$18() {
  _extends$18 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$18.apply(this, arguments);
}

function SvgBin(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$18({
    width: 14,
    height: 18,
    viewBox: "0 0 14 18",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$16 || (_path$16 = /*#__PURE__*/React.createElement("path", {
    d: "M1 16c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V4H1v12zM3 6h8v10H3V6zm7.5-5l-1-1h-5l-1 1H0v2h14V1h-3.5z"
  })));
}

var _path$15;

function _extends$17() {
  _extends$17 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$17.apply(this, arguments);
}

function SvgBurgerMenu(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$17({
    width: 18,
    height: 14,
    viewBox: "0 0 18 14",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$15 || (_path$15 = /*#__PURE__*/React.createElement("path", {
    d: "M0 1a1 1 0 011-1h16a1 1 0 110 2H1a1 1 0 01-1-1zm0 6a1 1 0 011-1h16a1 1 0 110 2H1a1 1 0 01-1-1zm1 5a1 1 0 100 2h16a1 1 0 100-2H1z"
  })));
}

var _path$14;

function _extends$16() {
  _extends$16 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$16.apply(this, arguments);
}

function SvgCalendar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$16({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$14 || (_path$14 = /*#__PURE__*/React.createElement("path", {
    d: "M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"
  })));
}

var _path$13;

function _extends$15() {
  _extends$15 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$15.apply(this, arguments);
}

function SvgCallsHistory(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$15({
    width: 18,
    height: 18,
    viewBox: "0 0 18 18",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$13 || (_path$13 = /*#__PURE__*/React.createElement("path", {
    d: "M10 6H8v2h2V6zm4 0h-2v2h2V6zm3 6.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 00-1.02.24l-2.2 2.2a15.074 15.074 0 01-6.59-6.58l2.2-2.21c.28-.27.36-.66.25-1.01A11.36 11.36 0 015.5 1c0-.55-.45-1-1-1H1C.45 0 0 .45 0 1c0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1zM16 6v2h2V6h-2z"
  })));
}

var _path$12;

function _extends$14() {
  _extends$14 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$14.apply(this, arguments);
}

function SvgCalls(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$14({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$12 || (_path$12 = /*#__PURE__*/React.createElement("path", {
    d: "M18 11l5-5-5-5v3h-4v4h4v3zm2 4.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 00-1.02.24l-2.2 2.2a15.045 15.045 0 01-6.59-6.59l2.2-2.21a.96.96 0 00.25-1A11.36 11.36 0 018.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"
  })));
}

var _path$11;

function _extends$13() {
  _extends$13 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$13.apply(this, arguments);
}

function SvgCart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$13({
    width: 20,
    height: 20,
    viewBox: "0 0 20 20",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$11 || (_path$11 = /*#__PURE__*/React.createElement("path", {
    d: "M6 16c-1.1 0-1.99.9-1.99 2S4.9 20 6 20s2-.9 2-2-.9-2-2-2zM0 0v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H6.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0019 2H4.21l-.94-2H0zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"
  })));
}

var _path$10;

function _extends$12() {
  _extends$12 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$12.apply(this, arguments);
}

function SvgCategory(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$12({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "#A8B8CA",
    viewBox: "0 0 20 20",
    width: 20,
    height: 20
  }, props), _path$10 || (_path$10 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 0a2 2 0 012 2v16a2 2 0 01-2 2H2a2 2 0 01-2-2V2a2 2 0 012-2h16zm-1.153 14.667H6.682a.8.8 0 00-.8.8v1.066a.8.8 0 00.8.8h10.165a.8.8 0 00.8-.8v-1.066a.8.8 0 00-.8-.8zm0-6H6.682a.8.8 0 00-.8.8v1.066a.8.8 0 00.8.8h10.165a.8.8 0 00.8-.8V9.467a.8.8 0 00-.8-.8zm0-6H6.682a.8.8 0 00-.8.8v1.066a.8.8 0 00.8.8h10.165a.8.8 0 00.8-.8V3.467a.8.8 0 00-.8-.8zm-13.53 0h-.752a.8.8 0 00-.8.8v1.066a.8.8 0 00.8.8h.753a.8.8 0 00.8-.8V3.467a.8.8 0 00-.8-.8z"
  })));
}

var _path$$;

function _extends$11() {
  _extends$11 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$11.apply(this, arguments);
}

function SvgCheckCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$11({
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$$ || (_path$$ = /*#__PURE__*/React.createElement("path", {
    d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
  })));
}

var _path$_;

function _extends$10() {
  _extends$10 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$10.apply(this, arguments);
}

function SvgCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$10({
    width: 13,
    height: 11,
    viewBox: "0 0 13 11",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$_ || (_path$_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M2.968 9.445L.14 6.617l1.414-1.414L4.382 8.03 11.454.96l1.414 1.414-7.071 7.071-1.414 1.415-1.415-1.415z"
  })));
}

var _path$Z;

function _extends$$() {
  _extends$$ = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$$.apply(this, arguments);
}

function SvgCheckmark(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$$({
    width: 22,
    height: 22,
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$Z || (_path$Z = /*#__PURE__*/React.createElement("path", {
    d: "M5.131 19.396h2.102a.63.63 0 01.484.203l1.491 1.482c1.22 1.23 2.354 1.22 3.574 0l1.491-1.482a.651.651 0 01.494-.203h2.092c1.733 0 2.537-.794 2.537-2.537v-2.092c0-.203.058-.339.203-.494l1.482-1.491c1.23-1.22 1.22-2.353 0-3.574L19.6 7.718a.63.63 0 01-.203-.485V5.13c0-1.723-.794-2.537-2.537-2.537h-2.092a.642.642 0 01-.494-.194L12.782.92c-1.22-1.23-2.353-1.22-3.574 0L7.718 2.4a.622.622 0 01-.485.194H5.13c-1.733 0-2.537.794-2.537 2.537v2.102a.622.622 0 01-.194.484L.92 9.208c-1.23 1.22-1.22 2.354 0 3.574L2.4 14.273c.146.155.194.29.194.494v2.092c0 1.733.804 2.537 2.537 2.537zm4.784-3.67c-.329 0-.6-.126-.852-.465l-2.43-2.983a1.075 1.075 0 01-.233-.63c0-.445.339-.803.784-.803.272 0 .485.097.727.416L9.877 13.8l4.135-6.644c.184-.3.436-.445.697-.445.417 0 .814.29.814.736 0 .223-.126.445-.243.64L10.73 15.26c-.204.31-.484.465-.814.465z",
    fill: "#939EAA"
  })));
}

var _path$Y;

function _extends$_() {
  _extends$_ = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$_.apply(this, arguments);
}

function SvgClip(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$_({
    viewBox: "0 0 24 24",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$Y || (_path$Y = /*#__PURE__*/React.createElement("path", {
    d: "M19.571 10.586l-8.132 8.132a3.999 3.999 0 01-5.656 0 3.999 3.999 0 010-5.657l8.838-8.84a2.501 2.501 0 013.536 3.536l-7.425 7.425a1.003 1.003 0 01-1.414 0 1.003 1.003 0 010-1.414l6.718-6.718-1.061-1.06-6.718 6.717a2.5 2.5 0 003.536 3.536l7.425-7.425a3.999 3.999 0 000-5.657 3.999 3.999 0 00-5.657 0L4.72 12a5.497 5.497 0 000 7.778 5.497 5.497 0 007.779 0l8.132-8.132-1.06-1.06z"
  })));
}

var _path$X;

function _extends$Z() {
  _extends$Z = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$Z.apply(this, arguments);
}

function SvgCollapse(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Z({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$X || (_path$X = /*#__PURE__*/React.createElement("path", {
    d: "M14.61 7.405h-4.477c-.495 0-.88-.137-1.155-.41-.28-.279-.42-.667-.42-1.166V1.48A.92.92 0 018.84.788a.982.982 0 01.712-.273c.28 0 .514.09.703.273a.918.918 0 01.274.691v.499l-.178 2.315 1.714-1.784L14.27.289A.934.934 0 0114.966 0c.307 0 .557.096.751.29.189.187.283.43.283.731 0 .134-.024.26-.073.378-.054.118-.129.225-.226.322l-2.214 2.203-1.802 1.696 2.343-.177h.582c.28 0 .515.094.703.282a.945.945 0 01.275.7c0 .283-.092.519-.275.707a.972.972 0 01-.703.273zM6.49 15.518a.972.972 0 01-.703-.274.93.93 0 01-.283-.7v-.554l.178-2.316-1.705 1.793-2.247 2.252a.93.93 0 01-.695.281 1.01 1.01 0 01-.743-.29.963.963 0 01-.291-.723c0-.14.027-.268.08-.386.055-.113.127-.217.219-.314l2.255-2.243 1.81-1.696-2.352.169h-.646a.944.944 0 01-.695-.274.92.92 0 01-.283-.691.94.94 0 01.283-.708.944.944 0 01.695-.273h4.533c.501 0 .889.137 1.164.41.274.273.412.657.412 1.15v4.414a.93.93 0 01-.283.7.972.972 0 01-.703.273z",
    fill: "#586776"
  })));
}

var _path$W;

function _extends$Y() {
  _extends$Y = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$Y.apply(this, arguments);
}

function SvgCompany(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Y({
    width: 20,
    height: 19,
    viewBox: "0 0 20 19",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$W || (_path$W = /*#__PURE__*/React.createElement("path", {
    d: "M18 4h-4V2c0-1.11-.89-2-2-2H8C6.89 0 6 .89 6 2v2H2C.89 4 .01 4.89.01 6L0 17c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm-6 0H8V2h4v2z"
  })));
}

var _path$V, _path2$2;

function _extends$X() {
  _extends$X = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$X.apply(this, arguments);
}

function SvgCrops(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$X({
    width: 21,
    height: 20,
    viewBox: "0 0 21 20",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$V || (_path$V = /*#__PURE__*/React.createElement("path", {
    d: "M10 10a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4zM10.001 2.4a.557.557 0 01-.56-.56V.56c0-.312.248-.56.56-.56.312 0 .56.248.56.56v1.28c0 .312-.248.56-.56.56zM14.398 6.8c0-.312.248-.56.56-.56h1.28c.312 0 .56.248.56.56 0 .312-.248.56-.56.56h-1.28a.557.557 0 01-.56-.56zM3.2 6.8c0-.312.247-.56.56-.56h1.28c.311 0 .56.248.56.56 0 .312-.249.56-.56.56H3.76a.557.557 0 01-.56-.56zM13.111 3.688a.565.565 0 010-.792l.904-.904a.565.565 0 01.792 0 .565.565 0 010 .792l-.904.904a.565.565 0 01-.792 0zM6.89 3.688a.565.565 0 01-.793 0l-.904-.904a.565.565 0 010-.792.565.565 0 01.792 0l.904.904a.565.565 0 010 .792zM1.266 14.064c1.712 3.064 4.976 5.136 8.736 5.136.288 0 .56-.016.84-.04a13.939 13.939 0 00-9.576-5.096zM0 9.2c0 .552.056 1.08.144 1.608 5.816.048 10.952 3 14.032 7.472.488-.224.968-.48 1.408-.784C12.2 12.496 6.48 9.2 0 9.2z"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/React.createElement("path", {
    d: "M.305 11.616c.136.56.32 1.104.552 1.632a14.758 14.758 0 0110.888 5.792 10.396 10.396 0 001.664-.448C10.497 14.448 5.72 11.72.305 11.616zM12.21 12.68a17.089 17.089 0 017.648-1.872 9.692 9.692 0 00.144-1.608c-3.36 0-6.504.888-9.24 2.44.504.328.984.672 1.448 1.04zM19.695 11.616c-2.44.048-4.744.632-6.816 1.632.432.376.856.776 1.256 1.192a14.681 14.681 0 015-1.192c.304-.696.6-1.816.56-1.632zM18.735 14.064a13.86 13.86 0 00-4.032.992 19.124 19.124 0 011.52 1.96 10.254 10.254 0 002.512-2.952z"
  })));
}

var _path$U;

function _extends$W() {
  _extends$W = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$W.apply(this, arguments);
}

function SvgCrossCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$W({
    width: 14,
    height: 14,
    viewBox: "0 0 14 14",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$U || (_path$U = /*#__PURE__*/React.createElement("path", {
    d: "M7 .333A6.66 6.66 0 00.333 7 6.66 6.66 0 007 13.667 6.66 6.66 0 0013.666 7 6.66 6.66 0 007 .333zm3.333 9.06l-.94.94L7 7.94l-2.394 2.393-.94-.94L6.06 7 3.666 4.607l.94-.94L7 6.06l2.393-2.393.94.94L7.94 7l2.393 2.393z"
  })));
}

var _path$T;

function _extends$V() {
  _extends$V = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$V.apply(this, arguments);
}

function SvgCross(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$V({
    width: 14,
    height: 14,
    viewBox: "0 0 14 14",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$T || (_path$T = /*#__PURE__*/React.createElement("path", {
    d: "M14 1.41L12.59 0 7 5.59 1.41 0 0 1.41 5.59 7 0 12.59 1.41 14 7 8.41 12.59 14 14 12.59 8.41 7 14 1.41z"
  })));
}

var _path$S;

function _extends$U() {
  _extends$U = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$U.apply(this, arguments);
}

function SvgDashboard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$U({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$S || (_path$S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11 13H3V3h8v10zm0 8H3v-6h8v6zm2 0h8V11h-8v10zm0-12V3h8v6h-8z"
  })));
}

var _path$R;

function _extends$T() {
  _extends$T = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$T.apply(this, arguments);
}

function SvgDeal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$T({
    fill: "#A8B8CA",
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$R || (_path$R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.774 0a.63.63 0 00-.631.63v1.892a.63.63 0 101.263 0V.631A.63.63 0 0011.774 0zM8.433 3.968L7.17 2.707a.632.632 0 00-.893.892l1.263 1.26a.632.632 0 00.893-.891zm7.945-1.261a.632.632 0 01.893.892l-1.262 1.26a.632.632 0 01-.893-.891l1.262-1.261zm5.54 6.12H19.98v8.828h1.936a.63.63 0 00.631-.63V9.458a.63.63 0 00-.63-.63zM1 9.459a.63.63 0 01.631-.63h1.936v8.827H1.631a.63.63 0 01-.631-.63V9.458zm10.292 0c-.481 0-.914.267-1.13.697l-1.92 3.84a2.51 2.51 0 003.267-1.185l.415-.83h1.221c.333 0 .658.135.893.37l4.68 4.675V9.458h-7.426zm1.412 3.784h.441l4.485 4.478a1.827 1.827 0 01-.816.495l-5.391 1.693a1.874 1.874 0 01-1.91-.466l-1.788-1.787H4.83V8.828h1.113c.24 0 .458-.135.565-.35l.565-1.127a1.895 1.895 0 011.694-1.046h4.901a.63.63 0 01.632.631c0 .452-.109.874-.283 1.261h-2.725c-.957 0-1.831.54-2.259 1.395L6.812 14.03c-.16.32-.019.71.308.859.804.365 1.481.646 2.46.566 1.328-.108 2.491-.95 3.088-2.14l.036-.073z"
  })));
}

var _path$Q;

function _extends$S() {
  _extends$S = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$S.apply(this, arguments);
}

function SvgDocument(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$S({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    fill: "#037BFF"
  }, props), _path$Q || (_path$Q = /*#__PURE__*/React.createElement("path", {
    d: "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
  })));
}

var _path$P;

function _extends$R() {
  _extends$R = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$R.apply(this, arguments);
}

function SvgDownload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$R({
    width: 18,
    height: 18,
    xmlns: "http://www.w3.org/2000/svg",
    fill: "#FFF",
    viewBox: "0 0 29.978 29.978"
  }, props), _path$P || (_path$P = /*#__PURE__*/React.createElement("path", {
    d: "M25.462 19.105v6.848H4.515v-6.848H.489v8.861c0 1.111.9 2.012 2.016 2.012h24.967c1.115 0 2.016-.9 2.016-2.012v-8.861h-4.026zm-10.842-.679l-5.764-6.965s-.877-.828.074-.828h3.248V.494S12.049 0 12.793 0h4.572c.536 0 .524.416.524.416v10.008h2.998c1.154 0 .285.867.285.867s-4.904 6.51-5.588 7.193c-.492.495-.964-.058-.964-.058z"
  })));
}

var _path$O;

function _extends$Q() {
  _extends$Q = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$Q.apply(this, arguments);
}

function SvgExit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Q({
    width: 18,
    height: 18,
    viewBox: "0 0 18 18",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$O || (_path$O = /*#__PURE__*/React.createElement("path", {
    d: "M7.09 12.59L8.5 14l5-5-5-5-1.41 1.41L9.67 8H0v2h9.67l-2.58 2.59zM16 0H2a2 2 0 00-2 2v4h2V2h14v14H2v-4H0v4a2 2 0 002 2h14c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2z"
  })));
}

var _path$N;

function _extends$P() {
  _extends$P = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$P.apply(this, arguments);
}

function SvgExpand(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$P({
    width: 13,
    height: 13,
    viewBox: "0 0 13 13",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$N || (_path$N = /*#__PURE__*/React.createElement("path", {
    d: "M12.24 5.307a.721.721 0 01-.531-.21.718.718 0 01-.217-.538v-.384l.13-1.781-1.305 1.38-1.695 1.7a.71.71 0 01-.532.217.783.783 0 01-.57-.217.75.75 0 01-.222-.563.793.793 0 01.229-.538l1.701-1.695 1.38-1.305-1.8.136h-.44a.729.729 0 01-.538-.21.742.742 0 01-.21-.538c0-.219.07-.4.21-.545A.718.718 0 018.368 0h3.42c.38 0 .677.107.892.322.21.21.315.505.315.884V4.56a.73.73 0 01-.754.748zm-6.612 6.7h-3.42c-.384 0-.68-.106-.891-.316-.215-.21-.322-.505-.322-.885V7.447c0-.21.074-.385.223-.525a.73.73 0 01.538-.217c.214 0 .394.07.538.21a.71.71 0 01.217.532v.39L2.374 9.62l1.306-1.38 1.695-1.7a.71.71 0 01.531-.217c.227 0 .417.072.57.216a.75.75 0 01.222.563c0 .103-.02.2-.062.29a.815.815 0 01-.167.242l-1.7 1.695-1.386 1.305 1.8-.13h.445c.219 0 .398.07.538.21.14.145.21.324.21.539a.729.729 0 01-.748.754z",
    fill: "#586776"
  })));
}

var _path$M;

function _extends$O() {
  _extends$O = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$O.apply(this, arguments);
}

function SvgEye(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$O({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$M || (_path$M = /*#__PURE__*/React.createElement("path", {
    d: "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
  })));
}

var _path$L;

function _extends$N() {
  _extends$N = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$N.apply(this, arguments);
}

function SvgFarmerOrders(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$N({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$L || (_path$L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M12 6h8c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2l.01-12c0-1.1.89-2 1.99-2h6l2 2zm4.546 5.938c-.327 2.208-3.427 2.484-3.627.018-.709-.233-1.212-.621-1.212-1.169l1.397-.392L13.66 9l2.091.119.564 1.345 1.312.37c0 .52-.442.887-1.082 1.113v-.009zm-4.477 2.69S11.28 15.59 11 17h8c-.326-1.681-1.146-2.47-1.146-2.47l-1.301-.52-2.191 1.664-1.171-1.476-1.122.43z"
  })));
}

var _path$K;

function _extends$M() {
  _extends$M = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$M.apply(this, arguments);
}

function SvgFarmerPlans(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$M({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$K || (_path$K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M1 4a3 3 0 013-3h16a3 3 0 013 3v16a3 3 0 01-3 3H4a3 3 0 01-3-3V4zm6.41 18H5a3.02 3.02 0 01-.387-.025l5.797-5.805 2 2 4.88-4.88L15 11h6v6l-2.29-2.29-6.3 6.29-2-2-3 3zM6.359 8.172c.35 4.316 5.773 3.833 6.347-.03v.015C13.826 7.761 14.6 7.12 14.6 6.21l-2.297-.648-.987-2.354L7.657 3l-.975 2.44-2.445.687c0 .959.88 1.638 2.122 2.045zm-2.854 7.122c.206-.52.433-.973.642-1.343.425-.754 1.149-1.264 1.956-1.575l.73-.28L9 13.5l3.41-1.404c1.407-.124 2.288.288 3.222 1.438l-3.222 3.222-2-2L5.5 19.5c-.902-.109-2.5-1.078-2.5-2 0-.978.048-1.093.31-1.724.055-.133.12-.29.195-.482z",
    fill: "#939EAA"
  })));
}

var _path$J;

function _extends$L() {
  _extends$L = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$L.apply(this, arguments);
}

function SvgFcc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$L({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$J || (_path$J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6 2a4 4 0 00-4 4v12c0 .229.02.453.056.672 1.49-1.59 2.606-3.42 2.606-3.42l3.099-5.257-2.328-.004 1.108-1.225 2.023-.013c2.664-3.204 4.207-3.902 4.207-3.902.437-.181.877-.34 1.356-.35 1.012-.022 2.018.776 1.724 1.88a.695.695 0 01-.598.509.355.355 0 01-.287-.103c-.097-.107-.092-.26-.086-.413.005-.152.011-.305-.086-.412a.371.371 0 00-.302-.105c-.505.034-1.05.385-1.422.702-1.64 1.402-2.065 2.229-2.065 2.229l1.81-.04-1.117 1.233-1.436.028c-.765.986-2.84 4.567-2.84 4.567-2.662 4.048-4.76 5.383-4.76 5.383l-.106.076A3.998 3.998 0 006 22h12a4 4 0 004-4V6a4 4 0 00-4-4H6zm13.336 7.988c-1.422-.485-2.896.405-3.136.55l-.017.01a1.843 1.843 0 00-.155.11c-.4-.485-.954-.67-.954-.67-.562-.192-1.171-.176-1.743-.036a4.722 4.722 0 00-1.338.554c-.315.193-.597.439-.876.681l-.126.11c-1.611 1.48-2.101 2.796-2.101 2.796-.338.79-.499 1.78-.217 2.61.156.46.46.873.885 1.13.223.135.486.197.747.207.584.023 1.176-.162 1.673-.461.085-.052.14-.096.209-.15l.056-.045a2.546 2.546 0 01-.387-.729h-.002c-.3.06-.708.142-.984-.038a.6.6 0 01-.233-.335 1.504 1.504 0 01-.056-.504 2.093 2.093 0 01.068-.43c.1-.482.299-.932.503-1.3.41-.828 1.053-1.537 1.536-1.995 1.28-1.3 2.148-1.098 2.148-1.098.21.03.365.14.475.292-.036.03-.057.05-.057.05-1.612 1.48-2.102 2.796-2.102 2.796-1.09 2.55.64 3.733.64 3.733 1.246.945 3.032-.088 3.702-.543.07-.059.241-.235.09-.49 0 0-.18-.347-.849-.125-.582.16-.995.16-.995.16-1.256-.026-.83-1.48-.83-1.48.1-.482.3-.932.504-1.3.41-.828 1.053-1.537 1.537-1.995 1.28-1.3 2.147-1.098 2.147-1.098.358.05.563.332.638.667.036.16.047.326.044.49-.003.175-.001.332.159.441.154.106.337.073.476-.036.173-.136.245-.36.27-.57.117-1.47-1.349-1.96-1.349-1.96z"
  })));
}

var _path$I;

function _extends$K() {
  _extends$K = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$K.apply(this, arguments);
}

function SvgFilter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$K({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$I || (_path$I = /*#__PURE__*/React.createElement("path", {
    d: "M17 21h2v-6h-2v6zM5 21h2V11H5v10zm16-10h-2V3h-2v8h-2v2h6v-2zM9 17h2v4h2v-4h2v-2H9v2zm4-14h-2v10h2V3zM9 9V7H7V3H5v4H3v2h6z"
  })));
}

var _path$H;

function _extends$J() {
  _extends$J = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$J.apply(this, arguments);
}

function SvgImage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$J({
    width: 24,
    height: 24,
    viewBox: "0 0 48 48",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$H || (_path$H = /*#__PURE__*/React.createElement("path", {
    d: "M42 38V10c0-2.2-1.8-4-4-4H10c-2.2 0-4 1.8-4 4v28c0 2.2 1.8 4 4 4h28c2.2 0 4-1.8 4-4zM17 27l5 6.02L29 24l9 12H10l7-9z"
  })));
}

var _path$G;

function _extends$I() {
  _extends$I = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$I.apply(this, arguments);
}

function SvgInvoice(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$I({
    width: 24,
    height: 24,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$G || (_path$G = /*#__PURE__*/React.createElement("path", {
    d: "M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z"
  })));
}

var _path$F, _defs$1;

function _extends$H() {
  _extends$H = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$H.apply(this, arguments);
}

function SvgLeaf(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$H({
    width: 24,
    height: 26,
    viewBox: "0 0 24 26",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$F || (_path$F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M12.29 20.432L12.58 26h-1.16l.29-5.568a.614.614 0 00-.717-.632l-5.541.974.748-2.064a.42.42 0 00-.129-.471L0 13.323l1.368-.64a.42.42 0 00.22-.509l-1.2-3.69 3.496.742a.42.42 0 00.47-.245l.678-1.594 2.73 2.93a.42.42 0 00.715-.369L7.161 3.161l2.11 1.22a.42.42 0 00.587-.175L12 0l2.142 4.206a.42.42 0 00.587.175l2.11-1.22-1.316 6.787a.419.419 0 00.716.368l2.729-2.929.677 1.594a.42.42 0 00.471.245l3.497-.742-1.2 3.69a.42.42 0 00.22.51l1.367.639-6.071 4.916a.419.419 0 00-.129.47l.748 2.065-5.541-.974a.614.614 0 00-.717.632z",
    fill: "url(#leaf_svg__paint0_linear)"
  })), _defs$1 || (_defs$1 = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
    id: "leaf_svg__paint0_linear",
    x1: 2.547,
    y1: 2.68,
    x2: 16.307,
    y2: 18.067,
    gradientUnits: "userSpaceOnUse"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "#F94F6D"
  }), /*#__PURE__*/React.createElement("stop", {
    offset: 1,
    stopColor: "#C73232"
  })))));
}

var _path$E;

function _extends$G() {
  _extends$G = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$G.apply(this, arguments);
}

function SvgLinkOpener(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$G({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$E || (_path$E = /*#__PURE__*/React.createElement("path", {
    d: "M12.667 12.667H3.333V3.333H8V2H3.333C2.593 2 2 2.6 2 3.333v9.334C2 13.4 2.593 14 3.333 14h9.334C13.4 14 14 13.4 14 12.667V8h-1.333v4.667zM9.333 2v1.333h2.394L5.173 9.887l.94.94 6.554-6.554v2.394H14V2H9.333z"
  })));
}

var _path$D;

function _extends$F() {
  _extends$F = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$F.apply(this, arguments);
}

function SvgLink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$F({
    width: 24,
    height: 24,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$D || (_path$D = /*#__PURE__*/React.createElement("path", {
    d: "M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.71-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z",
    fill: "#037BFF"
  })));
}

var _path$C;

function _extends$E() {
  _extends$E = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$E.apply(this, arguments);
}

function SvgLoader(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$E({
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$C || (_path$C = /*#__PURE__*/React.createElement("path", {
    d: "M22 15.867l-.105-6.214c-1.403 6.34-7.309 8.237-9.32 8.446 6.2 1.73 9.425-2.232 9.425-2.232zm-.733-8.841l-4.921-3.837c4.083 5.088 1.822 10.78.732 12.449 5.236-3.712 4.189-8.612 4.189-8.612zM13.812 2L7.717 3.48c6.555-.062 9.739 5.235 10.388 7.111C18.419 4.212 13.812 2 13.812 2zM5.309 4.648l-2.66 5.63c4.063-5.087 10.178-4.19 12.063-3.503-4.796-4.254-9.403-2.127-9.403-2.127zM2 12.99l2.911 5.505C3.277 12.218 7.822 8.005 9.497 6.92 3.194 8.172 2 12.99 2 12.99zm4.398 7.61l6.136 1.273C6.607 19.12 6.23 12.99 6.461 11.008c-3.037 5.61-.063 9.593-.063 9.593zm1.424-4.483c1.424 1.398 6.66 4.734 12.524 1.815l-4.796 4.066c0 .02-5.173-.02-7.728-5.88z"
  })));
}

var _path$B;

function _extends$D() {
  _extends$D = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$D.apply(this, arguments);
}

function SvgLocation(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$D({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$B || (_path$B = /*#__PURE__*/React.createElement("path", {
    d: "M12 23V12H1L23 1z"
  })));
}

var _path$A;

function _extends$C() {
  _extends$C = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$C.apply(this, arguments);
}

function SvgMagicWand(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$C({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$A || (_path$A = /*#__PURE__*/React.createElement("path", {
    d: "M18 8a2 2 0 012-2 2 2 0 01-2-2 2 2 0 01-2 2 2 2 0 012 2zM18.5 10a1.5 1.5 0 01-1.5 1.5 1.5 1.5 0 011.5 1.5 1.5 1.5 0 011.5-1.5 1.5 1.5 0 01-1.5-1.5zM11 10a3 3 0 013-3 3 3 0 01-3-3 3 3 0 01-3 3 3 3 0 013 3zM15 7L4 18l2 2L17 9l-2-2zm-1.707 3.207l1.414-1.414.5.5-1.414 1.414-.5-.5z",
    fill: "#037BFF"
  })));
}

var _path$z;

function _extends$B() {
  _extends$B = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$B.apply(this, arguments);
}

function SvgMailing(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$B({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$z || (_path$z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3 3h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2l.01-10c0-1.1.89-2 1.99-2zm7 8l7-4V5l-7 4-7-4v2l7 4zm13-3h-2v11H6v2h15c1.1 0 2-.9 2-2V8z"
  })));
}

var _path$y;

function _extends$A() {
  _extends$A = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$A.apply(this, arguments);
}

function SvgMarketingCampaigns(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$A({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$y || (_path$y = /*#__PURE__*/React.createElement("path", {
    d: "M18 18.396c0 1.12.902 1.879 2 1.879 1.116 0 2-.76 2-1.88V3.889C22 2.758 21.116 2 20 2c-1.098 0-2 .759-2 1.888v14.508zM9 15.64c2.652.317 5.509 1.102 8 2.394V4.313c-2.509 1.3-5.58 2.122-8 2.393v8.934zM5.866 6.707C3.34 6.706 2 8.026 2 10.482v1.373c0 2.458 1.34 3.776 3.866 3.776H8V6.706H5.866zM9.563 22c1.133 0 1.964-.795 1.67-2.213l-.59-2.819c-.456-.18-1.866-.28-2.554-.289H5.795l1.687 3.704C7.955 21.413 8.5 22 9.562 22z",
    fill: "#939EAA"
  })));
}

var _path$x;

function _extends$z() {
  _extends$z = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$z.apply(this, arguments);
}

function SvgMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$z({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$x || (_path$x = /*#__PURE__*/React.createElement("path", {
    d: "M19 13H5v-2h14v2z"
  })));
}

var _path$w;

function _extends$y() {
  _extends$y = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$y.apply(this, arguments);
}

function SvgMoreVert(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$y({
    width: 24,
    height: 24,
    viewBox: "0 0 4 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$w || (_path$w = /*#__PURE__*/React.createElement("path", {
    d: "M2 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2C.9 6 0 6.9 0 8s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z",
    fill: "#939EAA"
  })));
}

var _path$v;

function _extends$x() {
  _extends$x = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$x.apply(this, arguments);
}

function SvgOrder(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$x({
    width: 18,
    height: 20,
    viewBox: "0 0 18 20",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$v || (_path$v = /*#__PURE__*/React.createElement("path", {
    d: "M16 2h-4.18C11.4.84 10.3 0 9 0 7.7 0 6.6.84 6.18 2H2C.9 2 0 2.9 0 4v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H4v-2h7v2zm3-4H4v-2h10v2zm0-4H4V6h10v2z"
  })));
}

var _path$u;

function _extends$w() {
  _extends$w = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$w.apply(this, arguments);
}

function SvgOrders(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    width: 20,
    height: 16,
    viewBox: "0 0 20 16",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$u || (_path$u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M10 2h8c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2L.01 2C.01.9.9 0 2 0h6l2 2zm.93 4.877C10.93 5.582 11.801 5 12.7 5c1.02 0 2.071.752 1.907 2.064-.297 2.371-3.678 2.618-3.678-.187zM8.5 13H17c-.205-1.175-.389-2.454-.389-2.454-.568-.518-1.447-.855-2.42-1.018L12.808 11.5 11.42 9.515c-1.022.162-1.929.512-2.463 1.046 0 0-.224 1.269-.457 2.439z"
  })));
}

var _path$t;

function _extends$v() {
  _extends$v = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$v.apply(this, arguments);
}

function SvgPencil(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    width: 19,
    height: 19,
    viewBox: "0 0 19 19",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$t || (_path$t = /*#__PURE__*/React.createElement("path", {
    d: "M0 15.25V19h3.75L14.81 7.94l-3.75-3.75L0 15.25zM17.71 5.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
  })));
}

var _path$s;

function _extends$u() {
  _extends$u = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$u.apply(this, arguments);
}

function SvgPickup(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$u({
    width: 24,
    height: 16,
    viewBox: "0 0 24 16",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$s || (_path$s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M17.2.6l3.675 4.9H22a2 2 0 012 2V12a1 1 0 01-1 1h-2c0 1.7-1.3 3-3 3s-3-1.3-3-3H9c0 1.7-1.3 3-3 3s-3-1.3-3-3H1a1 1 0 01-1-1V6.5a1 1 0 011-1h9.5V1a1 1 0 011-1h4.765a1 1 0 01.832.445L17.2.6zm-1.2.9l3 4h-7v-4h4zm-10 13c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5zm12 0c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5zM12 7h2v1h-2V7z"
  })));
}

var _circle$1, _rect, _rect2;

function _extends$t() {
  _extends$t = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$t.apply(this, arguments);
}

function SvgPlusCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    width: 18,
    height: 18,
    viewBox: "0 0 18 18",
    fill: "#fff",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _circle$1 || (_circle$1 = /*#__PURE__*/React.createElement("circle", {
    cx: 9,
    cy: 9,
    r: 9
  })), _rect || (_rect = /*#__PURE__*/React.createElement("rect", {
    x: 8,
    y: 4,
    width: 2,
    height: 10,
    rx: 1,
    fill: "currentColor"
  })), _rect2 || (_rect2 = /*#__PURE__*/React.createElement("rect", {
    x: 14,
    y: 8,
    width: 2,
    height: 10,
    rx: 1,
    transform: "rotate(90 14 8)",
    fill: "currentColor"
  })));
}

var _path$r;

function _extends$s() {
  _extends$s = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$s.apply(this, arguments);
}

function SvgPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$r || (_path$r = /*#__PURE__*/React.createElement("path", {
    d: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
  })));
}

var _path$q;

function _extends$r() {
  _extends$r = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$r.apply(this, arguments);
}

function SvgPrint(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$q || (_path$q = /*#__PURE__*/React.createElement("path", {
    d: "M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"
  })));
}

var _path$p;

function _extends$q() {
  _extends$q = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$q.apply(this, arguments);
}

function SvgProducts(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    width: 20,
    height: 20,
    viewBox: "0 0 20 20",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$p || (_path$p = /*#__PURE__*/React.createElement("path", {
    d: "M19.41 9.58l-9-9C10.05.22 9.55 0 9 0H2C.9 0 0 .9 0 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM3.5 5C2.67 5 2 4.33 2 3.5S2.67 2 3.5 2 5 2.67 5 3.5 4.33 5 3.5 5z"
  })));
}

var _path$o;

function _extends$p() {
  _extends$p = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$p.apply(this, arguments);
}

function SvgPromocodes(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    xmlns: "http://www.w3.org/2000/svg",
    width: 21,
    height: 21,
    viewBox: "0 0 21 21",
    fill: "#A8B8CA"
  }, props), _path$o || (_path$o = /*#__PURE__*/React.createElement("path", {
    d: "M18.52.253c1.1 0 2 .9 2 2v7c0 .55-.22 1.05-.59 1.42l-9 9c-.36.36-.86.58-1.41.58-.55 0-1.05-.22-1.41-.59l-7-7c-.37-.36-.59-.86-.59-1.41 0-.55.23-1.06.59-1.42l9-9c.36-.36.86-.58 1.41-.58h7zM9.344 17.172a.5.5 0 00-.72.695l.695.719a.5.5 0 00.72-.695zM7.22 15.051a.5.5 0 10-.719.694l.695.72a.5.5 0 10.719-.695zm4.053-4.058c-.406.01-.77.155-1.093.439l-.136.13-.293.303c-.377.39-.56.818-.548 1.283.013.466.201.875.567 1.227.365.353.78.524 1.244.513.406-.01.77-.155 1.093-.438l.136-.13.289-.3c.377-.39.561-.817.553-1.281-.008-.464-.196-.874-.564-1.23-.368-.355-.784-.527-1.248-.516zM5.1 12.929a.5.5 0 00-.72.695l.695.72a.5.5 0 00.72-.695zm6.93-.91c.204.198.3.427.287.685a1.054 1.054 0 01-.243.609l-.099.112-.301.312c-.23.239-.474.37-.729.392a.847.847 0 01-.686-.26c-.208-.2-.305-.431-.292-.693a1.06 1.06 0 01.243-.612l.099-.112.301-.312c.228-.236.472-.364.731-.382a.868.868 0 01.688.262zm-9.051-1.211a.5.5 0 10-.72.695l.695.719a.5.5 0 10.72-.695zM14.947 8.5L7.32 10.403l.174.705 7.628-1.902-.174-.706zm-3.326-3.847c-.407.01-.772.157-1.095.44l-.136.13-.285.295c-.377.39-.56.819-.547 1.284.012.465.2.874.566 1.227.362.35.774.521 1.235.513a1.66 1.66 0 001.093-.438l.137-.13.288-.3c.37-.382.554-.805.554-1.269 0-.464-.186-.877-.56-1.238-.368-.355-.785-.527-1.25-.514zm.757 1.025c.209.2.304.43.286.69-.014.217-.097.42-.246.608l-.098.111-.297.308c-.23.239-.473.368-.729.388a.853.853 0 01-.682-.26c-.208-.2-.305-.431-.291-.693.01-.218.09-.42.24-.61l.097-.11.297-.308c.223-.23.464-.36.723-.386.259-.026.493.06.7.261zm4.643-3.425c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"
  })));
}

var _path$n;

function _extends$o() {
  _extends$o = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$o.apply(this, arguments);
}

function SvgQuestion(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    width: 20,
    height: 20,
    viewBox: "0 0 20 20",
    fill: "#A8B8CA",
    fillOpacity: 0.96,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$n || (_path$n = /*#__PURE__*/React.createElement("path", {
    d: "M9 16h2v-2H9v2zm1-16C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14C7.79 4 6 5.79 6 8h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"
  })));
}

var _path$m;

function _extends$n() {
  _extends$n = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$n.apply(this, arguments);
}

function SvgRefresh(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$m || (_path$m = /*#__PURE__*/React.createElement("path", {
    d: "M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8A5.87 5.87 0 016 12c0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z",
    fill: "#037BFF"
  })));
}

var _path$l;

function _extends$m() {
  _extends$m = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$m.apply(this, arguments);
}

function SvgSalesTeamPlans(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M1 4a3 3 0 013-3h16a3 3 0 013 3v16a3 3 0 01-3 3H4a3 3 0 01-3-3V4zm6.41 18H5a3.02 3.02 0 01-.387-.025l5.797-5.805 2 2 4.88-4.88L15 11h6v6l-2.29-2.29-6.3 6.29-2-2-3 3zm.544-10.741c.233.252.35.594.35 1.025 0 .415-.13.746-.388.994-.258.242-.61.362-1.056.362-.537 0-.95-.165-1.238-.497-.284-.336-.426-.817-.426-1.443H3c0 1.078.284 1.943.851 2.595.573.647 1.378 1.028 2.416 1.143V17h1.209v-1.57c.942-.1 1.682-.428 2.219-.985.537-.558.805-1.283.805-2.177 0-.478-.07-.896-.213-1.254a2.889 2.889 0 00-.615-.962 4.182 4.182 0 00-.988-.757c-.39-.226-.89-.465-1.497-.718-.603-.252-1.016-.5-1.239-.741-.223-.242-.334-.563-.334-.962 0-.431.114-.768.342-1.01.228-.247.55-.37.965-.37.42 0 .755.157 1.003.473.253.31.38.78.38 1.412H10.5c0-1.026-.253-1.859-.76-2.5-.506-.647-1.208-1.036-2.105-1.168V2H6.42v1.688c-.906.094-1.633.426-2.18.993-.548.568-.821 1.296-.821 2.185 0 .515.078.96.236 1.333.162.373.385.699.668.978a4.81 4.81 0 001.034.733c.4.21.876.423 1.428.639.552.216.942.452 1.17.71z",
    fill: "#939EAA"
  })));
}

var _path$k;

function _extends$l() {
  _extends$l = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$l.apply(this, arguments);
}

function SvgSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    width: 18,
    height: 18,
    viewBox: "0 0 18 18",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    d: "M12.5 11h-.79l-.28-.27A6.471 6.471 0 0013 6.5 6.5 6.5 0 106.5 13c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L17.49 16l-4.99-5zm-6 0C4.01 11 2 8.99 2 6.5S4.01 2 6.5 2 11 4.01 11 6.5 8.99 11 6.5 11z"
  })));
}

var _path$j, _path2$1;

function _extends$k() {
  _extends$k = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$k.apply(this, arguments);
}

function SvgSendEmail(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 32 32"
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    d: "M23 15c-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.7 8.7l-3.1 3c-.2.2-.4.3-.7.3-.3 0-.5-.1-.7-.3-.4-.4-.4-1 0-1.4l1.3-1.3H19c-.6 0-1-.4-1-1s.4-1 1-1h4.5l-1.3-1.3c-.4-.4-.4-1 0-1.4.4-.4 1-.4 1.4 0l3.1 3c.2.2.3.4.3.7s-.1.5-.3.7z"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    d: "M13 23c0-1.4.3-2.8.9-4-.2 0-.4-.1-.5-.2l-1.6-1.4-5.1 4.4c-.2.1-.5.2-.7.2-.3 0-.6-.1-.8-.3-.4-.4-.3-1 .1-1.4l4.9-4.2-4.9-4.2c-.4-.4-.5-1-.1-1.4.4-.4 1-.5 1.4-.1l7.3 6.4 7.3-6.4c.4-.4 1-.3 1.4.1.4.4.3 1-.1 1.4L21 13.2c.6-.1 1.3-.2 2-.2 1.4 0 2.8.3 4 .8V9c0-1.7-1.3-3-3-3H4C2.3 6 1 7.3 1 9v14c0 1.7 1.3 3 3 3h9.5c-.3-.9-.5-2-.5-3z"
  })));
}

var _path$i;

function _extends$j() {
  _extends$j = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$j.apply(this, arguments);
}

function SvgSettings(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    d: "M17 21h2v-6h-2v6zM5 21h2V11H5v10zm16-10h-2V3h-2v8h-2v2h6v-2zM9 17h2v4h2v-4h2v-2H9v2zm4-14h-2v10h2V3zM9 9V7H7V3H5v4H3v2h6z"
  })));
}

var _g, _defs;

function _extends$i() {
  _extends$i = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$i.apply(this, arguments);
}

function SvgSign(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    width: 25,
    height: 24,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    clipPath: "url(#sign_svg__clip0)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M1.139 18.664h5.207a3.773 3.773 0 00-.072.718c0 1.286.727 2.205 2.229 2.205 1.709 0 3.115-1.118 4.073-2.923h11.277a.655.655 0 00.647-.647.648.648 0 00-.647-.64h-10.71c.48-1.333.767-2.914.823-4.632a7.25 7.25 0 011.573-.167c.28 0 .44.12.44.327 0 .695-.376 1.238-.376 1.933 0 .607.44.974 1.03.974 1.621 0 3.57-2.771 4.225-2.771.591 0-.144 2.492 1.861 2.492.335 0 .759-.088 1.086-.304.192-.127.328-.32.328-.567 0-.303-.2-.55-.512-.55-.28 0-.51.23-.782.23-.863 0-.04-2.65-1.63-2.65-1.397 0-3.562 2.699-4.12 2.699-.064 0-.12-.04-.12-.136 0-.288.351-.942.351-1.581 0-.8-.663-1.31-1.693-1.31a8.675 8.675 0 00-1.661.16C13.79 7.307 11.793 4 8.742 4 6.69 4 5.1 5.749 5.1 7.977c0 2.604 1.702 4.832 3.81 6.31-.974.958-1.725 2.052-2.164 3.09H1.139a.64.64 0 00-.639.64c0 .351.287.647.639.647zM6.322 7.977c0-1.557 1.054-2.763 2.42-2.763 2.396 0 3.946 2.979 4.017 6.637a10.148 10.148 0 00-2.979 1.661c-1.669-1.15-3.458-3.05-3.458-5.535zM.915 15.55c.208.207.52.2.727 0l1.006-.999.999.999a.502.502 0 00.726 0 .498.498 0 000-.735l-.998-.99.998-.999a.489.489 0 000-.726.495.495 0 00-.726 0l-.999.99-1.006-.99a.49.49 0 00-.727 0 .49.49 0 000 .726l.998.999-.998.99a.498.498 0 000 .735zm9.728-.256a.76.76 0 00.303.072c.32 0 .56-.256.56-.535 0-.192-.104-.383-.336-.495-.104-.056-.223-.112-.335-.176a9.016 9.016 0 011.893-1.038c-.088 1.597-.408 3.067-.887 4.257H8.024c.407-.823 1.054-1.694 1.884-2.468.248.136.496.263.735.383zM7.52 19.151c0-.152.016-.32.048-.487h3.61c-.687 1.07-1.557 1.709-2.532 1.709-.766 0-1.126-.503-1.126-1.222z",
    fill: "#037BFF"
  }))), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
    id: "sign_svg__clip0"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    transform: "translate(.5)",
    d: "M0 0h24v24H0z"
  })))));
}

var _path$h;

function _extends$h() {
  _extends$h = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$h.apply(this, arguments);
}

function SvgSort(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#858585",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    d: "M23.25 19h-7.5v-2.5h7.5V19zm0-15v2.5H.75V4h22.5zm0 8.75h-15v-2.5h15v2.5z"
  })));
}

var _path$g;

function _extends$g() {
  _extends$g = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$g.apply(this, arguments);
}

function SvgSpa(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    d: "M15.49 9.63c-.18-2.79-1.31-5.51-3.43-7.63a12.188 12.188 0 00-3.55 7.63c1.28.68 2.46 1.56 3.49 2.63 1.03-1.06 2.21-1.94 3.49-2.63zm-6.5 2.65c-.14-.1-.3-.19-.45-.29.15.11.31.19.45.29zm6.42-.25c-.13.09-.27.16-.4.26.13-.1.27-.17.4-.26zM12 15.45C9.85 12.17 6.18 10 2 10c0 5.32 3.36 9.82 8.03 11.49.63.23 1.29.4 1.97.51.68-.12 1.33-.29 1.97-.51C18.64 19.82 22 15.32 22 10c-4.18 0-7.85 2.17-10 5.45z"
  })));
}

var _path$f;

function _extends$f() {
  _extends$f = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$f.apply(this, arguments);
}

function SvgStore(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    width: 18,
    height: 16,
    viewBox: "0 0 18 16",
    fill: "#889BB1",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    d: "M17 0H1v2h16V0zm1 10V8l-1-5H1L0 8v2h1v6h10v-6h4v6h2v-6h1zm-9 4H3v-4h6v4z"
  })));
}

var _path$e, _circle;

function _extends$e() {
  _extends$e = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$e.apply(this, arguments);
}

function SvgSuccess(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    width: 128,
    height: 128,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M96.357 47.334l-7.024-7.024L54 75.643l-14-14-7.024 7.024L54 89.69l42.357-42.356z",
    fill: "#037BFF"
  })), _circle || (_circle = /*#__PURE__*/React.createElement("circle", {
    cx: 64,
    cy: 64,
    r: 63.5,
    stroke: "#037BFF"
  })));
}

var _path$d;

function _extends$d() {
  _extends$d = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$d.apply(this, arguments);
}

function SvgSupport(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    d: "M12 1a9 9 0 00-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h4v1h-7v2h6c1.66 0 3-1.34 3-3V10a9 9 0 00-9-9z"
  })));
}

var _path$c;

function _extends$c() {
  _extends$c = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$c.apply(this, arguments);
}

function SvgTarget(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    d: "M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"
  })));
}

var _path$b;

function _extends$b() {
  _extends$b = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$b.apply(this, arguments);
}

function SvgTerritory(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 20 20",
    width: 20,
    height: 20,
    fill: "#A8B8CA"
  }, props), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M0 4.577L14.894 20H2a2 2 0 01-2-2V4.577zM18 0a2 2 0 012 2v16a2 2 0 01-2 2h-.325l-7.163-7.418L14.844 0H18zm-5.271 0L8.952 10.967.02 1.718A2 2 0 012 0h10.729z"
  })));
}

var _path$a;

function _extends$a() {
  _extends$a = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$a.apply(this, arguments);
}

function SvgTile(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    d: "M3.146 1.571c-.87 0-1.575.706-1.575 1.575v6.038c0 .87.705 1.575 1.575 1.575h7.613V3.146c0-.87-.705-1.575-1.575-1.575H3.146zm0 11.813c-.87 0-1.575.705-1.575 1.575v6.037c0 .87.705 1.575 1.575 1.575h6.038c.87 0 1.575-.705 1.575-1.575v-7.612H3.146zM13.384 3.146c0-.87.705-1.575 1.575-1.575h6.037c.87 0 1.575.706 1.575 1.575v6.038c0 .87-.705 1.575-1.575 1.575h-7.612V3.146zm7.612 10.238h-7.612v7.612c0 .87.705 1.576 1.575 1.576h6.037c.87 0 1.575-.705 1.575-1.575v-6.038c0-.87-.705-1.575-1.575-1.575z"
  })));
}

var _path$9, _path2;

function _extends$9() {
  _extends$9 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$9.apply(this, arguments);
}

function SvgTrialSeed(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M22 14a13.952 13.952 0 00-9.617 3.826A15.936 15.936 0 002 14v2c4.757 0 8.96 2.373 11.49 6h3.218A7.97 7.97 0 0122 20v-2a9.967 9.967 0 00-6.92 2.782 16.092 16.092 0 00-1.258-1.564A11.958 11.958 0 0122 16v-2zm-11.056 8A11.97 11.97 0 002 18v2c2.252 0 4.33.744 6.001 2h2.943z"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    d: "M17 7c0-2.758-2.242-5-5-5S7 4.242 7 7c0 3.75 5 9 5 9s5-5.25 5-9zm-6.667 0c0-.917.75-1.667 1.667-1.667s1.667.75 1.667 1.667a1.666 1.666 0 11-3.334 0z"
  })));
}

var _path$8;

function _extends$8() {
  _extends$8 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$8.apply(this, arguments);
}

function SvgTriangleDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    width: 10,
    height: 5,
    viewBox: "0 0 10 5",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    d: "M0 0l5 5 5-5H0z",
    fill: "#000",
    fillOpacity: 0.96
  })));
}

var iconTriangleDown = "data:image/svg+xml,%3Csvg%20width%3D%2210%22%20height%3D%225%22%20viewBox%3D%220%200%2010%205%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M0%200L5%205L10%200H0Z%22%20fill%3D%22black%22%20fill-opacity%3D%220.96%22%2F%3E%3C%2Fsvg%3E";

var _path$7;

function _extends$7() {
  _extends$7 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$7.apply(this, arguments);
}

function SvgTruck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    width: 22,
    height: 16,
    viewBox: "0 0 22 16",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    d: "M19 4h-3V0H2C.9 0 0 .9 0 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2V8l-3-4zM5 14.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9L20.46 8H16V5.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"
  })));
}

var _path$6;

function _extends$6() {
  _extends$6 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$6.apply(this, arguments);
}

function SvgUpload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    viewBox: "0 0 24 24"
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    d: "M15.007 16.514V10.5H19l-7-7-7 7h3.993v6.014h6.014zM19 20.555v-2.02H5v2.02h14z"
  })));
}

var _path$5;

function _extends$5() {
  _extends$5 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$5.apply(this, arguments);
}

function SvgUser(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2a7.2 7.2 0 01-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08a7.2 7.2 0 01-6 3.22z"
  })));
}

var _path$4;

function _extends$4() {
  _extends$4 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$4.apply(this, arguments);
}

function SvgUsersTeam(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    width: 21,
    height: 20,
    viewBox: "0 0 21 20",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    d: "M10.99 0c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10zm3.61 6.34c1.07 0 1.93.86 1.93 1.93 0 1.07-.86 1.93-1.93 1.93-1.07 0-1.93-.86-1.93-1.93-.01-1.07.86-1.93 1.93-1.93zm-6-1.58c1.3 0 2.36 1.06 2.36 2.36 0 1.3-1.06 2.36-2.36 2.36-1.3 0-2.36-1.06-2.36-2.36 0-1.31 1.05-2.36 2.36-2.36zm0 9.13v3.75c-2.4-.75-4.3-2.6-5.14-4.96 1.05-1.12 3.67-1.69 5.14-1.69.53 0 1.2.08 1.9.22-1.64.87-1.9 2.02-1.9 2.68zM10.99 18c-.27 0-.53-.01-.79-.04v-4.07c0-1.42 2.94-2.13 4.4-2.13 1.07 0 2.92.39 3.84 1.15-1.17 2.97-4.06 5.09-7.45 5.09z"
  })));
}

var _path$3;

function _extends$3() {
  _extends$3 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$3.apply(this, arguments);
}

function SvgUsers(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M10.99 8c0 1.66-1.33 3-2.99 3-1.66 0-3-1.34-3-3s1.34-3 3-3 2.99 1.34 2.99 3zm8 0c0 1.66-1.33 3-2.99 3-1.66 0-3-1.34-3-3s1.34-3 3-3 2.99 1.34 2.99 3zM8 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm7.03.05c.35-.03.68-.05.97-.05 2.33 0 7 1.17 7 3.5V19h-6v-2.5c0-1.48-.81-2.61-1.97-3.45z"
  })));
}

var _path$2;

function _extends$2() {
  _extends$2 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$2.apply(this, arguments);
}

function SvgVerifiedShield(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "#037BFF",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    d: "M3 5l9-4 9 4v6c0 5.55-3.84 10.74-9 12-5.16-1.26-9-6.45-9-12V5zm3 8l4 4 8-8-1.41-1.42L10 14.17l-2.59-2.58L6 13z"
  })));
}

var _path$1;

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

function SvgWarehouse(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    width: 24,
    height: 24,
    fill: "#A8B8CA",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M12 3L2 8.522V21h6v-7h8v7h6V8.522L12 3z"
  })));
}

var _path;

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function SvgWarning(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 24 23",
    width: 24,
    height: 23,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    d: "M.919 19.248C.257 20.47 1.082 22 2.402 22H21.6c1.32 0 2.145-1.53 1.483-2.752l-9.598-17.73c-.66-1.22-2.307-1.22-2.967 0L.919 19.248zm12.224-.915h-2.285V15.89h2.285v2.444zm0-4.888h-2.285v-4.89h2.285v4.89z"
  })));
}

const rotate = /*#__PURE__*/keyframes(["from{transform:rotate(0deg);}to{transform:rotate(360deg);}"]);
const bounce = /*#__PURE__*/keyframes(["0%{transform:scale(0);}50%{transform:scale(1.2);}100%{transform:scale(1);}"]);
const DefaultIcon = /*#__PURE__*/styled(SvgLoader).withConfig({
  displayName: "DefaultIcon",
  componentId: "sc-19zueli-0"
})(["width:100% !important;height:100% !important;fill:#fff;&[data-filled='true']{fill:#037bff;}"]);
const CustomIcon = /*#__PURE__*/styled.img.withConfig({
  displayName: "CustomIcon",
  componentId: "sc-19zueli-1"
})(["width:100%;height:100%;"]);
const Container$5 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-19zueli-2"
})(["display:flex;transform-origin:51% 51%;", ""], props => {
  let size;

  switch (props.size) {
    case 'small':
      size = '20px';
      break;

    case 'medium':
      size = '50px';
      break;

    case 'big':
      size = '80px';
      break;
  }

  return {
    fontSize: size,
    width: size,
    height: size
  };
});
const IconContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "IconContainer",
  componentId: "sc-19zueli-3"
})(["width:100%;height:100%;animation:", " 0.8s ease-in-out forwards;"], bounce);
const SpinnerContainer = /*#__PURE__*/styled(Container$5).withConfig({
  displayName: "SpinnerContainer",
  componentId: "sc-19zueli-4"
})(["animation:", " 1s linear infinite;"], rotate);
const IconSuccess = /*#__PURE__*/styled(SvgCheckCircle).withConfig({
  displayName: "IconSuccess",
  componentId: "sc-19zueli-5"
})(["fill:", ";"], props => props.theme.color.accentApproving);
const IconFail = /*#__PURE__*/styled(SvgWarning).withConfig({
  displayName: "IconFail",
  componentId: "sc-19zueli-6"
})(["fill:", ";"], props => props.theme.color.accentNotify);
const Spinner$1 = ({
  size: _size = 'medium',
  className,
  filled: _filled = true,
  spinnerImageUrl
}) => {
  const spinnerJsx = spinnerImageUrl ? jsx(CustomIcon, {
    src: spinnerImageUrl,
    alt: "spinner"
  }, void 0) : jsx(DefaultIcon, {
    "data-filled": _filled
  }, void 0);
  return jsx(SpinnerContainer, Object.assign({
    size: _size,
    className: className,
    "data-test-id": 'loader'
  }, {
    children: spinnerJsx
  }), void 0);
};
const Spinnerable = (_ref) => {
  let {
    progress,
    children
  } = _ref,
      spinnerProps = _objectWithoutProperties(_ref, ["progress", "children"]);

  const [status, setStatus] = useState('idle');
  useEffect(() => {
    if (progress === Progress.WORK) {
      setStatus('loading');
    }

    if ([Progress.ERROR, Progress.SUCCESS].includes(progress)) {
      setStatus('icon');
      const timeout = setTimeout(() => {
        setStatus('idle');
      }, 2500);
      return () => {
        setStatus('idle');
        clearTimeout(timeout);
      };
    }
  }, [progress]);

  if (progress === Progress.ERROR && status === 'icon') {
    return jsx(Container$5, Object.assign({
      size: spinnerProps.size || 'medium'
    }, {
      children: jsx(IconContainer, {
        children: jsx(IconFail, {}, void 0)
      }, void 0)
    }), void 0);
  }

  if (progress === Progress.SUCCESS && status === 'icon') {
    return jsx(Container$5, Object.assign({
      size: spinnerProps.size || 'medium'
    }, {
      children: jsx(IconContainer, {
        children: jsx(IconSuccess, {}, void 0)
      }, void 0)
    }), void 0);
  }

  if (progress === Progress.WORK) {
    return jsx(Spinner$1, Object.assign({}, spinnerProps), void 0);
  }

  if (status !== 'idle') {
    return jsx(Container$5, {
      size: spinnerProps.size || 'medium'
    }, void 0);
  }

  return jsx(Fragment, {
    children: children
  }, void 0);
};

const buttonColorCss = ({
  intent,
  theme,
  variant
}) => {
  if (variant === 'no-style') return css(["background:none;border:none;"]);
  let backgroundColor = variant !== 'text' ? theme.color.onPrimaryLight : 'transparent';
  let borderColor = 'transparent';
  let color = 'inherit';
  let focusBackgroundColor = 'transparent';
  const filled = variant === 'filled';
  const outlined = variant === 'outlined';

  switch (intent) {
    case 'primary':
      if (outlined) borderColor = theme.color.primary600;

      if (filled) {
        backgroundColor = theme.color.primary600;
        color = theme.color.onPrimaryLight;
        focusBackgroundColor = theme.color.primary500;
      } else {
        color = theme.color.primary600;
        focusBackgroundColor = theme.color.primary25;
      }

      break;

    case 'secondary':
      if (outlined) borderColor = theme.color.primary600;

      if (filled) {
        backgroundColor = theme.color.primary25;
        color = theme.color.primary600;
        focusBackgroundColor = theme.color.primary5;
      } else {
        color = theme.color.primary600;
        focusBackgroundColor = theme.color.primary5;
      }

      break;

    case 'warning':
      if (outlined) borderColor = theme.color.accentNotify;

      if (filled) {
        backgroundColor = theme.color.accentNotify;
        color = theme.color.onPrimaryLight;
        focusBackgroundColor = theme.color.accentNotify + 'BB';
      } else {
        color = theme.color.accentNotify;
        focusBackgroundColor = theme.color.accentNotify + '22';
      }

      break;

    case 'danger':
      if (outlined) borderColor = theme.color.accentDestructive;

      if (filled) {
        backgroundColor = theme.color.accentDestructive;
        color = theme.color.onPrimaryLight;
        focusBackgroundColor = theme.color.accentDestructive + 'BB';
      } else {
        color = theme.color.accentDestructive;
        focusBackgroundColor = theme.color.accentDestructive + '22';
      }

      break;

    case 'cancel':
      if (outlined) borderColor = theme.color.secondary200;

      if (filled) {
        backgroundColor = theme.color.secondary200;
        color = theme.color.onPrimaryLight;
        focusBackgroundColor = theme.color.secondary200 + 'BB';
      } else {
        backgroundColor = theme.color.onPrimaryLight;
        color = theme.color.secondary200;
        focusBackgroundColor = theme.color.secondary200 + '22';
      }

      break;

    case 'action':
      borderColor = theme.color.accentApproving;

      if (filled) {
        backgroundColor = theme.color.accentApproving;
        color = theme.color.onPrimaryLight;
        focusBackgroundColor = theme.color.accentApproving + 'BB';
      } else {
        color = theme.color.accentApproving;
        focusBackgroundColor = theme.color.accentApproving + '22';
      }

      break;

    case 'primary-action':
      borderColor = theme.color.accentApproving100;

      if (filled) {
        backgroundColor = theme.color.accentApproving100;
        color = theme.color.onPrimaryLight;
        focusBackgroundColor = theme.color.accentApproving100 + 'BB';
      } else {
        color = theme.color.accentApproving100;
        focusBackgroundColor = theme.color.accentApproving100 + '22';
      }

      break;

    case 'minor-action':
      if (outlined) borderColor = 'transparent';

      if (filled) {
        backgroundColor = theme.color.primary25;
        color = theme.color.primary600;
        focusBackgroundColor = theme.color.primary25 + 'BB';
      } else {
        color = theme.color.primary600;
        focusBackgroundColor = theme.color.primary25 + '22';
      }

      break;

    case 'clear':
      if (outlined) borderColor = theme.color.secondary50;

      if (filled) {
        backgroundColor = theme.color.secondary50;
        color = theme.color.primary600;
        focusBackgroundColor = theme.color.secondary50 + 'BB';
      } else {
        color = theme.color.primary600;
        focusBackgroundColor = theme.color.secondary50 + '22';
      }

      break;
  }

  return css(["background-color:", ";border-color:", ";color:", ";svg{fill:", ";}&:hover{background-color:", ";}&:focus{background-color:", ";box-shadow:0 0 4px ", ";}"], backgroundColor, borderColor, color, color, focusBackgroundColor, focusBackgroundColor, borderColor);
};

const buttonSizeCss = ({
  size: _size = 'medium',
  variant
}) => {
  if (variant === 'no-style') return css(["padding:0;height:auto;"]);

  switch (_size) {
    case 'small':
      return css(["height:32px;padding:6px 12px;"]);

    case 'medium':
      return css(["height:40px;padding:10px 24px;"]);

    case 'big':
      return css(["padding:24px;height:48px;min-width:160px;font-weight:600;font-size:16px;line-height:24px;"]);
  }
};

const buttonVariantCss = ({
  variant
}) => {
  switch (variant) {
    case 'text':
      return css(["padding:0;background-color:transparent !important;height:auto;& svg{width:24px;height:24px;}&:hover{opacity:0.8;}"]);

    case 'no-style':
      return css([""]);

    case 'outlined':
    case 'filled':
      return css(["border-width:1px;border-style:solid;border-radius:8px;& svg{width:1em;height:1em;}"]);
  }
};

const ButtonStyled$2 = /*#__PURE__*/styled.button.withConfig({
  displayName: "ButtonStyled",
  componentId: "m6qgfw-0"
})(["position:relative;display:inline-flex;align-items:center;justify-content:center;font-style:normal;font-weight:500;font-size:14px;line-height:20px;outline:none;cursor:pointer;transition:0.1s background-color ease-out;&:disabled{opacity:0.5;cursor:not-allowed;}pointer-events:", ";", " ", " ", " a{color:unset;width:100%;height:100%;display:flex;align-items:center;justify-content:center;}"], props => props.readOnly ? 'none' : 'auto', buttonColorCss, buttonSizeCss, buttonVariantCss);
const ButtonContent = /*#__PURE__*/styled.div.withConfig({
  displayName: "ButtonContent",
  componentId: "m6qgfw-1"
})(["display:flex;align-items:center;justify-content:center;visibility:", ";gap:8px;"], props => props.hide ? 'hidden' : 'visible');
const SpinnerStyled = /*#__PURE__*/styled(Spinner$1).withConfig({
  displayName: "SpinnerStyled",
  componentId: "m6qgfw-2"
})(["position:absolute;"]);
const Button$5 = (_ref) => {
  let {
    children,
    progress,
    size = 'medium',
    type = 'button',
    filled,
    readOnly,
    intent,
    variant = filled ? 'filled' : 'outlined'
  } = _ref,
      buttonProps = _objectWithoutProperties(_ref, ["children", "progress", "size", "type", "filled", "readOnly", "intent", "variant"]);

  const showSpinner = progress === Progress.WORK;
  const spinnerFilled = useMemo(() => filled && intent && ['clear', 'minor-action', 'secondary'].includes(intent) || !filled, [filled, intent]);
  return jsxs(ButtonStyled$2, Object.assign({
    variant: variant,
    type: type,
    size: size,
    filled: filled,
    readOnly: readOnly,
    intent: intent
  }, buttonProps, {
    children: [showSpinner ? jsx(SpinnerStyled, {
      size: 'small',
      filled: spinnerFilled
    }, void 0) : null, jsx(ButtonContent, Object.assign({
      hide: showSpinner
    }, {
      children: children
    }), void 0)]
  }), void 0);
};

const IconStyled$2 = /*#__PURE__*/styled(SvgPlusCircle).withConfig({
  displayName: "IconStyled",
  componentId: "sc-1l8hhfa-0"
})(["color:", ";transition:0.1s color ease-out;button:hover &{color:", ";}"], ({
  theme
}) => theme.color.accentApproving, ({
  theme
}) => helpersColor.alphaColor(theme.color.accentApproving, 0.75));
const AddButton = (_ref) => {
  let {
    to,
    text,
    noText,
    className,
    onClick
  } = _ref,
      props = _objectWithoutProperties(_ref, ["to", "text", "noText", "className", "onClick"]);

  const {
    t
  } = useTranslation('ui');
  const handleClick = useCallback(e => {
    e.stopPropagation();
    onClick && onClick(e);
  }, [onClick]);

  if (to) {
    return jsx(Link$1, Object.assign({
      to: to,
      onClick: handleClick,
      className: className
    }, {
      children: jsxs(Button$5, Object.assign({
        intent: 'action',
        filled: true,
        size: 'small',
        className: className
      }, props, {
        children: [jsx(IconStyled$2, {
          "data-no-text": noText
        }, void 0), !noText ? text || t('common.add') : null]
      }), void 0)
    }), void 0);
  }

  return jsxs(Button$5, Object.assign({
    intent: 'action',
    filled: true,
    size: 'small',
    className: className,
    onClick: handleClick
  }, props, {
    children: [jsx(IconStyled$2, {
      "data-no-text": noText
    }, void 0), !noText ? text || t('common.add') : null]
  }), void 0);
};

const BackBtnStyled = /*#__PURE__*/styled.button.withConfig({
  displayName: "BackBtnStyled",
  componentId: "sc-1scy8yd-0"
})(["font-weight:500;font-size:14px;line-height:20px;transition:0.3s color;display:inline-flex;align-items:center;background-color:transparent;border:none;color:", ";cursor:pointer;padding:0;&:hover{color:", ";}&:focus{outline:none;}margin-bottom:14px;"], ({
  theme
}) => theme.color.secondary200, ({
  theme
}) => theme.color.primary600);
const BackIconStyled = /*#__PURE__*/styled(SvgArrowBack).withConfig({
  displayName: "BackIconStyled",
  componentId: "sc-1scy8yd-1"
})(["transition:0.3s fill;fill:currentColor;margin-right:12px;"]);
const BackBtn = ({
  onClick,
  className
}) => {
  const {
    t
  } = useTranslation('ui');
  return jsxs(BackBtnStyled, Object.assign({
    onClick: onClick,
    className: className
  }, {
    children: [jsx(BackIconStyled, {}, void 0), t('common.back')]
  }), void 0);
};

const ButtonStyled$1 = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "ButtonStyled",
  componentId: "fa8fdn-0"
})(["", ""], props => {
  let width = '';

  switch (props.size) {
    case 'big':
      width = '48px';
      break;

    case 'medium':
      width = '40px';
      break;

    case 'small':
      width = '32px';
      break;
  }

  return css(["max-width:", ";min-width:", ";padding:0 10px;"], width, width);
});
const BackButton$2 = ({
  onClick,
  size: _size = 'medium'
}) => {
  return jsx(ButtonStyled$1, Object.assign({
    onClick: onClick,
    intent: 'cancel',
    size: _size
  }, {
    children: jsx(SvgArrowBack, {}, void 0)
  }), void 0);
};

const Button$4 = /*#__PURE__*/styled.button.withConfig({
  displayName: "Button",
  componentId: "sc-1j3tqgz-0"
})(["display:flex;align-items:center;justify-content:center;appearance:none;padding:0;border:0;outline:none;border-radius:8px;width:32px;height:32px;background-color:", ";cursor:pointer;"], props => props.theme.color.primary50);
const Icon$1 = /*#__PURE__*/styled(SvgArrowDown).withConfig({
  displayName: "Icon",
  componentId: "sc-1j3tqgz-1"
})(["fill:", ";transition:0.2s transform ease-in;[data-collapsed='true'] > &{transform:rotate(180deg);}"], props => props.theme.color.primary500);
const CollapseButton = ({
  collapsed,
  onClick
}) => {
  return jsx(Button$4, Object.assign({
    onClick: onClick,
    "data-collapsed": collapsed
  }, {
    children: jsx(Icon$1, {}, void 0)
  }), void 0);
};

const IconStyled$1 = /*#__PURE__*/styled(SvgDownload).withConfig({
  displayName: "IconStyled",
  componentId: "sc-14g6i1s-0"
})(["color:", ";transition:0.1s color ease-out;button:hover &{color:", ";}"], ({
  theme
}) => theme.color.accentApproving, ({
  theme
}) => helpersColor.alphaColor(theme.color.accentApproving, 0.75));
const Link = /*#__PURE__*/styled.a.withConfig({
  displayName: "Link",
  componentId: "sc-14g6i1s-1"
})(["", ""], props => {
  switch (props.align) {
    case 'left':
      return {
        display: 'flex',
        marginRight: 'auto',
        marginTop: 'auto',
        justifySelf: 'flex-start'
      };

    case 'right':
      return {
        display: 'flex',
        marginLeft: 'auto',
        marginTop: 'auto',
        justifySelf: 'flex-end'
      };

    case 'center':
      return {
        display: 'flex',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 'auto',
        justifySelf: 'center'
      };
  }
});
const DownloadButton = (_ref) => {
  let {
    href,
    text,
    className,
    align = 'left'
  } = _ref,
      props = _objectWithoutProperties(_ref, ["href", "text", "className", "align"]);

  const {
    t
  } = useTranslation('ui');
  return jsx(Link, Object.assign({
    href: href,
    className: className,
    align: align,
    download: true
  }, {
    children: jsxs(Button$5, Object.assign({
      intent: 'primary',
      filled: true
    }, props, {
      children: [jsx(IconStyled$1, {}, void 0), text || t('common.download')]
    }), void 0)
  }), void 0);
};

let _$m = t => t,
    _t$m;
const buttonStyle = /*#__PURE__*/css(["background-color:", ";border-radius:8px;height:48px;display:inline-flex;min-width:48px;border:none;align-items:center;justify-content:center;position:relative;padding:0 14px;cursor:pointer;font-weight:500;font-size:14px;line-height:20px;transition:0.2s background-color,0.2s color;color:", ";&:hover{background-color:", ";color:", ";}&:focus{outline:none;box-shadow:", ";}"], props => props.theme.color.primary5, props => props.theme.color.onPrimaryDark, props => props.theme.color.primary50, props => props.theme.color.primary600, props => props.theme.shadow.focus);
const Button$3 = /*#__PURE__*/styled.button.withConfig({
  displayName: "Button",
  componentId: "sc-1wlgxop-0"
})(["", " svg{fill:", ";}"], buttonStyle, props => props.color ? props.color : props.theme.color.primary600);
const Wrapper$m = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1wlgxop-1"
})(["", " svg{fill:", ";}"], buttonStyle, props => props.color ? props.color : props.theme.color.primary600);
const Text$2 = /*#__PURE__*/styled.span.withConfig({
  displayName: "Text",
  componentId: "sc-1wlgxop-2"
})(["margin-left:10px;"]);
const ButtonText = /*#__PURE__*/styled.span.withConfig({
  displayName: "ButtonText",
  componentId: "sc-1wlgxop-3"
})(["", ""], props => props.theme.media.mobile(_t$m || (_t$m = _$m`
    display: ${0}
  `), props.hideTextOnMobile ? 'none' : 'inline'));
const IconButton = (_ref) => {
  let {
    button = true,
    children,
    onClick,
    Icon,
    className,
    iconColor,
    doNotWrapText,
    hideTextOnMobile
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["button", "children", "onClick", "Icon", "className", "iconColor", "doNotWrapText", "hideTextOnMobile"]);

  const childrenNode = doNotWrapText ? children : !!children && jsx(Text$2, {
    children: children
  }, void 0);

  if (button) {
    return jsxs(Button$3, Object.assign({}, rest, {
      onClick: onClick,
      type: 'button',
      className: className,
      color: iconColor
    }, {
      children: [jsx(Icon, {}, void 0), jsx(ButtonText, Object.assign({
        hideTextOnMobile: hideTextOnMobile
      }, {
        children: childrenNode
      }), void 0)]
    }), void 0);
  } else {
    return jsxs(Wrapper$m, Object.assign({}, rest, {
      onClick: onClick,
      className: className,
      color: iconColor
    }, {
      children: [jsx(Icon, {}, void 0), jsx(ButtonText, Object.assign({
        hideTextOnMobile: hideTextOnMobile
      }, {
        children: childrenNode
      }), void 0)]
    }), void 0);
  }
};

const Label$3 = /*#__PURE__*/styled.label.withConfig({
  displayName: "Label",
  componentId: "sc-8dqmfy-0"
})(["color:", ";font-weight:500;font-size:12px;line-height:16px;opacity:0.48;margin-bottom:8px;white-space:nowrap;"], props => props.theme.color.onPrimaryDark);

const LabelWrapperStyled$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "LabelWrapperStyled",
  componentId: "sc-1l2a9f5-0"
})(["display:flex;flex-direction:column;width:", ";min-width:", ";max-width:", ";"], ({
  width
}) => width, ({
  minWidth
}) => minWidth, ({
  maxWidth
}) => maxWidth);
const WrapperStyled$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "WrapperStyled",
  componentId: "sc-1l2a9f5-1"
})(["position:relative;display:flex;align-items:center;width:", ";min-width:", ";max-width:", ";"], ({
  width
}) => width, ({
  minWidth
}) => minWidth, ({
  maxWidth
}) => maxWidth);
const TextAreaWrapperStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "TextAreaWrapperStyled",
  componentId: "sc-1l2a9f5-2"
})(["position:relative;width:", ";min-width:", ";max-width:", ";display:flex;align-items:center;border-width:1px;border-style:solid;border-radius:4px;opacity:1;color:#000;border-color:#ddd;align-items:flex-start;height:123px;"], ({
  width
}) => width, ({
  minWidth
}) => minWidth, ({
  maxWidth
}) => maxWidth);
const ErrorStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "ErrorStyled",
  componentId: "sc-1l2a9f5-3"
})(["color:", ";margin-top:4px;font-style:normal;font-weight:500;font-size:12px;line-height:16px;position:absolute;top:100%;"], props => props.theme.color.accentDestructive);
const CounterStyled = /*#__PURE__*/styled.span.withConfig({
  displayName: "CounterStyled",
  componentId: "sc-1l2a9f5-4"
})(["font-style:normal;font-weight:500;font-size:12px;line-height:20px;position:absolute;right:4px;bottom:0;color:", ";text-align:right;"], props => props.theme.color.secondary200);
const Optional$1 = /*#__PURE__*/styled.span.withConfig({
  displayName: "Optional",
  componentId: "sc-1l2a9f5-5"
})(["font-style:normal;font-weight:500;font-size:12px;line-height:16px;color:", ";margin-left:auto;text-transform:none;position:absolute;bottom:calc(100% + 8px);right:0;"], ({
  theme
}) => theme.color.accentNotify);

const Counter = ({
  limit,
  value
}) => {
  const total = typeof value === 'string' ? value.length : typeof value === 'number' ? value : 0;
  return jsx(CounterStyled, {
    children: `${total} / ${limit}`
  }, void 0);
};

const FormField = (_ref) => {
  let {
    label,
    limit,
    invalid,
    errorText,
    className,
    value,
    name,
    id,
    render,
    optional,
    required,
    testId,
    labelStyle,
    minWidth = '0',
    maxWidth = 'none',
    width = '100%',
    componentType
  } = _ref,
      props = _objectWithoutProperties(_ref, ["label", "limit", "invalid", "errorText", "className", "value", "name", "id", "render", "optional", "required", "testId", "labelStyle", "minWidth", "maxWidth", "width", "componentType"]);

  const {
    t
  } = useTranslation('ui');
  const nodeId = useId(id);
  const node = render({
    id: nodeId,
    name: name || ''
  });
  const Wrapper = componentType && componentType === 'textArea' ? TextAreaWrapperStyled : WrapperStyled$1;

  const field = jsxs(Wrapper, Object.assign({
    className: className,
    "data-test-id": testId,
    minWidth: minWidth,
    maxWidth: maxWidth,
    width: width
  }, props, {
    children: [node, invalid && jsx(ErrorStyled, {
      children: errorText
    }, void 0), typeof limit === 'number' && jsx(Counter, {
      value: value,
      limit: limit
    }, void 0), optional && jsx(Optional$1, Object.assign({
      hasLimit: !!limit
    }, {
      children: t('common.optional')
    }), void 0)]
  }), void 0);

  if (label) {
    if (required) {
      label = `${label} *`;
    }

    return jsxs(LabelWrapperStyled$1, Object.assign({
      minWidth: minWidth,
      maxWidth: maxWidth,
      width: width
    }, props, {
      children: [jsx(Label$3, Object.assign({
        htmlFor: name || nodeId,
        style: labelStyle
      }, {
        children: label
      }), void 0), field]
    }), void 0);
  }

  return field;
};

const TEST_ID_SELECTED = 'selected-item';
const bezierTimingFunction = 'cubic-bezier(0.060, 0.975, 0.195, 0.985)';

const segmentCss = ({
  flat,
  disabled
}) => {
  let opacity = 1;
  let pointerEvents = 'all';
  let padding = '4px';

  if (disabled) {
    opacity = 0.5;
    pointerEvents = 'none';
  }

  if (flat) {
    padding = '0';
  }

  return {
    opacity,
    pointerEvents,
    padding
  };
};

const SegmentStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "SegmentStyled",
  componentId: "sc-9a939o-0"
})(["position:relative;height:40px;border-radius:8px;border-width:1px;border-style:solid;border-color:", ";display:inline-flex;flex-direction:row;flex-grow:", ";flex-shrink:0;overflow:hidden;", ""], props => props.theme.color.onSurfaceLowEmphasys, props => props.responsive ? '1' : '0', segmentCss);

const segmentItemCss = ({
  theme,
  selected
}) => {
  let color = theme.color.primary600;
  let cursor = 'pointer';

  if (selected) {
    color = theme.color.onPrimaryLight;
    cursor = 'default';
  }

  return {
    color,
    cursor
  };
};

const SegmentItemStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "SegmentItemStyled",
  componentId: "sc-9a939o-1"
})(["display:flex;align-items:center;justify-content:center;border-radius:4px;padding:0 12px;transition:0.3s color ", ";z-index:1;flex-grow:", ";flex-shrink:0;", ""], bezierTimingFunction, props => props.responsive ? '1' : '0', segmentItemCss);

const caretCss = ({
  theme,
  offset,
  flat
}) => {
  if (!offset) {
    return {
      display: 'none'
    };
  }

  const backgroundColor = theme.color.primary600;
  let borderRadius = '4px';

  if (flat) {
    borderRadius = '0';
  }

  return {
    width: `${offset.width}px`,
    height: `${offset.height}px`,
    left: `${offset.left}px`,
    backgroundColor,
    borderRadius
  };
};

const CaretStyled = /*#__PURE__*/styled.span.withConfig({
  displayName: "CaretStyled",
  componentId: "sc-9a939o-2"
})(["position:absolute;transition:0.3s width,0.3s left,0.3s top,0.2s height;transition-timing-function:", ";padding:0 12px;", ""], bezierTimingFunction, caretCss);
const Segment = ({
  id,
  onChange,
  items,
  label,
  disabled,
  value,
  flat,
  className,
  responsive
}) => {
  const [parentRef, selectedRef, offset] = useParentOffset();
  return jsx(FormField, {
    id: id,
    label: label,
    render: ({
      id
    }) => jsxs(SegmentStyled, Object.assign({
      disabled: disabled,
      ref: parentRef,
      role: 'listbox',
      id: id,
      flat: flat,
      className: className,
      responsive: responsive
    }, {
      children: [jsx(CaretStyled, {
        offset: offset,
        flat: flat
      }, void 0), items.map(item => {
        const isSelected = item.id === value;
        return jsx(SegmentItemStyled, Object.assign({
          responsive: responsive,
          ref: isSelected ? selectedRef : null,
          "data-testid": isSelected ? TEST_ID_SELECTED : undefined,
          selected: isSelected,
          onClick: () => !disabled && onChange(item.id)
        }, {
          children: item.title
        }), item.id);
      })]
    }), void 0)
  }, void 0);
};

const Wrapper$l = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1blaiz1-0"
})(["display:flex;flex-direction:row;"]);

const pickOptionColor = ({
  theme,
  selected,
  disabled
}) => {
  if (disabled) {
    return theme.color.secondary200;
  } else if (selected) {
    return theme.color.onPrimaryLight;
  } else {
    return theme.color.primary600;
  }
};

const Button$2 = /*#__PURE__*/styled.button.withConfig({
  displayName: "Button",
  componentId: "sc-1blaiz1-1"
})(["", " box-sizing:border-box;width:160px;height:56px;border-radius:8px;font-family:Montserrat;font-style:normal;font-weight:500;font-size:14px;line-height:20px;outline:none;cursor:pointer;&:hover{", "}&:disabled{cursor:not-allowed;}&:not(:first-child){margin-left:16px;}"], ({
  theme,
  selected,
  disabled
}) => ({
  background: selected ? theme.color.primary600 : 'transparent',
  border: `1px solid ${disabled ? theme.color.secondary200 : theme.color.primary600}`,
  color: pickOptionColor({
    selected,
    disabled,
    theme
  })
}), ({
  theme,
  selected
}) => !selected && {
  background: theme.color.primary25
});
const SwitchButton = ({
  options,
  onChange,
  value,
  testId
}) => jsx(Wrapper$l, Object.assign({
  "data-test-id": testId
}, {
  children: options.map((opt, i) => jsx(Button$2, Object.assign({
    type: 'button',
    onClick: () => onChange(opt.id),
    selected: opt.id === value,
    disabled: opt.disabled
  }, {
    children: opt.title
  }), i))
}), void 0);

let _$l = t => t,
    _t$l;
const Container$4 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-1tj5ptx-0"
})(["display:flex;flex-direction:row;justify-content:flex-start;align-items:center;", ";&[data-hint='true']{align-items:flex-start;}&[data-hint='false']{align-items:center;}"], ({
  disabled
}) => ({
  pointerEvents: disabled ? 'none' : 'auto',
  opacity: disabled ? '0.5' : '1'
}));
const BoxWrapper$1 = /*#__PURE__*/styled.button.withConfig({
  displayName: "BoxWrapper",
  componentId: "sc-1tj5ptx-1"
})(["display:flex;justify-content:center;align-items:center;box-sizing:border-box;min-width:20px;height:20px;margin-right:8px;padding:0;border-radius:4px;outline:none;cursor:inherit;", " ", " [data-disabled='false'] &:focus,", " [data-disabled='false']:hover &{border-color:", ";}[data-hint='false'] &{margin-top:0;}"], ({
  theme
}) => ({
  border: `1px solid ${theme.color.secondary200}`,
  background: theme.color.onPrimaryLight
}), Container$4, Container$4, props => props.theme.color.primary600);
const CheckMark$1 = /*#__PURE__*/styled(SvgCheck).withConfig({
  displayName: "CheckMark",
  componentId: "sc-1tj5ptx-2"
})(["margin-bottom:2px;"]);
const PartlyCheckedMark = /*#__PURE__*/styled.div.withConfig({
  displayName: "PartlyCheckedMark",
  componentId: "sc-1tj5ptx-3"
})(["width:8px;height:8px;border-radius:", ";", ""], props => props.type === 'circle' ? '4px' : 0, ({
  theme
}) => ({
  background: theme.color.primary600
}));

const BoxArea$1 = (_ref) => {
  let {
    isChecked,
    isPartlyChecked,
    id,
    partialCheckType
  } = _ref,
      other = _objectWithoutProperties(_ref, ["isChecked", "isPartlyChecked", "id", "partialCheckType"]);

  return jsx(BoxWrapper$1, Object.assign({
    id: id,
    type: 'button',
    role: 'checkbox',
    "aria-checked": isChecked
  }, other, {
    children: isChecked ? isPartlyChecked ? jsx(PartlyCheckedMark, {
      type: partialCheckType
    }, void 0) : jsx(CheckMark$1, {}, void 0) : null
  }), void 0);
};

const Label$2 = /*#__PURE__*/styled.span.withConfig({
  displayName: "Label",
  componentId: "sc-1tj5ptx-4"
})(["font-weight:500;font-size:16px;line-height:24px;", " ", ""], ({
  theme
}) => ({
  color: theme.color.onPrimaryDark
}), props => props.theme.media.mobile(_t$l || (_t$l = _$l`
    font-size: 14px;
    line-height: 20px;
  `)));
const LabelContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "LabelContainer",
  componentId: "sc-1tj5ptx-5"
})(["display:flex;flex-direction:column;"]);
const Hint = /*#__PURE__*/styled.div.withConfig({
  displayName: "Hint",
  componentId: "sc-1tj5ptx-6"
})(["font-style:normal;font-weight:500;font-size:12px;line-height:16px;color:", ";opacity:0.48;"], ({
  theme
}) => theme.color.onPrimaryDark);
const Checkbox = (_ref2) => {
  let {
    value,
    isChecked,
    isPartlyChecked,
    label,
    disabled,
    onChange,
    id = 'uuid',
    className,
    testId,
    partialCheckType = 'circle',
    hint
  } = _ref2,
      other = _objectWithoutProperties(_ref2, ["value", "isChecked", "isPartlyChecked", "label", "disabled", "onChange", "id", "className", "testId", "partialCheckType", "hint"]);

  return jsxs(Container$4, Object.assign({
    disabled: !!disabled,
    onClick: e => !disabled && onChange(value || '', !isChecked, e),
    className: className,
    "data-test-id": testId,
    "data-disabled": !!disabled,
    "data-hint": !!hint
  }, {
    children: [jsx(BoxArea$1, Object.assign({
      id: id,
      isChecked: isChecked,
      isPartlyChecked: isPartlyChecked,
      partialCheckType: partialCheckType
    }, other), void 0), label && jsxs(LabelContainer, {
      children: [jsx(Label$2, {
        children: label
      }, void 0), hint && jsx(Hint, {
        children: hint
      }, void 0)]
    }, void 0)]
  }), void 0);
};

const RadioContext = /*#__PURE__*/React__default.createContext({
  value: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: () => {}
});
const RadioWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "RadioWrapper",
  componentId: "am9e68-0"
})(["display:flex;flex-direction:row;justify-content:flex-start;align-items:center;cursor:pointer;", ";"], ({
  disabled
}) => ({
  pointerEvents: disabled ? 'none' : 'auto',
  opacity: disabled ? '0.5' : '1'
}));
const BoxWrapper = /*#__PURE__*/styled.button.withConfig({
  displayName: "BoxWrapper",
  componentId: "am9e68-1"
})(["display:flex;justify-content:center;align-items:center;box-sizing:border-box;width:20px;height:20px;margin-right:8px;padding:0;border-radius:50%;outline:none;cursor:inherit;&:focus{border-color:", ";}", ""], props => props.theme.color.primary600, ({
  theme
}) => ({
  border: `1px solid ${theme.color.secondary200}`,
  background: theme.color.onPrimaryLight
}));
const CheckMark = /*#__PURE__*/styled.span.withConfig({
  displayName: "CheckMark",
  componentId: "am9e68-2"
})(["width:12px;height:12px;border-radius:50%;background:", ";"], props => props.theme.color.primary600);

const BoxArea = ({
  isChecked,
  disabled,
  value
}) => jsx(BoxWrapper, Object.assign({
  type: 'button',
  role: 'radio',
  "aria-checked": isChecked,
  title: `${value}`,
  disabled: disabled
}, {
  children: isChecked ? jsx(CheckMark, {}, void 0) : null
}), void 0);

const Label$1 = /*#__PURE__*/styled.span.withConfig({
  displayName: "Label",
  componentId: "am9e68-3"
})(["font-style:normal;font-weight:500;font-size:16px;line-height:24px;", ""], ({
  theme
}) => ({
  color: theme.color.onPrimaryDark
}));
const RadioItem = ({
  value,
  label,
  disabled,
  className
}) => {
  const context = useContext(RadioContext);
  const isChecked = context.value === value;
  const onChange = context.onChange;
  return jsxs(RadioWrapper, Object.assign({
    disabled: !!disabled,
    onClick: () => !disabled && onChange(value),
    className: className
  }, {
    children: [jsx(BoxArea, {
      isChecked: isChecked,
      disabled: !!disabled,
      value: value
    }, void 0), label && jsx(Label$1, {
      children: label
    }, void 0)]
  }), void 0);
};
const ContainerStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContainerStyled",
  componentId: "am9e68-4"
})(["", ""], ({
  direction
}) => {
  if (direction === 'column') {
    return css(["display:grid;grid-gap:10px;grid-template-columns:1fr;"]);
  } else if (direction === 'row') {
    return css(["display:flex;", "{&:not(:last-child){margin-right:10px;}}"], RadioWrapper);
  } else {
    return '';
  }
});
const Radio = ({
  value,
  onChange,
  className,
  children,
  direction: _direction = 'row'
}) => {
  return jsx(RadioContext.Provider, Object.assign({
    value: {
      value,
      onChange
    }
  }, {
    children: jsx(ContainerStyled, Object.assign({
      className: className,
      direction: _direction
    }, {
      children: children
    }), void 0)
  }), void 0);
};

const SectionBody$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "SectionBody",
  componentId: "sc-2uj3ot-0"
})(["display:grid;grid-gap:24px;"]);
const SectionWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "SectionWrapper",
  componentId: "sc-2uj3ot-1"
})(["display:flex;flex-direction:column;"]);
const SectionTitle$1 = /*#__PURE__*/styled.h3.withConfig({
  displayName: "SectionTitle",
  componentId: "sc-2uj3ot-2"
})(["margin:0 0 16px 0;font-style:normal;font-weight:bold;font-size:12px;line-height:20px;text-transform:uppercase;color:", ";"], ({
  theme
}) => theme.color.secondary200);
const LabeledContainerStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "LabeledContainerStyled",
  componentId: "sc-2uj3ot-3"
})(["display:flex;flex-direction:column;"]);
const LabelStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "LabelStyled",
  componentId: "sc-2uj3ot-4"
})(["color:", ";font-weight:500;font-size:12px;line-height:16px;opacity:0.48;margin-bottom:8px;"], props => props.theme.color.onPrimaryDark); // exportable

const FormContentBody = /*#__PURE__*/styled.form.withConfig({
  displayName: "FormContentBody",
  componentId: "sc-2uj3ot-5"
})(["display:grid;grid-gap:24px;padding:16px 24px;"]);
const FormContentFooter = /*#__PURE__*/styled.div.withConfig({
  displayName: "FormContentFooter",
  componentId: "sc-2uj3ot-6"
})(["display:flex;align-items:center;width:100%;height:72px;box-shadow:0px -1px 0px rgba(0,0,0,0.08);padding:0 24px;"]);
const FormContentWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "FormContentWrapper",
  componentId: "sc-2uj3ot-7"
})(["border-radius:8px;background:", ";"], ({
  theme
}) => theme.color.onPrimaryLight);
const FormButtonsSection = /*#__PURE__*/styled.div.withConfig({
  displayName: "FormButtonsSection",
  componentId: "sc-2uj3ot-8"
})(["display:flex;flex-direction:row;align-items:center;width:100%;background:", ";& > *:not(:last-child){margin-right:20px;}"], ({
  theme
}) => theme.color.onPrimaryLight);

const LabeledContainer = ({
  children,
  label
}) => {
  return jsxs(LabeledContainerStyled, {
    children: [jsx(LabelStyled, {
      children: label
    }, void 0), children]
  }, void 0);
};

const FormSection = ({
  title,
  className,
  children,
  testId
}) => jsxs(SectionWrapper, Object.assign({
  className: className,
  "data-test-id": testId
}, {
  children: [jsx(SectionTitle$1, {
    children: title
  }, void 0), jsx(SectionBody$1, {
    children: children
  }, void 0)]
}), void 0);

const FormSectionTextBlock = /*#__PURE__*/styled.div.withConfig({
  displayName: "FormSectionTextBlock",
  componentId: "sc-2uj3ot-9"
})(["display:flex;flex-direction:column;font-weight:500;font-size:14px;line-height:20px;"]);
const FormComponents = {
  LabeledContainer,
  FormSection,
  FormSectionTextBlock,
  FormContentBody,
  FormContentFooter,
  FormContentWrapper,
  FormButtonsSection
};

const FormText = ({
  label,
  children
}) => {
  return jsx(FormField, {
    label: label,
    render: () => children
  }, void 0);
};

function warn(...args) {
  if (process.env.NODE_ENV === 'development') {
    console.warn(...args);
  }
}

let _$k = t => t,
    _t$k,
    _t2$a,
    _t3$7,
    _t4$4,
    _t5$4,
    _t6$4,
    _t7$3;
const Wrapper$k = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1a0s8gm-0"
})(["background-color:", ";box-shadow:0 4px 8px rgba(0,0,0,0.04);border-radius:8px;display:flex;flex-direction:column;", " ", ""], props => props.theme.color.onPrimaryLight, props => !props.noDivider && css(["& > div:not(:last-child){border-bottom:1px solid ", ";}"], props => helpersColor.alphaColor(props.theme.color.secondary200, 0.24)), props => props.theme.media.mobile(_t$k || (_t$k = _$k`
    box-shadow: none;
    border-radius: 0;
  `)));
const BackButton$1 = /*#__PURE__*/styled(SvgArrowBack).withConfig({
  displayName: "BackButton",
  componentId: "sc-1a0s8gm-1"
})(["margin-right:24px;cursor:pointer;transition:0.2s fill ease-out;&:hover{fill:", ";}"], ({
  theme
}) => theme.color.primary600);
const LinkWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "LinkWrapper",
  componentId: "sc-1a0s8gm-2"
})(["display:flex;align-items:center;font-weight:500;font-size:16px;line-height:24px;color:", ";& > a{color:", ";}"], props => props.theme.color.primary600, props => props.theme.color.primary600);
const LinkIconStyled = /*#__PURE__*/styled(SvgLink).withConfig({
  displayName: "LinkIconStyled",
  componentId: "sc-1a0s8gm-3"
})(["margin-right:6px;"]);
const TitleContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TitleContainer",
  componentId: "sc-1a0s8gm-4"
})(["font-weight:600;font-size:18px;line-height:28px;min-height:72px;display:flex;align-items:center;width:100%;padding:0 24px;border-bottom:1px solid ", ";", " ", " ", ""], props => props.theme.color.secondary50, props => props.theme.media.desktop(_t2$a || (_t2$a = _$k`
    padding: 0 12px 0 24px;
    min-height: 52px;
  `)), props => props.theme.media.tablet(_t3$7 || (_t3$7 = _$k`
    padding: 0 12px 0 24px;
    min-height: 52px;
  `)), props => props.theme.media.mobile(_t4$4 || (_t4$4 = _$k`
    padding: 0 12px 0 16px;
    min-height: 48px;
  `)));
const TitleInner = /*#__PURE__*/styled.div.withConfig({
  displayName: "TitleInner",
  componentId: "sc-1a0s8gm-5"
})(["width:100%;display:flex;justify-content:space-between;align-items:center;"]); // exportable

const SectionTitle = ({
  onBack,
  onClick,
  backButton: _backButton = true,
  className,
  children
}) => {
  return jsxs(TitleContainer, Object.assign({
    onClick: onClick,
    className: className
  }, {
    children: [onBack && _backButton ? jsx(BackButton$1, {
      onClick: onBack
    }, void 0) : null, jsx(TitleInner, {
      children: children
    }, void 0)]
  }), void 0);
};
const SectionBody = /*#__PURE__*/styled.div.withConfig({
  displayName: "SectionBody",
  componentId: "sc-1a0s8gm-6"
})(["padding:16px 24px 24px;", " ", " ", " ", ""], props => !props.noGrid && css(["display:grid;grid-gap:32px;"]), props => props.theme.media.desktop(_t5$4 || (_t5$4 = _$k`
    padding: 16px 24px 24px;
  `)), props => props.theme.media.tablet(_t6$4 || (_t6$4 = _$k`
    padding: 16px 24px 24px;
  `)), props => props.theme.media.mobile(_t7$3 || (_t7$3 = _$k`
    padding: 12px ${0}px 24px;
  `), props => props.paddedOnMobile ? props.theme.padding.mobile.content : 0));
const SectionFooter = /*#__PURE__*/styled.div.withConfig({
  displayName: "SectionFooter",
  componentId: "sc-1a0s8gm-7"
})(["display:flex;flex-direction:row;align-items:center;min-height:64px;padding:0 24px;"]);
const SectionLinkWrapper = ({
  children
}) => {
  return jsxs(LinkWrapper, {
    children: [jsx(LinkIconStyled, {}, void 0), children]
  }, void 0);
};
const SectionContainer = ({
  children,
  className,
  noDivider
}) => {
  return jsx(Wrapper$k, Object.assign({
    className: className,
    noDivider: noDivider
  }, {
    children: children
  }), void 0);
};

let _$j = t => t,
    _t$j,
    _t2$9;

const bottomBorderHeight = props => {
  let height;

  switch (props.size) {
    case 'small':
      height = '2px';
      break;

    case 'big':
      height = '4px';
      break;

    case 'medium':
    default:
      height = '3px';
  }

  return {
    height
  };
};

const headerSizeCss = props => {
  switch (props.size) {
    case 'small':
      return css(["height:48px;font-weight:600;font-size:16px;line-height:24px;"]);

    case 'big':
      return css(["height:96px;font-weight:600;font-size:24px;line-height:36px;"]);

    case 'medium':
    default:
      return css(["height:72px;font-weight:600;font-size:20px;line-height:28px;"]);
  }
};

const StepsHeaderContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepsHeaderContainer",
  componentId: "sc-1i5qevp-0"
})(["position:relative;display:flex;flex-direction:row;justify-content:space-between;color:", ";border-bottom:1px solid ", ";&::after{position:absolute;background-color:", ";content:'';width:100%;display:", ";bottom:-1px;left:0;", ";}", ""], props => props.theme.color.secondary200, props => props.theme.color.secondary50, props => props.theme.color.primary600, props => props.completed ? 'block' : 'none', bottomBorderHeight, headerSizeCss);

const stepHeaderCss = props => {
  let color = props.theme.color.textSecondary;
  let fontWeight = 500;

  if (props.completed) {
    color = props.theme.color.primary600;
  } else if (props.current) {
    color = props.theme.color.onPrimaryDark;
    fontWeight = 600;
  }

  return css(["color:", ";pointer-events:", ";cursor:", ";font-weight:", ";"], color, props.disabled ? 'none' : 'default', props.disabled ? 'not-allowed' : 'pointer', fontWeight);
};

const CheckIconWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "CheckIconWrapper",
  componentId: "sc-1i5qevp-1"
})(["position:absolute;display:flex;align-items:center;justify-content:center;width:2em;padding:0 4px;right:0;top:0;bottom:0;", " > *{width:1em;height:auto;}"], props => props.theme.media.mobile(_t$j || (_t$j = _$j`
    display: none;
  `)));
const StepHeaderItemContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepHeaderItemContainer",
  componentId: "sc-1i5qevp-2"
})(["position:relative;height:100%;display:flex;flex-direction:row;align-items:center;padding-left:2em;padding-right:2em;white-space:nowrap;justify-content:center;width:100%;&::after{position:absolute;background-color:", ";content:'';width:100%;display:", ";bottom:-1px;left:0;", ";}", " ", ""], props => props.theme.color.primary600, props => props.completed || props.current ? 'block' : 'none', bottomBorderHeight, stepHeaderCss, props => props.theme.media.mobile(_t2$9 || (_t2$9 = _$j`
    padding-left: 1em;
    padding-right: 1em;
  `)));

const StepHeaderItem = ({
  className,
  current,
  completed,
  title,
  disabled: _disabled = false,
  checkIcon: _checkIcon = true,
  headerSize,
  onClick
}) => {
  const checkIconComponents = typeof _checkIcon === 'boolean' ? jsx(SvgCheck, {}, void 0) : _checkIcon;
  return jsxs(StepHeaderItemContainer, Object.assign({
    className: className,
    current: current,
    completed: completed,
    onClick: onClick,
    disabled: _disabled,
    size: headerSize
  }, {
    children: [title, _checkIcon ? jsx(CheckIconWrapper, {
      children: completed && checkIconComponents
    }, void 0) : null]
  }), void 0);
};

const StepFormStep = _props => {
  const props = _props;
  return jsx(StepHeaderItem, {
    current: props.current,
    completed: props.completed,
    title: props.title,
    onClick: props.onClick,
    disabled: props.disabled,
    className: props.className,
    checkIcon: props.checkIcon,
    id: props.id,
    headerSize: props.headerSize
  }, void 0);
};
const StepForm = ({
  step,
  completed,
  children,
  completeContent,
  onTabClick,
  lockOnLastStep: _lockOnLastStep = true,
  headerSize: _headerSize = 'medium'
}) => {
  var _current$props, _current$props2;

  const nodes = React__default.Children.toArray(children);
  const current = nodes.find(n => n.props.id === step);

  if (typeof children === 'string') {
    warn(`StepSection: invalid children`);
    return null;
  }

  if (!current) {
    warn(`StepSection: couldn't find step container with passed step id. Steps total "${nodes.map(n => n.props.id).join(', ')}", step id passed "${step}"`);
  }

  return jsxs(SectionContainer, Object.assign({
    noDivider: true
  }, {
    children: [jsx(StepsHeaderContainer, Object.assign({
      completed: completed,
      size: _headerSize
    }, {
      children: nodes.map((node, idx) => {
        return /*#__PURE__*/React__default.cloneElement(node, {
          onClick: () => !(_lockOnLastStep && completed) && onTabClick(node.props.id),
          current: step === node.props.id,
          completed: nodes.findIndex(n => n.props.id === step) > idx || completed,
          key: `step-${node.props.id}`,
          headerSize: _headerSize
        });
      })
    }), void 0), jsx(SectionBody, Object.assign({
      paddedOnMobile: true,
      id: current === null || current === void 0 ? void 0 : (_current$props = current.props) === null || _current$props === void 0 ? void 0 : _current$props.id,
      noGrid: true
    }, {
      children: completed && completeContent ? completeContent : (current === null || current === void 0 ? void 0 : (_current$props2 = current.props) === null || _current$props2 === void 0 ? void 0 : _current$props2.children) || null
    }), void 0)]
  }), void 0);
};

const pickColor$2 = props => {
  if (props.isWarning) {
    return props.theme.color.accentDestructive;
  }

  return props.isChecked ? props.theme.color.accentApproving : props.theme.color.secondary200;
};

const Wrapper$j = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1ocxwnv-0"
})(["display:flex;flex-direction:row;align-items:center;font-style:normal;font-weight:500;font-size:14px;line-height:20px;color:", ";"], pickColor$2);
const ButtonStyled = /*#__PURE__*/styled.button.withConfig({
  displayName: "ButtonStyled",
  componentId: "sc-1ocxwnv-1"
})(["border-width:1px;border-style:solid;border-radius:12px;background:none;outline:none;padding:0;display:flex;align-items:center;cursor:pointer;position:relative;transition:0.1s background-color ease-out,0.1s border-color ease-out;margin-right:8px;&:disabled{opacity:0.5;cursor:not-allowed;}", ";"], props => {
  const color = pickColor$2(props);
  let width;
  let height;

  switch (props.size) {
    case 'default':
      width = '44px';
      height = '24px';
      break;

    case 'small':
      width = '36px';
      height = '20px';
  }

  return {
    borderColor: color,
    backgroundColor: `${color}14`,
    width,
    height
  };
});
const SliderStyled = /*#__PURE__*/styled.span.withConfig({
  displayName: "SliderStyled",
  componentId: "sc-1ocxwnv-2"
})(["position:absolute;border-radius:50%;transition:0.1s background-color ease-out,0.1s transform ease-out;", ";"], props => {
  const color = pickColor$2(props);
  let offset;
  let size;

  switch (props.size) {
    case 'default':
      size = '16px';
      offset = props.isChecked ? 20 : 5;
      break;

    case 'small':
      size = '14px';
      offset = props.isChecked ? 17 : 3;
  }

  return {
    backgroundColor: color,
    transform: `translateX(${offset}px)`,
    width: size,
    height: size
  };
});
const FormFieldStyled = /*#__PURE__*/styled(FormField).withConfig({
  displayName: "FormFieldStyled",
  componentId: "sc-1ocxwnv-3"
})(["max-width:max-content;"]);
const Switch = ({
  on,
  onClick,
  disabled: _disabled = false,
  warning,
  label,
  id,
  className,
  children,
  name,
  size: _size = 'default',
  testId
}) => {
  return jsx(FormFieldStyled, {
    id: id,
    className: className,
    label: label,
    name: name,
    testId: testId,
    render: ({
      id,
      name
    }) => jsxs(Wrapper$j, Object.assign({
      isChecked: on,
      isWarning: warning,
      size: _size
    }, {
      children: [jsx(ButtonStyled, Object.assign({
        id: id,
        name: name,
        disabled: _disabled,
        isChecked: on,
        isWarning: warning,
        type: 'button',
        role: 'switch',
        "aria-checked": on,
        onClick: _disabled ? undefined : () => onClick(!on),
        size: _size
      }, {
        children: jsx(SliderStyled, {
          isChecked: on,
          isWarning: warning,
          size: _size
        }, void 0)
      }), void 0), children]
    }), void 0)
  }, void 0);
};

const Wrapper$i = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1ce3k2u-0"
})(["display:flex;flex-direction:row;font-style:normal;font-size:16px;padding:0 24px;"]);

const pickColor$1 = props => {
  const active = props.isActive ? 'true' : 'false';
  const borderBottom = {
    default: {
      true: `2px solid ${props.theme.color.primary600}`,
      false: 'none'
    },
    warning: {
      true: `2px solid ${props.theme.color.accentDestructive}`,
      false: 'none'
    }
  };
  const color = {
    default: {
      true: props.theme.color.primary600,
      false: props.theme.color.onPrimaryDark
    },
    warning: {
      true: props.theme.color.accentDestructive,
      false: props.theme.color.accentDestructive
    }
  };
  return css(["color:", ";border-bottom:", ";"], color[props.type][active], borderBottom[props.type][active]);
};

const TabItemWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TabItemWrapper",
  componentId: "sc-1ce3k2u-1"
})(["font-weight:500;line-height:24px;padding:12px 5px;margin-right:14px;border-top-right-radius:6px;border-top-left-radius:6px;", " ", ""], ({
  isActive
}) => ({
  cursor: isActive ? 'default' : 'pointer'
}), pickColor$1);

const TabItem = ({
  title,
  isActive,
  onClick,
  type: _type = 'default'
}) => {
  return jsx(TabItemWrapper, Object.assign({
    isActive: isActive,
    onClick: onClick,
    type: _type
  }, {
    children: title
  }), void 0);
};

const Tabs = ({
  options,
  active,
  onChange
}) => {
  return jsx(Wrapper$i, {
    children: options.map(tab => jsx(TabItem, {
      isActive: tab.value === active,
      title: tab.title,
      type: tab.type || 'default',
      onClick: () => onChange(tab.value)
    }, tab.value))
  }, void 0);
};

const makeMediaQuery = props => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const keyMap = {
    minWidth: px => `(min-width: ${px}px)`,
    maxWidth: px => `(max-width: ${px}px)`,
    minRatio: ratio => `(-webkit-min-device-pixel-ratio: ${ratio})`,
    maxRatio: ratio => `(-webkit-max-device-pixel-ratio: ${ratio})`
  };

  if (!props.maxWidth && !props.minWidth) {
    throw new Error('mediaQuery: both of maxWidth and minWidth was not provided. One of them is required');
  }

  return Object.entries(props).map(([key, val]) => keyMap[key](val)).join(' and ');
};
const mediaQuery = query => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (...styles) => {
    return css(["@media ", "{", "}"], query, // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    css(...styles));
  };
};
const creatMediaQueries = breakpoints => {
  return {
    desktop: makeMediaQuery(breakpoints.desktop),
    smallDesktop: makeMediaQuery(breakpoints.smallDesktop),
    tablet: makeMediaQuery(breakpoints.tablet),
    smallTablet: makeMediaQuery(breakpoints.smallTablet),
    mobile: makeMediaQuery(breakpoints.mobile),
    custom: makeMediaQuery
  };
};
const createMedia = breakpoints => {
  const queries = creatMediaQueries(breakpoints);
  return {
    desktop: mediaQuery(queries.desktop),
    tablet: mediaQuery(queries.tablet),
    smallTablet: mediaQuery(queries.smallTablet),
    smallDesktop: mediaQuery(queries.smallDesktop),
    mobile: mediaQuery(queries.mobile),
    custom: props => mediaQuery(makeMediaQuery(props))
  };
};

const color = {
  // Primary

  /** @var '#2535b8' */
  primary900: '#2535b8',

  /** @var '#1f58d8' */
  primary800: '#1f58d8',

  /** @var '#186aea' */
  primary700: '#186aea',

  /** @var '#037bff' */
  primary600: '#037bff',

  /** @var '#008bff' */
  primary500: '#008bff',

  /** @var '#269dff' */
  primary400: '#269dff',

  /** @var '#55aeff' */
  primary300: '#55aeff',

  /** @var '#8ac5ff' */
  primary200: '#8ac5ff',

  /** @var '#b9dbff' */
  primary100: '#b9dbff',

  /** @var '#e2f1ff' */
  primary50: '#e2f1ff',

  /** @var '#f5faff' */
  primary25: '#f5faff',

  /** @var 'rgba(3, 123, 255, 0.16)' */
  primary16a: 'rgba(3, 123, 255, 0.16)',

  /** @var 'rgba(3, 123, 255, 0.08)' */
  primary8a: 'rgba(3, 123, 255, 0.08)',

  /** @deprecated */
  primary5: 'rgb(245 250 255 / 70%)',
  // Secondary

  /** @var '#272d33' */
  secondary900: '#272d33',

  /** @var '#39414a' */
  secondary800: '#39414a',

  /** @var '#485460' */
  secondary700: '#485460',

  /** @var '#586776' */
  secondary600: '#586776',

  /** @var '#657687' */
  secondary500: '#657687',

  /** @var '#7c8998' */
  secondary400: '#7c8998',

  /** @var '#939eaa' */
  secondary300: '#939eaa',

  /** @var '#b2bac1' */
  secondary200: '#b2bac1',

  /** @var '#d1d5d9' */
  secondary100: '#d1d5d9',
  // secondary50: '#e7eef9', // like secondary200 + opacity: 0.24;

  /** @var '#eeeeee' */
  secondary50: '#eeeeee',

  /** @var '#f5f5f5' */
  secondary25: '#f5f5f5',
  // Accent Notify

  /** @var '#e65200' */
  accentNotify900: '#e65200',

  /** @var '#ff9900' */
  accentNotify600: '#ff9900',

  /** @var '#ffe0b2' */
  accentNotify100: '#ffe0b2',

  /** @var '#ffeed3' */
  accentNotify50: '#ffeed3',
  // Accent Approve

  /** @var '#008911' */
  accentApprove800: '#008911',

  /** @var '#20b02e' */
  accentApprove600: '#20b02e',

  /** @var '#34c759' */
  accentApprove400: '#34c759',

  /** @var '#c0ebc5' */
  accentApprove100: '#c0ebc5',

  /** @var '#e6f5e6' */
  accentApprove50: '#e6f5e6',
  // Accent Destruct

  /** @var '#d11922' */
  accentDestruct800: '#d11922',

  /** @var '#ff3b30' */
  accentDestruct500: '#ff3b30',

  /** @var '#ffb4bb' */
  accentDestruct200: '#ffb4bb',

  /** @var '#ffcdd2' */
  accentDestruct100: '#ffcdd2',

  /** @var '#rgba(255, 59, 48, 0.16)' */
  accentDestruct16a: 'rgba(255, 59, 48, 0.16)',
  // accent (old)

  /** @deprecated */
  accentApproving: '#20b02e',

  /** @deprecated */
  accentApproving50: '#e6f5e6',

  /** @deprecated */
  accentNotify: '#ff9900',

  /** @deprecated */
  accentDestructive: '#f10d00',

  /** @deprecated */
  accentDestructive50: `#FFE8E6`,

  /** @deprecated */
  discountEmphasisText: '#FFCD80',
  accentApproving100: '#658D1B',
  // onPrimary

  /** @var '#fff' */
  onPrimaryLight: '#fff',

  /** @var '#111' */
  onPrimaryDark: '#111',

  /** @deprecated */
  onPrimaryMid: '#b3b3b3',
  // onSurface

  /** @deprecated */
  onSurfaceHighEmphasys: 'rgba(0, 0, 0, 0.96)',

  /** @deprecated */
  onSurfaceMidEmphasys: '#858585',

  /** @deprecated */
  onSurfaceLowEmphasys: '#ddd',
  // background

  /** @deprecated */
  backgroundBase: '#fafafa',

  /** @var 'f1f3f5' */
  backgroundPrimary: '#f1f3f5',

  /** @var '#ffffff' */
  backgroundSecondary: '#ffffff',

  /** @var '#e6f5e6' */
  backgroundApproved: '#e6f5e6',

  /** @var '#e5ecf2' */
  backgroundTertiary: '#e5ecf2',
  // Text

  /** @var '#000000' */
  textPrimary: '#000000',

  /** @var 'rgba(60, 60, 67, 0.18)' */
  textQuarternary: 'rgba(60, 60, 67, 0.18)',

  /** @var 'rgba(60, 60, 67, 0.3)' */
  textTertiary: 'rgba(60, 60, 67, 0.3)',

  /** @var 'rgba(60, 60, 67, 0.6)' */
  textSecondary: 'rgba(60, 60, 67, 0.6)',
  // outline

  /** @var '#037bff' */
  outlinePrimary: '#037bff',

  /** @var '#a8b8ca' */
  outlineSecondary: '#a8b8ca',

  /** @var '#ddd' */
  outlineNeutral: '#ddd',

  /** @var rgba(0, 0, 0, 0.08) */
  outlineMid: 'rgba(0, 0, 0, 0.08)',
  // shadow

  /** @var rgba(39, 45, 51, 0.04) */
  shadow4a: 'rgba(39, 45, 51, 0.04)',

  /** @var rgba(39, 45, 51, 0.16) */
  shadow16a: 'rgba(39, 45, 51, 0.16)',

  /** @var rgba(39, 45, 51, 0.25) */
  shadow25a: 'rgba(39, 45, 51, 0.25)'
};
const size = {
  /** @var 236 */
  leftSideBarWidth: 236,

  /** @var 1280 */
  maxContentWidth: 1280,

  /** @var 64 */
  headerHeight: 64,

  /** @var 48 */
  mobileHeaderHeight: 48
};
const padding = {
  mobile: {
    /** @var `0 0 3px #037bff` */
    content: 16
  }
};
const shadow = {
  /** @var `0 0 3px #037bff` */
  focus: `0 0 3px ${color.primary600}`,

  /** @var `0 16px 8px 0 rgba(39, 45, 51, 0.04)` */
  blur8: `0 16px 8px 0 ${color.shadow4a}`,

  /** @var `0 16px 24px 0 rgba(39, 45, 51, 0.04)` */
  blur24: `0 16px 24px 0 ${color.shadow4a}`,

  /** @var `0 16px 40px 0 rgba(39, 45, 51, 0.16)` */
  blur40: `0 16px 40px 0 ${color.shadow16a}`,

  /** @var `0 16px 40px 0 rgba(39, 45, 51, 0.25)` */
  blur40Dark: `0 16px 40px 0 ${color.shadow25a}`
};
const zIndex = {
  /** @var 1000 */
  modal: 1000,

  /** @var 100 */
  popup: 100,

  /** @var 500 */
  tooltip: 500,
  snackbar: 1001
};

const mediaQueryStub = (..._styles) => '';

const mediaStub = {
  desktop: mediaQueryStub,
  tablet: mediaQueryStub,
  smallTablet: mediaQueryStub,
  smallDesktop: mediaQueryStub,
  mobile: mediaQueryStub,
  custom: mediaQuery
};
const defaultTheme = {
  color,
  size,
  padding,
  shadow,
  zIndex,
  media: mediaStub,
  queries: {
    desktop: '',
    smallDesktop: '',
    tablet: '',
    smallTablet: '',
    mobile: '',
    custom: makeMediaQuery
  }
};

function ownKeys$c(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$c(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$c(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$c(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const DropdownIndicatorStyled$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "DropdownIndicatorStyled",
  componentId: "kkfmc9-0"
})(["width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;margin-right:15px;cursor:pointer;", ""], props => ({
  [props.menuIsOpen ? 'borderBottom' : 'borderTop']: '5px solid black;'
}));
const ClearIndicatorStyled = /*#__PURE__*/styled(SvgCross).withConfig({
  displayName: "ClearIndicatorStyled",
  componentId: "kkfmc9-1"
})(["margin:0 8px;cursor:pointer;fill:", ";width:10px;height:10px;"], props => props.theme.color.onPrimaryDark);
/**
 * @deprecated use `components` or `variant` instead
 */

const smallSelectStyles = invalid => ({
  control: (...args) => _objectSpread$c(_objectSpread$c({}, controlStyle(...args)), {}, {
    minHeight: '32px',
    borderColor: invalid ? defaultTheme.color.accentDestructive : defaultTheme.color.onSurfaceLowEmphasys
  }),
  menuList: base => _objectSpread$c(_objectSpread$c({}, base), menuListStyle),
  option: base => _objectSpread$c(_objectSpread$c(_objectSpread$c({}, base), optionStyle), {}, {
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center'
  })
});
/**
 * @deprecated use `components` or `variant` instead
 */

const controlStyle = (base, props) => _objectSpread$c(_objectSpread$c({}, base), {}, {
  backgroundColor: props.isDisabled ? defaultTheme.color.backgroundBase : defaultTheme.color.onPrimaryLight,
  cursor: props.isDisabled ? 'not-allowed' : 'pointer',
  borderColor: defaultTheme.color.onSurfaceLowEmphasys,
  opacity: props.isDisabled ? 0.8 : 1,
  minHeight: '40px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '14px',
  borderRadius: '4px',
  boxShadow: 'none',
  '@media (max-width: 767px)': {
    fontSize: '16px'
  }
});
/**
 * @deprecated use `components` or `variant` instead
 */

const optionStyle = {
  minHeight: '40px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  borderRadius: '4px',
  borderColor: defaultTheme.color.onSurfaceLowEmphasys,
  boxSizing: 'border-box',
  marginTop: '4px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer'
};
/**
 * @deprecated use `components` or `variant` instead
 */

const menuListStyle = {
  padding: '0 4px 4px 4px',
  maxHeight: '50vh',
  minWidth: '100%'
};
/**
 * @deprecated use `components` or `variant` instead
 */

const menuStyle = {
  minWidth: '100%',
  width: '100%'
};
/**
 * @deprecated use `components` or `variant` instead
 */

const baseCustomStyles = invalid => ({
  container: base => _objectSpread$c(_objectSpread$c({}, base), {}, {
    width: '100%'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  control: (...args) => {
    return _objectSpread$c(_objectSpread$c({}, controlStyle(...args)), {}, {
      borderColor: invalid ? defaultTheme.color.accentDestructive : defaultTheme.color.onSurfaceLowEmphasys
    });
  },
  valueContainer: base => _objectSpread$c(_objectSpread$c({}, base), {}, {
    gap: '4px',
    padding: '4px 0 4px 8px'
  }),
  input: base => _objectSpread$c(_objectSpread$c({}, base), {}, {
    padding: 0
  })
});

const isOverflown = el => {
  return el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
};

function ownKeys$b(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$b(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$b(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$b(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const TextInnerAnimation = /*#__PURE__*/keyframes(["0%{margin-left:0;}100%{margin-left:100%;}"]);
const TextContentAnimation = /*#__PURE__*/keyframes(["0%{margin-left:0;}100%{margin-left:-100%;}"]);
const AnimatedText = /*#__PURE__*/styled.div.withConfig({
  displayName: "AnimatedText",
  componentId: "ipcfeg-0"
})(["width:max-content;", ""], ({
  animate
}) => !animate && css(["animation:none !important;& > div{animation:none !important;}"]));
const StyledOption = /*#__PURE__*/styled(components.Option).withConfig({
  displayName: "StyledOption",
  componentId: "ipcfeg-1"
})(["div&{position:relative;min-height:40px;font-family:Montserrat;font-style:normal;font-weight:500;font-size:14px;line-height:24px;border-radius:4px;box-sizing:border-box;margin-top:4px;cursor:pointer;overflow:hidden;text-overflow:ellipsis;", " &:hover > ", ",&:focus > ", "{animation:", " 2.5s linear infinite alternate;& > div{animation:", " 2.5s linear infinite alternate;}}}"], ({
  wrap
}) => wrap ? css(["white-space:break-spaces;"]) : css(["white-space:nowrap;"]), AnimatedText, AnimatedText, TextInnerAnimation, TextContentAnimation);
const OptionWithAnimatedText = (_ref) => {
  let {
    children,
    innerProps
  } = _ref,
      props = _objectWithoutProperties(_ref, ["children", "innerProps"]);

  const [ref, setRef] = useState(null);
  const overflown = useMemo(() => ref && isOverflown(ref), [ref]);
  return jsx(StyledOption, Object.assign({}, props, {
    innerRef: setRef,
    innerProps: _objectSpread$b(_objectSpread$b({}, innerProps), {}, {
      'data-test-id': props.value
    })
  }, {
    children: jsx(AnimatedText, Object.assign({
      animate: overflown
    }, {
      children: jsx("div", {
        children: children
      }, void 0)
    }), void 0)
  }), void 0);
};
const Option$1 = props => {
  const [ref, setRef] = useState(null);
  const overflown = useMemo(() => ref && isOverflown(ref), [ref]);
  const {
    innerProps,
    label
  } = props;
  return jsx(StyledOption, Object.assign({}, props, {
    innerRef: setRef,
    innerProps: _objectSpread$b(_objectSpread$b({}, innerProps), {}, {
      'data-test-id': props.value,
      title: overflown ? label : undefined
    })
  }), void 0);
};
const OptionWithWrappedText = props => {
  const {
    innerProps,
    label
  } = props;
  return jsx(StyledOption, Object.assign({}, props, {
    innerProps: _objectSpread$b(_objectSpread$b({}, innerProps), {}, {
      'data-test-id': props.value,
      title: label
    }),
    wrap: true
  }), void 0);
};

function ownKeys$a(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$a(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$a(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$a(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const StyledMultiValueContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledMultiValueContainer",
  componentId: "ays6so-0"
})(["display:grid;grid-template-columns:auto max-content;align-items:center;background:rgba(3,123,255,0.08);border-radius:4px;box-sizing:border-box;max-width:80%;"]);
const StyledMultiValueLabel = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledMultiValueLabel",
  componentId: "ays6so-1"
})(["padding:4px 0 4px 8px;font-weight:500;font-size:12px;line-height:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
const StyledMultiValueRemove = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledMultiValueRemove",
  componentId: "ays6so-2"
})(["display:flex;align-items:center;height:100%;padding:4px 6px;cursor:pointer;& > svg{transition:0.2s opacity ease;}&:hover,&:focus{& > svg{opacity:0.8;}}"]);
const StyledPlaceholder = /*#__PURE__*/styled(components.Placeholder).withConfig({
  displayName: "StyledPlaceholder",
  componentId: "ays6so-3"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
const StyledMenu = /*#__PURE__*/styled(components.Menu).withConfig({
  displayName: "StyledMenu",
  componentId: "ays6so-4"
})(["z-index:10 !important;width:", " !important;min-width:", " !important;max-width:", " !important;"], ({
  width
}) => width, ({
  minWidth
}) => minWidth, ({
  maxWidth
}) => maxWidth);
const StyledMenuList = /*#__PURE__*/styled(components.MenuList).withConfig({
  displayName: "StyledMenuList",
  componentId: "ays6so-5"
})(["padding:0 4px 4px 4px !important;max-height:", " !important;"], ({
  maxHeight
}) => maxHeight);
const SingleValueStyled = /*#__PURE__*/styled(components.SingleValue).withConfig({
  displayName: "SingleValueStyled",
  componentId: "ays6so-6"
})(["overflow:visible !important;word-break:break-word !important;text-overflow:unset !important;white-space:normal !important;"]);

const DropdownIndicator$1 = ({
  selectProps
}) => jsx(DropdownIndicatorStyled$1, {
  menuIsOpen: !!selectProps.menuIsOpen
}, void 0);

const ClearIndicator = ({
  innerProps
}) => jsx("div", Object.assign({}, innerProps, {
  children: jsx(ClearIndicatorStyled, {}, void 0)
}), void 0);

const MultiValueContainer = ({
  children
}) => jsx(StyledMultiValueContainer, {
  children: children
}, void 0);

const MultiValueLabel = ({
  children
}) => jsx(StyledMultiValueLabel, {
  children: children
}, void 0);

const MultiValueRemove = (_ref) => {
  let innerProps = _objectWithoutProperties(_ref.innerProps, ["ref", "className"]);

  return jsx(StyledMultiValueRemove, Object.assign({}, innerProps, {
    children: jsx(SvgCrossCircle, {}, void 0)
  }), void 0);
};

const Placeholder = props => jsx(StyledPlaceholder, Object.assign({}, props), void 0);

const SingleValueWithWrappedText = props => jsx(SingleValueStyled, Object.assign({}, props), void 0);

const DISABLED_FILTER_CB = () => true;
/**
 * Basic select without any async logic and value is an object.
 */


const BasicSelectForward = /*#__PURE__*/React__default.forwardRef((_ref2, ref) => {
  let {
    id,
    value,
    label,
    invalid = false,
    errorText,
    customStyles = {},
    labelStyle = {},
    menuPlacement,
    required,
    className,
    name,
    components: componentsFromProps,
    progress,
    loadingMessage = 'Loading ...',
    optional,
    onSearchChange,
    variant = 'default',
    disableFilterOnSearchChange,
    searchValue,
    minWidth = '0',
    maxWidth = 'none',
    width = '100%',
    menuMinWidth = '100%',
    menuMaxWidth = '175%',
    menuWidth = 'auto',
    menuMaxHeight = '50vh',
    disableTags,
    isMulti,
    animateOverflowingText,
    disableSearchClearOnSelect = isMulti,
    disableFilterOptionsOnSearchChange,
    wrapOptionsText,
    wrapValueText,
    options,
    filterOption: filterOptionFromProps,
    hideSelectedOptions = isMulti && !disableTags,
    'data-test-id': dataTestId = 'select',
    closeMenuOnSelect = !isMulti,
    menuIsOpen,
    onMenuOpen,
    onMenuClose
  } = _ref2,
      props = _objectWithoutProperties(_ref2, ["id", "value", "label", "invalid", "errorText", "customStyles", "labelStyle", "menuPlacement", "required", "className", "name", "components", "progress", "loadingMessage", "optional", "onSearchChange", "variant", "disableFilterOnSearchChange", "searchValue", "minWidth", "maxWidth", "width", "menuMinWidth", "menuMaxWidth", "menuWidth", "menuMaxHeight", "disableTags", "isMulti", "animateOverflowingText", "disableSearchClearOnSelect", "disableFilterOptionsOnSearchChange", "wrapOptionsText", "wrapValueText", "options", "filterOption", "hideSelectedOptions", "data-test-id", "closeMenuOnSelect", "menuIsOpen", "onMenuOpen", "onMenuClose"]);

  const {
    t
  } = useTranslation('ui');
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState(searchValue);
  const smallStyles = variant === 'small' ? smallSelectStyles(invalid) : {};
  useEffect(() => {
    if (searchValue !== inputValue) setInputValue(searchValue); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);
  const MultiValue = useCallback(props => disableTags ? null : jsx(components.MultiValue, Object.assign({}, props), void 0), [disableTags]);
  const ValueContainer = useCallback((_ref3) => {
    let {
      selectProps,
      children
    } = _ref3,
        props = _objectWithoutProperties(_ref3, ["selectProps", "children"]);

    return jsxs(components.ValueContainer, Object.assign({
      selectProps: selectProps,
      "data-test-id": "value-container"
    }, props, {
      children: [!!(disableTags && Array.isArray(selectProps.value) && selectProps.value.length) && (selectProps.value.length === 1 ? t('BasicSelect.one-item-selected') : t('BasicSelect.nth-items-selected', {
        count: selectProps.value.length
      })), children]
    }), void 0);
  }, [disableTags, t]);
  const Menu = useCallback(props => jsx(StyledMenu, Object.assign({}, props, {
    minWidth: menuMinWidth,
    maxWidth: menuMaxWidth,
    width: menuWidth
  }), void 0), [menuMaxWidth, menuMinWidth, menuWidth]);
  const MenuList = useCallback((_ref4) => {
    let {
      children
    } = _ref4,
        props = _objectWithoutProperties(_ref4, ["children"]);

    return jsx(StyledMenuList, Object.assign({}, props, {
      maxHeight: menuMaxHeight
    }, {
      children: children
    }), void 0);
  }, [menuMaxHeight]);
  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true);
    onMenuOpen === null || onMenuOpen === void 0 ? void 0 : onMenuOpen();
  }, [onMenuOpen]);
  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    onMenuClose === null || onMenuClose === void 0 ? void 0 : onMenuClose();
  }, [onMenuClose]);
  return jsx(FormField, {
    className: className,
    label: label,
    labelStyle: labelStyle,
    invalid: invalid,
    errorText: errorText,
    required: required,
    name: name,
    id: id,
    optional: optional,
    width: width,
    maxWidth: maxWidth,
    minWidth: minWidth,
    testId: dataTestId,
    "data-is-multi": isMulti,
    "data-is-open": menuIsOpen || menuOpen,
    render: ({
      id,
      name
    }) => jsx(ReactSelect, Object.assign({
      isMulti: isMulti,
      inputId: id,
      name: name,
      isLoading: progress === Progress.WORK,
      loadingMessage: () => loadingMessage,
      value: value || null,
      options: options,
      styles: _objectSpread$a(_objectSpread$a(_objectSpread$a({}, baseCustomStyles(invalid)), smallStyles), customStyles),
      ref: ref,
      "data-test-id": "select",
      inputValue: inputValue,
      filterOption: filterOptionFromProps ? filterOptionFromProps : disableFilterOptionsOnSearchChange ? DISABLED_FILTER_CB : undefined,
      onInputChange: (value, actionMeta) => {
        if (!disableSearchClearOnSelect || actionMeta.action === 'input-change' || actionMeta.action === 'menu-close') {
          setInputValue(value);

          if (!disableFilterOnSearchChange || actionMeta.action === 'input-change') {
            onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(value);
          }
        }
      },
      menuIsOpen: menuIsOpen || menuOpen,
      onMenuClose: handleMenuClose,
      onMenuOpen: handleMenuOpen,
      closeMenuOnSelect: closeMenuOnSelect,
      components: _objectSpread$a({
        Placeholder,
        ValueContainer,
        DropdownIndicator: DropdownIndicator$1,
        ClearIndicator,
        MultiValue,
        MultiValueContainer,
        MultiValueLabel,
        MultiValueRemove,
        Option: animateOverflowingText && !wrapOptionsText ? OptionWithAnimatedText : wrapOptionsText ? OptionWithWrappedText : Option$1,
        Menu,
        MenuList,
        SingleValue: wrapValueText ? SingleValueWithWrappedText : components.SingleValue
      }, componentsFromProps),
      menuPlacement: menuPlacement,
      hideSelectedOptions: hideSelectedOptions
    }, props), void 0)
  }, void 0);
});
const BasicSelect = BasicSelectForward;

/**
 * Same as BasicSelect but:
 * 1. Value is a primitive value instead of an object
 * 2. Full object value to pass to the BasicSelect is searched through the passed options based on the id field
 * 3. You don't need to pass getOptionValue and getOptionLabel because option type must contain id and title fields.
 */

const SimpleSelect = (_ref) => {
  let {
    options = [],
    value: _value,
    //@ts-expect-error for good it is necessary that only string be, but errors have already been made
    //in the select and now you can do it normally only by breaking the places where in the title jsx elements,
    //so let's just ignore errors
    getOptionLabel = option => option.title,
    getOptionValue = option => `${option.id}`,
    onChange
  } = _ref,
      props = _objectWithoutProperties(_ref, ["options", "value", "getOptionLabel", "getOptionValue", "onChange"]);

  const value = useMemo(() => options.find(opt => getOptionValue(opt) === _value), [_value, getOptionValue, options]);
  return jsx(BasicSelect, Object.assign({
    value: value,
    options: options,
    getOptionLabel: getOptionLabel,
    getOptionValue: getOptionValue,
    onChange: opt => onChange(opt ? getOptionValue(opt) : undefined, opt ? getOptionLabel(opt) : undefined, opt)
  }, props), void 0);
};

const noop$2 = () => {};

const customStyles$2 = {
  control: () => ({}),
  valueContainer: () => ({
    display: 'none'
  }),
  indicatorsContainer: () => ({
    display: 'none'
  })
};
const OptionStyled$4 = /*#__PURE__*/styled.div.withConfig({
  displayName: "OptionStyled",
  componentId: "sc-1iqv779-0"
})(["display:grid;grid-template-columns:max-content auto;margin:0;cursor:pointer;color:", ";transition:0.3s background-color;font-weight:500;line-height:20px;min-height:40px;font-size:14px;border-radius:4px;padding:10px 8px;box-sizing:border-box;align-items:start;&[data-disabled='true']{pointer-events:none;span{opacity:0.7;}&:hover{background-color:transparent;}}&:hover{background-color:", ";button[role='checkbox']{border-color:", ";}}&[data-nested-level='0']{font-weight:700;}&[data-nested-level='1']{font-weight:600;margin-left:12px;}&[data-nested-level='2']{margin-left:24px;}"], props => props.theme.color.onPrimaryDark, props => props.theme.color.primary50, props => props.theme.color.primary500);
const PlaceholderStyled$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "PlaceholderStyled",
  componentId: "sc-1iqv779-1"
})(["font-family:'Montserrat',-apple-system,BlinkMacSystemFont,sans-serif;color:", ";white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:calc(100% - 40px);position:absolute;left:10px;"], props => props.all ? '#808080' : props.theme.color.onPrimaryDark);
const ClearButtonStyled$1 = /*#__PURE__*/styled(ClearIndicatorStyled).withConfig({
  displayName: "ClearButtonStyled",
  componentId: "sc-1iqv779-2"
})(["position:absolute;right:30px;bottom:11px;"]);
const Wrapper$h = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1iqv779-3"
})(["position:relative;top:-8px;right:-14px;"]);
const SwitchSelect = (_ref) => {
  let {
    selected = [],
    showingLabel,
    onChange,
    options,
    onClear,
    singleSelected,
    noneSelected
  } = _ref,
      props = _objectWithoutProperties(_ref, ["selected", "showingLabel", "onChange", "options", "onClear", "singleSelected", "noneSelected"]);

  const {
    t
  } = useTranslation('ui');

  const handleClear = e => {
    e.stopPropagation();
    e.preventDefault();
    e.bubbles = false;
    onClear && onClear();
  };

  const unselectedOptions = useMemo(() => options.filter(o => !selected.includes(o.id)), [options, selected]);
  const selectedOptions = useMemo(() => options.filter(o => selected.includes(o.id)), [options, selected]);

  const Option = ({
    value,
    data,
    label
  }) => {
    return jsxs(OptionStyled$4, Object.assign({
      onClick: () => onChange(value),
      id: value,
      "data-nested-level": data.nestedLevel || '',
      "data-disabled": data.isParentSelected
    }, {
      children: [jsx(Switch, {
        on: !!(selected.includes(value) || data.isParentSelected),
        onClick: noop$2,
        disabled: data.isParentSelected,
        size: 'small'
      }, void 0), jsx("span", {
        children: data.labelToRender || label
      }, void 0)]
    }), void 0);
  };

  const Placeholder = ({
    selectProps
  }) => {
    return jsx(PlaceholderStyled$1, Object.assign({
      all: selected.length === 0
    }, {
      children: selected.length > 0 ? singleSelected || t('CheckboxSelect.multipleSelected', {
        items: selectProps.placeholder
      }) : noneSelected ? noneSelected : t('CheckboxSelect.allSelected', {
        items: selectProps.placeholder
      })
    }), void 0);
  };

  return jsxs(Wrapper$h, {
    children: [jsx(SimpleSelect, Object.assign({}, props, {
      onChange: noop$2,
      customStyles: customStyles$2,
      options: [...unselectedOptions, {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        label: showingLabel,
        options: selectedOptions
      }],
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      components: {
        Option,
        Placeholder
      },
      filterOption: () => true,
      isSearchable: false,
      noneSelected: noneSelected,
      menuIsOpen: true,
      minMenuHeight: 800
    }), void 0), !!selected.length && !!onClear && jsx(ClearButtonStyled$1, {
      onClick: handleClear
    }, void 0)]
  }, void 0);
};

const Wrapper$g = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-7u9avw-0"
})(["position:relative;display:flex;align-items:baseline;gap:24px;padding-right:", ";width:100%;"], ({
  showTabsSelect
}) => showTabsSelect ? '24px' : '170px');
const TabsWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TabsWrapper",
  componentId: "sc-7u9avw-1"
})(["position:relative;display:flex;justify-content:space-between;align-items:baseline;gap:24px;overflow:hidden;width:100%;white-space:nowrap;margin-left:24px;& > [class^='Wrapper']{overflow-x:auto;padding:0;}"]);
const MoreButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "MoreButton",
  componentId: "sc-7u9avw-2"
})(["min-width:160px;min-height:32px;background-color:", ";& svg{width:10px;height:5px;fill:", ";}&:hover{background-color:", ";}"], props => props.theme.color.primary50, props => props.theme.color.primary600, props => props.theme.color.primary100);
const IconTriangleUp = /*#__PURE__*/styled(SvgTriangleDown).withConfig({
  displayName: "IconTriangleUp",
  componentId: "sc-7u9avw-3"
})(["color:", ";transform:rotate(180deg);"], props => props.theme.color.primary600);
const SwitchSelectWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "SwitchSelectWrapper",
  componentId: "sc-7u9avw-4"
})(["position:absolute;right:41px;top:50px;width:280px;height:fit-content;overflow:visible;"]);
const TabsWithSelect = ({
  tabsOptions,
  changeTabsText,
  tabsMoreText,
  showingLabel,
  showTabsSelect: _showTabsSelect = true,
  activeTab,
  selectedOptions,
  onTabChange,
  onSelectedOptionsChange
}) => {
  const {
    t
  } = useTranslation('ui');
  const [initialTabChosen, setInitialTabChosen] = useState(!!activeTab);
  const selectOptions = useMemo(() => {
    return tabsOptions.map(tab => ({
      id: tab.value,
      title: tab.title
    }));
  }, [tabsOptions]);
  const [selectedOptionsState, setSelectedOptionsState] = useState(selectedOptions);
  useEffect(() => {
    if (!selectedOptions) {
      setSelectedOptionsState(selectOptions.map(o => o.id));
    }
  }, [selectOptions, selectedOptions]);
  const tabsState = useMemo(() => {
    return _showTabsSelect ? tabsOptions.filter(tab => selectedOptionsState === null || selectedOptionsState === void 0 ? void 0 : selectedOptionsState.includes(tab.value)) : tabsOptions;
  }, [selectedOptionsState, _showTabsSelect, tabsOptions]);
  useEffect(() => {
    if (!!tabsState.length && !initialTabChosen) {
      onTabChange(tabsState[0].value);
      setInitialTabChosen(true);
    }
  }, [initialTabChosen, onTabChange, tabsState, tabsState.length]);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => setShowDropdown(false));
  const moreButtonText = useMemo(() => {
    const unselectedCount = selectOptions.length - tabsState.length;
    return tabsState.length === selectOptions.length ? changeTabsText || t('TabsWithSelect:changeTabs') : `+${unselectedCount} ${tabsMoreText || t('TabsWithSelect:tabsMore')}`;
  }, [changeTabsText, selectOptions.length, t, tabsMoreText, tabsState.length]);
  const onSelectOptionChange = useCallback(value => {
    const saved = [...(selectedOptionsState || [])];

    if (!(selectedOptionsState !== null && selectedOptionsState !== void 0 && selectedOptionsState.includes(value))) {
      saved.push(value);
    } else {
      saved.splice(saved.indexOf(value), 1);
    }

    setSelectedOptionsState(saved);
    onSelectedOptionsChange(saved);
  }, [onSelectedOptionsChange, selectedOptionsState]);
  return jsxs(Wrapper$g, Object.assign({
    showTabsSelect: _showTabsSelect
  }, {
    children: [jsx(TabsWrapper, {
      children: jsx(Tabs, {
        options: tabsState,
        active: activeTab || '',
        onChange: onTabChange
      }, void 0)
    }, void 0), _showTabsSelect && jsxs(Fragment, {
      children: [jsxs(MoreButton, Object.assign({
        intent: "secondary",
        size: "small",
        filled: true,
        onClick: () => setShowDropdown(!showDropdown)
      }, {
        children: [moreButtonText, showDropdown ? jsx(IconTriangleUp, {}, void 0) : jsx(SvgTriangleDown, {}, void 0)]
      }), void 0), showDropdown && jsx(SwitchSelectWrapper, Object.assign({
        ref: ref
      }, {
        children: jsx(SwitchSelect, {
          options: selectOptions,
          selected: selectedOptionsState,
          onChange: onSelectOptionChange,
          onMenuClose: () => setShowDropdown(false),
          showingLabel: showingLabel || t('TabsWithSelect:showing')
        }, void 0)
      }), void 0)]
    }, void 0)]
  }), void 0);
};

const Wrapper$f = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "gt8glr-0"
})(["position:relative;outline:none;max-width:220px;"]);
const DatePicker = ({
  date: propsDate = '',
  onChange,
  label,
  disabled,
  required,
  errorText,
  invalid,
  name,
  className,
  format,
  testId,
  optional
}) => {
  var _window$navigator;

  const date = parseISO(propsDate);
  const isFirstChange = useRef(true);
  const handleDateChange = useCallback(value => {
    // default time is 12:00
    let selectedDateTime = value || '';

    if (value && isFirstChange.current) {
      selectedDateTime = set(new Date(value), {
        hours: 12,
        minutes: 0,
        seconds: 0,
        milliseconds: 0
      });
      isFirstChange.current = false;
    } else if (value) {
      selectedDateTime = new Date(value);
    }

    onChange(selectedDateTime instanceof Date ? selectedDateTime.toISOString() : '', selectedDateTime || null);
  }, [onChange]);
  let locale = 'en-CA'; // window is undefined in SSR

  if (typeof window !== 'undefined' && (_window$navigator = window.navigator) !== null && _window$navigator !== void 0 && _window$navigator.language) {
    locale = window.navigator.language;
  }

  return jsx(Wrapper$f, Object.assign({
    "data-invalid": invalid,
    "data-test-id": testId
  }, {
    children: jsx(FormField, {
      name: name,
      className: className,
      label: label,
      invalid: invalid,
      errorText: errorText,
      required: required,
      optional: optional,
      render: () => jsx(DateTimePicker, {
        dayPlaceholder: 'DD',
        monthPlaceholder: 'MM',
        yearPlaceholder: 'YY',
        value: isValid(date) ? date : null,
        onChange: handleDateChange,
        format: format || 'dd/MM/yy HH:mm',
        locale: locale,
        calendarIcon: jsx(SvgCalendar, {}, void 0),
        disabled: disabled,
        name: 'datetime-picker',
        showLeadingZeros: true,
        disableClock: true
      }, void 0)
    }, void 0)
  }), void 0);
};

const apiDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss.SSS';
const AVAILABLE_CURRENCY = [Currency.CAD, Currency.EUR, Currency.RUB, Currency.USD];
const DATE_FORMAT = 'dd.MM.yy';
var STORAGE_KEYS;

(function (STORAGE_KEYS) {
  STORAGE_KEYS["ProcoteQuestionnaire"] = "procote_questionnaire";
})(STORAGE_KEYS || (STORAGE_KEYS = {}));

const POUNDS_TO_KILOGRAMS = 2204.62;
const EMPTY_ARRAY = [];

let _$i = t => t,
    _t$i;
const ClearIcon$1 = /*#__PURE__*/styled(SvgCross).withConfig({
  displayName: "ClearIcon",
  componentId: "sc-1fn95nc-0"
})(["width:9.26px;height:9.26px;position:absolute;top:50%;right:7px;transform:translateY(-50%);opacity:1;transition:0.3s opacity,0.3s fill;cursor:pointer;&:hover{fill:", ";}"], ({
  theme
}) => theme.color.primary600);
const CalendarContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "CalendarContainer",
  componentId: "sc-1fn95nc-1"
})(["position:absolute;width:min-content;top:40px;"]);
const Dash = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dash",
  componentId: "sc-1fn95nc-2"
})(["height:1px;width:10px;background-color:", ";margin-right:6px;margin-left:6px;"], ({
  theme
}) => theme.color.secondary200);
const InputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputWrapper",
  componentId: "sc-1fn95nc-3"
})(["display:flex;justify-content:flex-start;align-items:center;height:32px;width:210px;padding:6px 15px;position:relative;background:", ";border:1px solid;box-sizing:border-box;border-radius:4px;transition:0.3s border-color;", " &:hover{border-color:", ";}"], ({
  theme
}) => theme.color.onPrimaryLight, ({
  theme,
  isFocused,
  isValid
}) => ({
  borderColor: isValid ? isFocused ? theme.color.primary600 : theme.color.onSurfaceLowEmphasys : theme.color.accentDestructive
}), ({
  theme,
  isValid
}) => isValid ? theme.color.onPrimaryMid : theme.color.accentDestructive);
const Input$3 = /*#__PURE__*/styled.input.withConfig({
  displayName: "Input",
  componentId: "sc-1fn95nc-4"
})(["text-align:center;width:80px;padding:0;flex-grow:1;font-weight:500;font-size:14px;line-height:20px;font-family:Montserrat;font-style:normal;color:", ";outline:none;border:none;&::placeholder{opacity:0.48;}", ""], ({
  theme
}) => theme.color.onPrimaryDark, props => props.theme.media.mobile(_t$i || (_t$i = _$i`
    font-size: 16px;
  `)));
const Wrapper$e = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1fn95nc-5"
})(["position:relative;outline:none;"]);
const Optional = /*#__PURE__*/styled.span.withConfig({
  displayName: "Optional",
  componentId: "sc-1fn95nc-6"
})(["font-style:normal;font-weight:500;font-size:12px;line-height:16px;color:", ";margin-left:auto;text-transform:none;position:absolute;bottom:calc(100% + 8px);right:0;"], ({
  theme
}) => theme.color.accentNotify);
const ErrorText = /*#__PURE__*/styled.div.withConfig({
  displayName: "ErrorText",
  componentId: "sc-1fn95nc-7"
})(["color:", ";margin-top:4px;font-style:normal;font-weight:500;font-size:12px;line-height:16px;position:absolute;top:100%;"], ({
  theme
}) => theme.color.accentDestructive);

const RangeDatePicker = (_ref) => {
  let {
    start,
    end,
    onChange,
    onClear,
    format: format$1 = DATE_FORMAT,
    calendarProps,
    placeholder = '',
    optional,
    invalid,
    errorText = '',
    isClearable,
    onFocus,
    onBlur
  } = _ref,
      divProps = _objectWithoutProperties(_ref, ["start", "end", "onChange", "onClear", "format", "calendarProps", "placeholder", "optional", "invalid", "errorText", "isClearable", "onFocus", "onBlur"]);

  const {
    t
  } = useTranslation('ui');
  const [showCalendar, setShowCalendar] = useState(false);
  const timerId = useRef(0);
  const dateRange = useMemo(() => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    return [isValid(startDate) ? startDate : new Date(), isValid(endDate) ? endDate : new Date()];
  }, [start, end]);
  const [startInputValue, setStartInputValue] = useState(start ? format(parseISO(start), format$1) : '');
  const [endInputValue, setEndInputValue] = useState(end ? format(parseISO(end), format$1) : '');
  useEffect(() => {
    setStartInputValue(start ? format(parseISO(start), format$1) : '');
  }, [format$1, start]);
  useEffect(() => {
    setEndInputValue(end ? format(parseISO(end), format$1) : '');
  }, [end, format$1]);
  const handleStartInputChange = useCallback(e => {
    const value = e.target.value.replace(/[^.\/0-9]+/, '');

    if (!value && !endInputValue) {
      onChange(['', '']);
    }

    if (value.length > 8) {
      return;
    }

    if (value.length === 8) {
      const start = parse(value, format$1, new Date());
      const end = parse(endInputValue, format$1, new Date());

      if (isValid(start) && isValid(end)) {
        onChange([start.toISOString(), end.toISOString()]);
      }
    }

    setStartInputValue(value);
  }, [endInputValue, format$1, onChange]);
  const handleEndInputChange = useCallback(e => {
    const value = e.target.value.replace(/[^.\/0-9]+/, '');

    if (!value && !startInputValue) {
      onChange(['', '']);
    }

    if (value.length > 8) {
      return;
    }

    if (value.length === 8) {
      const start = parse(startInputValue, format$1, new Date());
      const end = parse(value, format$1, new Date());

      if (isValid(start) && isValid(end)) {
        onChange([start.toISOString(), end.toISOString()]);
      }
    }

    setEndInputValue(value);
  }, [format$1, onChange, startInputValue]);

  const handleClear = () => {
    setStartInputValue('');
    setEndInputValue('');
    onChange(['', '']);
    onClear && onClear();
  };

  const isDateRangeSet = useMemo(() => !start && !end || isValid(parseISO(start)) && isValid(parseISO(end)), [end, start]);
  const isInputValid = !invalid && isDateRangeSet;
  const isClearIconVisible = isClearable || onClear;
  const ref = useRef(null); // like onBlur function but for Safari

  const handleClickOut = event => {
    var _ref$current;

    if (!((_ref$current = ref.current) !== null && _ref$current !== void 0 && _ref$current.contains(event.target))) {
      timerId.current = window.setTimeout(() => setShowCalendar(false), 0);
    }
  };

  return jsxs(Wrapper$e, Object.assign({}, divProps, {
    ref: ref,
    onFocus: (...args) => {
      clearTimeout(timerId.current);
      setShowCalendar(true);
      window.addEventListener('click', handleClickOut);
      onFocus === null || onFocus === void 0 ? void 0 : onFocus(...args);
    },
    onBlur: (...args) => {
      window.removeEventListener('click', handleClickOut);
      onBlur === null || onBlur === void 0 ? void 0 : onBlur(...args);
    }
  }, {
    children: [jsxs(InputWrapper, Object.assign({
      isFocused: showCalendar,
      isValid: isInputValid
    }, {
      children: [jsx(Input$3, {
        value: startInputValue,
        placeholder: placeholder || (format$1 === null || format$1 === void 0 ? void 0 : format$1.toUpperCase()),
        onChange: handleStartInputChange
      }, void 0), jsx(Dash, {}, void 0), jsx(Input$3, {
        value: endInputValue,
        placeholder: placeholder || (format$1 === null || format$1 === void 0 ? void 0 : format$1.toUpperCase()),
        onChange: handleEndInputChange
      }, void 0), isClearIconVisible && jsx(ClearIcon$1, {
        onClick: handleClear
      }, void 0), optional && jsx(Optional, {
        children: t('common.optional')
      }, void 0)]
    }), void 0), invalid && jsx(ErrorText, {
      children: errorText
    }, void 0), jsx(CalendarContainer, Object.assign({
      style: {
        display: showCalendar ? 'block' : 'none',
        zIndex: defaultTheme.zIndex.popup
      }
    }, {
      children: jsx(Calendar, Object.assign({
        value: dateRange,
        onChange: value => {
          const startDate = value[0];
          const endDate = value[1];
          onChange([startDate.toISOString(), endDate.toISOString()]);
          setStartInputValue(format(startDate, format$1));
          setEndInputValue(format(endDate, format$1));
          setShowCalendar(false);
        },
        selectRange: true
      }, calendarProps), void 0)
    }), void 0)]
  }), void 0);
};

const pickColor = props => {
  switch (props.color) {
    case 'blue':
      return props.theme.color.primary600;

    case 'green':
      return props.theme.color.accentApprove600;

    case 'darkgreen':
      return props.theme.color.accentApproving100;

    case 'orange':
      return props.theme.color.accentNotify600;

    case 'gray':
      return props.theme.color.backgroundPrimary;

    case 'red':
      return props.theme.color.accentDestruct500;
  }
};

const chipColorCss = props => {
  const selectedColor = pickColor(props);
  let backgroundColor;
  let color;
  let borderRadius;
  let height;
  const borderColor = backgroundColor = color = selectedColor;
  let hoverBackgroundColor;

  if (props.active) {
    color = props.color === 'gray' ? props.theme.color.onSurfaceMidEmphasys : props.theme.color.onPrimaryLight;
    hoverBackgroundColor = `${backgroundColor}AA`;
  } else {
    backgroundColor = props.theme.color.onPrimaryLight;
    hoverBackgroundColor = `${selectedColor}22`;
  }

  if (!props.clickable) {
    hoverBackgroundColor = backgroundColor;
  }

  switch (props.size) {
    case 'small':
      borderRadius = '8px';
      height = '28px';
      break;

    default:
      borderRadius = '4px';
      height = '32px';
  }

  return css(["border-color:", ";background-color:", ";color:", ";border-radius:", ";height:", ";&:hover{background-color:", ";}&:focus{box-shadow:0 0 4px ", ";}"], borderColor, backgroundColor, color, borderRadius, height, hoverBackgroundColor, borderColor);
};

const ChipStyled = /*#__PURE__*/styled.button.withConfig({
  displayName: "ChipStyled",
  componentId: "svgst4-0"
})(["height:32px;border-width:1px;border-style:solid;font-weight:500;font-size:14px;line-height:20px;display:flex;align-items:center;justify-content:center;padding:6px 12px;box-sizing:border-box;border-radius:4px;transition:0.1s background-color ease-out;cursor:", ";outline:none;flex-shrink:0;", ""], props => props.clickable ? 'pointer' : 'default', chipColorCss);
const BulletStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "BulletStyled",
  componentId: "svgst4-1"
})(["width:6px;height:6px;border-radius:50%;margin-right:8px;background-color:", ";"], props => props.active ? props.theme.color.onPrimaryLight : pickColor(props));
const Chip = (_ref) => {
  let {
    children,
    color = 'blue',
    size = 'medium',
    active,
    bullet,
    value,
    onClick,
    className,
    tabIndex
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["children", "color", "size", "active", "bullet", "value", "onClick", "className", "tabIndex"]);

  const clickable = !!onClick;
  return jsxs(ChipStyled, Object.assign({
    clickable: clickable,
    color: color,
    active: active,
    onClick: e => onClick && onClick(value, e),
    className: className,
    size: size,
    tabIndex: clickable ? tabIndex : -1
  }, rest, {
    children: [bullet && jsx(BulletStyled, {
      color: color,
      active: active
    }, void 0), children]
  }), void 0);
};

const Wrapper$d = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1lktof1-0"
})(["background:", ";border-radius:12px;font-style:normal;font-weight:600;font-size:12px;line-height:20px;text-align:center;color:", ";", ""], ({
  theme
}) => theme.color.accentNotify, ({
  theme
}) => theme.color.onPrimaryLight, ({
  size
}) => {
  switch (size) {
    case 'small':
      return {
        width: '16px',
        height: '16px',
        lineHeight: '16px',
        marginLeft: '4px',
        padding: 'auto'
      };

    case 'big':
      return {
        lineHeight: '20px',
        padding: '2px 8px'
      };
  }
});
const DiscountBadge = ({
  text,
  className,
  size: _size = 'big'
}) => {
  return jsx(Wrapper$d, Object.assign({
    size: _size,
    className: className
  }, {
    children: text
  }), void 0);
};

const getContainerAccentColor = props => {
  if (props.dragStatus === 'none') {
    return props.theme.color.primary500;
  }

  if (props.dragStatus === 'active') {
    return props.theme.color.primary600;
  }

  if (props.dragStatus === 'accept') {
    return props.theme.color.accentApproving;
  }

  if (props.dragStatus === 'reject') {
    return props.theme.color.accentDestructive;
  }

  if (props.dragStatus === 'invalid-count') {
    return props.theme.color.accentDestructive;
  }

  return props.theme.color.primary500;
};

const Container$3 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "ul72rp-0"
})(["width:100%;height:100%;border-radius:8px;border:2px dashed ", ";background-color:", ";"], getContainerAccentColor, props => helpersColor.alphaColor(getContainerAccentColor(props), 0.1));
const ContentContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentContainer",
  componentId: "ul72rp-1"
})(["width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-weight:500;font-size:14px;line-height:20px;"]);
const ContentMessageAccept = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentMessageAccept",
  componentId: "ul72rp-2"
})(["color:", ";"], props => props.theme.color.accentApproving);
const ContentMessageReject = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentMessageReject",
  componentId: "ul72rp-3"
})(["color:", ";"], props => props.theme.color.accentDestructive);
const ContentMessageDefault = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentMessageDefault",
  componentId: "ul72rp-4"
})(["color:", ";"], props => props.theme.color.onPrimaryDark);
const DropLink = /*#__PURE__*/styled.span.withConfig({
  displayName: "DropLink",
  componentId: "ul72rp-5"
})(["color:", ";cursor:pointer;&:hover{text-decoration:underline;}"], props => props.theme.color.primary500);

const DropzoneInnerContent = ({
  dragStatus,
  accept,
  maxFiles
}) => {
  let content = null;
  const {
    t
  } = useTranslation('ui');

  switch (dragStatus) {
    case 'accept':
      content = jsx(ContentMessageAccept, {
        children: t('FileDropzone.dragStatus.accept')
      }, void 0);
      break;

    case 'reject':
      const formats = Array.isArray(accept) ? accept.join(', ') : accept;
      content = jsx(ContentMessageReject, {
        children: t('FileDropzone.dragStatus.reject', {
          formats
        })
      }, void 0);
      break;

    case 'invalid-count':
      content = jsx(ContentMessageReject, {
        children: t('FileDropzone.dragStatus.invalid-count', {
          fileCount: maxFiles
        })
      }, void 0);
      break;

    default:
      content = jsxs(ContentMessageDefault, {
        children: [t('FileDropzone.dropText'), jsx(DropLink, {
          children: t('FileDropzone.dropLink')
        }, void 0)]
      }, void 0);
  }

  return jsx(ContentContainer, {
    children: content
  }, void 0);
};

const FileDropzone = ({
  onDrop,
  render,
  accept,
  maxSize,
  minSize,
  maxFiles: _maxFiles = 1,
  disabled: _disabled = false
}) => {
  const onDropAccepted = useCallback(files => {
    onDrop(files);
  }, [onDrop]);
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    draggedFiles
  } = useDropzone({
    onDropAccepted,
    accept,
    // validator: file => {
    //   console.log(file)
    //   return null
    // },
    maxSize,
    minSize,
    maxFiles: _maxFiles,
    disabled: _disabled,
    preventDropOnDocument: true
  });
  let dragStatus = 'none';

  if (isDragActive) {
    dragStatus = 'active';
  }

  if (isDragAccept) {
    dragStatus = 'accept';
  }

  if (isDragReject) {
    dragStatus = 'reject';
  }

  if (_maxFiles < draggedFiles.length) {
    dragStatus = 'invalid-count';
  }

  return jsxs(Container$3, Object.assign({}, getRootProps(), {
    dragStatus: dragStatus
  }, {
    children: [jsx("input", Object.assign({}, getInputProps()), void 0), render ? render({
      dragStatus,
      maxFiles: _maxFiles,
      draggedFilesCount: draggedFiles.length
    }) : jsx(DropzoneInnerContent, {
      dragStatus: dragStatus,
      maxFiles: _maxFiles,
      accept: accept
    }, void 0)]
  }), void 0);
};

const FilePicker = ({
  render,
  className,
  multiple: _multiple = false,
  value,
  onChange,
  accept,
  appendMode: _appendMode = false,
  addAllowed: _addAllowed = true,
  fileValidator
}) => {
  const ref = useRef(null);
  const onClick = useCallback(() => {
    if (ref.current && _addAllowed) {
      ref.current.click();
    }
  }, [_addAllowed]);
  const handleChange = useCallback(async e => {
    let items = [];

    if (_appendMode) {
      items = [...(value || []), ...(e.target.files || [])];
    } else {
      items = e.target.files ? Array.from(e.target.files) : [];
    }

    let result = items.map(file => {
      return {
        file,
        valid: true,
        error: null
      };
    });

    if (fileValidator) {
      const validationResults = await Promise.all(items.map(fileValidator));
      result = [];
      validationResults.forEach((error, i) => {
        result.push({
          file: items[i],
          valid: !error,
          error
        });
      });
    }

    onChange(result.length ? result : undefined);
  }, [_appendMode, fileValidator, onChange, value]);
  return jsxs("div", Object.assign({
    className: className
  }, {
    children: [render({
      onClick,
      files: value,
      addAllowed: _addAllowed
    }), jsx("input", {
      ref: ref,
      hidden: true,
      type: 'file',
      onChange: handleChange,
      multiple: _multiple,
      accept: accept,
      value: ""
    }, void 0)]
  }), void 0);
};

var RU = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAeCAYAAABaKIzgAAAAAXNSR0IArs4c6QAAA7hJREFUWAmtll1y20gMhMUqve5x9mkPkBvnRHsFV/lFUvA10EMQliXZ8SQlgECj8TMzpLdbrtN31rZtpwg/IVnWkaxnfoFe/NkulwulCj6Jn3HMwiZ+8k389M/4/ny+Xq/r2QVbLscD5Rm2+61/Jmea3sjZQRP0yjOxJruHtw/Jmvhn/s65vb+/57536zf1ryS+l+JRvLb+1Y4n+Vcn9KiQyT2ft7e3tx+b6CT/yefDZXpGPCcy8T894Z7vS5eJQliWs9DpM87yb/wPz+gspHc4fS7CGJ6tI7/jV1D9bP/8+l1zCgsapLZYr0QnBkpOn2rrWUdR7mK6J/3icvwEtPyx9de9LmoIsONUkYpyZa1I1PhWiGt9M0Yn4dS2V6N5BMoW8dst9PiH1EJcg8MFWA95vvFlKocGGbokka7PUgbAZaAQkZkgXFv4yk0R+b8MRC7y0pVv9zs2oLnKpYlWmoNDD4BaXamH0XVFkTnR+4kUusM1AOELnrFBt/hSZ6fmOsestYXToWcY+sSsI1nhFmklyg7wpcHTllQAUywlhNT42U0+FkUIuDrT1n96KyEDu7hDa5VRgG52FX73lke0t/uDf8SvNF2pLmKicZJamwedAqKjPpHOkb66UeHI8w6+9k6xr/sP3DwcJ3ppfmZHC55h6LqR1VZtg7YDVBAJXY3S5NYmKB75is964Y1FshRPc/ZXo+zEOasWDmgpljw2XRMOU211nG4VhmRByN+3SK1KqBxh+NBIvZ5yGBAExjnAl47UrRcpP+QjR6sNsxf5/e7EluVQbC4IE1ME5loAqMNX7lu9yiSh4MSArZODydjt///+3ctiAspUJutILezOjsG6/QI9+HkS/yD/x7+evF0uI56PZVQT4eerlje5j+BBnXLt8WtcNTZxtaPDpbTtfG2FzH59tpYUoafIPONMUmy1MuONPDa6NzLxK08NizjXl9/6SgRF73en7NqOUJFxLJD3Vk6bc5ul3rvVKuQTf+eMred4Z/IPE4CA7j4h6h2LdOJ5n4ZtvVcD5FcPeOuWC1v5Vu6oQVuvJASWYikgttqKVJf3BXxg+3tYO0d8Thi+w9LOMBzvkPV4j9KNUxPeafB5+w6EfhgTtPkozR5WClBMFTLiM/9+eW/xntJHIeT5wvY8Wm2aEyZiminH08bANb4Z7/OKZOHPj8opJyrrN358Iw/ErfBJ6d3yjOcnGPw6r+hFgMz36J3DW5ijGFuF02TSmURgVrKJD//NNmJLl0yCOhrJyq+mHvIP7dx40F+TDIMAAAAASUVORK5CYII=";

var US = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAeCAYAAABaKIzgAAAAAXNSR0IArs4c6QAABDlJREFUWAm9l81qZUUQx0/DfRYVV6IbRQIDA/MQfjyDG1dOQi4zg4qP4URfwIXgLFwJLhR9DVGEQEJWOWfqV9X/03X7npOba8BOcqq66l+fXadvbnny5Hwa6pqmaSilDFCWeOih9c1fvwxYgZRDzHAlc/HV/YLLPQ/VWxk2kdTs2hyPNRx+JA+6H6g5HsHymzJ1qxDjLPSV+l5YqK1iP5P9QFnioZbo6BW/ePGJV392duEdZc86PWU/DHmPnD2devr0wuk7f7xCfNQ65gQ3nrclMo6Wt2WkYx9HClDJhprPy/vke4PYmsw264/KNfltPlqs5qs8fvyFySM4eT1//qlrz85eGi22j862Tof+9PSlF4ItBX39969+YHiKg6OE4MO7u73zobYs2dejDxVB8x+hGA3qiBHhRYs9Re3KDBvCoKSEvypj27+cKhLKUkGiWVaPfhqePVPnvnUj9tjTWaj0dJLFnhzoNPTde8xon1ifuDteeWxiFofh9va2VhxvPTOLTh0ex6ZXgOgEXQ/vfSJLMcNmScMB7F6PGVUePfo8dboM2+1Hrt9uvzc62f7juv+u0t29C+3x1T+/tYGMSbJZ7a6bOxJxPxoTKIsOVFm9R0NOYlQVf/5Kpz3VRtWBTvUhcN3s1yHmaYeGf0SyJSH4lJirpTfq7HyPluH8XJ26ALq4p4DtNu5Z8IwAe+h7v//kdg959EevEYPay8QdOvofAt2fGO3OaHQ468HIXrwLVh45MJA+MclkLp+OOzn5TH2WfpVaHeY8jjectqP+8t8/V+2aglA67iZtnHR1FJpCH6GhUBK1UZpjp9hwE0TXW23Cjsb0hczjJ7jnQaXKwAW2kaArJG3Lzc2N3ckBXDoKuYT2R5d1/4Xv4/X+s95nFIFW5iVbo9nRGuaQPMcTL4qt+PLz2ydzlghVFSDx0P9l0TDNTyQwvxTdPdoqAKcKRPeS7RyD09SBFa8yD+nnJNWY5L9cX1/fe0b7RB/a8WNO8MEzSvKao76Q++yzrXjRbF9evfUhDfbVH1V/dukkVvDmQSAQ4ucIbrb+0HwuHP2G7zr6jiIeyir2BYjPa6hWjrmPZ67BB5rOePHV6NCoTPYJ6Ri+MrAwtrsbWq6urtRQ1+WHB7Lqlo4CXB+4xx+rz7Hhs7+9t34J3Mu0VwGiyJf4JVnGZr18i0pXfnzzg+itNHdSmu+H6ShOVGOFoO+gg9KDoMIEvo1xwOQbymrxNj4Cdaj6wAFuz0iKYFVmFFeeuxF93kNdjL4Wwz6k+i/VIVVWeS8Em0BmRLm8vFy9R1U9lNV3JBy15yF8r2+WwfX+M/7Oe1SVieIu80uBekzGi89UyWAHr/93sx/w5Yc33ped6Wg53VPrxUdH4+hxiJvjlwc04xRwx4mShvbLZjTuuqZQkk0iLrBW9c4MSXuYEn4nXle560y25P81nZiyf9FvKNUAAAAASUVORK5CYII=";

var CA = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAeCAYAAABaKIzgAAAAAXNSR0IArs4c6QAABBJJREFUWAm1V71KJEEQ7tldD+4OAzMDIxHxB4RNNPQNzI0MxcBEEX0HMTH0BYwUNBIfQUTBzMTIwFg4D0535vrr7q+nuqZ3VjmuYbaquv6+quqe3S2qfr8yVWU+ve7vo2ll/YqisO7enzzoyNXvjzSRBj1TlmYoUCQECJGYoBhEy9zPUVlYkWsOc2V0HmguKvboICiT5VxKWzS6CZpb1LmOs0FsBhzAY180hnFSoHSi4ZAK2UUkbAPOJKT0cz4shk2AEXlSOlrac8qMwtkAsKww2DEhbCSvZQcoFANdUhhi4UEO5icPqlbaUaWMARjI6pkcpuQ1WIZJgGn7f+qorkhXHBBIYJonOIKVeuxFmUBpCNqSv2cGg9q0xdAZWX1MVHslnNSTJ2URoPFIsRmIwslJGjD5M8pU0kDvyUDU5ejTk9+dnm5o5Vuhi1y5fNor2PgzCtRY2AwV+A31aXXsjtJEsXN97fhyczPukRnZURqSCjwdBw7nBQ8UpOD1Y3XDgHYPDkxxe2s6V1fucfz+vksJH/0kuZhfU4EnvfXs5ogOZ8G+vprexgZ74fhydTUpjH6OAtQXVtpRXCwEAJU8K0WFYsnE5dycMR8fyVPOzwtrxSKm6JjjIQ95/K1nB1WshmiDEFz3+NiUKyum8/DgzKrFRQ8SUphMtbBgOicnTl8uLZnOzY0ZbG87OQKCBHCknCpk8pY2bz2VzrP5AaC4FOXysvm2vu4NrDxYW/PTwA4S273O5aXpXlzEIH9OTyPvJldLNUfQ3AlyOnpsciSSV6N3YPFzj0fEjrx7fp6MHcege3bm92Bn5eLuzqV3U2Ee0lw+cfzSy8QqJA0VhQy1BglwJrE4BVAs+HDP7/hP68Oj42wYm5S+0ifwo3+UMGlwYKKPrS1j3t7M2NHRcGBMaEG/7+wY50NQpLQBZa6Mrv0yoWvoDGhYbuyhc0gO3djhYb6Dwed9b8+87+4a9Dv6Y6x6MVeIL9VFNTERrpzdhgGrghV5UeHb87P0b/Df8Zqy9r8fHxs6ufFjakqKI/nRo2dXESoA5vh1dPc2mJmJ2+we7ZOvUMTCw+bAi3ymo+2XKQAjQMRicskTCGg5OwuVs5NU8s6ex4k5vAE+Y0O84D+Lany8/heqK9IVW59fLy/SP+FZBIEnSivIjv6cnNTqVvm//h7VmVlA7KhuTNMhHofmNxOMc+MI++yajpmTaUuAiQ1yMA9pYhCEoGs/o9oxHPJsYm0b89QvlcSElxRAMpfH2VJnadrRJFJGsA7sErTkCVyewZw+iQgQeLxhokqEYPO1joYIBOZz1B3jPqnWJwB465PN4UL7e1T7fXH0usNJuM+MXji0f4UKQ8faMfAPmlblZHZWUoJ3Y9ejh8w3AQKSt/QvneCxKS0qwosAAAAASUVORK5CYII=";

var ES = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAeCAYAAABaKIzgAAAAAXNSR0IArs4c6QAABBRJREFUWAntVztrVFEQnru5eRXGwsJiFUGxsxAJSGwU/B+WtgE7CQim8CfYWAj+DoV0wRdooxZiY1KpTQQxye5e55vHubOzdzeK7Z6Qc+aemfnmOzNzbnKrz2evNOSjYbGqiLDKYJkgY8Vw2fRui7VDLW7uInp+cB88u1z8kz4A1MPRCC7tKCRBrzFqSkxj6p44uG1cPTgMXJ5GJOvh41iQJbquSnRKwsR21tSVcIDruTQfHLiFZ1nUZjDiFUqsXSMcpB42nNFGoSSu+cIPuav4p82hWLBG7fX0LMcsxJiCyxuGL3YeHAEgC1nDkxNCNhAUG4+8ckax6eV3I3VUTCarjxxnxDITx+FkGIoRV3uND3WDbLIJ1jK6ZNtzW6wY2IaIVTKKrKkiEREr1pijZBdkp9hPElXSzk0PCfIWL1csZFAIwcz26hFQChIUdgwWG5YFXLIurmPTJHG25x/PoPsWYnxxdW9GRVhVMhri14PhMGiMB3OVgQN4mrDhsiF592LFwOx7umFAnggxsr1gW/yNpHeWhBOyjNu8v9Z6ZmJSYg0vgfOUWsOYKmPYnqTPeDMAamoGLTAcW9qTgTKw9U+5i9nfsXwFuL8JBCudJD1G3jWNmKiVUq8ZW3upLMDO1k/af30k0OeuL9HN7VNKGX7oX/ePyF1E1KudJyrIqnIomDmXimoi7tGojLJBfv90bBLRtw+Q2QdDDuRgujU+s66QN3tpPAuSD5rxYGYQVfP2Er/Pg6OdcufBb4l5a3tF1jydpM/2//usPeqlM5Igvv+KW4LHj4/H9GLrkI5/aajFVaLbj5aLXiviZUgZLNmcTISghXiKPn3W0ne/1sTrYG9A52/0aONej+jiY9q9/5QOvr5sEUdoBRDsGJm39zNWGWwAsu6f7cNB+DKFQO7vK0Msr43oy/OGf3GauwJ/4WEg1uA9LNttO7p/XmHme5AzsfwWgd72uPQh0JgnZ5DHyukhnbnco3pZIwwOK95DWyyInojlqRVJTEKGzHmcOOLHDLvMq/ZoZ0qWBWutP6CDvUW6ekdv+rtnC7TWBzMjioP6yKX1Gx7vQCEDpw5i7uOYtk6+nkzRXx/K67FeGdDGZkNvniyJZmPziOqVIfXXe/r6xB+MOGJpXfYVdl1y117EZLlqdleDWSpVvrXJ+Z8fPVsxw753Apj26NgxZ3g4aAnEtn42uLmMFQMp8D15tg3pPWzwCGmSEkHn+GohMxPlW++KDOxBPHC+lYgS/3a77P/RZ31hbYB/g28+1fwrFAWbaBUrq5c+6+HjOsilL/j1JJ/LXtpcejGeMeXSwV/KrT6i5sAtPMvoFicgrzO2LX+p1K/M4SDzr1BkZf4VKs3kfYOMsOxfkqVvgjD/CrV/cuQuSbKI/gDG8yPNoTXooAAAAABJRU5ErkJggg==";

var GB = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAeCAYAAABaKIzgAAAAAXNSR0IArs4c6QAACQdJREFUWAmVWAlUVNcZ/t4wgAZlGBILihoXpFJFrcYlHguIBtBGTNDIafVYE6OJiRL3JdYlRg0iCCEaqAmptuWkcYslclxIwKhEikvV1iXUrREVokF2GJCZ/v+97848CZzEy3nv++e///Ld/7/vvhm05vp6h+bpiavXqzE7vhBFZ37A44zqO3HQNA2lweE/dtMA/0tHhb5jl8/gcDiELeNPjeHPPIXtaSPgd+UU7q/ZDNOt6KmoP3MBvXt2wJfZY7Fu5QB4eFAGqGAKObSSGdVFEiX+6cuu2yiU/pK0iuWAp6eG9asG4vCfh8AraSPKk9Phl54Ak3XZmyibsxzlCVuBxkYseDMYBUei8esBVgpsZxYCWZZkFBoTKdpkK5bASBJ/cA6uppxl5IvjKWR58CArThyOwsyA71Ay7vdw7x6Abjl/Q7vBITB9WhqArgez0FRyFyUTpqPh/CX0DfJGfk4kVi0NgdmdM+kJDKiRLKvBa5EygS4zKlm30214QtmrGB7uGtYsD0HuJ4NhTVqPivSd8Ps4CdYlc+BwN2P7jmKYFiwvwsTXTsO2bAWsb72KslmLUZ6UDlPzQyyd3w/HDkZjQD8fQVYScyUiKiKpLBovhodCMStVQst6shfzLnlgfx8cOxSF1wNu4i4Vyj24DwKyd8IzJBi3SmoRE5eHhStOwcQrPHqiFMMjcrCn4mkE5PwVjf+9gdsTZ6DxYjH6BVvwNZFdtqA/3Mycl5JwC/kiX36QVIVaop3mhQejkF3o5gasWNgfX2YOxpOJ76Bq52747XwfPlQsh5sJO7KuYlj4AeQfuyt8TSphdXUj5i4uxEvx59C8dg0ss6eh9OX5qEjLhBuasXJxCPIORNG2sMikemJZZVLxEkjnRDEvdYItzUhbB4J/aaGtFYW5Xa6hLHYGPJ8ZiM77PoZH30DcKa1D7NQ8zFtSiJraJlkQ8jXJ4LIlLB/Jv42hYV8gu6kPOmfvgO3CZdyJnQnblav0gPni+KFoLJj7K5hMolaSgy4Kmlw09cfEdcKsZZ9F8f1wNHMA/DetQc3eHPhnbYPPG38AqMRZu65hOFUxN/+Oc1FqcaL1HIQv3tyMVVU2vBZfgGnLL0J7bz06TpuMsunxqPhwJzyo/WtXDMKRf0SiT29vWUUiYxcnBJwoQrmWgaBAb3yVHYkFnYtxb8pstA9/Fv67/gT3wB4oLatD3Ix8zJn/DSooN/MQD5oR2z2VyezaHL5WTyRvHIaYoe3xYHUivCZEwmtilLC32Zrp3KPNRuN231C5XuMRTHKXy1+Lfcy2TQdzUbvnAKzvLoV7j25Cv2f/TSxa+U88qGj8sb+ILG+azWZz8APBg8usHg45/fPvgijVwZWNfR3o+u1xZ/tV7J+bz2gvn2PnPpJkFb2WxI2OgoZhYWovSV/VJHkiGOMpv9awtfjKTrvWY5iBpgrZOmr6zmGUgwmpKrbuY9RyIm5eWwnVnN5goyvMfNa1nViRkMTssAtbRtdQ1XNRltauTaAsWG9nssq5BTPRFdLJ7jzqb+an08GHNw+xZB2FQiidEmeQwVwqo6QIKWzhzanEUOhkzex5tFwJqR2sIzSrs08Y8u2RvqiQEjXyYntGYcoy/YkYQvN4N+Fr6KiDCIt9+ghxiknpxMOkwnO11IaWOn05Yqm8MG45E5OtZ1s+PxnF4PUoF1YoWU3TvOq2sHcuURZCFU0t3FgYM5NTReQgdrEqGUbUXIgykAquZpUto3MYRMGTPus85bbhhRoSyq2kLDijK4CSGbW6ujrXjDNb60LLiqvqM34XNKoVJw3di4+3on98laios3Ut/NskxhWhF7dxXlSmhT+XUtnwlJKVrXGhrc0bw5l9umZyBKlTG4iRBt+t1nZI3vAsXozpJRLZis6hYlUiLG/HQxsx1PkK5Qhsr5BEYa9QvW6ZZP3BfNRnH4ZlzSJ8XliFJX88Sa9Qfse38KfPSkdl4YeBFEIjZdbxNS6yO05+9YIkWd+AyndT8WDZBiI5Dxe9e+M3kfuYhyDEBJikE3VZzbPt2XP3xHz7qDC4D+qPe5NnYby9GIV5sYge243m+CGlOITyZ5AL6fuoK5GSLRYPZKSFIytzLH7R6Qk0FJ7B9zEvw97QAJ/dHyGhwITnYvaj+FqFSMxkeKiWCrJ6l5SObcdM2I91CafQ+NCBDrOnwjczGTVZe2FevQ5ZiYPxYWoYLB3ptw8XjS8eOtL3UWJNx404cij4mNEBVMXJiIsNhL22DpXrUlD5dgK8V72F/8XNRNhLR5C67bw4HYiZDEZ3Ra41FEZka7fbkbL1HMLG7cP5/9yHe1AvdPosAx4hfXF/0iy80O4mTuZNRkRYF4rnqi5Xmb7K8qENeHf0QFpSKHb/JRqd/Z+A7eQZ3H9xJhxND2Hdm4mUIjdEPP85vi1+QB76n2gRt0q/FGFGwc54I41YmAOXLv+AiPH7kLDlDJqIj9ec6bBmbEJt5t/RbuN72JU6Au8nhsKrg1lw42zii3PoqC4oyJ2EaXFBcNTUonJtMipXb6bNvhAlv3sFEVMOYdOW02hu1veMoMEvByMR4qGfp4zi0iuuFsL0WWbkWAkUcwwt/tKVcpiDA/EkVdfc+2ncj30VU3zv0s/2SRg10p8Dw5S0cRT2f/pbdA3ogIYTRbKKFIz3YtppE0aP34t/X9T/eyLawXnkw6YIKGTakghLLlnquJU8FJJIeS7QFggftxfJH5xFMx15XvNegc/WDajetgPeqSnYnxGKzRtGQauqqnLYq2tRvTkdTUX/gvc7i3DDtyfemJ+Ps+e/p6q53iRKVucuE3hQ8rpIfyNwpMBHbxp6Xi0QKt9uGYTcAq6obAX7q5hsNGSQHz1Qo+lniw8ctkbUEtn6L3Lp+ZgPk+14Ecrpx5vm4Q7rno+QcVZDaNRuIlkmgqpNLRPItjllQ+t5hodCKRs/0ZzeEfEAC5m3AldYxuWcoVG78EHGOdD/ldBh4WxYUtaiZst2aNdHxjg6UhVvdeIq5uHUWSbY1uCAqirSpvzWHCFcb7WiQK+r34h5327pBl+5Qt7jVFTnXjdWeNgQf6puBHr1sFB1bfg/Z4Ot87kjna4AAAAASUVORK5CYII=";

var EU = "data:image/png;base64,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";

// icons are from https://github.com/madebybowtie/FlagKit
var flagsDict = {
  RU,
  US,
  CA,
  ES,
  GB,
  EU
};

const StyledImg = /*#__PURE__*/styled.img.withConfig({
  displayName: "StyledImg",
  componentId: "ws1nq0-0"
})(["box-shadow:0 0 1px rgba(0,0,0,0.5);"]);
const Stub = /*#__PURE__*/styled.span.withConfig({
  displayName: "Stub",
  componentId: "ws1nq0-1"
})(["width:", ";padding-top:", ";display:inline-block;background:linear-gradient( 45deg,rgba(0,0,0,0.2) 0%,rgba(0,0,0,0.2) 30%,rgba(0,0,0,0.15) 30%,rgba(0,0,0,0.15) 65%,rgba(0,0,0,0.1) 65% );"], props => `${props.size}px`, props => `calc(${props.size}px * 0.6)`);
const Flag = (_ref) => {
  let {
    code,
    className,
    width = 20
  } = _ref,
      props = _objectWithoutProperties(_ref, ["code", "className", "width"]);

  const flag = flagsDict[code];
  return flag ? jsx(StyledImg, Object.assign({}, props, {
    className: className,
    src: flag,
    width: width
  }), void 0) : jsx(Stub, {
    size: width
  }, void 0);
};

const Wrapper$c = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "thv9ua-0"
})(["grid-area:", ";"], ({
  area
}) => area);
const GridArea = ({
  area,
  children
}) => {
  return jsx(Wrapper$c, Object.assign({
    area: area
  }, {
    children: children
  }), void 0);
};

GridArea.ofType = function ofType() {
  const Component = ({
    area,
    children
  }) => jsx(Wrapper$c, Object.assign({
    area: area
  }, {
    children: children
  }), void 0);

  Component.displayName = 'GridArea';
  return Component;
};

let _$h = t => t,
    _t$h,
    _t2$8,
    _t3$6;
const ModalRaw = props => {
  return jsx(ReactModal, Object.assign({
    style: {
      overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.48)',
        zIndex: defaultTheme.zIndex.modal,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      },
      content: {
        position: 'static',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: 'none',
        background: 'none',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: 0,
        outline: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },
    ariaHideApp: false
  }, props), void 0);
};
const StyledModalContent = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledModalContent",
  componentId: "bfyg1w-0"
})(["position:relative;background:", ";box-shadow:0px 16px 24px rgba(0,0,0,0.04);border-radius:16px;width:96vw;display:flex;flex-direction:column;align-items:center;font-weight:500;font-size:16px;line-height:24px;color:", ";&[data-padding='noPadding']{padding:0px !important;}&[data-size='small']{padding:16px 40px 40px;max-width:278px;}&[data-size='medium']{padding:24px 40px 40px;max-width:480px;}&[data-size='large']{padding:32px 40px 40px;max-width:800px;}&[data-size='responsive']{padding:16px 40px 40px;width:auto;}", ""], props => props.theme.color.onPrimaryLight, props => props.theme.color.onPrimaryDark, props => props.theme.media.mobile(_t$h || (_t$h = _$h`
    font-size: 14px;
    line-height: 18px;
  `)));
const CrossStyled$1 = /*#__PURE__*/styled(SvgCross).withConfig({
  displayName: "CrossStyled",
  componentId: "bfyg1w-1"
})(["cursor:pointer;transition:0.3s fill;position:absolute;top:14px;right:14px;:hover{fill:", ";}"], props => props.theme.color.secondary300);
const ModalHeading = /*#__PURE__*/styled.header.withConfig({
  displayName: "ModalHeading",
  componentId: "bfyg1w-2"
})(["font-weight:bold;font-size:24px;line-height:32px;color:", ";align-self:", ";margin:24px 0px 8px;", ""], props => props.theme.color.onSurfaceHighEmphasys, props => props.compact ? 'flex-start' : 'center', props => props.theme.media.mobile(_t2$8 || (_t2$8 = _$h`
    font-size: 20px;
    line-height: 38px;
    margin: 16px 0px 8px;
  `)));
const ModalWizardHeading = /*#__PURE__*/styled.header.withConfig({
  displayName: "ModalWizardHeading",
  componentId: "bfyg1w-3"
})(["font-weight:600;font-size:18px;line-height:24px;color:", ";align-self:", ";margin:24px 0px 8px;", ""], props => props.theme.color.onSurfaceHighEmphasys, props => props.compact ? 'flex-start' : 'center', props => props.theme.media.mobile(_t3$6 || (_t3$6 = _$h`
  font-size: 20px;
  line-height: 38px;
  margin: 16px 0px 8px;
`)));
const Modal = (_ref) => {
  let {
    children,
    onClose,
    heading,
    size = 'medium',
    className,
    modalPadding,
    isWizard
  } = _ref,
      props = _objectWithoutProperties(_ref, ["children", "onClose", "heading", "size", "className", "modalPadding", "isWizard"]);

  const ModalHeader = isWizard ? ModalWizardHeading : ModalHeading;
  return jsx(ModalRaw, Object.assign({
    shouldCloseOnEsc: true,
    shouldCloseOnOverlayClick: true
  }, props, {
    onRequestClose: onClose
  }, {
    children: jsxs(StyledModalContent, Object.assign({
      "data-size": size,
      "data-padding": modalPadding,
      className: className
    }, {
      children: [jsx(CrossStyled$1, {
        onClick: onClose
      }, void 0), !!heading && jsx(ModalHeader, {
        children: heading
      }, void 0), children]
    }), void 0)
  }), void 0);
};

const ProgressCircle = ({
  radius: _radius = 30,
  stroke: _stroke = 6,
  percent
}) => {
  const normalizedRadius = _radius - _stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - percent / 100 * circumference;
  return jsx("svg", Object.assign({
    height: _radius * 2,
    width: _radius * 2
  }, {
    children: jsx("circle", {
      stroke: defaultTheme.color.primary600,
      fill: "transparent",
      strokeWidth: _stroke,
      strokeDasharray: circumference + ' ' + circumference,
      style: {
        strokeDashoffset
      },
      r: normalizedRadius,
      cx: _radius,
      cy: _radius
    }, void 0)
  }), void 0);
};

const ContentWrapper$2 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentWrapper",
  componentId: "sc-1fnnrie-0"
})(["background:", ";padding:20px;box-shadow:0 4px 8px rgba(0,0,0,0.04);margin-bottom:25px;max-width:300px;"], props => props.theme.color.onPrimaryLight);
const Text$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Text",
  componentId: "sc-1fnnrie-1"
})(["color:", ";font-size:14px;margin-bottom:15px;"], props => props.theme.color.onSurfaceMidEmphasys);
const ButtonsWrapper$2 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ButtonsWrapper",
  componentId: "sc-1fnnrie-2"
})(["display:grid;grid-template-columns:auto auto;grid-gap:15px;"]);

const PopoverBody$1 = ({
  text,
  cancelText,
  submitText,
  cancelIntent: _cancelIntent = 'cancel',
  submitIntent: _submitIntent = 'danger',
  onRemove,
  onCancel
}) => {
  const {
    t
  } = useTranslation('ui');
  return jsxs(ContentWrapper$2, {
    children: [jsx(Text$1, {
      children: text
    }, void 0), jsxs(ButtonsWrapper$2, {
      children: [jsx(Button$5, Object.assign({
        intent: _cancelIntent,
        size: 'small',
        onClick: onCancel
      }, {
        children: cancelText || t('RemovePopover.cancel')
      }), void 0), jsx(Button$5, Object.assign({
        intent: _submitIntent,
        size: 'small',
        onClick: onRemove
      }, {
        children: submitText || t('RemovePopover.submit')
      }), void 0)]
    }, void 0)]
  }, void 0);
};
/** @deprecated use PopoverWithBtns instead */


const RemovePopover = ({
  children,
  text,
  cancelText,
  submitText,
  cancelIntent,
  submitIntent,
  onRemove,
  position: _position = 'start'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const togglePopover = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);
  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, []);
  const handleRemove = useCallback(() => {
    setIsOpen(false);
    onRemove();
  }, [onRemove]);
  return jsx(Popover, Object.assign({
    isOpen: isOpen,
    body: jsx(PopoverBody$1, {
      text: text,
      cancelText: cancelText,
      submitText: submitText,
      cancelIntent: cancelIntent,
      submitIntent: submitIntent,
      onCancel: closePopover,
      onRemove: handleRemove
    }, void 0),
    onOuterAction: closePopover,
    preferPlace: _position,
    tipSize: 0.01
  }, {
    children: /*#__PURE__*/React__default.cloneElement(children, {
      onClick: togglePopover
    })
  }), void 0);
};

const ContentWrapper$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentWrapper",
  componentId: "p92j42-0"
})(["background:", ";padding:20px;box-shadow:0 4px 8px rgba(0,0,0,0.04);margin-bottom:25px;max-width:", ";border-radius:2px;"], props => props.theme.color.onPrimaryLight, props => props.maxWidth || '300px');
const BodyContentWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "BodyContentWrapper",
  componentId: "p92j42-1"
})(["padding:10px 0;"]);
const Text = /*#__PURE__*/styled.div.withConfig({
  displayName: "Text",
  componentId: "p92j42-2"
})(["color:", ";font-size:14px;margin-bottom:15px;"], props => props.theme.color.onSurfaceMidEmphasys);
const ButtonsWrapper$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ButtonsWrapper",
  componentId: "p92j42-3"
})(["display:grid;grid-template-columns:auto auto;grid-gap:15px;"]);
const Heading = /*#__PURE__*/styled.div.withConfig({
  displayName: "Heading",
  componentId: "p92j42-4"
})(["color:", ";font-size:16px;font-weight:bold;margin-bottom:15px;"], props => props.theme.color.onPrimaryDark);

const PopoverBody = ({
  heading,
  text,
  cancelText,
  submitText,
  cancelIntent: _cancelIntent = 'cancel',
  submitIntent: _submitIntent = 'danger',
  bodyContent,
  onSubmit,
  onCancel,
  maxWidth
}) => {
  const {
    t
  } = useTranslation('ui');
  return jsxs(ContentWrapper$1, Object.assign({
    maxWidth: maxWidth
  }, {
    children: [jsx(Heading, {
      children: heading
    }, void 0), text && jsx(Text, {
      children: text
    }, void 0), bodyContent && jsx(BodyContentWrapper, {
      children: bodyContent
    }, void 0), jsxs(ButtonsWrapper$1, {
      children: [jsx(Button$5, Object.assign({
        intent: _cancelIntent,
        size: 'small',
        onClick: onCancel
      }, {
        children: cancelText || t('RemovePopover.cancel')
      }), void 0), jsx(Button$5, Object.assign({
        intent: _submitIntent,
        size: 'small',
        onClick: onSubmit
      }, {
        children: submitText || t('RemovePopover.submit')
      }), void 0)]
    }, void 0)]
  }), void 0);
};

const PopoverWithBtns = ({
  children,
  text,
  cancelText,
  submitText,
  cancelIntent,
  submitIntent,
  onSubmit,
  onCancel,
  position: _position = 'start',
  heading,
  bodyContent,
  maxWidth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const togglePopover = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);
  const closePopover = useCallback(() => {
    setIsOpen(false);
    onCancel && onCancel();
  }, [onCancel]);
  const handleSubmit = useCallback(() => {
    setIsOpen(false);
    onSubmit();
  }, [onSubmit]);
  return jsx(Popover, Object.assign({
    isOpen: isOpen,
    body: jsx(PopoverBody, {
      heading: heading,
      text: text,
      cancelText: cancelText,
      submitText: submitText,
      cancelIntent: cancelIntent,
      submitIntent: submitIntent,
      onCancel: closePopover,
      onSubmit: handleSubmit,
      bodyContent: bodyContent,
      maxWidth: maxWidth
    }, void 0),
    onOuterAction: closePopover,
    preferPlace: _position,
    tipSize: 0.01,
    style: {
      zIndex: 9999
    }
  }, {
    children: /*#__PURE__*/React__default.cloneElement(children, {
      onClick: togglePopover
    })
  }), void 0);
};

const RemoveIconStyled = /*#__PURE__*/styled(SvgCrossCircle).withConfig({
  displayName: "RemoveIconStyled",
  componentId: "sc-16k4yo9-0"
})(["transition:0.2s opacity ease;margin-left:5px;&:hover{opacity:0.8;cursor:pointer;}"]);
const TagStyled$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "TagStyled",
  componentId: "sc-16k4yo9-1"
})(["display:grid;grid-template-columns:auto max-content;align-items:center;font-weight:500;font-size:14px;line-height:20px;background:rgba(3,123,255,0.08);border-radius:4px;box-sizing:border-box;padding:4px 8px;margin:0 8px 8px 0;"]);
const Tag = ({
  title,
  index,
  onRemove,
  className
}) => {
  return jsxs(TagStyled$1, Object.assign({
    className: className
  }, {
    children: [title, jsx(RemoveIconStyled, {
      onClick: e => {
        e.stopPropagation();
        onRemove === null || onRemove === void 0 ? void 0 : onRemove(index);
      },
      role: "button"
    }, void 0)]
  }), void 0);
};

const Container$2 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-12q1bcf-0"
})(["font-size:14px;line-height:20px;position:relative;"]);

const positionCss$1 = ({
  position
}) => {
  const offset = '8px';

  switch (position) {
    case 'top':
      return css(["left:50%;top:0;transform:translate(-50%,calc(-100% - ", "));"], offset);

    case 'right':
      return css(["right:0;top:50%;transform:translate(calc(100% + ", "),-50%);"], offset);

    case 'bottom':
      return css(["left:50%;bottom:0;transform:translate(-50%,calc(100% + ", "));"], offset);

    case 'left':
      return css(["left:0;top:50%;transform:translate(calc(-100% - ", "),-50%);"], offset);
  }
};

const TooltipContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TooltipContainer",
  componentId: "sc-12q1bcf-1"
})(["position:absolute;background-color:", ";opacity:0.96;border-radius:8px;padding:4px 8px;z-index:", ";", ""], props => props.theme.color.primary100, props => props.theme.zIndex.tooltip, positionCss$1);
const Tooltip = ({
  children,
  tooltipContent,
  className,
  visible: _visible = true,
  position: _position = 'top',
  temporary,
  visibleOnHover,
  timeout: _timeout = 1000,
  portalProps,
  style: _style = {}
}) => {
  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const isTooltipVisible = useTempBoolean(hovered, _timeout, !temporary || hovered && visibleOnHover) && _visible;

  return jsxs(Container$2, Object.assign({
    className: className,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  }, {
    children: [children, isTooltipVisible && !!tooltipContent ? !portalProps ? jsx(TooltipContainer, Object.assign({
      position: _position,
      style: _style
    }, {
      children: tooltipContent
    }), void 0) : jsx(Portal, Object.assign({}, portalProps, {
      children: jsx(TooltipContainer, Object.assign({
        position: _position,
        style: _style
      }, {
        children: tooltipContent
      }), void 0)
    }), void 0) : null]
  }), void 0);
};

const Portal = ({
  children,
  coords,
  mount
}) => {
  const el = React__default.useMemo(() => document.createElement('div'), []);
  useEffect(() => {
    if (mount) {
      mount.appendChild(el);
      return () => {
        mount.removeChild(el);
      };
    }
  }, [el, mount]);
  return /*#__PURE__*/createPortal(jsx("div", Object.assign({
    style: {
      position: 'absolute',
      top: coords.top,
      left: coords.left
    }
  }, {
    children: children
  }), void 0), el);
};

const StatusIcon = /*#__PURE__*/styled.span.withConfig({
  displayName: "StatusIcon",
  componentId: "rkmz2k-0"
})(["border-radius:50%;width:8px;height:8px;margin-right:8px;margin-left:2px;flex-shrink:0;display:inline-block;", ";"], ({
  theme,
  status
}) => {
  switch (status) {
    case 'active':
      return {
        background: theme.color.accentApproving
      };

    case 'deleted':
      return {
        background: theme.color.accentDestructive
      };

    case 'inactive':
      return {
        background: theme.color.accentNotify
      };

    default:
      return {
        border: `1px solid ${theme.color.onSurfaceMidEmphasys}`
      };
  }
});

let _$g = t => t,
    _t$g,
    _t2$7,
    _t3$5,
    _t4$3,
    _t5$3,
    _t6$3,
    _t7$2,
    _t8$2;
const wrapperSmall = /*#__PURE__*/css(["padding:24px 16px 16px;text-align:left;"]);
const wrapperLarge = /*#__PURE__*/css(["padding:16px 32px;", ""], props => props.theme.media.mobile(_t$g || (_t$g = _$g`
    ${0};
  `), wrapperSmall));
const Wrapper$b = /*#__PURE__*/styled.div.attrs(({
  startColor,
  endColor
}) => {
  return {
    style: {
      background: `${startColor && endColor ? `linear-gradient(180deg, ${startColor} 0%, ${endColor} 100%)` : startColor || '#ebb320'}`
    }
  };
}).withConfig({
  displayName: "Wrapper",
  componentId: "mziujy-0"
})(["position:relative;overflow:hidden;cursor:pointer;border-radius:16px;text-align:center;", ""], props => props.size === 'large' ? wrapperLarge : wrapperSmall);
const primaryTextSmall = /*#__PURE__*/css(["font-size:14px;line-height:18px;text-align:left;"]);
const primaryTextLarge = /*#__PURE__*/css(["font-weight:bold;font-size:32px;line-height:40px;text-align:center;", " ", " ", ""], props => props.theme.media.tablet(_t2$7 || (_t2$7 = _$g`
    font-size: 28px;
    line-height: 40px;
`)), props => props.theme.media.smallTablet(_t3$5 || (_t3$5 = _$g`
     font-size: 22px;
     line-height: 32px;
  `)), props => props.theme.media.mobile(_t4$3 || (_t4$3 = _$g`
    ${0};
  `), primaryTextSmall));
const PrimaryText = /*#__PURE__*/styled.p.attrs(({
  color,
  theme
}) => {
  return {
    style: {
      color: color || theme.color.onPrimaryLight
    }
  };
}).withConfig({
  displayName: "PrimaryText",
  componentId: "mziujy-1"
})(["font-weight:bold;position:relative;margin:0;", ""], props => props.size === 'large' ? primaryTextLarge : primaryTextSmall);
const secondaryTextSmall = /*#__PURE__*/css(["font-weight:500;font-size:12px;line-height:16px;margin-top:24px;text-align:left;"]);
const secondaryTextLarge = /*#__PURE__*/css(["font-weight:600;font-size:16px;line-height:24px;text-align:center;margin-top:8px;", " ", ""], props => props.theme.media.smallTablet(_t5$3 || (_t5$3 = _$g`
     font-size: 14px;
     line-height: 20px;
  `)), props => props.theme.media.mobile(_t6$3 || (_t6$3 = _$g`
     ${0}
  `), secondaryTextSmall));
const SecondaryText = /*#__PURE__*/styled.p.attrs(({
  color,
  theme
}) => {
  return {
    style: {
      color: color || theme.color.onPrimaryLight
    }
  };
}).withConfig({
  displayName: "SecondaryText",
  componentId: "mziujy-2"
})(["font-weight:500;margin:0;position:relative;", ""], props => props.size === 'large' ? secondaryTextLarge : secondaryTextSmall);
const CloseBtn = /*#__PURE__*/styled(SvgCross).withConfig({
  displayName: "CloseBtn",
  componentId: "mziujy-3"
})(["position:absolute;right:10px;top:10px;cursor:pointer;transition:0.3s opacity;fill:", ";opacity:0.8;width:", ";height:", ";:hover{opacity:1;}", ""], props => props.color || props.theme.color.onPrimaryLight, props => props.size === 'small' ? '14px' : '16px', props => props.size === 'small' ? '14px' : '16px', props => props.theme.media.mobile(_t7$2 || (_t7$2 = _$g`
     right: 12px;
     top: 8px;
     width: 14px;
     height: 14px;
  `)));
const LearnMore = /*#__PURE__*/styled.span.withConfig({
  displayName: "LearnMore",
  componentId: "mziujy-4"
})(["text-decoration-line:underline;white-space:nowrap;opacity:0.5;margin:", ";display:", ";", ""], props => props.size === 'small' ? '4px 0 0' : '0 0 0 4px', props => props.size === 'small' ? 'block' : 'inline-block', props => props.theme.media.mobile(_t8$2 || (_t8$2 = _$g`
     margin: 0 0 0 4px;
     display: block;
  `)));
const Banner = ({
  size: _size = 'small',
  primaryText,
  secondaryText,
  className,
  id,
  learnMore: _learnMore = true,
  onClick,
  onClose,
  styles
}) => {
  const {
    t
  } = useTranslation('ui');
  return jsxs(Wrapper$b, Object.assign({
    id: id,
    size: _size,
    className: className,
    startColor: styles === null || styles === void 0 ? void 0 : styles.startColor,
    endColor: styles === null || styles === void 0 ? void 0 : styles.endColor,
    onClick: onClick
  }, {
    children: [!!onClose && jsx(CloseBtn, {
      onClick: onClose,
      size: _size
    }, void 0), jsx(PrimaryText, Object.assign({
      size: _size,
      color: styles === null || styles === void 0 ? void 0 : styles.primaryColor
    }, {
      children: primaryText
    }), void 0), jsxs(SecondaryText, Object.assign({
      size: _size,
      color: styles === null || styles === void 0 ? void 0 : styles.secondaryColor
    }, {
      children: [secondaryText, _learnMore && jsx(LearnMore, Object.assign({
        size: _size,
        color: styles === null || styles === void 0 ? void 0 : styles.secondaryColor
      }, {
        children: t('Banner.learnMore')
      }), void 0)]
    }), void 0)]
  }), void 0);
};

const calculatePercents = totalDuration => {
  let timeInPercents = 300 / totalDuration * 100;
  if (timeInPercents > 25) timeInPercents = 25;
  return timeInPercents;
};

const appearRight = /*#__PURE__*/keyframes(["0%{transform:translateX(calc(100% + 96px))}100%{transform:translateX(0);}"]);

const appearAndDisappearRight = totalDuration => {
  const timeInPercents = calculatePercents(totalDuration);
  return keyframes(["0%{transform:translateX(calc(100% + 96px))}", "%{transform:translateX(0);}", "%{transform:translateX(0);}100%{transform:translateX(calc(100% + 96px))}"], timeInPercents, 100 - timeInPercents);
};

const appearLeft = /*#__PURE__*/keyframes(["0%{transform:translateX(calc(-100% - 96px))}100%{transform:translateX(0);}"]);

const appearAndDisappearLeft = totalDuration => {
  const timeInPercents = calculatePercents(totalDuration);
  return keyframes(["0%{transform:translateX(calc(-100% - 96px))}", "%{transform:translateX(0);}", "%{transform:translateX(0);}100%{transform:translateX(calc(-100% - 96px))}"], timeInPercents, 100 - timeInPercents);
};

const appearTop = /*#__PURE__*/keyframes(["0%{transform:translateX(-50%) translateY(calc(-100% - 96px))}100%{transform:translateX(-50%) translateY(0);}"]);

const appearAndDisappearTop = totalDuration => {
  const timeInPercents = calculatePercents(totalDuration);
  return keyframes(["0%{transform:translateX(-50%) translateY(calc(-100% - 96px))}", "%{transform:translateX(-50%) translateY(0);}", "%{transform:translateX(-50%) translateY(0);}100%{transform:translateX(-50%) translateY(calc(-100% - 96px))}"], timeInPercents, 100 - timeInPercents);
};

const appearBottom = /*#__PURE__*/keyframes(["0%{transform:translateX(-50%) translateY(calc(100% + 96px))}100%{transform:translateX(-50%) translateY(0);}"]);

const appearAndDisappearBottom = totalDuration => {
  let timeInPercents = 300 / totalDuration * 100;
  if (timeInPercents > 25) timeInPercents = 25;
  return keyframes(["0%{transform:translateX(-50%) translateY(calc(100% + 96px))}", "%{transform:translateX(-50%) translateY(0);}", "%{transform:translateX(-50%) translateY(0);}100%{transform:translateX(-50%) translateY(calc(100% + 96px))}"], timeInPercents, 100 - timeInPercents);
};

const Wrapper$a = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1sqqry6-0"
})(["position:absolute;z-index:", ";"], defaultTheme.zIndex.snackbar);
const severityToColor = {
  error: 'accentDestruct500',
  warning: 'accentNotify600',
  info: 'secondary500',
  success: 'accentApprove600'
};

const getPositionCSS = (open, marginHorizontal, marginVertical, autoHideDuration, position) => {
  const animationFunc = 'cubic-bezier(0.175, 0.885, 0.32, 1.275)';
  const animationDuration = autoHideDuration ? `${autoHideDuration}ms` : '0.3s';

  switch (position) {
    case 'topLeft':
      return css(["top:", ";left:", ";transform:translateX(calc(-100% - 96px));animation:", ";"], marginVertical, marginHorizontal, open ? css(["", " ", " ", " forwards}"], autoHideDuration ? appearAndDisappearLeft(autoHideDuration) : appearTop, animationDuration, animationFunc) : 'none');

    case 'topRight':
      return css(["top:", ";right:", ";transform:translateX(calc(100% + 96px));animation:", ";"], marginVertical, marginHorizontal, open ? css(["", " ", " ", " forwards}"], autoHideDuration ? appearAndDisappearRight(autoHideDuration) : appearRight, animationDuration, animationFunc) : 'none');

    case 'topCenter':
      return css(["top:", ";left:50%;transform:translateX(-50%) translateY(calc(-100% - 96px));max-width:500px;animation:", ";"], marginVertical, open ? css(["", " ", " ", " forwards}"], autoHideDuration ? appearAndDisappearTop(autoHideDuration) : appearTop, animationDuration, animationFunc) : 'none');

    case 'bottomLeft':
      return css(["bottom:", ";left:", ";transform:translateX(calc(-100% - 96px));animation:", ";"], marginVertical, marginHorizontal, open ? css(["", " ", " ", " forwards}"], autoHideDuration ? appearAndDisappearLeft(autoHideDuration) : appearLeft, animationDuration, animationFunc) : 'none');

    case 'bottomRight':
      return css(["bottom:", ";right:", ";transform:translateX(calc(100% + 96px));animation:", ";"], marginVertical, marginHorizontal, open ? css(["", " ", " ", " forwards}"], autoHideDuration ? appearAndDisappearRight(autoHideDuration) : appearRight, animationDuration, animationFunc) : 'none');

    case 'bottomCenter':
      return css(["bottom:", ";left:50%;transform:translateX(-50%) translateY(calc(100% + 96px));max-width:500px;animation:", ";"], marginVertical, open ? css(["", " ", " ", " forwards}"], autoHideDuration ? appearAndDisappearBottom(autoHideDuration) : appearBottom, animationDuration, animationFunc) : 'none');

    case 'bottomRight':
    default:
      return css(["bottom:", ";right:", ";animation:", ";"], marginVertical, marginHorizontal, open ? css(["", " ", " ", " forwards}"], autoHideDuration ? appearAndDisappearRight(autoHideDuration) : appearRight, animationDuration, animationFunc) : 'none');
  }
};

const Container$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-1sqqry6-1"
})(["display:flex;align-items:flex-start;background:", ";border-radius:8px;padding:", ";position:fixed;font-size:14px;line-height:20px;font-weight:500;max-width:350px;overflow:hidden;", ";"], ({
  theme
}) => theme.color.onPrimaryLight, ({
  hasClose
}) => hasClose ? '8px 8px 8px 12px' : '8px 12px', ({
  open: _open = false,
  theme,
  severity: _severity = 'error',
  autoHideDuration,
  marginHorizontal: _marginHorizontal = '12px',
  marginVertical: _marginVertical = '96px',
  position
}) => css(["color:", ";border:1px solid ", ";", ""], theme.color[severityToColor[_severity]], theme.color[severityToColor[_severity]], getPositionCSS(_open, _marginHorizontal, _marginVertical, autoHideDuration, position)));
const CrossStyled = /*#__PURE__*/styled(SvgCross).withConfig({
  displayName: "CrossStyled",
  componentId: "sc-1sqqry6-2"
})(["cursor:pointer;fill:", ";width:10px;height:10px;transition:opacity 0.2s ease;:hover{opacity:0.8;}"], ({
  theme,
  severity: _severity2 = 'error'
}) => theme.color[severityToColor[_severity2]]);
const StyledButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "StyledButton",
  componentId: "sc-1sqqry6-3"
})(["padding:4px;margin-left:8px;"]);
const Content = /*#__PURE__*/styled.div.withConfig({
  displayName: "Content",
  componentId: "sc-1sqqry6-4"
})(["display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:4;overflow:hidden;"]);
const Snackbar = (_ref) => {
  let {
    open = true,
    children,
    onHide,
    hideCloseButton,
    autoHideDuration = hideCloseButton ? 5000 : 0,
    onClose,
    severity = 'error',
    marginHorizontal,
    marginVertical,
    position = 'bottomRight'
  } = _ref,
      props = _objectWithoutProperties(_ref, ["open", "children", "onHide", "hideCloseButton", "autoHideDuration", "onClose", "severity", "marginHorizontal", "marginVertical", "position"]);

  useEffect(() => {
    if (autoHideDuration && open) {
      const timer = setTimeout(() => {
        onHide === null || onHide === void 0 ? void 0 : onHide();
      }, autoHideDuration);
      return () => {
        clearTimeout(timer);
      };
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [open]);
  const showClose = !!onClose && !hideCloseButton;
  return jsx(Wrapper$a, Object.assign({
    "data-test-id": "notification-popup"
  }, props, {
    children: jsxs(Container$1, Object.assign({
      open: open,
      autoHideDuration: autoHideDuration,
      severity: severity,
      hasClose: showClose,
      marginHorizontal: marginHorizontal,
      marginVertical: marginVertical,
      position: position
    }, {
      children: [jsx(Content, {
        children: children
      }, void 0), showClose && jsx(StyledButton, Object.assign({
        onClick: onClose,
        variant: "no-style"
      }, {
        children: jsx(CrossStyled, {
          severity: severity
        }, void 0)
      }), void 0)]
    }), void 0)
  }), String(open));
};

let _$f = t => t,
    _t$f;

const positionCss = ({
  position
}) => {
  const offset = '8px';

  switch (position) {
    case 'top':
      return css(["left:50%;top:0;transform:translate(-50%,calc(-100% - ", "));"], offset);

    case 'right':
      return css(["right:0;top:50%;transform:translate(calc(100% + ", "),-50%);"], offset);

    case 'bottom':
      return css(["left:50%;bottom:0;transform:translate(-50%,calc(100% + ", "));"], offset);

    case 'left':
      return css(["left:0;top:50%;transform:translate(calc(-100% - ", "),-50%);"], offset);
  }
};

const WizardTooltipContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "WizardTooltipContainer",
  componentId: "lv1321-0"
})(["position:absolute;background-color:", ";opacity:0.96;border-radius:8px;padding:4px 8px;z-index:", ";", " width:292px;top:-10px;left:152px;position:absolute;background-color:white;border-radius:12px;box-shadow:0px 0px 40px rgb(0 0 0 / 16%);color:", ";font-size:12px;line-height:16px;padding:16px;text-align:left;", ";"], props => props.theme.color.primary100, props => props.theme.zIndex.tooltip, positionCss, props => props.theme.color.textPrimary, props => props.theme.media.mobile(_t$f || (_t$f = _$f`
  width: 124px;
  left: -54px;
`)));

const Container = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "pxkxv0-0"
})(["font-size:14px;line-height:20px;position:relative;"]);
const WizardTooltip = ({
  children,
  tooltipContent,
  className,
  visible: _visible = true,
  position: _position = 'top',
  temporary,
  visibleOnHover,
  timeout: _timeout = 1000
}) => {
  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const isTooltipVisible = useTempBoolean(hovered, _timeout, !temporary || hovered && visibleOnHover) && _visible;

  return jsxs(Container, Object.assign({
    className: className,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  }, {
    children: [children, isTooltipVisible && !!tooltipContent ? jsx(WizardTooltipContainer, Object.assign({
      position: _position
    }, {
      children: tooltipContent
    }), void 0) : null]
  }), void 0);
};

let _$e = t => t,
    _t$e,
    _t2$6,
    _t3$4,
    _t4$2,
    _t5$2,
    _t6$2,
    _t7$1,
    _t8$1,
    _t9$1,
    _t10;
const MenuContainerStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "MenuContainerStyled",
  componentId: "iz2zzw-0"
})(["display:flex;flex-direction:column;padding:12px;height:100%;", " ", " ", " ", " &[data-compact-menu=\"true\"]{width:auto;}"], props => props.theme.media.desktop(_t$e || (_t$e = _$e`
    padding: 24px;
    width: 302px;
  `)), props => props.theme.media.tablet(_t2$6 || (_t2$6 = _$e`
    width: 280px;
    padding: 24px 12px;
  `)), props => props.theme.media.smallTablet(_t3$4 || (_t3$4 = _$e`
    flex-direction: row;
    width: 100%;
  `)), props => props.theme.media.mobile(_t4$2 || (_t4$2 = _$e`
    flex-direction: row;
    width: 100%;
    padding: 12px 16px 16px;
  `)));
const InnerWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "InnerWrapper",
  componentId: "iz2zzw-1"
})(["display:inline-flex;white-space:nowrap;width:100%;height:100%;align-items:center;justify-content:flex-start;", " [data-compact-menu=\"true\"] &{display:none;}"], props => props.theme.media.mobile(_t5$2 || (_t5$2 = _$e`
    display: ${0};
  `), ({
  active
}) => active ? 'inline-flex' : 'none'));
const IconWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "IconWrapper",
  componentId: "iz2zzw-2"
})(["position:relative;display:flex;align-items:center;margin-right:10px;> svg{transition:0.3s fill;width:24px;fill:", ";}", " [data-compact-menu=\"true\"] &{margin-right:0;}"], ({
  theme,
  active
}) => active ? theme.color.primary600 : theme.color.secondary200, props => props.theme.media.mobile(_t6$2 || (_t6$2 = _$e`
      margin-right: ${0};
  `), ({
  active
}) => active ? '10px' : 0));
const ContentWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentWrapper",
  componentId: "iz2zzw-3"
})(["white-space:break-spaces;width:100%;height:100%;display:flex;align-items:center;cursor:pointer;user-select:none;"]);
const TextWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TextWrapper",
  componentId: "iz2zzw-4"
})(["margin-right:15px;"]);
const ItemStyle = /*#__PURE__*/css(["border-radius:8px;width:100%;height:48px;display:flex;align-items:center;font-weight:500;font-size:14px;line-height:20px;padding:0 12px;transition:0.3s background-color,0.3s color;border:none;color:", ";background-color:", ";cursor:", ";&:hover{color:", ";> ", " svg{fill:", ";}}&:focus{outline:none;}&:not(:first-child){margin-top:8px;", " ", "}", " ", " [data-compact-menu=\"true\"] &{width:48px;}"], ({
  active,
  theme
}) => active ? theme.color.primary600 : theme.color.onPrimaryDark, ({
  active,
  theme
}) => active ? theme.color.primary25 : theme.color.onPrimaryLight, ({
  active
}) => active ? 'default' : 'pointer', ({
  theme
}) => theme.color.primary600, IconWrapper, ({
  theme
}) => theme.color.primary500, props => props.theme.media.mobile(_t7$1 || (_t7$1 = _$e`
      margin-top: 0;
      padding: 0 ${0};
    `), ({
  active
}) => active ? '12px' : '16px'), props => props.theme.media.tablet(_t8$1 || (_t8$1 = _$e`
      margin-top: 0;
      padding: 0 ${0};
    `), ({
  active
}) => active ? '12px' : '16px'), props => props.theme.media.mobile(_t9$1 || (_t9$1 = _$e`
    margin-top: 0;
    font-size: 12px;
    width: auto;
    display: grid;
    grid-template-columns: max-content max-content;
    grid-gap: 0;
  `)), props => props.theme.media.smallTablet(_t10 || (_t10 = _$e`
    width: auto;
  `))); // eslint-disable-next-line @typescript-eslint/no-unused-vars

const ItemLinkStyled = /*#__PURE__*/styled((_ref) => {
  let props = _objectWithoutProperties(_ref, ["active"]);

  return jsx(Link$1, Object.assign({}, props), void 0);
}).withConfig({
  displayName: "ItemLinkStyled",
  componentId: "iz2zzw-5"
})(["position:relative;", ""], ItemStyle);
const ItemNotLinkStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "ItemNotLinkStyled",
  componentId: "iz2zzw-6"
})(["position:relative;", ""], ItemStyle);
const ItemStyled = /*#__PURE__*/styled.button.withConfig({
  displayName: "ItemStyled",
  componentId: "iz2zzw-7"
})(["", ""], ItemStyle);
const Count = /*#__PURE__*/styled.div.withConfig({
  displayName: "Count",
  componentId: "iz2zzw-8"
})(["position:absolute;display:flex;align-items:center;justify-content:center;color:", ";font-size:10px;line-height:16px;min-width:14px;height:14px;border-radius:8px;background-color:", ";top:50%;right:8px;padding:0px 6px;transform:translateY(-50%);font-weight:600;"], props => props.theme.color.onPrimaryLight, props => props.theme.color.accentNotify);
const BarMenuFullWidthItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "BarMenuFullWidthItem",
  componentId: "iz2zzw-9"
})(["width:100%;border-top:1px solid ", ";transition:0.3s background-color,0.3s color;&:hover{color:", ";}cursor:pointer;"], ({
  theme
}) => theme.color.onSurfaceLowEmphasys, ({
  theme
}) => theme.color.primary600);
const MenuItemLink = /*#__PURE__*/React__default.forwardRef((_ref2, ref) => {
  let {
    children,
    active,
    icon: IconComponent,
    path,
    count,
    className,
    onClick,
    overlay = null,
    overlayOpen
  } = _ref2,
      rest = _objectWithoutProperties(_ref2, ["children", "active", "icon", "path", "count", "className", "onClick", "overlay", "overlayOpen"]);

  const inner = jsxs(Fragment, {
    children: [jsxs(ContentWrapper, Object.assign({
      onClick: onClick
    }, {
      children: [jsx(TextWrapper, {
        children: children
      }, void 0), count ? jsx(Count, {
        children: count
      }, void 0) : null]
    }), void 0), overlayOpen ? overlay : null]
  }, void 0);

  if (path) {
    return jsxs(ItemLinkStyled, Object.assign({}, rest, {
      active: active,
      to: path || '',
      className: className,
      ref: ref
    }, {
      children: [jsx(IconWrapper, Object.assign({
        active: active
      }, {
        children: jsx(IconComponent, {}, void 0)
      }), void 0), jsx(InnerWrapper, Object.assign({
        active: active
      }, {
        children: inner
      }), void 0)]
    }), void 0);
  }

  return jsxs(ItemNotLinkStyled, Object.assign({}, rest, {
    active: active,
    className: className,
    ref: ref
  }, {
    children: [jsx(IconWrapper, Object.assign({
      active: active
    }, {
      children: jsx(IconComponent, {}, void 0)
    }), void 0), jsx(InnerWrapper, Object.assign({
      active: active
    }, {
      children: inner
    }), void 0)]
  }), void 0);
});
const MenuContainer = ({
  children,
  className,
  compact
}) => {
  return jsx(MenuContainerStyled, Object.assign({
    className: className,
    "data-compact-menu": compact
  }, {
    children: children
  }), void 0);
};
MenuItemLink.displayName = 'MenuItemLink';
const MenuItem = ({
  children,
  active,
  icon: IconComponent,
  onClick,
  className
}) => {
  return jsxs(ItemStyled, Object.assign({
    active: active,
    onClick: onClick,
    className: className
  }, {
    children: [IconComponent && jsx(IconWrapper, Object.assign({
      active: active
    }, {
      children: jsx(IconComponent, {}, void 0)
    }), void 0), jsx(InnerWrapper, Object.assign({
      active: active
    }, {
      children: children
    }), void 0)]
  }), void 0);
};

let _$d = t => t,
    _t$d;
const Wrapper$9 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-48hkx-0"
})(["display:flex;margin:20px 0;flex-wrap:wrap;", ""], props => props.theme.media.mobile(_t$d || (_t$d = _$d`
      margin: 8px 16px;
  `)));
const Breadcrumb = /*#__PURE__*/css(["color:", ";font-weight:500;font-size:14px;line-height:20px;transition:0.3s color;display:flex;align-items:center;&:not(:last-child){&::after{content:'';display:block;margin:0 8px;width:4px;height:4px;border-radius:50%;background-color:", ";}}"], props => props.theme.color.secondary200, props => props.theme.color.secondary200);
const StaticBreadcrumb = /*#__PURE__*/styled.span.withConfig({
  displayName: "StaticBreadcrumb",
  componentId: "sc-48hkx-1"
})(["", ";cursor:default;"], Breadcrumb);
const LinkBreadcrumb = /*#__PURE__*/styled.span.withConfig({
  displayName: "LinkBreadcrumb",
  componentId: "sc-48hkx-2"
})(["", ";a{text-decoration:none;color:inherit;}&:hover{color:", ";}"], Breadcrumb, props => props.theme.color.primary600);
const Breadcrumbs = ({
  items: _items = [],
  breadcrumbs: _breadcrumbs = [],
  onClick
}) => {
  return jsx(Wrapper$9, {
    children: [..._breadcrumbs, ..._items].map((item, i) => {
      return item.path ? jsx(LinkBreadcrumb, Object.assign({
        onClick: () => onClick && onClick(item)
      }, {
        children: item.title
      }), i) : jsx(StaticBreadcrumb, Object.assign({
        onClick: () => onClick && onClick(item)
      }, {
        children: item.title
      }), i);
    })
  }, void 0);
};

const Wrapper$8 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1aav82v-0"
})(["display:flex;flex-wrap:nowrap;justify-content:flex-start;margin:24px;user-select:none;"]);

const buttonCss = props => {
  return css(["color:", ";background-color:", ";pointer-events:", ";"], props.isActive ? props.theme.color.onPrimaryLight : props.theme.color.primary600, props.isActive ? props.theme.color.primary600 : 'transparent', props.isActive ? 'none' : 'initial');
};

const Button$1 = /*#__PURE__*/styled.button.withConfig({
  displayName: "Button",
  componentId: "sc-1aav82v-1"
})(["font-size:14px;font-weight:500;margin:0 3px;min-width:24px;height:24px;cursor:pointer;border:0;outline:none;border-radius:4px;", " &:hover{background-color:", ";}"], buttonCss, props => props.theme.color.primary50);
const Spacer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Spacer",
  componentId: "sc-1aav82v-2"
})(["min-width:24px;height:24px;display:flex;align-items:flex-end;justify-content:center;border-radius:4px;font-weight:500;font-size:14px;line-height:20px;&:hover{background-color:", ";cursor:pointer;}&::before{content:'...';color:", ";}"], props => props.theme.color.primary50, props => props.theme.color.secondary200);
const Pagination = ({
  total,
  current,
  max: _max = 10,
  onClick
}) => {
  const handleClick = useCallback(pageNumber => () => {
    onClick(pageNumber);
  }, [onClick]);
  const mid = Math.ceil(_max / 2);
  const showFirst = current > mid + 1;
  const showLast = total > current + mid;
  const knobs = useMemo(() => {
    const result = [];
    let end = current + mid;
    const start = current - mid - Math.floor(Math.max(0, end - total));

    for (let i = start; i <= end; ++i) {
      if (i < 1) {
        end = end + 1;
        continue;
      }

      if (i > total) {
        break;
      }

      result.push(i);
    }

    return result;
  }, [total, current, mid]);
  const start = showFirst ? jsxs(Fragment, {
    children: [jsx(Button$1, Object.assign({
      onClick: handleClick(1),
      isActive: false
    }, {
      children: "1"
    }), void 0), knobs[0] === 2 ? null : jsx(Spacer, {
      onClick: handleClick(current - mid - 1)
    }, void 0)]
  }, void 0) : null;
  const end = showLast ? jsxs(Fragment, {
    children: [knobs[knobs.length - 1] === total - 1 ? null : jsx(Spacer, {
      onClick: handleClick(current + mid + 1)
    }, void 0), jsx(Button$1, Object.assign({
      onClick: handleClick(total),
      isActive: false
    }, {
      children: total
    }), void 0)]
  }, void 0) : null;
  return jsxs(Wrapper$8, {
    children: [start, knobs.map(pageNumber => {
      return jsx(Button$1, Object.assign({
        onClick: handleClick(pageNumber),
        isActive: current === pageNumber
      }, {
        children: pageNumber
      }), pageNumber);
    }), end]
  }, void 0);
};

const Wrapper$7 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1m4yhec-0"
})(["display:flex;flex-direction:column;", ""], ({
  indent: _indent = 0
}) => ({
  marginLeft: _indent ? '26px' : '0'
}));
const ItemWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ItemWrapper",
  componentId: "sc-1m4yhec-1"
})(["display:flex;flex-direction:row;justify-content:flex-start;align-items:center;margin-bottom:12px;"]);
const ExpandButtonWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ExpandButtonWrapper",
  componentId: "sc-1m4yhec-2"
})(["display:flex;flex-direction:row;justify-content:center;align-items:center;width:10px;height:10px;margin-right:10px;"]);
const ExpandButton = /*#__PURE__*/styled.img.withConfig({
  displayName: "ExpandButton",
  componentId: "sc-1m4yhec-3"
})(["", ""], ({
  expanded
}) => ({
  transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)'
}));

const Item = ({
  id,
  expanded,
  expandable,
  onExpand,
  children
}) => jsxs(ItemWrapper, Object.assign({
  onClick: () => onExpand(id)
}, {
  children: [jsx(ExpandButtonWrapper, {
    children: expandable && jsx(ExpandButton, {
      src: iconTriangleDown,
      expanded: expanded
    }, void 0)
  }, void 0), children]
}), void 0);

const TreeView = ({
  nodes,
  indent: _indent2 = 0,
  itemComponent,
  expandAll
}) => {
  const [expandedIds, setExpandedIds] = useState([]);
  const onExpand = useCallback(id => {
    if (expandedIds.includes(id)) {
      setExpandedIds(expandedIds.filter(eid => eid !== id));
    } else {
      setExpandedIds([...expandedIds, id]);
    }
  }, [setExpandedIds, expandedIds]);
  return jsx(Wrapper$7, Object.assign({
    indent: _indent2
  }, {
    children: nodes.map(node => {
      var _node$nodes;

      const childNodes = node.nodes;
      const isExpanded = expandedIds.includes(node.id) || !!expandAll;
      return jsxs("div", {
        children: [jsx(Item, Object.assign({
          id: node.id,
          expanded: isExpanded,
          expandable: !!((_node$nodes = node.nodes) !== null && _node$nodes !== void 0 && _node$nodes.length),
          onExpand: onExpand
        }, {
          children: itemComponent(node.props)
        }), void 0), childNodes && !!childNodes.length && isExpanded && jsx(TreeView, {
          indent: _indent2 + 1,
          nodes: childNodes,
          itemComponent: itemComponent
        }, void 0)]
      }, node.id);
    })
  }), void 0);
};

const Wrapper$6 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "o3fpg7-0"
})([""]);

const mapOptions = (options, onChange, lvl = 0) => {
  return options.map(option => {
    var _option$options, _option$options2;

    const checkedChildOptionsCount = option.options && option.options.reduce((acc, o) => o.isChecked ? acc + 1 : acc, 0);
    const allChildOptionsChecked = checkedChildOptionsCount && checkedChildOptionsCount === ((_option$options = option.options) === null || _option$options === void 0 ? void 0 : _option$options.length);
    const isPartlyChecked = option.options && checkedChildOptionsCount && checkedChildOptionsCount !== option.options.length;
    const isChecked = option.isChecked || isPartlyChecked || allChildOptionsChecked;
    return {
      id: option.id,
      props: {
        value: option.id,
        label: option.title,
        isChecked,
        isPartlyChecked,
        disabled: lvl === 0 && !((_option$options2 = option.options) !== null && _option$options2 !== void 0 && _option$options2.length),
        onChange
      },
      nodes: option.options && mapOptions(option.options, onChange, lvl + 1)
    };
  });
};

const CheckboxTree = ({
  options,
  expandAll,
  onChange
}) => {
  const nodes = mapOptions(options, onChange);
  return jsx(Wrapper$6, {
    children: jsx(TreeView, {
      nodes: nodes,
      expandAll: expandAll,
      itemComponent: props => jsx("div", Object.assign({
        onClick: e => e.stopPropagation()
      }, {
        children: jsx(Checkbox, Object.assign({}, props), void 0)
      }), void 0)
    }, void 0)
  }, void 0);
};

const WrapperStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "WrapperStyled",
  componentId: "sc-8rlzo6-0"
})(["display:flex;flex-wrap:wrap;"]);
const TagList = ({
  list,
  onRemove,
  label,
  name,
  className
}) => {
  return jsx(FormField, {
    name: name,
    label: label,
    render: () => jsx(WrapperStyled, Object.assign({
      className: className
    }, {
      children: list.map(tag => jsx(Tag, {
        title: tag.title,
        index: tag.id,
        onRemove: () => onRemove === null || onRemove === void 0 ? void 0 : onRemove(tag)
      }, `tag-${tag.id}`))
    }), void 0)
  }, void 0);
};

let _$c = t => t,
    _t$c;

function ownKeys$9(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$9(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$9(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$9(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const customStyles$1 = {
  control: (base, props) => {
    return _objectSpread$9(_objectSpread$9({}, controlStyle(base, props)), {}, {
      height: '40px',
      minHeight: '40px',
      borderColor: props.isFocused ? defaultTheme.color.primary600 : defaultTheme.color.onSurfaceLowEmphasys,
      borderRadius: 0,
      borderTopLeftRadius: '8px',
      borderBottomLeftRadius: '8px',
      zIndex: 1,
      cursor: 'pointer',
      boxShadow: 'none',
      transition: '0.3s border-color'
    });
  },
  menu: base => _objectSpread$9(_objectSpread$9({}, base), {}, {
    zIndex: 2
  })
};
const Wrapper$5 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "td7rtg-0"
})(["height:40px;display:inline-grid;flex-wrap:nowrap;grid-template-columns:150px auto;align-items:center;width:450px;position:relative;"]);
const ClearIcon = /*#__PURE__*/styled(SvgCrossCircle).withConfig({
  displayName: "ClearIcon",
  componentId: "td7rtg-1"
})(["position:absolute;top:50%;right:10px;transform:translateY(-50%);opacity:1;transition:0.3s opacity,0.3s fill;z-index:3;cursor:pointer;&:hover{fill:", ";}"], ({
  theme
}) => theme.color.primary600);
const Input$2 = /*#__PURE__*/styled.input.withConfig({
  displayName: "Input",
  componentId: "td7rtg-2"
})(["border:1px solid ", ";outline:none;font-weight:500;font-size:14px;line-height:20px;height:40px;color:", ";flex-grow:1;border-top-right-radius:8px;border-bottom-right-radius:8px;padding:0 8px 0 16px;background-color:", ";margin-left:-1px;transition:0.3s color,0.3s border-color;position:relative;z-index:2;border-left-color:transparent;background-clip:padding-box;&::placeholder{font-weight:500;font-size:14px;line-height:20px;color:", ";}&:hover{border-color:", ";z-index:2;}&:focus{border-color:", ";}&:placeholder-shown ~ ", "{opacity:0;pointer-events:none;}", ""], ({
  theme
}) => theme.color.onSurfaceLowEmphasys, ({
  theme
}) => theme.color.onPrimaryDark, ({
  theme
}) => theme.color.primary5, ({
  theme
}) => theme.color.secondary200, ({
  theme
}) => theme.color.onPrimaryMid, ({
  theme
}) => theme.color.primary600, ClearIcon, props => props.theme.media.mobile(_t$c || (_t$c = _$c`
    font-size: 16px;
    &::placeholder {
      font-size: 16px;
    }
  `)));

const AdvancedSearchField = ({
  options,
  onChange,
  defaultOption,
  className,
  optionValue,
  value: _value = ''
}) => {
  const [search, setSearch] = useState(_value);
  const [option, setOption] = useState(optionValue || defaultOption || options[0].id);
  const initial = useRef(!search);
  const debouncedSearch = useDebounce(search, 500);
  const onInputChange = useCallback(event => {
    setSearch(event.target.value);
  }, []);
  const onSelectChange = useCallback(id => {
    setOption(id);
  }, []);
  const onClear = useCallback(() => {
    setSearch('');
  }, []);
  useEffect(() => {
    if (!initial.current && option !== optionValue) setOption(optionValue); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionValue]);
  useEffect(() => {
    if (!initial.current && search !== _value) setSearch(_value); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_value]);
  useEffect(() => {
    if (!initial.current && (search !== _value || option !== optionValue)) {
      onChange(debouncedSearch || '', option);
    } else {
      initial.current = false;
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [debouncedSearch, option]);
  const currentOption = useMemo(() => options.find(item => item.id === option), [option, options]);
  return jsxs(Wrapper$5, Object.assign({
    className: className
  }, {
    children: [jsx(SimpleSelect, {
      options: options,
      value: option,
      onChange: onSelectChange,
      customStyles: customStyles$1
    }, void 0), jsx(Input$2, {
      placeholder: (currentOption === null || currentOption === void 0 ? void 0 : currentOption.placeholder) || 'default placeholder',
      value: search,
      onChange: onInputChange
    }, void 0), jsx(ClearIcon, {
      onClick: onClear
    }, void 0)]
  }), void 0);
};

let _$b = t => t,
    _t$b;
const dynamicInputCss = ({
  disabled,
  theme,
  invalid,
  inputSize
}) => {
  let opacity = 1;
  let color = theme.color.onPrimaryDark;
  let borderColor = theme.color.onSurfaceLowEmphasys;
  let cursor;
  let height;

  if (disabled) {
    opacity = 0.5;
    cursor = 'not-allowed';
  }

  if (invalid) {
    color = theme.color.accentDestructive;
    borderColor = theme.color.accentDestructive;
  }

  switch (inputSize) {
    case 'thin':
      height = '28px';
      break;

    case 'small':
      height = '32px';
      break;

    case 'medium':
      height = '40px';
      break;

    case 'big':
      height = '48px';
      break;

    default:
      height = '40px';
  }

  return {
    opacity,
    color,
    borderColor,
    cursor,
    height
  };
};
const focusInputCss = ({
  invalid,
  theme
}) => {
  let borderColor = theme.color.primary600;
  let color = theme.color.onPrimaryDark;
  const borderWidth = '1px';

  if (invalid) {
    borderColor = theme.color.accentDestructive;
    color = theme.color.accentDestructive;
  }

  return {
    borderColor,
    color,
    borderWidth
  };
};
const hoverInputCss = ({
  invalid,
  theme
}) => {
  let borderColor = theme.color.onPrimaryMid;

  if (invalid) {
    borderColor = theme.color.accentDestructive;
  }

  return {
    borderColor
  };
};
const InputStyled$2 = /*#__PURE__*/styled.input.withConfig({
  displayName: "InputStyled",
  componentId: "m9eb2e-0"
})(["height:40px;width:100%;border-width:1px;border-style:solid;border-radius:4px;font-family:Montserrat;font-style:normal;font-weight:500;font-size:14px;line-height:20px;padding:8px 10px;box-sizing:border-box;outline:none;transition:0.3s all;position:relative;&::-webkit-credentials-auto-fill-button{position:relative;right:-35px;}box-shadow:0 0 0 2em ", " inset;&:hover{", "}&:read-only{cursor:not-allowed;}&:not(:read-only):focus{", "}", " ", ""], props => props.theme.color.onPrimaryLight, hoverInputCss, focusInputCss, props => props.theme.media.mobile(_t$b || (_t$b = _$b`
    font-size: 16px;
  `)), dynamicInputCss);

const countryArr = [];
const countryCallingCodeToCountryCodeMap = {}; // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore this is correct typings, but we need to fulfill this map later

const countryCodeToCallingCodeMap = {};
const countries = getCountries();
countries.forEach(country => {
  const callingCode = getCountryCallingCode(country);
  countryCodeToCallingCodeMap[country] = getCountryCallingCode(country);
  countryCallingCodeToCountryCodeMap[callingCode] = country;
});
const matchCountryCode = phoneCode => {
  return countryCallingCodeToCountryCodeMap[phoneCode];
};
const matchCallingCode = countryCode => {
  return countryCodeToCallingCodeMap[countryCode];
};
const getCountryByCountryCode = countryCode => {
  return countryArr.find(item => item.country === countryCode);
};
const countryList = countryArr;

const OptionalNumber = value => {
  if (typeof value !== 'string') return value;
  return Number(value);
};
const OptionalString = value => {
  if (typeof value !== 'number') return value;
  return String(value);
};

function ownKeys$8(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$8(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$8(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

const SelectInput = (_ref) => {
  let props = _objectWithoutProperties(_ref, ["autoComplete"]);

  return (// eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore wrong react-select library typings
    jsx(components.Input, Object.assign({}, props, {
      autoComplete: "no"
    }), void 0)
  );
};

const SingleValue = props => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore wrong react-select library typings
  return jsx(components.SingleValue, Object.assign({}, props, {
    children: props.data.singleValueLabel
  }), void 0);
};

const DropdownIndicatorStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "DropdownIndicatorStyled",
  componentId: "sc-1hmro5e-0"
})(["position:absolute;right:-8px;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;margin-right:15px;", ""], props => ({
  [props.menuIsOpen ? 'borderBottom' : 'borderTop']: '5px solid #858585;'
}));
const customStyles = {
  control: base => _objectSpread$8(_objectSpread$8({}, base), {}, {
    height: '38px',
    minWidth: '85px',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    borderRadius: '4px',
    borderColor: defaultTheme.color.onSurfaceLowEmphasys,
    borderRight: 'none',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    border: 'none',
    outline: 'none',
    boxShadow: 'none'
  }),
  option: (base, props) => _objectSpread$8(_objectSpread$8({}, base), {}, {
    height: '38px',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    borderRadius: '4px',
    borderColor: defaultTheme.color.onSurfaceLowEmphasys,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    color: props.isSelected ? '#fff' : '#000'
  }),
  menuList: base => _objectSpread$8(_objectSpread$8({}, base), {}, {
    padding: '4px'
  }),
  menu: base => _objectSpread$8(_objectSpread$8({}, base), {}, {
    minWidth: 'max-content'
  }),
  valueContainer: base => _objectSpread$8(_objectSpread$8({}, base), {}, {
    padding: '0 4px 0 8px',
    display: 'flex',
    justifyContent: 'flex-start'
  })
};
const InputsContainer$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputsContainer",
  componentId: "sc-1hmro5e-1"
})(["display:grid;grid-template-columns:85px auto;border-width:1px;border-style:solid;border-radius:4px;height:40px;", " ", ""], dynamicInputCss, props => props.focused && focusInputCss(props));
const PhoneInputStyled = /*#__PURE__*/styled(InputStyled$2).withConfig({
  displayName: "PhoneInputStyled",
  componentId: "sc-1hmro5e-2"
})(["border:none;height:38px;padding-left:10px;"]);
const OptionStyled$3 = /*#__PURE__*/styled.span.withConfig({
  displayName: "OptionStyled",
  componentId: "sc-1hmro5e-3"
})(["display:flex;align-items:center;font-style:normal;font-weight:500;"]);
const FlagStyled$2 = /*#__PURE__*/styled.span.withConfig({
  displayName: "FlagStyled",
  componentId: "sc-1hmro5e-4"
})(["font-size:18px;padding-right:8px;display:flex;align-items:center;"]);
const CodeStyled = /*#__PURE__*/styled.span.withConfig({
  displayName: "CodeStyled",
  componentId: "sc-1hmro5e-5"
})(["font-weight:600;margin-left:4px;"]);
const SpinnerWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "SpinnerWrapper",
  componentId: "sc-1hmro5e-6"
})(["display:flex;align-items:center;justify-content:center;position:relative;&::after{content:'';position:absolute;background-color:hsl(0,0%,80%);width:1px;height:calc(100% - 16px);top:8px;right:0;}"]);

const makeFullNumber = (code, number) => `+${code || ''}${number || ''}`;

const CountryPhoneInput = ({
  defaultCountryCode,
  phoneNumber,
  onChange,
  invalid,
  label,
  id,
  name,
  onFocus,
  onBlur,
  errorText,
  className,
  disabled,
  required,
  testId,
  countryOptions: _countryOptions = [],
  countryOptionsProgress
}) => {
  const parsedNumber = phoneNumber ? parsePhoneNumberFromString(phoneNumber) || null : null;
  const prevDefaultCountryCode = useRef(defaultCountryCode);
  const [focused, setFocused] = useState(false);
  const [countryCode, setCountryCode] = useState((parsedNumber === null || parsedNumber === void 0 ? void 0 : parsedNumber.country) || defaultCountryCode);
  const [callingCode, setCallingCode] = useState((parsedNumber === null || parsedNumber === void 0 ? void 0 : parsedNumber.countryCallingCode) || defaultCountryCode && matchCallingCode(defaultCountryCode));
  const [nationalNumber, setNationalNumber] = useState((parsedNumber === null || parsedNumber === void 0 ? void 0 : parsedNumber.nationalNumber) || '');
  const inputRef = useRef(null);
  useEffect(() => {
    if (prevDefaultCountryCode.current !== defaultCountryCode) {
      setCountryCode((parsedNumber === null || parsedNumber === void 0 ? void 0 : parsedNumber.country) || defaultCountryCode);
      setCallingCode((parsedNumber === null || parsedNumber === void 0 ? void 0 : parsedNumber.countryCallingCode) || defaultCountryCode && matchCallingCode(defaultCountryCode));
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [defaultCountryCode]);
  const options = useMemo(() => {
    return _countryOptions.filter(helpersObject.nonNullable).map(entry => ({
      value: entry.id,
      singleValueLabel: jsxs(OptionStyled$3, {
        children: [jsx(FlagStyled$2, {
          children: jsx(Flag, {
            code: entry.id
          }, void 0)
        }, void 0), "+", entry.phone_prefix]
      }, void 0),
      label: jsxs(OptionStyled$3, {
        children: [jsx(FlagStyled$2, {
          children: jsx(Flag, {
            code: entry.id
          }, void 0)
        }, void 0), jsx("span", {
          children: entry.name
        }, void 0), jsx(CodeStyled, {
          children: ` +${entry.phone_prefix}`
        }, void 0)]
      }, void 0),
      code: entry.phone_prefix
    }));
  }, [_countryOptions]);
  const selectedOption = useMemo(() => options.find(opt => opt.value === countryCode), [countryCode, options]);
  const handleChange = useCallback((number, callingCode, countryCode) => {
    const fullNumber = makeFullNumber(callingCode || '', number || '');
    const code = `+${callingCode || ''}`;
    const isValid = isValidNumberForRegion(number, countryCode);
    onChange(fullNumber, code, number || '', isValid, countryCode);
  }, [onChange]);
  const handleNumberChange = useCallback(e => {
    const value = e.target.value.replace(/\D/g, '');
    const fullNumber = makeFullNumber(callingCode, value);
    const parsedNumber = parsePhoneNumberFromString(fullNumber);
    setNationalNumber((parsedNumber === null || parsedNumber === void 0 ? void 0 : parsedNumber.nationalNumber) || value || '');
    handleChange((parsedNumber === null || parsedNumber === void 0 ? void 0 : parsedNumber.nationalNumber) || value || '', callingCode, countryCode);
  }, [countryCode, callingCode, handleChange]);
  const handleCountryCodeChange = useCallback(code => {
    const callingCode = getCountryCallingCode(code);
    setCountryCode(code);
    setCallingCode(callingCode);
    handleChange(nationalNumber, callingCode, code);
  }, [handleChange, nationalNumber]);
  const handleFocus = useCallback(e => {
    setFocused(true);
    onFocus && onFocus(e);
  }, [onFocus]);
  const handleBlur = useCallback(e => {
    setFocused(false);
    onBlur && onBlur(e);
  }, [onBlur]);
  const handleSelectClose = useCallback(() => {
    var _inputRef$current;

    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  }, []);
  return jsx(FormField, {
    id: id,
    name: name,
    invalid: invalid,
    errorText: errorText,
    className: className,
    label: label,
    required: required,
    testId: testId,
    render: ({
      id,
      name
    }) => jsxs(InputsContainer$1, Object.assign({
      focused: focused,
      invalid: invalid
    }, {
      children: [countryOptionsProgress === Progress.WORK ? jsx(SpinnerWrapper, {
        children: jsx(Spinner$1, {
          size: 'small'
        }, void 0)
      }, void 0) : jsx(ReactSelect, {
        id: name,
        instanceId: name,
        name: `${name}-select`,
        isSearchable: false,
        onFocus: handleFocus,
        placeholder: '',
        noOptionsMessage: () => 'Empty',
        styles: customStyles,
        options: options,
        value: selectedOption,
        onChange: v => handleCountryCodeChange(v.value),
        onMenuClose: handleSelectClose,
        isDisabled: disabled,
        components: {
          Input: SelectInput,
          SingleValue,
          // eslint-disable-next-line react/display-name
          DropdownIndicator: ({
            selectProps
          }) => jsx(DropdownIndicatorStyled, {
            menuIsOpen: !!selectProps.menuIsOpen
          }, void 0)
        }
      }, void 0), jsx(PhoneInputStyled, {
        id: id,
        name: name,
        onBlur: handleBlur,
        onFocus: handleFocus,
        value: nationalNumber,
        onChange: handleNumberChange,
        disabled: disabled || countryOptionsProgress === Progress.WORK,
        "data-field-invalid": invalid,
        autoComplete: 'tel-national',
        ref: inputRef,
        inputMode: "numeric",
        pattern: '[0-9]*',
        type: 'tel'
      }, void 0)]
    }), void 0)
  }, void 0);
};

let _$a = t => t,
    _t$a,
    _t2$5,
    _t3$3;
const Wrapper$4 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "krc538-0"
})(["display:grid;grid-template-columns:max-content auto max-content;align-items:center;border:1px solid ", ";border-radius:8px;max-width:100%;&[data-size='small']{height:32px;padding:0 4px;width:128px;}&[data-size='medium']{width:160px;height:40px;padding:0 4px;}&[data-size='large']{padding:0 8px;height:48px;max-width:100%;}", ""], props => props.theme.color.onSurfaceLowEmphasys, props => props.theme.media.mobile(_t$a || (_t$a = _$a`
    &[data-size='small'] {
        height: 32px;
        padding: 0 4px;
      }

      &[data-size='medium'] {
        height: 32px;
        padding: 0 4px;
      }
      &[data-size='large'] {
        height: 40px;
        padding: 0 5px;
      }
  `)));
const Input$1 = /*#__PURE__*/styled.input.withConfig({
  displayName: "Input",
  componentId: "krc538-1"
})(["flex-grow:1;min-width:50px;font-weight:500;border:none;padding:0;text-align:center;margin:0 3px;&:focus{outline:none;}[data-size='small'] &{font-size:14px;}[data-size='medium'] &{font-size:16px;}[data-size='large'] &{font-size:24px;}[data-disabled='true'] &{cursor:default;}", " &::-webkit-outer-spin-button,&::-webkit-inner-spin-button{appearance:none;margin:0;}&[type='number']{appearance:textfield;}"], props => props.theme.media.mobile(_t2$5 || (_t2$5 = _$a`
    [data-size='small'] & {
      font-size: 16px;
    }

    [data-size='medium'] & {
      font-size: 16px;
    }

    [data-size='large'] & {
      font-size: 16px;
    }
  `)));
const Button = /*#__PURE__*/styled.button.withConfig({
  displayName: "Button",
  componentId: "krc538-2"
})(["border:none;background:none;display:flex;align-items:center;justify-content:center;cursor:pointer;margin:0;&:focus{outline:none;}height:32px;width:32px;border-radius:4px;background-color:", ";transition:0.3s background-color;padding:0;&:hover,&:focus{background-color:", ";}[data-disabled='false'] &:focus{box-shadow:", ";}[data-size='small'] &{width:24px;height:24px;padding:1px;}[data-size='medium'] &{padding:5px;}[data-disabled='true'] &,&:disabled{background-color:", ";cursor:not-allowed;}", " &:disabled{cursor:not-allowed;opacity:0.4;}"], props => props.theme.color.primary25, props => props.theme.color.primary50, props => props.theme.shadow.focus, props => props.theme.color.backgroundBase, props => props.theme.media.mobile(_t3$3 || (_t3$3 = _$a`
    [data-size='medium'] & {
      width: 24px;
      height: 24px;
      padding: 1px;
    }
  `)));
const Icon = /*#__PURE__*/css(["transition:0.3s fill;width:24px;height:24px;fill:", ";[data-disabled='true'] &,button:disabled &{fill:", ";}"], props => props.theme.color.primary600, props => props.theme.color.onSurfaceLowEmphasys);
const Plus = /*#__PURE__*/styled(SvgAdd).withConfig({
  displayName: "Plus",
  componentId: "krc538-3"
})(["", ""], Icon);
const Minus = /*#__PURE__*/styled(SvgMinus).withConfig({
  displayName: "Minus",
  componentId: "krc538-4"
})(["", ""], Icon);
const getStepDecimals = step => {
  var _step$toString$split$;

  return ((_step$toString$split$ = step.toString().split('.')[1]) === null || _step$toString$split$ === void 0 ? void 0 : _step$toString$split$.length) || 0;
};
const numberToStep = (num, step) => {
  return Math.round(num / step) * step;
};
const NumberInput$1 = (_ref) => {
  let {
    onChange,
    onDecrease,
    onIncrease,
    step: stepProp,
    inputStep: inputStepProp,
    value: valueProp,
    min: minProp,
    max: maxProp,
    size = 'large',
    className,
    disabled,
    testId = 'input',
    selectTextOnFocus,
    name,
    showNothing,
    updateOnInputChange: updateOnInputChangeProp
  } = _ref,
      divProps = _objectWithoutProperties(_ref, ["onChange", "onDecrease", "onIncrease", "step", "inputStep", "value", "min", "max", "size", "className", "disabled", "testId", "selectTextOnFocus", "name", "showNothing", "updateOnInputChange"]);

  const max = typeof maxProp === 'number' ? maxProp : Infinity;
  const step = typeof stepProp === 'number' ? stepProp : 1;
  const inputStep = typeof inputStepProp === 'number' ? inputStepProp : step;
  const min = typeof minProp === 'number' ? minProp : step;
  const value = typeof valueProp === 'number' ? valueProp : 0;
  const updateOnInputChange = updateOnInputChangeProp ? updateOnInputChangeProp : inputStep === 1;
  const isMaxReached = value >= max;
  const isMinReached = value <= min;
  const {
    t
  } = useTranslation('ui');
  const inputRef = useRef(null);
  const stepDecimals = useMemo(() => getStepDecimals(inputStep), [inputStep]);

  const valueToFixed = value => stepDecimals ? value.toFixed(stepDecimals) : value.toString();

  const [inputValue, setInputValue] = useState(valueToFixed(numberToStep(value, inputStep)));

  const changeValue = newValue => {
    const parsedValue = numberToStep(Math.min(Math.max(Number(newValue), min), max), inputStep);
    const toFixed = valueToFixed(parsedValue);

    if (valueProp !== parsedValue) {
      onChange(Number(toFixed));
    }

    setInputValue(toFixed);
  };

  useEffect(() => {
    if (valueProp !== Number(inputValue)) changeValue(value); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueProp]);

  const handleInputChange = e => {
    const val = e.target.value;
    if (updateOnInputChange && val && val >= min) return changeValue(val);
    setInputValue(!val ? '' : val);
  };

  const handleBlur = () => {
    changeValue(inputValue);
  };

  const handleIncrease = () => {
    if (disabled) return;
    onIncrease && (onIncrease === null || onIncrease === void 0 ? void 0 : onIncrease(value));
    const newValue = numberToStep(Math.min(value + step, max), inputStep);
    onChange(newValue);
    setInputValue(valueToFixed(newValue));
  };

  const handleDecrease = () => {
    if (disabled) return;
    onDecrease && onDecrease(value);
    const newValue = numberToStep(Math.max(value - step, min), inputStep);
    onChange(newValue);
    setInputValue(valueToFixed(newValue));
  };

  useKey(inputRef, 'keydown', handleIncrease, 'ArrowUp');
  useKey(inputRef, 'keydown', handleDecrease, 'ArrowDown');
  return jsxs(Wrapper$4, Object.assign({
    "data-test-id": 'number-input',
    "data-size": size,
    className: className,
    "data-disabled": disabled
  }, divProps, {
    children: [jsx(Button, Object.assign({
      onClick: handleDecrease,
      disabled: isMinReached || disabled,
      "data-test-id": 'decrement',
      "aria-label": t('NumberInput.remove', {
        count: step
      })
    }, {
      children: jsx(Minus, {}, void 0)
    }), void 0), jsx(Input$1, {
      "aria-label": t('NumberInput.label'),
      value: showNothing ? '' : inputValue,
      onChange: handleInputChange,
      onFocus: selectTextOnFocus ? e => e.target.select() : undefined,
      onBlur: handleBlur,
      ref: inputRef,
      type: "number",
      readOnly: disabled,
      "data-test-id": testId,
      name: name,
      step: inputStep,
      role: "number-input",
      min: min,
      max: max
    }, void 0), jsx(Button, Object.assign({
      onClick: handleIncrease,
      disabled: isMaxReached || disabled,
      "data-test-id": 'increment',
      "aria-label": t('add', {
        count: step
      })
    }, {
      children: jsx(Plus, {}, void 0)
    }), void 0)]
  }), void 0);
};

const noop$1 = () => {};

const EyeIcon = /*#__PURE__*/styled(SvgEye).withConfig({
  displayName: "EyeIcon",
  componentId: "jwjocv-0"
})(["position:absolute;right:8px;top:8px;transition:0.2s opacity ease;cursor:pointer;&:hover{opacity:0.8;}"]);

const BaseInputWORef = (_ref, ref) => {
  let {
    onChange,
    invalid
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["onChange", "invalid"]);

  // Prevent cursor jumping to the end of line
  const [value, setValue] = useState(rest.value);
  const handleChange = useCallback(e => {
    setValue(e.target.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(e, e.target.value);
  }, [onChange]);
  useEffect(() => {
    if (rest.value !== value) setValue(rest.value); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.value]);
  return jsx(InputStyled$2, Object.assign({
    ref: ref,
    onChange: e => handleChange(e),
    "data-field-invalid": invalid,
    invalid: invalid
  }, rest, {
    value: value
  }), void 0);
};

const BaseInput = /*#__PURE__*/React__default.forwardRef(BaseInputWORef);
const PasswordInput = /*#__PURE__*/React__default.forwardRef((_ref2, ref) => {
  let {
    onChange,
    onFocus = noop$1,
    onBlur = noop$1,
    passwordVisible = false,
    onPasswordVisibleChange,
    value,
    type: _type
  } = _ref2,
      rest = _objectWithoutProperties(_ref2, ["onChange", "onFocus", "onBlur", "passwordVisible", "onPasswordVisibleChange", "value", "type"]);

  const [pwVisible, setVisible] = useBoolean(passwordVisible);
  const [focused, focus, blur] = useBoolean(false);
  const inputType = pwVisible ? 'text' : 'password';
  const showEyeIcon = focused || inputType === 'text';
  useEffect(() => {
    setVisible(passwordVisible);
  }, [passwordVisible, setVisible]);

  const handleVisibilityChange = val => {
    if (onPasswordVisibleChange) {
      onPasswordVisibleChange(val);
    } else {
      setVisible(val);
    }
  };

  return jsxs(Fragment, {
    children: [jsx(InputStyled$2, Object.assign({
      ref: ref,
      onFocus: e => {
        focus();
        onFocus(e);
      },
      onBlur: e => {
        blur();
        onBlur(e);
      },
      onChange: e => onChange === null || onChange === void 0 ? void 0 : onChange(e, e.target.value),
      type: inputType,
      value: value
    }, rest), void 0), showEyeIcon && jsx(EyeIcon, {
      onMouseDown: e => {
        e.preventDefault();
        handleVisibilityChange(!pwVisible);
      }
    }, void 0)]
  }, void 0);
});
const NumberInput = /*#__PURE__*/React__default.forwardRef((_ref3, ref) => {
  let {
    onChange,
    step: stepProp,
    updateOnInputChange: updateOnInputChangeProp,
    value: valueProp,
    onBlur,
    min: minProp,
    max: maxProp
  } = _ref3,
      props = _objectWithoutProperties(_ref3, ["onChange", "step", "updateOnInputChange", "value", "onBlur", "min", "max"]);

  const max = typeof maxProp === 'number' ? maxProp : Infinity;
  const step = typeof stepProp === 'number' ? stepProp : 1;
  const min = typeof minProp === 'number' ? minProp : step;
  const value = typeof valueProp === 'string' ? Number(valueProp) : valueProp;
  const updateOnInputChange = updateOnInputChangeProp ? updateOnInputChangeProp : step === 1;
  const stepDecimals = useMemo(() => getStepDecimals(step), [step]);

  const valueToFixed = value => stepDecimals ? value.toFixed(stepDecimals) : value.toString();

  const [inputValue, setInputValue] = useState(typeof value === 'number' ? valueToFixed(numberToStep(value, step)) : '');

  const changeValue = (e, newValue) => {
    const parsedValue = numberToStep(Math.min(Math.max(Number(newValue), min), max), step);
    setInputValue(valueToFixed(parsedValue));

    if (valueProp !== parsedValue) {
      onChange === null || onChange === void 0 ? void 0 : onChange(e, parsedValue);
    }
  };

  const handleInputChange = e => {
    if (updateOnInputChange && onChange) {
      changeValue(e, e.target.value);
    } else {
      setInputValue(e.target.value ? e.target.value : '');
    }
  };

  const handleBlur = e => {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    if (e.target.value) changeValue(e, e.target.value);else {
      setInputValue('');
      onChange === null || onChange === void 0 ? void 0 : onChange(e, null);
    }
  };

  useEffect(() => {
    if (Number(valueProp) !== Number(inputValue)) {
      setInputValue(String(valueProp));
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [valueProp]);
  return jsx(InputStyled$2, Object.assign({
    ref: ref,
    onChange: handleInputChange,
    onBlur: handleBlur,
    type: "number",
    value: inputValue,
    max: max,
    min: min,
    step: step
  }, props), void 0);
});

function isEmptyValue(value) {
  const t = typeof value;
  return t !== 'string' && t !== 'number' || Number.isNaN(value);
}

const InputWORef = (_ref4, ref) => {
  let {
    className,
    id,
    type,
    label,
    value = '',
    invalid,
    limit,
    disabled,
    name,
    errorText,
    required,
    optional,
    //@ts-expect-error ts can't find this bcs of the type generic
    enableStrictStep
  } = _ref4,
      rest = _objectWithoutProperties(_ref4, ["className", "id", "type", "label", "value", "invalid", "limit", "disabled", "name", "errorText", "required", "optional", "enableStrictStep"]);

  let InputComponent;

  switch (type) {
    case 'password':
      InputComponent = PasswordInput;
      break;

    case 'number':
      InputComponent = enableStrictStep ? NumberInput : BaseInput;
      break;

    default:
      InputComponent = BaseInput;
  }

  return jsx(FormField, {
    id: id || name,
    name: name,
    className: className,
    value: value || '',
    limit: limit,
    label: label,
    invalid: invalid,
    errorText: errorText,
    required: required,
    optional: optional,
    render: ({
      id,
      name
    }) => //@ts-expect-error TS can't handle this
    jsx(InputComponent, Object.assign({
      id: id,
      ref: ref,
      name: name,
      maxLength: limit,
      invalid: invalid,
      value: type !== 'number' && !enableStrictStep ? isEmptyValue(value) ? '' : value || '' : value,
      role: 'input',
      disabled: disabled,
      type: type
    }, rest), void 0)
  }, void 0);
};

const Input = /*#__PURE__*/React__default.forwardRef(InputWORef);

const InputsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputsContainer",
  componentId: "qq64w-0"
})(["display:flex;flex-direction:row;border-width:1px;border-style:solid;border-radius:4px;align-items:stretch;width:100%;background:", ";", " ", ";"], ({
  theme
}) => theme.color.onPrimaryLight, dynamicInputCss, props => props.focused && focusInputCss(props));

const inputCss = ({
  inputSize
}) => {
  let height;

  switch (inputSize) {
    case 'thin':
      height = '26px';
      break;

    case 'small':
      height = '30px';
      break;

    case 'medium':
      height = '38px';
      break;

    case 'big':
      height = '46px';
      break;

    default:
      height = '38px';
  }

  return {
    height
  };
};

const InputStyled$1 = /*#__PURE__*/styled(InputStyled$2).withConfig({
  displayName: "InputStyled",
  componentId: "qq64w-1"
})(["border:none;", ";"], inputCss);
const PrefixContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PrefixContainer",
  componentId: "qq64w-2"
})(["display:flex;align-items:center;font-size:14px;padding:", ";order:", ";white-space:nowrap;font-weight:500;line-height:20px;color:", ";"], props => props.placement === 'left' ? '0 0 0 8px' : '0 8px 0 0', props => props.placement === 'left' ? 0 : 1, props => props.theme.color.onSurfaceMidEmphasys);
const PrefixInput = (_ref) => {
  let {
    value = '',
    prefix,
    prefixPlacement = 'left',
    onChange,
    invalid,
    label,
    id,
    name,
    disabled = false,
    onFocus,
    onBlur,
    errorText,
    placeholder,
    className,
    type,
    inputSize = 'medium'
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["value", "prefix", "prefixPlacement", "onChange", "invalid", "label", "id", "name", "disabled", "onFocus", "onBlur", "errorText", "placeholder", "className", "type", "inputSize"]);

  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const handleFocus = useCallback(e => {
    setFocused(true);
    onFocus && onFocus(e);
  }, [onFocus]);
  const handleBlur = useCallback(e => {
    setFocused(false);
    onBlur && onBlur(e);
  }, [onBlur]);
  const handlePrefixClick = useCallback(() => {
    var _inputRef$current;

    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  }, []); // Prevent cursor jumping to the end of line

  const [val, setVal] = useState(value);
  const handleChange = useCallback(event => {
    setVal(event.target.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(event, event.target.value);
  }, [onChange]);
  useEffect(() => {
    if (value !== val) setVal(value); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return jsx(FormField, {
    id: id,
    name: name,
    invalid: invalid,
    errorText: errorText,
    className: className,
    label: label,
    render: ({
      id,
      name
    }) => jsxs(InputsContainer, Object.assign({
      focused: focused,
      invalid: invalid,
      inputSize: inputSize
    }, {
      children: [jsx(PrefixContainer, Object.assign({
        placement: prefixPlacement,
        onClick: handlePrefixClick
      }, {
        children: prefix
      }), void 0), jsx(InputStyled$1, Object.assign({
        ref: inputRef,
        type: type,
        id: id,
        name: name,
        value: val || '',
        placeholder: placeholder,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        disabled: disabled,
        inputSize: inputSize
      }, rest), void 0)]
    }), void 0)
  }, void 0);
};

const currencySymbol = currency => {
  return 0 .toLocaleString('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).replace(/\d/g, '').trim();
};

const CurrencyInput = (_ref) => {
  let {
    currency = Currency.CAD,
    onChange
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["currency", "onChange"]);

  const handleChange = useCallback((_, value) => {
    onChange(value);
  }, [onChange]);
  return jsx(PrefixInput, Object.assign({}, rest, {
    onChange: handleChange,
    prefix: currencySymbol(currency)
  }), void 0);
};

let _$9 = t => t,
    _t$9;
const Wrapper$3 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-2hphlc-0"
})(["height:20px;display:flex;flex-wrap:nowrap;align-items:center;"]);
const InputStyled = /*#__PURE__*/styled.input.withConfig({
  displayName: "InputStyled",
  componentId: "sc-2hphlc-1"
})(["border:none;outline:none;background:none;font-weight:500;font-size:14px;line-height:20px;order:2;color:", ";flex-grow:1;&::placeholder{font-weight:500;font-size:14px;line-height:20px;color:", ";}", ""], ({
  theme
}) => theme.color.onPrimaryDark, ({
  theme
}) => theme.color.secondary200, props => props.theme.media.mobile(_t$9 || (_t$9 = _$9`
      font-size: 16px;
      &::placeholder {
        font-size: 16px;
      }
  `)));
const IconStyled = /*#__PURE__*/styled(SvgSearch).withConfig({
  displayName: "IconStyled",
  componentId: "sc-2hphlc-2"
})(["margin-right:11px;order:1;transition:0.3s fill;", ":focus + &{fill:", ";}"], InputStyled, ({
  theme
}) => theme.color.primary600);
const SearchInput = ({
  placeholder: _placeholder = '',
  onChange,
  value,
  className,
  inputRef,
  autoFocus,
  debounceValue: _debounceValue = 500,
  'data-test-id': dataTestId
}) => {
  const [search, setSearch] = useState(value);
  const initial = useRef(!value);
  const debouncedSearch = useDebounce(search, _debounceValue);
  const onSearchChange = useCallback(event => {
    setSearch(event.target.value);
  }, []);
  useEffect(() => {
    if (!initial.current) {
      onChange(debouncedSearch || undefined);
    } else {
      initial.current = false;
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [debouncedSearch]);
  return jsxs(Wrapper$3, Object.assign({
    className: className
  }, {
    children: [jsx(InputStyled, {
      placeholder: _placeholder,
      value: search || '',
      onChange: onSearchChange,
      ref: inputRef,
      autoFocus: autoFocus,
      "data-test-id": dataTestId
    }, void 0), jsx(IconStyled, {}, void 0)]
  }), void 0);
};

let _$8 = t => t,
    _t$8;
const TextAreaStyled$1 = /*#__PURE__*/styled.textarea.withConfig({
  displayName: "TextAreaStyled",
  componentId: "sc-1jn5vo5-0"
})(["min-height:104px;width:100%;font-family:Montserrat;font-style:normal;font-weight:500;font-size:14px;line-height:20px;padding:8px 10px;box-sizing:border-box;outline:none;resize:none;margin:0;border:none;&:not(:read-only){&:focus{", "}}", " ", " &:read-only{background-color:", ";cursor:not-allowed;}"], focusInputCss, props => props.theme.media.mobile(_t$8 || (_t$8 = _$8`
    font-size: 16px;
  `)), dynamicInputCss, props => props.theme.color.backgroundBase);
const TextArea = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  let {
    className,
    id,
    name,
    limit,
    value,
    onChange,
    errorText,
    invalid,
    disabled,
    label,
    required,
    readonly,
    optional
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["className", "id", "name", "limit", "value", "onChange", "errorText", "invalid", "disabled", "label", "required", "readonly", "optional"]);

  return jsx(FormField, {
    componentType: 'textArea',
    id: id,
    name: name,
    limit: limit,
    errorText: errorText,
    invalid: invalid,
    label: label,
    value: value,
    required: required,
    optional: optional,
    render: ({
      id,
      name
    }) => jsx(TextAreaStyled$1, Object.assign({
      className: className,
      disabled: disabled,
      invalid: invalid,
      ref: ref,
      maxLength: limit,
      id: id,
      name: name,
      value: value || '',
      onChange: onChange,
      readOnly: readonly,
      "data-field-invalid": invalid
    }, rest), void 0)
  }, void 0);
});

let _$7 = t => t,
    _t$7;
const Wrapper$2 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-1csl63z-0"
})(["display:flex;flex-direction:column;"]);
const TagsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TagsContainer",
  componentId: "sc-1csl63z-1"
})(["position:relative;display:flex;"]);
const TagWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TagWrapper",
  componentId: "sc-1csl63z-2"
})(["background-color:#fff;position:relative;padding:6px 8px 7px;top:1px;border:1px solid transparent;border-bottom:none;&:only-child{", "}&:first-child{", "}", " ", ""], props => !props.selectable && css(["padding:6px 0 7px;"]), props => !props.selectable && css(["padding:6px 8px 7px 0;"]), props => {
  const borderColor = props.focused ? props.theme.color.primary600 : props.theme.color.onSurfaceLowEmphasys;
  return props.selected && props.selectable && css(["z-index:1;border:1px solid ", ";border-bottom:none;border-radius:8px 8px 0 0;"], borderColor);
}, props => {
  return props.selectable && css(["cursor:pointer;"]);
});
const TextAreaStyled = /*#__PURE__*/styled(TextArea).withConfig({
  displayName: "TextAreaStyled",
  componentId: "sc-1csl63z-3"
})(["width:100%;border-radius:0 0 8px 8px;min-width:450px;", ""], props => props.theme.media.mobile(_t$7 || (_t$7 = _$7`
    min-width: 100%;
  `)));
const TagStyled = /*#__PURE__*/styled(Tag).withConfig({
  displayName: "TagStyled",
  componentId: "sc-1csl63z-4"
})(["background-color:", ";> svg{fill:", ";}"], props => props.selected ? props.theme.color.primary50 : props.theme.color.backgroundSecondary, props => props.selected ? props.theme.color.primary100 : props.theme.color.onSurfaceLowEmphasys);
const TextAreaWithTags = ({
  items,
  selected,
  onTextChange,
  onSelectionChange,
  onRemove
}) => {
  const handleChange = useCallback(e => {
    selected && onTextChange(selected, e.target.value);
  }, [onTextChange, selected]);
  const textAreaRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const handleSelectionChange = useCallback(id => {
    onSelectionChange(id);
  }, [onSelectionChange]);
  const selectedItem = useMemo(() => items.find(item => item.id === selected), [items, selected]);
  const hasEditable = useMemo(() => items.some(item => item.editable !== false), [items]);
  const hasReadonly = useMemo(() => items.some(item => item.readonly !== false), [items]);
  const isFocusable = !(selectedItem !== null && selectedItem !== void 0 && selectedItem.readonly);
  return jsxs(Wrapper$2, {
    children: [jsx(TagsContainer, Object.assign({
      "data-test-id": 'tags-container'
    }, {
      children: [...items].sort(item => item.editable !== false ? -1 : 1).map(item => {
        const isSelected = item.id === selected && typeof selected !== 'undefined';
        return jsx(TagWrapper, Object.assign({
          selected: isSelected,
          selectable: item.selectable !== false,
          focused: focused,
          onClick: () => item.selectable !== false && handleSelectionChange(item.id)
        }, {
          children: jsx(TagStyled, {
            selected: isSelected,
            title: item.title,
            index: item.id,
            onRemove: onRemove
          }, void 0)
        }), item.id);
      })
    }), void 0), hasEditable || hasReadonly ? jsx(TextAreaStyled, {
      value: (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value) || '',
      ref: textAreaRef,
      name: 'Promocode text',
      onChange: handleChange,
      placeholder: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.placeholder,
      onFocus: () => isFocusable && setFocused(true),
      onBlur: () => isFocusable && setFocused(false),
      readonly: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.readonly
    }, void 0) : null]
  }, void 0);
};

const noop = () => {};

const OptionStyled$2 = /*#__PURE__*/styled.div.withConfig({
  displayName: "OptionStyled",
  componentId: "sc-152la03-0"
})(["display:grid;grid-template-columns:max-content auto;margin:0;cursor:pointer;color:", ";transition:0.3s background-color;font-weight:500;line-height:20px;min-height:40px;font-size:14px;border-radius:4px;padding:10px 8px;box-sizing:border-box;align-items:start;&[data-disabled='true']{pointer-events:none;span{opacity:0.7;}&:hover{background-color:transparent;}}&:hover{background-color:", ";button[role='checkbox']{border-color:", ";}}&[data-nested-level='0']{font-weight:700;}&[data-nested-level='1']{font-weight:600;margin-left:12px;}&[data-nested-level='2']{margin-left:24px;}"], props => props.theme.color.onPrimaryDark, props => props.theme.color.primary50, props => props.theme.color.primary500);
const PlaceholderStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "PlaceholderStyled",
  componentId: "sc-152la03-1"
})(["font-family:'Montserrat',-apple-system,BlinkMacSystemFont,sans-serif;color:", ";white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:calc(100% - 40px);position:absolute;left:10px;"], props => props.all ? '#808080' : props.theme.color.onPrimaryDark);
const ClearButtonStyled = /*#__PURE__*/styled(ClearIndicatorStyled).withConfig({
  displayName: "ClearButtonStyled",
  componentId: "sc-152la03-2"
})(["position:absolute;right:30px;bottom:11px;"]);
const Wrapper$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-152la03-3"
})(["position:relative;"]);
const CheckboxSelect = (_ref) => {
  let {
    selected,
    onChange,
    options,
    isSearchable,
    onClear,
    singleSelected,
    noneSelected,
    onSearchChange,
    onMenuClose
  } = _ref,
      props = _objectWithoutProperties(_ref, ["selected", "onChange", "options", "isSearchable", "onClear", "singleSelected", "noneSelected", "onSearchChange", "onMenuClose"]);

  // custom search since react-select has problems with it https://github.com/JedWatson/react-select/issues/4133
  const [search, setSearch] = useState('');
  const {
    t
  } = useTranslation('ui');

  const handleClear = e => {
    e.stopPropagation();
    e.preventDefault();
    e.bubbles = false;
    onClear && onClear();
  };

  const Option = ({
    value,
    data,
    label
  }) => {
    return jsxs(OptionStyled$2, Object.assign({
      onClick: () => onChange(value),
      id: value,
      "data-nested-level": data.nestedLevel || '',
      "data-disabled": data.isParentSelected
    }, {
      children: [jsx(Checkbox, {
        onChange: noop,
        isChecked: selected.includes(value) || data.isParentSelected,
        disabled: data.isParentSelected
      }, void 0), jsx("span", {
        children: data.labelToRender || label
      }, void 0)]
    }), void 0);
  };

  const Placeholder = ({
    selectProps
  }) => {
    return jsx(PlaceholderStyled, Object.assign({
      all: selected.length === 0
    }, {
      children: selected.length > 0 ? singleSelected || t('CheckboxSelect.multipleSelected', {
        items: selectProps.placeholder
      }) : noneSelected ? noneSelected : t('CheckboxSelect.allSelected', {
        items: selectProps.placeholder
      })
    }), void 0);
  };

  const filteredOptions = useMemo(() => {
    if (!search) {
      return options;
    }

    return options.filter(option => search && typeof option.title === 'string' ? option.title.toLowerCase().includes(search.toLowerCase()) : true);
  }, [options, search]);

  const handleSearchChange = value => {
    setSearch(value);
    onSearchChange && onSearchChange(value);
  };

  const handleMenuClose = () => {
    setSearch('');
    onMenuClose && onMenuClose();
  };

  return jsxs(Wrapper$1, {
    children: [jsx(SimpleSelect, Object.assign({}, props, {
      onChange: noop,
      options: filteredOptions,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      components: {
        Option,
        Placeholder
      },
      onSearchChange: handleSearchChange,
      onMenuClose: handleMenuClose,
      filterOption: () => true,
      isSearchable: isSearchable,
      noneSelected: noneSelected
    }), void 0), !!selected.length && !!onClear && jsx(ClearButtonStyled, {
      onClick: handleClear
    }, void 0)]
  }, void 0);
};

let _$6 = t => t,
    _t$6;
const Wrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-190tckh-0"
})(["position:relative;width:100%;min-width:200px;"]);
const List = /*#__PURE__*/styled.div.withConfig({
  displayName: "List",
  componentId: "sc-190tckh-1"
})(["background:", ";max-height:60vh;overflow-y:auto;position:absolute;z-index:10;width:calc(100% + 50px);padding:4px;top:100%;border-radius:4px;box-shadow:0 0 0 1px hsl(0deg 0% 0% / 10%),0 4px 11px hsl(0deg 0% 0% / 10%);margin-top:8px;box-sizing:border-box;"], props => props.theme.color.onPrimaryLight);
const Option = /*#__PURE__*/styled.div.withConfig({
  displayName: "Option",
  componentId: "sc-190tckh-2"
})(["position:relative;display:flex;align-items:center;cursor:pointer;min-height:40px;font-size:14px;line-height:16px;color:", ";font-weight:", ";border-radius:4px;button{cursor:pointer;}&:hover{background:#deebff;}&[data-children-filtered='true']{cursor:not-allowed;}&[data-children-filtered='true'] button{pointer-events:none;}padding:8px 12px 8px ", ";"], props => props.theme.color.onPrimaryDark, props => props.isParent ? 700 : 500, props => {
  if (props.isChild) return '38px';
  if (props.isParent) return '0';
  return '20px';
});
const OptionInner = /*#__PURE__*/styled.div.withConfig({
  displayName: "OptionInner",
  componentId: "sc-190tckh-3"
})(["display:inline-flex;align-items:center;flex-grow:1;[data-children-filtered='true'] &{cursor:not-allowed;> *{pointer-events:none;}}"]);
const Only = /*#__PURE__*/styled.button.withConfig({
  displayName: "Only",
  componentId: "sc-190tckh-4"
})(["margin-left:auto;opacity:0;padding:2px 6px;font-size:12px;transition:0.3s all;height:20px;line-height:12px;position:absolute;top:50%;right:8px;background:", ";color:", ";border:1px solid ", ";border-radius:4px;transform:translateY(-150%);min-width:95px;", ":hover &{opacity:1;transform:translateY(-50%);box-shadow:0 0 0 5px #deebff,0 0 12px 5px #deebff;}[data-children-filtered='true']:hover .only{opacity:0;}&:hover{background:", ";}"], props => props.theme.color.onPrimaryLight, props => props.theme.color.primary600, props => props.theme.color.primary600, Option, props => props.theme.color.primary50);
const NestedList = /*#__PURE__*/styled.div.withConfig({
  displayName: "NestedList",
  componentId: "sc-190tckh-5"
})(["grid-template-columns:1fr;display:", ";border-left:none;border-right:none;background:", ";border-radius:4px;"], props => props.isOpen ? 'grid' : 'none', props => props.theme.color.primary25);
const Control = /*#__PURE__*/styled.div.withConfig({
  displayName: "Control",
  componentId: "sc-190tckh-6"
})(["min-height:32px;font-size:14px;line-height:20px;border:1px solid ", ";border-radius:4px;display:flex;align-items:center;transition:0.3s box-shadow,0.3s border-color;cursor:pointer;&:hover{border-color:", ";}&[data-is-disabled='true']{cursor:not-allowed;opacity:0.7;}&[data-is-open='true']{border-color:", ";}"], props => props.theme.color.onSurfaceLowEmphasys, props => props.theme.color.onPrimaryMid, props => props.theme.color.primary600);
const Value = /*#__PURE__*/styled.span.withConfig({
  displayName: "Value",
  componentId: "sc-190tckh-7"
})(["white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:500;padding:2px;[data-is-typing='true'] &{width:0;opacity:0;overflow:hidden;padding:0;}[data-is-empty='true'] &{color:#808080;}"]);
const SearchField = /*#__PURE__*/styled.input.withConfig({
  displayName: "SearchField",
  componentId: "sc-190tckh-8"
})(["background:no-repeat;border:none;font-size:14px;line-height:20px;width:20px;cursor:text;&:focus{outline:none;}[data-is-typing='true'] &{width:100%;}", ""], props => props.theme.media.mobile(_t$6 || (_t$6 = _$6`
    font-size: 16px;
  `)));
const ValueContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ValueContainer",
  componentId: "sc-190tckh-9"
})(["padding:2px 0 2px 8px;display:flex;overflow:hidden;flex-grow:1;cursor:default;"]);
const ClearButton = /*#__PURE__*/styled(SvgCross).withConfig({
  displayName: "ClearButton",
  componentId: "sc-190tckh-10"
})(["flex-shrink:0;margin:0 12px 0 8px;cursor:pointer;fill:", ";width:10px;height:10px;[data-is-empty='true'] &{opacity:0;}"], props => props.theme.color.onPrimaryDark);
const DropdownIndicator = /*#__PURE__*/styled.span.withConfig({
  displayName: "DropdownIndicator",
  componentId: "sc-190tckh-11"
})(["flex-shrink:0;width:5px;height:5px;margin-right:15px;cursor:pointer;display:flex;align-items:center;justify-content:center;&::before{content:'';width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid ", ";}&[data-is-open='true']::before{border-bottom:5px solid ", ";border-top:none;}"], props => props.theme.color.onPrimaryDark, props => props.theme.color.onPrimaryDark);
const NestedIndicator = /*#__PURE__*/styled.span.withConfig({
  displayName: "NestedIndicator",
  componentId: "sc-190tckh-12"
})(["flex-shrink:0;width:20px;height:20px;cursor:pointer;display:flex;align-items:center;justify-content:center;&::before{content:'';width:0;height:0;border-top:5px solid transparent;border-bottom:5px solid transparent;border-left:7px solid ", ";}&[data-is-open='true']::before{border-left:5px solid transparent;border-right:5px solid transparent;border-top:7px solid ", ";margin-top:5px;}"], props => props.theme.color.onPrimaryDark, props => props.theme.color.onPrimaryDark);
const EmptyMsg = /*#__PURE__*/styled.div.withConfig({
  displayName: "EmptyMsg",
  componentId: "sc-190tckh-13"
})(["text-align:center;color:", ";padding:12px;"], props => props.theme.color.onSurfaceLowEmphasys);
const Label = /*#__PURE__*/styled.label.withConfig({
  displayName: "Label",
  componentId: "sc-190tckh-14"
})(["cursor:pointer;flex-grow:1;[data-children-filtered='true'] &{pointer-events:none;}"]);
const LabelWrapperStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "LabelWrapperStyled",
  componentId: "sc-190tckh-15"
})(["display:flex;flex-direction:column;width:100%;"]);

function ownKeys$7(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$7(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$7(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function useOptionsMapping(options, filter, disableFilterOnSearchChange) {
  return useMemo(() => {
    const map = {};
    const nested = {};
    const list = [];
    const filtered = !disableFilterOnSearchChange && filter ? options.filter(filter) : options;

    for (const item of filtered) {
      map[item.id] = _objectSpread$7(_objectSpread$7({}, item), {}, {
        children: [],
        total: 0,
        isChecked: false,
        checkedChildren: 0,
        childrenMapped: [],
        isChildrenEqualToTotal: false
      });
    }

    for (const item of filtered) {
      if (item.hasChildren) {
        nested[item.id] = 0;
      }

      if (item.parent && map[item.parent]) {
        const parentId = item.parent;
        map[parentId].children.push(item.id);
        map[parentId].total++;
      } else {
        list.push(item.id);
      }
    }

    return [map, nested, list];
  }, [disableFilterOnSearchChange, filter, options]);
}

function useFilteredList(list, search, map, checked, childrenCheckedMap, disableFilterOnSearchChange) {
  const isItemFitsSearch = useCallback(id => map[id].title.toLowerCase().includes(search.toLowerCase()), [search, map]);
  return useMemo(() => {
    return list.map(id => {
      const isChecked = checked.includes(id);

      if (!search && !map[id].hasChildren) {
        return _objectSpread$7(_objectSpread$7({}, map[id]), {}, {
          isChecked
        });
      } // if item is not a parent or a child


      if (!map[id].parent && !map[id].total) {
        return isItemFitsSearch(id) && !disableFilterOnSearchChange ? _objectSpread$7(_objectSpread$7({}, map[id]), {}, {
          isChecked
        }) : null;
      } // if item is a parent


      if (!search && !map[id].hasChildren || isItemFitsSearch(id) && !disableFilterOnSearchChange) {
        return _objectSpread$7(_objectSpread$7({}, map[id]), {}, {
          isChecked,
          childrenMapped: map[id].children.map(childId => _objectSpread$7(_objectSpread$7({}, map[childId]), {}, {
            isChecked: checked.includes(childId)
          })),
          checkedChildren: childrenCheckedMap[id]
        });
      } // if a parent doesn't fit check it's children


      const filteredChildren = !disableFilterOnSearchChange ? map[id].children.filter(isItemFitsSearch) : map[id].children;

      if (filteredChildren.length) {
        return _objectSpread$7(_objectSpread$7({}, map[id]), {}, {
          isChecked,
          childrenMapped: filteredChildren.map(childId => _objectSpread$7(_objectSpread$7({}, map[childId]), {}, {
            isChecked: checked.includes(childId)
          })),
          isChildrenEqualToTotal: filteredChildren.length === map[id].total,
          childrenChecked: childrenCheckedMap[id]
        });
      }

      return null;
    }).filter(Boolean);
  }, [checked, childrenCheckedMap, disableFilterOnSearchChange, isItemFitsSearch, list, map, search]);
}

const SimpleOption = ({
  item,
  onChange,
  customOptionTitle,
  innerRef
}) => {
  return jsxs(Option, Object.assign({
    isChild: !!item.parent,
    onClick: () => onChange(item.id),
    ref: innerRef
  }, {
    children: [jsx(Checkbox, {
      isChecked: item.isChecked,
      onChange: () => {}
    }, void 0), jsx(Label, {
      children: customOptionTitle ? customOptionTitle(item) : item.title
    }, void 0)]
  }), void 0);
};

const ParentOption = ({
  item,
  onChange,
  customOptionTitle
}) => {
  const {
    t
  } = useTranslation('ui');
  const [isExpanded, setIsExpanded] = useState(false);
  const onlyChildrenChecked = item.checkedChildren === item.total && !item.isChecked;
  const someChildrenChecked = item.checkedChildren > 0 && item.checkedChildren !== item.total;
  const onlyParentChecked = !item.checkedChildren && item.isChecked;
  const isSemiChecked = onlyChildrenChecked || someChildrenChecked || onlyParentChecked;
  const childrenFiltered = !!item.total && item.childrenMapped.length < item.total || item.isChildrenEqualToTotal;

  const onToggle = e => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleOptionSelect = useCallback(() => onChange(item.id), [item.id, onChange]);
  const handleOnlyBtnClick = useCallback(() => onChange(item.id, true), [item.id, onChange]);
  return jsxs(Fragment, {
    children: [jsxs(Option, Object.assign({
      title: childrenFiltered ? t('CheckboxTreeSelect.nestedOptionsFilteredWarning') : '',
      "data-id": item.id,
      isParent: true
    }, {
      children: [jsx(NestedIndicator, {
        onClick: onToggle,
        "data-is-open": isExpanded || childrenFiltered
      }, void 0), jsxs(OptionInner, {
        children: [jsx(Checkbox, {
          isChecked: item.isChecked || isSemiChecked,
          isPartlyChecked: isSemiChecked,
          partialCheckType: item.isChecked ? 'square' : 'circle',
          onChange: handleOptionSelect
        }, void 0), jsx("label", Object.assign({
          onClick: handleOptionSelect
        }, {
          children: customOptionTitle ? customOptionTitle(item) : item.title
        }), void 0), !childrenFiltered && jsx(Only, Object.assign({
          onClick: handleOnlyBtnClick
        }, {
          children: item.isChecked ? t('CheckboxTreeSelect.deselectOnly') : t('CheckboxTreeSelect.selectOnly')
        }), void 0)]
      }, void 0)]
    }), void 0), jsx(NestedList, Object.assign({
      isOpen: isExpanded || childrenFiltered
    }, {
      children: item.childrenMapped.map(child => jsx(SimpleOption, {
        item: child,
        onChange: onChange,
        customOptionTitle: customOptionTitle
      }, child.id))
    }), void 0)]
  }, void 0);
};

const CheckboxTreeSelect = ({
  options: _options = [],
  onChange,
  customOptionTitle,
  selected: _selected = [],
  placeholder,
  selectedMultipleItemsNames,
  filter,
  isDisabled,
  label,
  loadingText,
  noOptionText,
  onMenuScrollToBottom,
  onSearchChange,
  progress,
  disableFilterOnSearchChange
}) => {
  const {
    t
  } = useTranslation('ui');
  const [map, nested, list] = useOptionsMapping(_options, filter, disableFilterOnSearchChange);
  const [checked, setChecked] = useState(_selected);
  const [checkedChildrenQty, setCheckedChildrenQty] = useState(nested);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const {
    ref: lastItemRef
  } = useInView(onMenuScrollToBottom, 0.25);
  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange('');
  }, [onSearchChange]);

  const updateCheckedChildrenQty = (parentId, qty) => {
    setCheckedChildrenQty(_objectSpread$7(_objectSpread$7({}, checkedChildrenQty), {}, {
      [parentId]: Math.max(0, checkedChildrenQty[parentId] + qty)
    }));
  };

  const handleCheckedChange = (id, only) => {
    const set = new Set(checked);

    const toggle = () => set.has(id) ? set.delete(id) : set.add(id);

    const manageBranch = (id, mode) => {
      mode === 'add' ? set.add(id) : set.delete(id);
      map[id].children.forEach(childId => mode === 'add' ? set.add(childId) : set.delete(childId));
      setCheckedChildrenQty(_objectSpread$7(_objectSpread$7({}, checkedChildrenQty), {}, {
        [id]: mode === 'add' ? map[id].total : 0
      }));
    }; // simple item (no parent or children) or only parent item


    if (!map[id].parent && !map[id].total || map[id].total && only) {
      toggle();
    } // parent item and all it's children (whole branch)


    if (map[id].total && !only) {
      manageBranch(id, checkedChildrenQty[id] !== map[id].total ? 'add' : 'remove');
    } // child item


    const parent = map[id].parent;

    if (parent) {
      updateCheckedChildrenQty(parent, set.has(id) ? -1 : 1);
      toggle();
    }

    setChecked([...set]);
    onChange([...set]);
    inputRef.current && inputRef.current.focus();
  };

  const handleSearchChange = e => {
    setSearch(e.target.value);
    onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(e.target.value);

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleReset = e => {
    e.preventDefault();
    e.stopPropagation();
    setChecked([]);
    setCheckedChildrenQty(nested);
    setSearch('');
    onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange('');
    onChange([]);
  };

  const handleControlClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
      inputRef.current && !isOpen && inputRef.current.focus();
    }
  };

  useOnClickOutside(wrapperRef, handleClickOutside);
  const filteredList = useFilteredList(list, search, map, checked, checkedChildrenQty, disableFilterOnSearchChange);
  let value = placeholder;

  if (checked.length > 1) {
    value = t('CheckboxTreeSelect.multipleText', {
      selectedMultipleItemsNames
    });
  } else if (checked.length === 1 && map[checked[0]]) {
    value = map[checked[0]].title;
  }

  const isLoading = progress === Progress.WORK || progress === Progress.IDLE;
  return jsxs(Wrapper, Object.assign({
    ref: wrapperRef
  }, {
    children: [!!label && jsx(LabelWrapperStyled, {
      children: jsx(Label$3, {
        children: label
      }, void 0)
    }, void 0), jsxs(Control, Object.assign({
      onClick: handleControlClick,
      "data-is-open": isOpen,
      "data-is-empty": !checked.length,
      "data-is-disabled": isDisabled
    }, {
      children: [jsxs(ValueContainer, Object.assign({
        "data-is-typing": !!search
      }, {
        children: [jsx(Value, {
          children: value
        }, void 0), jsx(SearchField, {
          type: "text",
          value: search,
          onChange: handleSearchChange,
          ref: inputRef,
          onFocus: () => setIsOpen(true)
        }, void 0)]
      }), void 0), jsx(ClearButton, {
        onClick: handleReset
      }, void 0), jsx(DropdownIndicator, {
        "data-is-open": isOpen
      }, void 0)]
    }), void 0), isOpen && jsx(List, {
      children: filteredList.length ? filteredList.map((item, index) => {
        if (!item) {
          return null;
        }

        if (item.total && item.childrenMapped.length) {
          return jsx(ParentOption, {
            item: item,
            onChange: handleCheckedChange,
            customOptionTitle: customOptionTitle
          }, item.id);
        }

        return jsx(SimpleOption, {
          item: item,
          onChange: handleCheckedChange,
          customOptionTitle: customOptionTitle,
          innerRef: index === filteredList.length - 1 ? lastItemRef : undefined
        }, item.id);
      }) : jsxs(EmptyMsg, {
        children: [!!(_options !== null && _options !== void 0 && _options.length) && !isLoading && (noOptionText || t('CheckboxTreeSelect.noOptionsText')), isLoading && (loadingText || t('CheckboxTreeSelect.loadingText'))]
      }, void 0)
    }, void 0)]
  }), void 0);
};

const OptionStyled$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "OptionStyled",
  componentId: "cole9u-0"
})(["display:flex;align-items:center;"]);
const FlagStyled$1 = /*#__PURE__*/styled(Flag).withConfig({
  displayName: "FlagStyled",
  componentId: "cole9u-1"
})(["margin-right:5px;"]);
const CountrySelect = (_ref) => {
  let {
    list = [],
    value
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["list", "value"]);

  const {
    t
  } = useTranslation('ui');
  return jsx(SimpleSelect, Object.assign({
    options: list,
    formatOptionLabel: data => jsxs(OptionStyled$1, {
      children: [jsx(FlagStyled$1, {
        code: data.id
      }, void 0), data.title]
    }, void 0),
    value: value,
    placeholder: t('CountrySelect.placeholder')
  }, rest), void 0);
};

const OptionStyled = /*#__PURE__*/styled.div.withConfig({
  displayName: "OptionStyled",
  componentId: "sc-1fd4ca-0"
})(["display:flex;align-items:center;"]);
const FlagStyled = /*#__PURE__*/styled(Flag).withConfig({
  displayName: "FlagStyled",
  componentId: "sc-1fd4ca-1"
})(["margin-right:5px;"]);
const mapCurrencyToFlag = {
  [Currency.CAD]: jsx(FlagStyled, {
    code: 'CA'
  }, void 0),
  [Currency.EUR]: jsx(FlagStyled, {
    code: 'EU'
  }, void 0),
  [Currency.RUB]: jsx(FlagStyled, {
    code: 'RU'
  }, void 0),
  [Currency.USD]: jsx(FlagStyled, {
    code: 'US'
  }, void 0)
};
const CurrencySelect = (_ref) => {
  let {
    list = AVAILABLE_CURRENCY,
    value
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["list", "value"]);

  const options = useMemo(() => list.map(currency => ({
    id: currency,
    title: currency
  })), [list]);
  return jsx(SimpleSelect, Object.assign({
    options: options,
    formatOptionLabel: data => jsxs(OptionStyled, {
      children: [mapCurrencyToFlag[data.id], data.id]
    }, void 0),
    value: value
  }, rest), void 0);
};

const TagListWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TagListWrapper",
  componentId: "sc-1if5u8z-0"
})(["margin-top:16px;"]);
const TagListStyled = /*#__PURE__*/styled(TagList).withConfig({
  displayName: "TagListStyled",
  componentId: "sc-1if5u8z-1"
})(["display:flex;flex-direction:row;"]);
const ShowMoreBtn = /*#__PURE__*/styled.button.withConfig({
  displayName: "ShowMoreBtn",
  componentId: "sc-1if5u8z-2"
})(["font-weight:500;font-size:14px;line-height:20px;transition:0.3s color;display:inline-flex;align-items:center;background-color:transparent;border:none;color:", ";cursor:pointer;padding:0;&:hover{color:", ";}&:focus{outline:none;}margin-bottom:14px;"], ({
  theme
}) => theme.color.secondary200, ({
  theme
}) => theme.color.primary600);
const SelectedOptions = ({
  value: _value = [],
  onRemove,
  collapseCount: _collapseCount = 5,
  getOptionLabel,
  getOptionValue
}) => {
  const {
    t
  } = useTranslation('ui');
  const [forceOpened, setForceOpened] = useState(false);
  let collapseBtn;

  if (_value.length > _collapseCount) {
    collapseBtn = jsxs(ShowMoreBtn, Object.assign({
      onClick: e => {
        e.preventDefault();
        setForceOpened(prev => !prev);
      }
    }, {
      children: [forceOpened ? t('TagsSelect.hideSelected') : t('TagsSelect.showSelected'), ' ']
    }), void 0);
  }

  const valueMappedToTags = useMemo(() => _value.map(v => ({
    title: getOptionLabel(v),
    id: getOptionValue(v)
  })), [getOptionLabel, getOptionValue, _value]);
  return jsxs(TagListWrapper, {
    children: [(!collapseBtn || forceOpened) && jsx(TagListStyled, {
      list: valueMappedToTags,
      onRemove: ({
        id
      }) => onRemove(id)
    }, void 0), collapseBtn]
  }, void 0);
};

/**
 * Same as multi BasicSelect but with a custom multi value component outside of the select.
 */

const TagsSelect = (_ref) => {
  let {
    onChange,
    value = EMPTY_ARRAY,
    getOptionValue,
    getOptionLabel,
    collapseCount
  } = _ref,
      props = _objectWithoutProperties(_ref, ["onChange", "value", "getOptionValue", "getOptionLabel", "collapseCount"]);

  const handleRemove = useCallback(id => {
    if (!onChange) return;
    let removedValue;
    const newValues = value.filter(v => {
      if (getOptionValue(v) === id) {
        removedValue = v;
        return false;
      }

      return true;
    }); //@ts-expect-error here for some reason removedValue says that it is used before initialization

    onChange(newValues, {
      action: 'remove-value',
      removedValue
    });
  }, [getOptionValue, onChange, value]);
  return jsxs(Fragment, {
    children: [jsx(BasicSelect, Object.assign({
      value: value,
      getOptionLabel: getOptionLabel,
      onChange: onChange,
      getOptionValue: getOptionValue,
      isMulti: true,
      disableTags: true,
      disableSearchClearOnSelect: true
    }, props), void 0), jsx(SelectedOptions, {
      value: value,
      getOptionValue: getOptionValue,
      getOptionLabel: getOptionLabel,
      onRemove: handleRemove,
      collapseCount: collapseCount
    }, void 0)]
  }, void 0);
};

const MenuListScrolledArea = /*#__PURE__*/styled.div.withConfig({
  displayName: "MenuListScrolledArea",
  componentId: "sc-1oc14j5-0"
})(["position:relative;"]);
const BottomAnchor = /*#__PURE__*/styled.div.withConfig({
  displayName: "BottomAnchor",
  componentId: "sc-1oc14j5-1"
})(["position:absolute;bottom:0;width:100%;height:", ";z-index:-1;"], ({
  maxHeight
}) => maxHeight);
const useIntersectionObserverForSelect = ({
  onMenuScrollToBottom,
  scrollToBottomThreshold: _scrollToBottomThreshold = 0.5,
  onMenuOpen,
  onMenuClose,
  menuMaxHeight
}) => {
  var _selectRef$current;

  const selectRef = useRef(null);
  const instancePrefix = (_selectRef$current = selectRef.current) === null || _selectRef$current === void 0 ? void 0 : _selectRef$current.instancePrefix;
  const bottomAnchorId = `${instancePrefix}-bottom-anchor`;
  const MenuList = useCallback((_ref) => {
    let {
      children
    } = _ref,
        props = _objectWithoutProperties(_ref, ["children"]);

    return jsx(StyledMenuList, Object.assign({}, props, {
      maxHeight: menuMaxHeight
    }, {
      children: jsxs(MenuListScrolledArea, {
        children: [children, jsx(BottomAnchor, {
          maxHeight: menuMaxHeight,
          id: bottomAnchorId
        }, void 0)]
      }, void 0)
    }), void 0);
  }, [menuMaxHeight, bottomAnchorId]);
  const {
    ref
  } = useInView(onMenuScrollToBottom, _scrollToBottomThreshold); // We need additional logic with observers only if onMenuScrollToBottom is provided.

  const handleMenuOpen = onMenuScrollToBottom && selectRef.current && instancePrefix ? () => {
    onMenuOpen === null || onMenuOpen === void 0 ? void 0 : onMenuOpen(); // This is because menuListRef is not available in onMenuOpen callback immediately.

    setTimeout(() => {
      var _selectRef$current2;

      const bottomAnchor = (_selectRef$current2 = selectRef.current) !== null && _selectRef$current2 !== void 0 && _selectRef$current2.menuListRef ? document.getElementById(bottomAnchorId) : null;
      bottomAnchor && ref(bottomAnchor);
    }, 1);
  } : undefined;
  const handleMenuClose = onMenuScrollToBottom ? () => {
    onMenuClose === null || onMenuClose === void 0 ? void 0 : onMenuClose(); // we need to stop observing the bottom anchor element when the menu is closed to prevent memory leaks.

    ref(null);
  } : undefined;
  return {
    ref: selectRef,
    components: {
      MenuList
    },
    onMenuOpen: handleMenuOpen,
    onMenuClose: handleMenuClose
  };
};

const useSelectWithPaginationState = ({
  initialOptions: _initialOptions = [],
  initialPage: _initialPage = 1,
  onUpdateRequested,
  newOptions,
  pageSize: _pageSize = 50,
  total,
  progress,
  clearDeps: _clearDeps = []
}) => {
  const [options, setOptions] = useState(_initialOptions);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(_initialPage);
  const debouncedSearch = useDebounce(search, 100); // Reset options

  useEffect(() => {
    setPage(_initialPage);
    onUpdateRequested(debouncedSearch, _initialPage, _pageSize); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, ..._clearDeps]); // Call onUpdateRequested on page change

  useEffect(() => {
    if (progress !== Progress.WORK && page !== _initialPage) {
      onUpdateRequested(debouncedSearch, page, _pageSize);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [page]); // Adding new options to the end of the list

  useEffect(() => {
    if (newOptions) {
      setOptions(prevOptions => {
        if (page === _initialPage) return newOptions;
        return [...prevOptions, ...newOptions];
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [newOptions]);
  const fetchNextPage = useCallback(() => {
    if (progress !== Progress.WORK) {
      setPage(page => ++page);
    }
  }, [progress]);
  return {
    page,
    options,
    setOptions,
    setSearch,
    setPage,
    search: debouncedSearch,
    fetchNextPage: page * _pageSize <= total && total > _pageSize && search === debouncedSearch ? fetchNextPage : undefined
  };
};

function ownKeys$6(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$6(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$6(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Select with pagination. First mount calls onUpdateRequested with { page: 1 }.
 * Next, on scroll to the end calls onUpdateRequested with { page: page + 1 }.
 * New options should be passed to the prop newOptions (when the reference is changed this array is added to the end of the list).
 */

const SelectWithPagination = (_ref) => {
  let {
    initialOptions,
    initialPage,
    onUpdateRequested,
    pageSize,
    newOptions,
    total,
    progress,
    clearDeps,
    menuMaxHeight = '50vh',
    onChange
  } = _ref,
      props = _objectWithoutProperties(_ref, ["initialOptions", "initialPage", "onUpdateRequested", "pageSize", "newOptions", "total", "progress", "clearDeps", "menuMaxHeight", "onChange"]);

  const {
    options,
    setSearch,
    fetchNextPage,
    search
  } = useSelectWithPaginationState({
    initialOptions,
    initialPage,
    onUpdateRequested,
    newOptions,
    total,
    progress,
    pageSize,
    clearDeps
  });
  const intersectionProps = useIntersectionObserverForSelect(_objectSpread$6(_objectSpread$6({}, props), {}, {
    menuMaxHeight,
    onMenuScrollToBottom: fetchNextPage
  }));
  return jsx(BasicSelect, Object.assign({
    isSearchable: true
  }, intersectionProps, {
    progress: progress,
    options: options,
    onSearchChange: setSearch,
    searchValue: search,
    onChange: onChange
  }, props), void 0);
};

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$5(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * MultiSelect with pagination. First mount calls onUpdateRequested with { page: 1 }.
 * Next, on scroll to the end calls onUpdateRequested with { page: page + 1 }.
 * New options should be passed to the prop newOptions (when the reference is changed this array is added to the end of the list).
 */

const MultiSelectWithPagination = (_ref) => {
  let {
    initialOptions,
    initialPage,
    onUpdateRequested,
    pageSize,
    newOptions,
    total,
    progress,
    clearDeps,
    disableTagsOutside,
    disableTags,
    menuMaxHeight = '50vh',
    onChange
  } = _ref,
      props = _objectWithoutProperties(_ref, ["initialOptions", "initialPage", "onUpdateRequested", "pageSize", "newOptions", "total", "progress", "clearDeps", "disableTagsOutside", "disableTags", "menuMaxHeight", "onChange"]);

  const {
    options,
    setSearch,
    fetchNextPage,
    search
  } = useSelectWithPaginationState({
    initialOptions,
    initialPage,
    onUpdateRequested,
    newOptions,
    total,
    progress,
    pageSize,
    clearDeps
  });
  const intersectionProps = useIntersectionObserverForSelect(_objectSpread$5(_objectSpread$5({}, props), {}, {
    menuMaxHeight,
    onMenuScrollToBottom: fetchNextPage
  }));
  return disableTagsOutside || disableTags ? jsx(BasicSelect, Object.assign({
    isMulti: true,
    onMenuScrollToBottom: fetchNextPage,
    isSearchable: true,
    progress: progress,
    options: options,
    onSearchChange: setSearch,
    searchValue: search,
    disableTags: disableTags,
    onChange: onChange
  }, intersectionProps, props), void 0) : jsx(TagsSelect, Object.assign({
    isSearchable: true,
    onMenuScrollToBottom: fetchNextPage,
    progress: progress,
    options: options,
    onSearchChange: setSearch,
    searchValue: search,
    onChange: onChange
  }, intersectionProps, props), void 0);
};

const Table$1 = /*#__PURE__*/styled.table.withConfig({
  displayName: "Table",
  componentId: "eqa0xa-0"
})(["flex-grow:1;border-spacing:0 8px;width:100%;min-width:1110px;table-layout:", ";border-collapse:separate;"], props => props.fixedLayout ? 'fixed' : 'auto');
const TableWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableWrapper",
  componentId: "eqa0xa-1"
})(["flex-grow:1;width:100%;overflow:auto;min-height:0;padding-bottom:", ";"], ({
  isSummaryOpen
}) => isSummaryOpen ? '120px' : 0);
const TableInner = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableInner",
  componentId: "eqa0xa-2"
})(["position:relative;min-height:100%;padding:0 24px 60px;"]);
const Body = /*#__PURE__*/styled.tbody.withConfig({
  displayName: "Body",
  componentId: "eqa0xa-3"
})(["overflow:auto;"]);

const headCellBorderCSS = props => {
  let border = `1px solid ${props.theme.color.outlineMid}`;

  if (props.transparent) {
    border = 'none';
  }

  return css(["border:", ";border-left:none;&:first-child,&:last-child{&::before{border:", ";border-left:none;border-right:none;}}"], border, border);
};

const headCellBackgroundCSS = props => {
  let backgroundColor = props.theme.color.onPrimaryLight;

  if (props.transparent) {
    backgroundColor = 'unset';
  }

  if (props.emphatic) {
    backgroundColor = props.theme.color.primary25;
  }

  return css(["background-color:", ";&:first-child,&:last-child{&::before{background-color:", ";}}"], backgroundColor, backgroundColor);
};

const headerCellAlignCSS = props => {
  const align = props.headerAlign || 'left';
  const valign = props.headerValign || 'middle';
  return css(["vertical-align:", ";text-align:", ";"], valign, align);
};

const HeadCell = /*#__PURE__*/styled.th.withConfig({
  displayName: "HeadCell",
  componentId: "eqa0xa-4"
})(["", ";", ";", ";position:sticky;height:40px;max-height:40px;font-weight:500;font-size:14px;line-height:18px;padding:0 12px;white-space:normal;word-break:keep-all;top:0;box-sizing:border-box;&:last-child{border-right:none;}width:", ";cursor:", ";&:first-child,&:last-child{&::before{content:'';box-sizing:border-box;height:40px;width:24px;position:absolute;display:inline-block;top:-1px;}}&:first-child{&::before{left:-24px;}}&:last-child{&::before{right:-24px;}}"], headCellBorderCSS, headCellBackgroundCSS, headerCellAlignCSS, ({
  width
}) => typeof width === 'string' ? width : '', ({
  sortable
}) => sortable ? 'pointer' : 'default');
const mapVerticalAlignToAlignItems = {
  top: 'flex-start',
  bottom: 'flex-end',
  middle: 'center'
};
const HeadCellWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "HeadCellWrapper",
  componentId: "eqa0xa-5"
})(["display:inline-flex;flex-direction:row;align-items:", ";width:100%;"], props => mapVerticalAlignToAlignItems[props.headerValign]);
const HeadCellInner = /*#__PURE__*/styled.div.withConfig({
  displayName: "HeadCellInner",
  componentId: "eqa0xa-6"
})(["max-width:", ";text-overflow:ellipsis;overflow:hidden;display:inline-block;transition:0.3s color;vertical-align:middle;color:", ";"], ({
  sortable
}) => sortable ? 'calc(100% - 20px)' : '100%', ({
  isSorted,
  theme
}) => isSorted ? theme.color.primary600 : theme.color.onSurfaceMidEmphasys);

const bodyCellAlignCSS = props => {
  const valign = props.cellValign || 'top';
  const align = props.cellAlign || 'left';
  return css(["vertical-align:", ";text-align:", ";"], valign, align);
};

const BodyCell = /*#__PURE__*/styled.td.withConfig({
  displayName: "BodyCell",
  componentId: "eqa0xa-7"
})(["", ";font-weight:500;font-size:14px;line-height:20px;color:", ";background-color:", ";border:1px solid rgba(0,0,0,0.08);border-width:1px 0;transition:0.2s background-color,0.2s border-color;padding:13px 12px;&:first-child{border-top-left-radius:8px;border-bottom-left-radius:8px;border-left-width:1px;}&:last-child{border-bottom-right-radius:8px;border-top-right-radius:8px;border-right-width:1px;}"], bodyCellAlignCSS, ({
  theme
}) => theme.color.onPrimaryDark, ({
  theme,
  emphatic
}) => emphatic ? theme.color.primary25 : theme.color.onPrimaryLight);
const Row = /*#__PURE__*/styled.tr.withConfig({
  displayName: "Row",
  componentId: "eqa0xa-8"
})(["min-height:56px;position:relative;", " ", ";"], ({
  selected,
  theme
}) => {
  if (selected) {
    return css(["cursor:pointer;", "{background:", ";border-color:", ";}&:hover{", "{border-color:", ";}}"], BodyCell, helpersColor.alphaColor(theme.color.primary600, 0.04), helpersColor.alphaColor(theme.color.primary600, 0.48), BodyCell, helpersColor.alphaColor(theme.color.primary600, 0.48));
  }
}, ({
  clickable
}) => {
  if (clickable) {
    return css(["cursor:pointer;&:hover{", "{border-color:rgba(3,123,255,0.48);}}"], BodyCell);
  }
});
const EllipsisContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "EllipsisContainer",
  componentId: "eqa0xa-9"
})(["display:inline-block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
const Sort = /*#__PURE__*/styled.div.withConfig({
  displayName: "Sort",
  componentId: "eqa0xa-10"
})(["transform:", ";order:", ";margin:", ";display:inline-block;> svg{transform:scale(", ",", ");display:block;height:20px;width:20px;}"], props => `rotate(${props.isSortDesc ? 0 : '180deg'})`, props => props.headerAlign === 'left' ? '1' : '-1', props => props.headerAlign === 'left' ? '0 0 0 8px' : '0 8px 0 0', props => props.headerAlign === 'left' ? -1 : 1, props => props.desc ? -1 : 1);
const EmptyRowCell = /*#__PURE__*/styled.td.withConfig({
  displayName: "EmptyRowCell",
  componentId: "eqa0xa-11"
})(["text-align:center;padding:40px;color:", ";"], ({
  theme
}) => theme.color.secondary200);
const ActionPanelWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ActionPanelWrapper",
  componentId: "eqa0xa-12"
})(["position:fixed;bottom:20px;width:", ";padding:24px 24px 0 24px;"], ({
  theme
}) => `calc(100% - ${theme.size.leftSideBarWidth}px)`);
const ActionPanelContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ActionPanelContainer",
  componentId: "eqa0xa-13"
})(["display:flex;justify-content:flex-start;align-items:center;padding:20px 20px 20px 40px;background:", ";box-shadow:0 24px 48px rgba(0,0,0,0.12);border-radius:16px;"], ({
  theme
}) => theme.color.onPrimaryLight);
const ActionPanelContentWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ActionPanelContentWrapper",
  componentId: "eqa0xa-14"
})(["display:flex;width:100%;"]);
const CloseButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "CloseButton",
  componentId: "eqa0xa-15"
})(["justify-self:flex-end;margin-left:auto;padding:13px;"]);
const Spinner = /*#__PURE__*/styled(Spinner$1).withConfig({
  displayName: "Spinner",
  componentId: "eqa0xa-16"
})(["margin:20px auto;"]);
const PaginationContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PaginationContainer",
  componentId: "eqa0xa-17"
})(["position:absolute;bottom:0;"]);
const skeletonGradient = /*#__PURE__*/keyframes(["0%{background-position-x:100%;}50%{background-position-x:0;}100%{background-position-x:-100%;}"]);
const SkeletonCell = /*#__PURE__*/styled(BodyCell).withConfig({
  displayName: "SkeletonCell",
  componentId: "eqa0xa-19"
})(["position:relative;height:40px;width:100%;border-radius:4px;overflow:hidden;&::after{content:'';position:absolute;left:0;top:0;right:0;height:100%;width:100%;background:linear-gradient(to left,rgba(255,255,255,0) 48%,#fff 50%,rgba(255,255,255,0) 52%);background-size:200% 200%;animation:", " 1.1s cubic-bezier(0.5,0.02,0.46,0.92) infinite;}&::before{width:100%;content:'';position:absolute;top:0;left:0;height:100%;background-color:#efeded;display:block;border-radius:4px;}"], skeletonGradient);
const StickyHead = /*#__PURE__*/styled.thead.withConfig({
  displayName: "StickyHead",
  componentId: "eqa0xa-20"
})(["position:sticky;z-index:1;top:0;&::before{content:'';width:calc(100% + 48px);height:", ";top:-8px;background:", ";position:absolute;margin:0 -24px;}& tr{position:relative;top:-16px;@media not all and (min-resolution:0.001dpcm){@supports (-webkit-appearance:none){& th{position:relative;top:-16px;}}}}"], props => props.height ? `${props.height}px` : '40px', props => props.theme.color.onPrimaryLight);

const InfiniteScrollTable = (_ref) => {
  let {
    children,
    hasMore,
    handler,
    delta,
    isLoading,
    isSummaryOpen,
    summary
  } = _ref,
      props = _objectWithoutProperties(_ref, ["children", "hasMore", "handler", "delta", "isLoading", "isSummaryOpen", "summary"]);

  const {
    wrapperRef,
    anchor
  } = useInfiniteScroll(handler, hasMore, isLoading, delta);
  return jsxs(TableWrapper, Object.assign({
    ref: wrapperRef,
    isSummaryOpen: isSummaryOpen
  }, {
    children: [jsx(Table$1, Object.assign({}, props, {
      children: children
    }), void 0), isLoading ? jsx(Spinner, {}, void 0) : null, anchor, summary]
  }), void 0);
};
const Table = /*#__PURE__*/React__default.forwardRef((_ref2, ref) => {
  let {
    children,
    isSummaryOpen,
    total,
    pageSize,
    currentPage,
    onSetPage,
    pages
  } = _ref2,
      props = _objectWithoutProperties(_ref2, ["children", "isSummaryOpen", "total", "pageSize", "currentPage", "onSetPage", "pages"]);

  return jsx(TableWrapper, Object.assign({
    isSummaryOpen: isSummaryOpen,
    ref: ref
  }, {
    children: jsxs(TableInner, {
      children: [jsx(Table$1, Object.assign({}, props, {
        children: children
      }), void 0), total > pageSize ? jsx(PaginationContainer, {
        children: jsx(Pagination, {
          total: pages,
          current: currentPage,
          onClick: onSetPage
        }, void 0)
      }, void 0) : null]
    }, void 0)
  }), void 0);
});
Table.displayName = 'Table';
const TableHead = (_ref3) => {
  let {
    children,
    disableStickyHead,
    height
  } = _ref3,
      props = _objectWithoutProperties(_ref3, ["children", "disableStickyHead", "height"]);

  return jsx(Fragment, {
    children: disableStickyHead ? jsx("thead", Object.assign({}, props, {
      children: children
    }), void 0) : jsx(StickyHead, Object.assign({
      height: height
    }, props, {
      children: children
    }), void 0)
  }, void 0);
};
const TableBody = (_ref4) => {
  let {
    children
  } = _ref4,
      props = _objectWithoutProperties(_ref4, ["children"]);

  return jsx(Body, Object.assign({}, props, {
    children: children
  }), void 0);
};
const TableHeadRow = (_ref5) => {
  let {
    children
  } = _ref5,
      props = _objectWithoutProperties(_ref5, ["children"]);

  return jsx("tr", Object.assign({}, props, {
    children: children
  }), void 0);
};
const TableBodyRow = (_ref6) => {
  let {
    children,
    onClick,
    selected
  } = _ref6,
      props = _objectWithoutProperties(_ref6, ["children", "onClick", "selected"]);

  return jsx(Row, Object.assign({}, props, {
    onClick: onClick,
    selected: selected,
    clickable: !!onClick
  }, {
    children: children
  }), void 0);
};
const TableHeadCell = ({
  children,
  isSorted,
  testId,
  desc,
  className,
  width,
  sortable,
  onClick,
  hidden,
  top,
  zIndex: _zIndex = 0,
  emphatic: _emphatic = false,
  transparent: _transparent = false,
  headerAlign: _headerAlign = 'left',
  headerValign: _headerValign = 'middle',
  title,
  isSortDesc
}) => {
  if (hidden) {
    return null;
  }

  return jsx(HeadCell, Object.assign({
    width: width,
    style: {
      zIndex: _zIndex
    },
    "data-test-id": testId,
    emphatic: _emphatic,
    sortable: sortable,
    transparent: _transparent,
    onClick: onClick,
    top: top,
    headerAlign: _headerAlign,
    headerValign: _headerValign,
    title: title ? title : typeof children === 'string' ? children : '',
    className: className
  }, {
    children: jsxs(HeadCellWrapper, Object.assign({
      headerValign: _headerValign
    }, {
      children: [jsx(HeadCellInner, Object.assign({
        isSorted: isSorted
      }, {
        children: children
      }), void 0), isSorted && jsx(Sort, Object.assign({
        isSortDesc: isSortDesc,
        desc: desc,
        headerAlign: _headerAlign
      }, {
        children: jsx(SvgSort, {}, void 0)
      }), void 0)]
    }), void 0)
  }), void 0);
};
const TableBodyCell = (_ref7) => {
  let {
    children,
    noWrap,
    stopClickPropagation,
    className
  } = _ref7,
      props = _objectWithoutProperties(_ref7, ["children", "noWrap", "stopClickPropagation", "className"]);

  return jsx(BodyCell, Object.assign({}, props, {
    className: className,
    onClick: e => stopClickPropagation && e.stopPropagation()
  }, {
    children: noWrap ? jsx(EllipsisContainer, {
      children: children
    }, void 0) : children
  }), void 0);
};
const TableNoData = ({
  children: _children = 'Sorry, nothing found',
  loading: _loading = 'Loading...',
  isEmpty,
  colSpan,
  progress,
  className
}) => {
  if (progress === Progress.WORK || progress === Progress.IDLE) {
    return jsx("tr", {
      children: jsx(EmptyRowCell, Object.assign({
        className: className,
        colSpan: colSpan
      }, {
        children: _loading
      }), void 0)
    }, void 0);
  }

  if ([Progress.SUCCESS, Progress.ERROR].includes(progress) && isEmpty) {
    return jsx("tr", {
      children: jsx(EmptyRowCell, Object.assign({
        className: className,
        colSpan: colSpan
      }, {
        children: _children
      }), void 0)
    }, void 0);
  }

  return null;
};
const TableActionPanel = ({
  children,
  onClose,
  className
}) => {
  return jsx(ActionPanelWrapper, Object.assign({
    className: className
  }, {
    children: jsxs(ActionPanelContainer, {
      children: [jsx(ActionPanelContentWrapper, {
        children: children
      }, void 0), jsx(CloseButton, Object.assign({
        intent: 'cancel',
        onClick: onClose
      }, {
        children: jsx(SvgCross, {}, void 0)
      }), void 0)]
    }, void 0)
  }), void 0);
};
const TableSkeletonRow = ({
  className,
  colSpan
}) => {
  return jsx(TableBodyRow, {
    children: jsx(SkeletonCell, {
      className: className,
      colSpan: colSpan
    }, void 0)
  }, void 0);
};

function AdvancedHeadCell() {
  return function InnerAdvancedHeadCell(_ref) {
    let {
      id,
      sortable,
      children,
      sortDesc,
      sortField,
      testId,
      onChange
    } = _ref,
        props = _objectWithoutProperties(_ref, ["id", "sortable", "children", "sortDesc", "sortField", "testId", "onChange"]);

    const handleChange = () => {
      let sort_field = id;
      let sort_reversed = false;

      if (sortField === id) {
        if (sortDesc) {
          sort_field = undefined;
          sort_reversed = undefined;
        } else {
          sort_reversed = true;
        }
      }

      onChange({
        sort_field,
        sort_reversed
      });
    };

    if (!sortable) {
      return jsx(TableHeadCell, Object.assign({
        testId: testId
      }, props, {
        children: children
      }), void 0);
    }

    const isSorted = sortField === id;
    return jsx(TableHeadCell, Object.assign({
      testId: testId,
      isSorted: isSorted,
      desc: isSorted && sortDesc,
      onClick: handleChange,
      sortable: sortable
    }, props, {
      children: children
    }), void 0);
  };
}

const SectionTable = /*#__PURE__*/styled.table.withConfig({
  displayName: "SectionTable",
  componentId: "sc-1i7w3gc-0"
})(["width:100%;"]);
const SectionTableHead = /*#__PURE__*/styled.thead.withConfig({
  displayName: "SectionTableHead",
  componentId: "sc-1i7w3gc-1"
})([""]);
const SectionTableBody = /*#__PURE__*/styled.tbody.withConfig({
  displayName: "SectionTableBody",
  componentId: "sc-1i7w3gc-2"
})([""]);
const SectionTableHeadRow = (_ref) => {
  let {
    children
  } = _ref,
      props = _objectWithoutProperties(_ref, ["children"]);

  return jsx("tr", Object.assign({}, props, {
    children: children
  }), void 0);
};
const SectionTableHeadCell = /*#__PURE__*/styled.td.withConfig({
  displayName: "SectionTableHeadCell",
  componentId: "sc-1i7w3gc-3"
})(["text-transform:", ";text-align:", ";font-weight:500;font-size:12px;line-height:20px;padding:0 8px 8px;color:", ";&:first-child{padding-left:0;}&:last-child{padding-right:0;}"], props => props.textTransform, props => props.textAlign, props => props.theme.color.secondary200);
const SectionTableBodyRow = (_ref2) => {
  let {
    children
  } = _ref2,
      rest = _objectWithoutProperties(_ref2, ["children"]);

  return jsx("tr", Object.assign({}, rest, {
    children: children
  }), void 0);
};
const SectionTableBodyCell = /*#__PURE__*/styled.td.withConfig({
  displayName: "SectionTableBodyCell",
  componentId: "sc-1i7w3gc-4"
})(["text-align:", ";vertical-align:", ";font-style:normal;font-weight:500;font-size:14px;line-height:20px;padding:8px;&:first-child{padding-left:0;}&:last-child{padding-right:0;}"], props => props.textAlign || 'left', props => props.verticalAlign || 'top');

const TableComponentsWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableComponentsWrapper",
  componentId: "k0a7pf-0"
})(["display:grid;grid-template-rows:", ";height:100%;overflow:hidden;"], props => props.noFilters ? 'auto max-content' : 'max-content auto max-content');
const TableComponentsFilters = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableComponentsFilters",
  componentId: "k0a7pf-1"
})(["position:sticky;top:0;border-top:", ";background-color:", ";display:flex;padding:12px 36px 16px;z-index:1;"], ({
  theme
}) => `1px solid ${theme.color.outlineMid}`, ({
  theme
}) => theme.color.onPrimaryLight);
const TableComponentsFlag = /*#__PURE__*/styled(Flag).withConfig({
  displayName: "TableComponentsFlag",
  componentId: "k0a7pf-2"
})(["margin-right:5px;"]);
const appear = /*#__PURE__*/keyframes(["to{opacity:1;}"]);
const TableComponentsSpinnerWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableComponentsSpinnerWrapper",
  componentId: "k0a7pf-3"
})(["margin:20px auto;display:flex;justify-content:center;opacity:0;animation:", " 0.3s;animation-fill-mode:forwards;animation-delay:0.3s;"], appear);
const TableComponentsSpinner = () => {
  return jsx(TableComponentsSpinnerWrapper, {
    children: jsx(Spinner$1, {}, void 0)
  }, void 0);
};
const TableComponentsSummaryWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableComponentsSummaryWrapper",
  componentId: "k0a7pf-4"
})(["display:flex;justify-content:flex-end;align-items:center;width:100%;margin-right:24px;"]);
const TableComponentsSummaryTitle = /*#__PURE__*/styled.span.withConfig({
  displayName: "TableComponentsSummaryTitle",
  componentId: "k0a7pf-5"
})(["display:flex;justify-self:flex-start;margin-left:0;margin-right:24px;font-style:normal;font-weight:bold;font-size:18px;line-height:24px;color:", ";"], ({
  theme
}) => theme.color.onPrimaryDark);
const TableComponentsSummaryLabel = /*#__PURE__*/styled.span.withConfig({
  displayName: "TableComponentsSummaryLabel",
  componentId: "k0a7pf-6"
})(["font-weight:500;font-size:14px;line-height:20px;margin-right:8px;color:", ";"], props => props.theme.color.onPrimaryDark);
const TableComponentsCheckboxWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableComponentsCheckboxWrapper",
  componentId: "k0a7pf-7"
})(["display:grid;grid-gap:8px;grid-template-columns:max-content auto;"]);
const TableComponentsClearButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "TableComponentsClearButton",
  componentId: "k0a7pf-8"
})(["margin-top:20px;"]);
const TableComponentsLinkButton = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableComponentsLinkButton",
  componentId: "k0a7pf-9"
})(["justify-self:flex-start;margin-left:0;margin-right:auto;font-style:normal;font-weight:500;font-size:18px;line-height:20px;color:", ";cursor:pointer;"], props => props.theme.color.primary600);
const TableComponentsActionButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "TableComponentsActionButton",
  componentId: "k0a7pf-10"
})(["margin-right:16px;"]);

const FontSize = {
  Small: css(["font-size:12px;line-height:16px;"]),
  Base: css(["font-size:14px;line-height:20px;"])
};
const FontWeight = {
  Light: css(["font-weight:300;"]),
  Normal: css(["font-weight:400;"]),
  Medium: css(["font-weight:400;"]),
  SemiBold: css(["font-weight:600;"]),
  Bold: css(["font-weight:700;"])
};
const Typography = {
  Text: css(["", " ", ""], FontSize.Base, FontWeight.Normal),
  Link: css(["color:", ";cursor:pointer;text-decoration:none;"], props => props.theme.color.primary600)
};

const shimmer = /*#__PURE__*/keyframes(["100%{transform:translateX(100%);}"]);
const Skeleton = /*#__PURE__*/styled.div.withConfig({
  displayName: "Skeleton",
  componentId: "sc-1gab35g-0"
})(["display:inline-block;width:", ";height:", ";position:relative;overflow:hidden;background-color:#dddbdd;border-radius:", ";&::after{position:absolute;top:0;right:0;bottom:0;left:0;transform:translateX(-100%);background-image:linear-gradient( 90deg,rgba(255,255,255,0) 0,rgba(255,255,255,0.2) 20%,rgba(255,255,255,0.5) 60%,rgba(255,255,255,0) );animation:", " 1s infinite;content:'';}"], props => props.width || '100px', props => props.height || '1em', props => props.radial ? '50%' : '4px', shimmer);

let _$5 = t => t,
    _t$5,
    _t2$4,
    _t3$2,
    _t4$1,
    _t5$1,
    _t6$1,
    _t7,
    _t8,
    _t9;
const ProductWizardMakeItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardMakeItem",
  componentId: "sc-15vkkty-0"
})(["width:186px;height:104px;border-radius:12px;background-color:", ";overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:block;text-align:center;line-height:48px;padding:5px;cursor:pointer;&:hover{background-color:", ";}", ""], props => props.theme.color.primary25, props => props.theme.color.primary100, props => props.theme.media.mobile(_t$5 || (_t$5 = _$5`
  width: 124px;
  height: 80px;
`)));
const StepFormWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepFormWrapper",
  componentId: "sc-15vkkty-1"
})(["height:390px;overflow-y:hidden;", ""], props => props.theme.media.mobile(_t2$4 || (_t2$4 = _$5`
    overflow-x: hidden;
    width: 300px;
  `)));
const ProductWizardModelsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardModelsContainer",
  componentId: "sc-15vkkty-2"
})(["height:280px;display:flex;justify-content:center;overflow-y:auto;"]);
const ProductWizardMakeContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardMakeContainer",
  componentId: "sc-15vkkty-3"
})(["height:325px;display:flex;justify-content:center;overflow-y:auto;", ""], props => props.theme.media.mobile(_t3$2 || (_t3$2 = _$5`
    max-width: none;
  `)));
const ProductWizardModelsButtonWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardModelsButtonWrapper",
  componentId: "sc-15vkkty-4"
})(["position:sticky;top:0;padding-top:14px;", ""], props => props.theme.media.mobile(_t4$1 || (_t4$1 = _$5`
    padding-top: 17px;
  `)));
const StepFormStepWizardModelsWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepFormStepWizardModelsWrapper",
  componentId: "sc-15vkkty-5"
})(["max-height:380px;"]);
const HeaderSizeTitle = /*#__PURE__*/styled.div.withConfig({
  displayName: "HeaderSizeTitle",
  componentId: "sc-15vkkty-6"
})(["padding-top:12px;font-weight:600;padding-bottom:16px;", ""], props => props.theme.media.mobile(_t5$1 || (_t5$1 = _$5`
    padding-bottom: 8px;
  `)));
const BattAndReelsTitle = /*#__PURE__*/styled(HeaderSizeTitle).withConfig({
  displayName: "BattAndReelsTitle",
  componentId: "sc-15vkkty-7"
})(["padding-top:0;"]);
const BattAndHeaderButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "BattAndHeaderButton",
  componentId: "sc-15vkkty-8"
})(["width:186px;height:56px;font-size:16px;", ""], props => props.theme.media.mobile(_t6$1 || (_t6$1 = _$5`
    width: 124px;
    height: 48px;
  `)));
const BattAndHeaderWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "BattAndHeaderWrapper",
  componentId: "sc-15vkkty-9"
})(["margin-bottom:24px;"]);
const ProductWizardHeaderButtonWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardHeaderButtonWrapper",
  componentId: "sc-15vkkty-10"
})(["padding-top:5px;", ""], props => props.theme.media.mobile(_t7 || (_t7 = _$5`
    padding-top: 8px;
  `)));
const ProductWizardResultWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultWrapper",
  componentId: "sc-15vkkty-11"
})(["display:flex;flex-direction:column;align-items:center;justify-content:center;"]);
const ProductWizardResultTitle = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultTitle",
  componentId: "sc-15vkkty-12"
})(["max-width:600px;display:flex;justify-content:center;margin-top:24px;font-weight:600;font-size:18px;", ""], props => props.theme.media.mobile(_t8 || (_t8 = _$5`
    text-align: center;
  `)));
const ProductWizardResultPrice = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultPrice",
  componentId: "sc-15vkkty-13"
})(["display:flex;justify-content:center;margin-top:16px;font-weight:700;font-size:24px;line-height:32px;"]);
const ProductWizardResultQty$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultQty",
  componentId: "sc-15vkkty-14"
})(["margin-top:16px;display:flex;flex-direction:column;align-items:center;"]);
const ProductWizardResultQtyLabel$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultQtyLabel",
  componentId: "sc-15vkkty-15"
})(["margin-top:4px;margin-bottom:8px;font-size:14px;font-weight:600;"]);
const ProductWizardResultButtonWrap = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultButtonWrap",
  componentId: "sc-15vkkty-16"
})(["display:flex;align-items:center;justify-content:center;"]);
const ProductWizardResultButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "ProductWizardResultButton",
  componentId: "sc-15vkkty-17"
})(["width:184px;height:48px;margin-top:30px;&:first-child{margin-right:25px;}&:last-child{margin-left:25px;}"]);
const ProductWizardResultContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultContainer",
  componentId: "sc-15vkkty-18"
})(["height:325px;display:flex;flex-direction:column;justify-content:space-between;"]);
const ResultButtonsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ResultButtonsContainer",
  componentId: "sc-15vkkty-19"
})(["display:flex;justify-content:space-between;", ""], props => props.theme.media.mobile(_t9 || (_t9 = _$5`
    padding-top: 15px;
  `)));
const BattAndHeaderContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "BattAndHeaderContainer",
  componentId: "sc-15vkkty-20"
})(["height:289px;display:flex;justify-content:center;overflow-y:auto;"]);
const LogoImg = /*#__PURE__*/styled.img.withConfig({
  displayName: "LogoImg",
  componentId: "sc-15vkkty-21"
})(["max-width:77px;max-height:51px;"]);
const LogoContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "LogoContainer",
  componentId: "sc-15vkkty-22"
})(["display:block;height:50%;"]);

let _$4 = t => t,
    _t$4,
    _t2$3,
    _t3$1;
const ProductWizardWrapper$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardWrapper",
  componentId: "sc-163sw44-0"
})(["display:grid;grid-template-columns:repeat(3,186px);grid-gap:17px;", ""], props => props.theme.media.mobile(_t$4 || (_t$4 = _$4`
    grid-template-columns: repeat(2, 124px);
    grid-gap: 8px;
  `)));
const ProductWizardCardItem$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardCardItem",
  componentId: "sc-163sw44-1"
})(["display:flex;justify-content:center;align-items:center;padding:5px;width:186px;height:104px;border-radius:12px;background-color:", ";white-space:normal;text-align:center;cursor:pointer;overflow:hidden;word-break:break-word;&:hover{background-color:", ";}", ""], props => props.theme.color.primary25, props => props.theme.color.primary100, props => props.theme.media.mobile(_t2$3 || (_t2$3 = _$4`
    width: 124px;
    height: 80px;
  `)));
const StepWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepWrapper",
  componentId: "sc-163sw44-2"
})(["width:672px;min-height:399px;display:flex;flex-direction:column;justify-content:space-between;", ""], props => props.theme.media.mobile(_t3$1 || (_t3$1 = _$4`
    width: auto;
  `)));
const ButtonsWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ButtonsWrapper",
  componentId: "sc-163sw44-3"
})(["display:flex;justify-content:space-between;align-items:center;height:40px;"]);
const BackButton = /*#__PURE__*/styled(BackBtn).withConfig({
  displayName: "BackButton",
  componentId: "sc-163sw44-6"
})(["margin-bottom:0px;"]);

var DuckFootStepNameForAnalytics;

(function (DuckFootStepNameForAnalytics) {
  DuckFootStepNameForAnalytics["ChooseMake"] = "Choose Make";
  DuckFootStepNameForAnalytics["ChooseModel"] = "Choose Model";
  DuckFootStepNameForAnalytics["BattAndHeader"] = "Batt & Header";
  DuckFootStepNameForAnalytics["Result"] = "Result";
  DuckFootStepNameForAnalytics["Unknown"] = "Unknown";
})(DuckFootStepNameForAnalytics || (DuckFootStepNameForAnalytics = {}));

const DuckFootMake = ({
  opened,
  handleStepMake,
  getProducers,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const producers = getProducers();
  const renderProducersListJSX = producers.map(producer => {
    const options = producer === null || producer === void 0 ? void 0 : producer.options;
    return jsxs(ProductWizardMakeItem, Object.assign({
      "data-test-id": producer.id,
      title: producer.title,
      onClick: () => {
        handleStepMake(producer.id, producer.title);
        onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
          step: DuckFootStepNameForAnalytics.ChooseMake,
          choice: producer.title
        });
      }
    }, {
      children: [jsx(LogoContainer, {
        children: options.url && jsx(LogoImg, {
          src: options.url
        }, void 0)
      }, void 0), producer.title]
    }), producer.id);
  });
  useDidMount(() => {
    // this 'if' is here because the DuckFootMake re-renders after closing the wizard
    if (opened) {
      onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
        step: DuckFootStepNameForAnalytics.ChooseMake
      });
    }
  });
  return jsx(ProductWizardWrapper$1, {
    children: renderProducersListJSX
  }, void 0);
};

const DuckFootModels = ({
  handleStepModel,
  getModels,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const productWizardStepModels = getModels();
  const sortModels = useMemo(() => [...productWizardStepModels].sort((a, b) => {
    if (parseInt(a.title) && parseInt(b.title)) {
      return parseInt(a.title) - parseInt(b.title);
    } else {
      if (a.title > b.title) {
        return 1;
      }

      if (a.title < b.title) {
        return -1;
      }

      return 0;
    }
  }), [productWizardStepModels]);
  const renderModelJSX = sortModels.map(model => {
    return jsx(ProductWizardCardItem$1, Object.assign({
      "data-test-id": model.id,
      title: model.title,
      onClick: () => {
        handleStepModel(model.id, model.title, model.tags.product_ids[0]);
        onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
          step: DuckFootStepNameForAnalytics.ChooseModel,
          choice: model.title
        });
      }
    }, {
      children: model.title
    }), model.id);
  });
  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: DuckFootStepNameForAnalytics.ChooseModel
    });
  });
  return jsx(ProductWizardWrapper$1, {
    children: renderModelJSX
  }, void 0);
};

const DuckFootHeader = ({
  handleStepHeader,
  setBatt,
  getHeaders,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const {
    t
  } = useTranslation('productWizard');
  const headers = getHeaders();
  const renderBattJSX = headers.map(model => {
    return jsx("div", {
      children: model.title
    }, model.id);
  });
  const renderHeaderJSX = headers.map(model => {
    if (model.children) {
      return model.children.map(header => {
        return jsx(BattAndHeaderButton, Object.assign({
          "data-test-id": header.id,
          onClick: () => {
            handleStepHeader(header.id, header.title, header.options);
            setBatt(model.title);
            onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
              step: DuckFootStepNameForAnalytics.BattAndHeader,
              choice: header.title
            });
          },
          intent: 'primary'
        }, {
          children: header.title
        }), header.id);
      });
    }
  });
  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: DuckFootStepNameForAnalytics.BattAndHeader
    });
  });
  return jsx(BattAndHeaderContainer, {
    children: jsxs(BattAndHeaderWrapper, {
      children: [jsx(BattAndReelsTitle, {
        children: t('stepForm.battReelsHeader')
      }, void 0), jsx(BattAndHeaderButton, Object.assign({
        intent: 'primary',
        filled: true,
        onClick: () => null,
        disabled: true
      }, {
        children: renderBattJSX
      }), void 0), jsx(HeaderSizeTitle, {
        children: t('stepForm.headerSizeHeader')
      }, void 0), jsx(ProductWizardWrapper$1, {
        children: renderHeaderJSX
      }, void 0)]
    }, void 0)
  }, void 0);
};

const Qty$1 = ({
  value,
  onChange,
  max,
  min: _min = 1
}) => {
  const [qty, setQty] = useState(value);
  const handleChange = useCallback(value => {
    setQty(value);
    onChange(value);
  }, [onChange]);
  useEffect(() => {
    setQty(value);
  }, [value]);
  return jsx(NumberInput$1, {
    min: _min,
    max: max,
    selectTextOnFocus: true,
    value: qty,
    onChange: val => handleChange(val),
    size: 'medium'
  }, void 0);
};

const ProductWizardResult = ({
  qty,
  makeName,
  modelName,
  headerName,
  batt,
  onAddToCart,
  getPrettyPrice,
  price,
  currency,
  minQty,
  handleRecalculatedQty,
  recalculatedQty,
  maxQty,
  priceType,
  numberOfMachines,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const {
    t
  } = useTranslation('productWizard');
  const result = price && recalculatedQty ? getPrettyPrice(+price * recalculatedQty, currency || Currency.CAD, priceType) : `${recalculatedQty} pcs`;

  const handleAddToCart = () => {
    onAddToCart();
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: DuckFootStepNameForAnalytics.Result,
      choice: 'add_to_cart'
    });
  };

  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: DuckFootStepNameForAnalytics.Result
    });
  });
  return jsxs(ProductWizardResultWrapper, {
    children: [jsx(ProductWizardResultTitle, {
      children: `${makeName} / ${modelName} / ${batt}, ${t('note.header')} ${headerName} / ${recalculatedQty} ${t('note.pcs')} / ${numberOfMachines} ${t('note.packs')}`
    }, void 0), jsx(ProductWizardResultPrice, {
      children: result
    }, void 0), jsxs(ProductWizardResultQty$1, {
      children: [jsx(ProductWizardResultQtyLabel$1, {
        children: t('qtyLabel')
      }, void 0), jsx(Qty$1, {
        value: numberOfMachines,
        onChange: ratio => handleRecalculatedQty(ratio, qty),
        min: minQty,
        max: maxQty
      }, void 0)]
    }, void 0), jsx(ProductWizardResultButtonWrap, {
      children: jsx(ProductWizardResultButton, Object.assign({
        "data-test-id": 'duck_foot_submit',
        intent: 'primary',
        filled: true,
        onClick: handleAddToCart
      }, {
        children: t('stepForm.addToCart')
      }), void 0)
    }, void 0)]
  }, void 0);
};

var ProductWizardDuckFootSteps;

(function (ProductWizardDuckFootSteps) {
  ProductWizardDuckFootSteps["Make"] = "wizard_make";
  ProductWizardDuckFootSteps["Model"] = "wizard_model";
  ProductWizardDuckFootSteps["BattHeader"] = "wizard_batt_header";
  ProductWizardDuckFootSteps["Result"] = "wizard_result";
})(ProductWizardDuckFootSteps || (ProductWizardDuckFootSteps = {}));

const DuckFoot = ({
  opened,
  onClose,
  isMobile,
  productWizardData,
  getPrettyPrice,
  redirectToCart,
  handleProductId,
  submit,
  changeOrderList,
  product,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const {
    t
  } = useTranslation('productWizard');
  const [step, setStep] = useState(ProductWizardDuckFootSteps.Make);
  const [makeId, setMakeId] = useState('');
  const [makeName, setMakeName] = useState('');
  const [modelId, setModelId] = useState('');
  const [modelName, setModelName] = useState('');
  const [batt, setBatt] = useState('');
  const [headerName, setHeaderName] = useState('');
  const [qty, setQty] = useState();
  const [recalculatedQty, setRecalculatedQty] = useState(null);
  const [numberOfMachines, setNumberOfMachines] = useState(1);
  const [productId, setProductId] = useState('');
  const getProducers = useCallback(() => {
    if (productWizardData !== null && productWizardData !== void 0 && productWizardData.children) {
      return productWizardData.children;
    } else {
      return [];
    }
  }, [productWizardData === null || productWizardData === void 0 ? void 0 : productWizardData.children]);
  const getModels = useCallback(() => {
    var _currentMake$;

    const producers = getProducers();
    if (!(producers !== null && producers !== void 0 && producers.length)) return [];
    const currentMake = producers.filter(item => {
      return item.id === makeId;
    });
    return ((_currentMake$ = currentMake[0]) === null || _currentMake$ === void 0 ? void 0 : _currentMake$.children) || [];
  }, [getProducers, makeId]);
  const getHeaders = useCallback(() => {
    var _currentMake$2;

    const data = getProducers();
    if (!(data !== null && data !== void 0 && data.length)) return [];
    const currentMake = data.filter(item => {
      return item.id === makeId;
    });

    if (!((_currentMake$2 = currentMake[0]) !== null && _currentMake$2 !== void 0 && _currentMake$2.children)) {
      return [];
    } else {
      var _currentModel$;

      const currentModel = currentMake[0].children.filter(item => {
        return item.id === modelId;
      });
      return ((_currentModel$ = currentModel[0]) === null || _currentModel$ === void 0 ? void 0 : _currentModel$.children) || [];
    }
  }, [getProducers, makeId, modelId]);
  const handleWizardClose = useCallback(() => {
    onClose();
    setStep(ProductWizardDuckFootSteps.Make);
    setMakeId('');
    setMakeName('');
    setModelId('');
    setModelName('');
    setBatt('');
    setHeaderName('');
    setQty(null);
    setProductId('');
    setRecalculatedQty(null);
    setNumberOfMachines(1);
  }, [onClose]);
  const handleStepMake = useCallback((id, name) => {
    setStep(ProductWizardDuckFootSteps.Model);
    setMakeId(id);
    setMakeName(name);
  }, []);
  const handleStepModel = useCallback((modelId, modelName, productId) => {
    setStep(ProductWizardDuckFootSteps.BattHeader);
    setModelId(modelId);
    setModelName(modelName);
    setProductId(productId);
    handleProductId(productId);
  }, [handleProductId]);
  const handleStepHeader = useCallback((id, name, qty) => {
    setStep(ProductWizardDuckFootSteps.Result);
    setHeaderName(name);
    setQty(qty);
    setRecalculatedQty(qty);
  }, []);
  const handleRecalculatedQty = useCallback((ratio, qty) => {
    setNumberOfMachines(ratio);
    setRecalculatedQty(ratio * qty);
  }, []);
  const handleAddToCart = useCallback(() => {
    const duckFootQty = recalculatedQty || 0;
    const wizardComment = `${makeName} :: ${modelName} :: ${batt} :: ${headerName} :: ${recalculatedQty} ${t('note.pcs')} :: ${numberOfMachines} ${t('note.packs')}`;
    handleWizardClose();
    submit && submit(product, {
      qty: duckFootQty,
      packaging: null,
      wizard_comment: wizardComment
    });
    submit && redirectToCart && redirectToCart();
    changeOrderList && changeOrderList(productId, duckFootQty, wizardComment);
  }, [recalculatedQty, handleWizardClose, submit, product, makeName, modelName, batt, headerName, t, numberOfMachines, redirectToCart, changeOrderList, productId]);

  const mapStepNames = () => {
    switch (step) {
      case ProductWizardDuckFootSteps.Make:
        return DuckFootStepNameForAnalytics.ChooseMake;

      case ProductWizardDuckFootSteps.Model:
        return DuckFootStepNameForAnalytics.ChooseModel;

      case ProductWizardDuckFootSteps.BattHeader:
        return DuckFootStepNameForAnalytics.BattAndHeader;

      case ProductWizardDuckFootSteps.Result:
        return DuckFootStepNameForAnalytics.Result;

      default:
        return DuckFootStepNameForAnalytics.Unknown;
    }
  };

  return jsx(Modal, Object.assign({
    isOpen: opened,
    heading: t('title'),
    size: isMobile ? 'medium' : 'responsive',
    onClose: () => {
      handleWizardClose();
      onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
        step: mapStepNames(),
        action: 'close'
      });
    }
  }, {
    children: jsx(StepFormWrapper, {
      children: jsxs(StepForm, Object.assign({
        step: step,
        completed: false,
        onTabClick: () => undefined,
        headerSize: 'small'
      }, {
        children: [jsx(StepFormStep, Object.assign({
          id: ProductWizardDuckFootSteps.Make,
          title: t('stepForm.makeTitle')
        }, {
          children: jsx(ProductWizardMakeContainer, {
            children: jsx(DuckFootMake, {
              opened: opened,
              handleStepMake: handleStepMake,
              getProducers: getProducers,
              onSendAnalyticsPage: onSendAnalyticsPage,
              onSendAnalyticsTrack: onSendAnalyticsTrack
            }, void 0)
          }, void 0)
        }), void 0), jsx(StepFormStep, Object.assign({
          id: ProductWizardDuckFootSteps.Model,
          title: t('stepForm.modelTitle')
        }, {
          children: jsxs(StepFormStepWizardModelsWrapper, {
            children: [jsx(ProductWizardModelsContainer, {
              children: jsx(DuckFootModels, {
                handleStepModel: handleStepModel,
                getModels: getModels,
                onSendAnalyticsPage: onSendAnalyticsPage,
                onSendAnalyticsTrack: onSendAnalyticsTrack
              }, void 0)
            }, void 0), jsx(ProductWizardModelsButtonWrapper, {
              children: jsx(BackButton$2, {
                "data-test-id": ProductWizardDuckFootSteps.Model,
                onClick: () => {
                  setStep(ProductWizardDuckFootSteps.Make);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: DuckFootStepNameForAnalytics.ChooseModel,
                    action: 'back'
                  });
                },
                size: 'small'
              }, void 0)
            }, void 0)]
          }, void 0)
        }), void 0), jsxs(StepFormStep, Object.assign({
          id: ProductWizardDuckFootSteps.BattHeader,
          title: t('stepForm.battAndHeaderTitle')
        }, {
          children: [jsx(DuckFootHeader, {
            handleStepHeader: handleStepHeader,
            setBatt: setBatt,
            getHeaders: getHeaders,
            onSendAnalyticsPage: onSendAnalyticsPage,
            onSendAnalyticsTrack: onSendAnalyticsTrack
          }, void 0), jsx(ProductWizardHeaderButtonWrapper, {
            children: jsx(BackButton$2, {
              "data-test-id": ProductWizardDuckFootSteps.BattHeader,
              onClick: () => {
                setStep(ProductWizardDuckFootSteps.Model);
                onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                  step: DuckFootStepNameForAnalytics.BattAndHeader,
                  action: 'back'
                });
              },
              size: 'small'
            }, void 0)
          }, void 0)]
        }), void 0), jsx(StepFormStep, Object.assign({
          id: ProductWizardDuckFootSteps.Result,
          title: t('stepForm.resultTitle')
        }, {
          children: jsxs(ProductWizardResultContainer, {
            children: [jsx(ProductWizardResult, {
              qty: qty || 0,
              makeName: makeName,
              modelName: modelName,
              headerName: headerName,
              batt: batt,
              onAddToCart: handleAddToCart,
              getPrettyPrice: getPrettyPrice,
              price: product === null || product === void 0 ? void 0 : product.price,
              currency: product === null || product === void 0 ? void 0 : product.currency,
              priceType: product === null || product === void 0 ? void 0 : product.price_type,
              minQty: product === null || product === void 0 ? void 0 : product.min_qty,
              maxQty: product !== null && product !== void 0 && product.max_qty && qty ? Math.floor((product === null || product === void 0 ? void 0 : product.max_qty) / qty) : undefined,
              handleRecalculatedQty: handleRecalculatedQty,
              recalculatedQty: recalculatedQty,
              numberOfMachines: numberOfMachines,
              onSendAnalyticsPage: onSendAnalyticsPage,
              onSendAnalyticsTrack: onSendAnalyticsTrack
            }, void 0), jsx(ResultButtonsContainer, {
              children: jsx(BackButton$2, {
                "data-test-id": ProductWizardDuckFootSteps.Result,
                onClick: () => {
                  setStep(ProductWizardDuckFootSteps.BattHeader);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: DuckFootStepNameForAnalytics.Result,
                    action: 'back'
                  });
                },
                size: 'small'
              }, void 0)
            }, void 0)]
          }, void 0)
        }), void 0)]
      }), void 0)
    }, void 0)
  }), void 0);
};

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$4(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

const PersistentStorage = {
  setLocalData(name, value) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(name, JSON.stringify(value));
    }
  },

  getLocalData(key) {
    if (typeof window !== 'undefined') {
      const value = localStorage.getItem(key);

      if (value == null) {
        return null;
      }

      return JSON.parse(value);
    } else return null;
  },

  removeLocalData(key) {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(key);
    }
  },

  changeItemFromLocalData(key, name, value) {
    if (typeof window !== 'undefined') {
      const data = this.getLocalData(key) || {};

      const newData = _objectSpread$4(_objectSpread$4({}, data), {}, {
        [name]: value
      });

      this.setLocalData(key, newData);
    }
  }

};

let _$3 = t => t,
    _t$3,
    _t2$2;
const ProductWizardWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardWrapper",
  componentId: "sc-1j28e9r-0"
})(["display:grid;grid-template-columns:repeat(3,186px);grid-gap:17px;grid-template-rows:96px 96px 96px;height:333px;overflow-y:auto;justify-content:center;margin-bottom:26px;", ""], props => props.theme.media.mobile(_t$3 || (_t$3 = _$3`
    grid-template-columns: repeat(2, 124px);
    grid-gap: 8px;
    height: 208px;
    overflow-y: auto;
  `)));
const ProductWizardCardItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardCardItem",
  componentId: "sc-1j28e9r-1"
})(["display:flex;justify-content:center;align-items:center;padding:5px;width:186px;height:104px;border-radius:12px;background-color:", ";white-space:normal;text-align:center;cursor:pointer;overflow:hidden;word-break:break-word;&:hover{background-color:", ";}", ""], props => props.theme.color.primary25, props => props.theme.color.primary100, props => props.theme.media.mobile(_t2$2 || (_t2$2 = _$3`
    width: 124px;
    height: 80px;
  `)));

var ProductWizardProcoteSteps;

(function (ProductWizardProcoteSteps) {
  ProductWizardProcoteSteps["ChooseCrop"] = "wizard_choose_crop";
  ProductWizardProcoteSteps["YieldAndPounds"] = "wizard_enter_yield_and_pounds";
  ProductWizardProcoteSteps["Result"] = "wizard_result";
  ProductWizardProcoteSteps["ChooseItem"] = "wizard_choose_item";
})(ProductWizardProcoteSteps || (ProductWizardProcoteSteps = {}));

var ProcoteStepNameForAnalytics;

(function (ProcoteStepNameForAnalytics) {
  ProcoteStepNameForAnalytics["EntryQuestion"] = "Entry question";
  ProcoteStepNameForAnalytics["ChooseCrop"] = "Choose crop";
  ProcoteStepNameForAnalytics["ChooseProcote"] = "Choose Procote";
  ProcoteStepNameForAnalytics["EnterPounds"] = "Enter pounds";
  ProcoteStepNameForAnalytics["Result"] = "Result";
  ProcoteStepNameForAnalytics["CloseQuestion"] = "Close question";
  ProcoteStepNameForAnalytics["Unknown"] = "Unknown";
})(ProcoteStepNameForAnalytics || (ProcoteStepNameForAnalytics = {}));

const StepChooseItem = ({
  getItems,
  handleNextStep,
  onSendAnalyticsPage,
  onSendAnalyticsTrack,
  userKnowsWhichProductIsNeeded
}) => {
  const items = getItems();
  const renderItemListJSX = items === null || items === void 0 ? void 0 : items.map(item => {
    const handleChooseItem = () => {
      handleNextStep(item);
      onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
        step: userKnowsWhichProductIsNeeded ? ProcoteStepNameForAnalytics.ChooseProcote : ProcoteStepNameForAnalytics.ChooseCrop,
        choice: item.title
      });
    };

    return jsx(ProductWizardCardItem, Object.assign({
      "data-test-id": item.id,
      title: item.title,
      onClick: handleChooseItem
    }, {
      children: item.title
    }), item.id);
  });
  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: userKnowsWhichProductIsNeeded ? ProcoteStepNameForAnalytics.ChooseProcote : ProcoteStepNameForAnalytics.ChooseCrop
    });
  });
  return jsx(ProductWizardWrapper, {
    children: renderItemListJSX
  }, void 0);
};

let _$2 = t => t,
    _t$2,
    _t2$1;
const IconCheckCircle = /*#__PURE__*/styled(SvgCheck).withConfig({
  displayName: "IconCheckCircle",
  componentId: "sc-1vrz1vx-0"
})(["position:absolute;top:20px;left:17px;width:32px;height:24px;color:", ";"], props => props.theme.color.outlinePrimary);
const IconBorder = /*#__PURE__*/styled.div.withConfig({
  displayName: "IconBorder",
  componentId: "sc-1vrz1vx-1"
})(["width:64px;height:64px;top:64px;left:288px;border-radius:50%;border:1px solid ", ";position:absolute;", ""], props => props.theme.color.secondary300, props => props.theme.media.mobile(_t$2 || (_t$2 = _$2`
    left: 208px;
  `)));
const StepQuestionnaireWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepQuestionnaireWrapper",
  componentId: "sc-1vrz1vx-2"
})(["width:560px;", ""], props => props.theme.media.mobile(_t2$1 || (_t2$1 = _$2`
    width: 408px;
  `)));
const ProductContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductContainer",
  componentId: "sc-1vrz1vx-3"
})(["font-weight:600;padding-top:136px;text-align:center;padding-bottom:40px;border-bottom:1px solid ", ";"], props => props.theme.color.secondary100);
const Footer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Footer",
  componentId: "sc-1vrz1vx-4"
})(["height:54%;display:flex;flex-direction:column;justify-content:end;align-items:center;"]);
const SkipAndClose = /*#__PURE__*/styled.div.withConfig({
  displayName: "SkipAndClose",
  componentId: "sc-1vrz1vx-5"
})(["line-height:22px;color:", ";cursor:pointer;"], props => props.theme.color.secondary300);
const GoToCartButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "GoToCartButton",
  componentId: "sc-1vrz1vx-6"
})(["margin-top:12px;"]);

const RadioOption = /*#__PURE__*/styled(RadioItem).withConfig({
  displayName: "RadioOption",
  componentId: "s036yt-0"
})(["&:last-child{margin-left:40px;}& > span{font-size:14px;line-height:20px;}"]);
const SubmitButton = /*#__PURE__*/styled(Button$5).withConfig({
  displayName: "SubmitButton",
  componentId: "s036yt-1"
})(["width:186px;height:48px;margin-top:24px;margin-bottom:40px;"]);
const LabelCrops = /*#__PURE__*/styled.div.withConfig({
  displayName: "LabelCrops",
  componentId: "s036yt-3"
})(["max-width:340px;margin-top:24px;font-size:16px;font-weight:500;text-align:center;"]);
const LabelFertilizer = /*#__PURE__*/styled(LabelCrops).withConfig({
  displayName: "LabelFertilizer",
  componentId: "s036yt-4"
})(["margin-bottom:16px;"]);

const FormQuestionnaire = ({
  submit,
  value
}) => {
  const {
    t
  } = useTranslation(['procote', 'common']);
  const [haveFertilizer, setHaveFertilizer] = useState(value);

  const radioFormJSX = () => {
    return jsx("div", Object.assign({
      "data-test-id": 'questionnaire-form'
    }, {
      children: jsxs(Radio, Object.assign({
        value: haveFertilizer ? 'yes' : 'no',
        direction: 'row',
        onChange: value => {
          setHaveFertilizer(value === 'yes');
        }
      }, {
        children: [jsx(RadioOption, {
          value: 'yes',
          label: t('labels.yes')
        }, void 0), jsx(RadioOption, {
          value: 'no',
          label: t('labels.no')
        }, void 0)]
      }), void 0)
    }), void 0);
  };

  const handleSubmit = () => {
    submit(haveFertilizer);
  };

  return jsxs(Fragment, {
    children: [jsx(LabelFertilizer, {
      children: t('labels.fertilizerLabels')
    }, void 0), radioFormJSX(), jsx(SubmitButton, Object.assign({
      intent: 'primary',
      filled: true,
      onClick: handleSubmit,
      "data-test-id": 'procote-submit-btn'
    }, {
      children: t('common:submit')
    }), void 0)]
  }, void 0);
};

const StepQuestionnaire = ({
  opened,
  onClose,
  isMobile,
  onSubmit,
  onGoToCart,
  productName,
  qty,
  onSendAnalyticsPage,
  onSendAnalyticsTrack,
  value
}) => {
  const {
    t
  } = useTranslation(['procote', 'common']);

  const handleClose = () => {
    onClose();
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.CloseQuestion,
      action: 'close'
    });
  };

  const handleGoToCart = () => {
    onGoToCart();
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.CloseQuestion,
      action: 'go_to_cart'
    });
  };

  const handleSkip = () => {
    onClose();
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.CloseQuestion,
      action: 'skip'
    });
  };

  const handleSubmit = haveFertilizer => {
    onSubmit(haveFertilizer);
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.CloseQuestion,
      choice: typeof haveFertilizer === 'boolean' ? haveFertilizer ? 'yes' : 'no' : 'empty'
    });
  };

  useDidMount(() => {
    if (opened) {
      onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
        step: ProcoteStepNameForAnalytics.CloseQuestion
      });
    }
  });
  return jsx(Modal, Object.assign({
    isOpen: opened,
    onClose: handleClose,
    size: isMobile ? 'medium' : 'responsive'
  }, {
    children: jsxs(StepQuestionnaireWrapper, {
      children: [jsx(IconBorder, {
        children: jsx(IconCheckCircle, {}, void 0)
      }, void 0), jsxs(ProductContainer, {
        children: [jsx("div", {
          children: `${productName} ${qty} L`
        }, void 0), jsx("div", {
          children: t('stepQuestionnaire.addedToCart')
        }, void 0), jsx(GoToCartButton, Object.assign({
          intent: 'primary',
          onClick: handleGoToCart
        }, {
          children: t('stepQuestionnaire.goToCart')
        }), void 0)]
      }, void 0), jsxs(Footer, {
        children: [jsx(FormQuestionnaire, {
          submit: handleSubmit,
          value: value
        }, void 0), jsx(SkipAndClose, Object.assign({
          onClick: handleSkip
        }, {
          children: t('stepQuestionnaire.skipAndClose')
        }), void 0)]
      }, void 0)]
    }, void 0)
  }), void 0);
};

const getUnit = node => {
  var _node$options;

  return node === null || node === void 0 ? void 0 : (_node$options = node.options) === null || _node$options === void 0 ? void 0 : _node$options.unit_name;
};

const PROCOTE_WIZARD_VALUE = 'L';

function getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier) {
  const litresPerTonne = POUNDS_TO_KILOGRAMS / fertilizer * (expectedYield * multiplier) / 1000;
  if (litresPerTonne > 3) return 3;else if (litresPerTonne < 1) return 1;else return litresPerTonne;
}
function getLitresPerTonneWithoutCrop(fertilizer) {
  let litresPerTonne;

  switch (true) {
    case fertilizer >= 0 && fertilizer <= 100:
      litresPerTonne = 4;
      break;

    case fertilizer >= 101 && fertilizer <= 150:
      litresPerTonne = 3;
      break;

    case fertilizer >= 151 && fertilizer <= 250:
      litresPerTonne = 2.5;
      break;

    case fertilizer >= 251:
      litresPerTonne = 2;
      break;
  }

  return litresPerTonne;
}

function getFinalQtyWithCrop(fertilizer, expectedYield, acres, multiplier) {
  const litresPerTonne = getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier);
  const totalLitres = acres * fertilizer / POUNDS_TO_KILOGRAMS * litresPerTonne;
  return Math.round(totalLitres) || 1;
}
function getFinalQtyWithoutCrop(fertilizer, acres) {
  const litresPerTonne = getLitresPerTonneWithoutCrop(fertilizer);
  const totalPounds = fertilizer * acres;
  const poundsToTons = totalPounds / POUNDS_TO_KILOGRAMS;
  const totalLitres = poundsToTons * litresPerTonne;
  return Math.round(totalLitres) || 1;
}

let _$1 = t => t,
    _t$1,
    _t2,
    _t3,
    _t4,
    _t5,
    _t6;
const TableHeader = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableHeader",
  componentId: "sjfoy0-0"
})(["color:", ";"], props => props.theme.color.secondary300);
const TableCell = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableCell",
  componentId: "sjfoy0-1"
})(["font-weight:bold;"]);
const ResultTable = /*#__PURE__*/styled.div.withConfig({
  displayName: "ResultTable",
  componentId: "sjfoy0-2"
})(["margin-top:8px;display:flex;justify-content:center;text-align:center;column-gap:46px;", ""], props => props.theme.media.mobile(_t$1 || (_t$1 = _$1`
    width: auto;
  `)));
const MatchingProductsHeader = /*#__PURE__*/styled.div.withConfig({
  displayName: "MatchingProductsHeader",
  componentId: "sjfoy0-3"
})(["margin-top:40px;margin-bottom:24px;font-weight:bold;font-size:18px;line-height:24px;text-transform:uppercase;text-align:center;"]);
const MatchingRecommendedTitle = /*#__PURE__*/styled.div.withConfig({
  displayName: "MatchingRecommendedTitle",
  componentId: "sjfoy0-4"
})(["color:;font-weight:600;font-size:10px;line-height:12px;margin-top:-10px;color:", ";"], ({
  isActive
}) => isActive ? props => props.theme.color.primary200 : props => props.theme.color.textTertiary);
const MatchingCard = /*#__PURE__*/styled.div.withConfig({
  displayName: "MatchingCard",
  componentId: "sjfoy0-5"
})(["padding:8px;padding-top:20px;color:", ";font-weight:500;width:186px;height:104px;border-radius:12px;background-color:", ";white-space:normal;text-align:center;cursor:pointer;word-break:break-word;display:flex;flex-direction:column;justify-content:center;&:hover{background-color:", ";}", ""], props => props.theme.color.textPrimary, props => props.theme.color.primary25, props => props.theme.color.primary100, props => props.theme.media.mobile(_t2 || (_t2 = _$1`
    width: 124px;
    height: 80px;
  `)));
const MatchingCardChoosed = /*#__PURE__*/styled.div.withConfig({
  displayName: "MatchingCardChoosed",
  componentId: "sjfoy0-6"
})(["padding:8px;padding-top:20px;color:", ";font-weight:500;width:186px;height:104px;border-radius:12px;background-color:", ";white-space:normal;text-align:center;cursor:pointer;word-break:break-word;display:flex;flex-direction:column;justify-content:center;", ""], props => props.theme.color.backgroundSecondary, props => props.theme.color.outlinePrimary, props => props.theme.media.mobile(_t3 || (_t3 = _$1`
    width: 124px;
    height: 80px;
  `)));
const MatchingBody = /*#__PURE__*/styled.div.withConfig({
  displayName: "MatchingBody",
  componentId: "sjfoy0-7"
})(["font-size:16px;line-height:24px;text-transform:uppercase;", ""], props => props.theme.media.mobile(_t4 || (_t4 = _$1`
    font-size: 14px;
    line-height: 18px;
  `)));
const MatchingCardWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "MatchingCardWrapper",
  componentId: "sjfoy0-8"
})(["display:grid;grid-template-columns:repeat(2,186px);grid-gap:17px;justify-content:center;", ""], props => props.theme.media.mobile(_t5 || (_t5 = _$1`
    grid-template-columns: repeat(2, 124px);
    grid-gap: 8px;
  `)));
const CardWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "CardWrapper",
  componentId: "sjfoy0-9"
})(["display:flex;justify-content:center;align-content:center;margin-bottom:24px;"]);
const ShowMoreProductsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ShowMoreProductsContainer",
  componentId: "sjfoy0-10"
})(["color:", ";display:flex;justify-content:center;margin:24px 0;cursor:pointer;"], props => props.theme.color.outlinePrimary);
const ShowMoreProducts = /*#__PURE__*/styled.div.withConfig({
  displayName: "ShowMoreProducts",
  componentId: "sjfoy0-11"
})(["cursor:pointer;"]);
const StepResultContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepResultContainer",
  componentId: "sjfoy0-12"
})(["display:flex;flex-direction:column;align-items:center;"]);
const MoreProductsCardWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "MoreProductsCardWrapper",
  componentId: "sjfoy0-13"
})(["display:grid;grid-template-columns:repeat(3,186px);grid-gap:17px;justify-content:center;", ""], props => props.theme.media.mobile(_t6 || (_t6 = _$1`
    grid-template-columns: repeat(2, 124px);
    grid-gap: 8px;
  `)));
const MoreProductsContainter = /*#__PURE__*/styled.div.withConfig({
  displayName: "MoreProductsContainter",
  componentId: "sjfoy0-14"
})(["padding:40px 0 0;"]);
const DescriptionIcon = /*#__PURE__*/styled.div.withConfig({
  displayName: "DescriptionIcon",
  componentId: "sjfoy0-16"
})(["position:absolute;right:2px;top:-14px;"]);
const StyledIconQuestion = /*#__PURE__*/styled(SvgQuestion).withConfig({
  displayName: "StyledIconQuestion",
  componentId: "sjfoy0-17"
})(["width:16px;height:16px;"]);
const UnitsOfMeasurement = /*#__PURE__*/styled.div.withConfig({
  displayName: "UnitsOfMeasurement",
  componentId: "sjfoy0-18"
})(["margin-bottom:8px;font-weight:600;font-size:12px;line-height:16px;color:", ";text-transform:none;"], ({
  isActive
}) => isActive ? props => props.theme.color.primary200 : props => props.theme.color.textTertiary);
const MatchingBodyFinalQty = /*#__PURE__*/styled.div.withConfig({
  displayName: "MatchingBodyFinalQty",
  componentId: "sjfoy0-19"
})(["text-transform:none;"]);

const isWizard$1 = obj => {
  return obj.hasOwnProperty('options');
};

const StepResult = ({
  getSelectedNode,
  getWizardProducts,
  expectedYield,
  fertilizer,
  acres,
  categoryName,
  handleResult,
  productId,
  userKnowsWhichProductIsNeeded,
  products,
  setLitresPerTonne,
  onSendAnalyticsPage
}) => {
  const {
    t
  } = useTranslation(['procote']);
  const [showProducts, setShowProducts] = useState(false);
  const wizardProducts = getWizardProducts();
  const resultedCrop = getSelectedNode();
  const unit = resultedCrop && getUnit(resultedCrop);
  const selected = products === null || products === void 0 ? void 0 : products.find(p => p.id === productId);
  const matchingProducts = selected && userKnowsWhichProductIsNeeded ? [selected] : wizardProducts.slice(0, 2);
  const renderMatchingProducts = matchingProducts.map((product, idx) => {
    const recommended = idx === 0 ? t('stepForm.recommended') : null;
    const options = product && isWizard$1(product) ? product.options : undefined;
    const multiplier = Number(options === null || options === void 0 ? void 0 : options.multiplier);
    const productName = product === null || product === void 0 ? void 0 : product.title;
    const description = userKnowsWhichProductIsNeeded && 'description' in product ? product.description : options === null || options === void 0 ? void 0 : options.description;
    const finalQty = userKnowsWhichProductIsNeeded ? getFinalQtyWithoutCrop(fertilizer, acres) : getFinalQtyWithCrop(fertilizer, expectedYield, acres, multiplier);
    const litresPerTonne = userKnowsWhichProductIsNeeded ? getLitresPerTonneWithoutCrop(fertilizer).toFixed(1) : getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier).toFixed(1);
    const floatLitresPerTonne = parseFloat(litresPerTonne);
    const id = userKnowsWhichProductIsNeeded ? product.id : options === null || options === void 0 ? void 0 : options.product_id;
    const isActive = productId === (options === null || options === void 0 ? void 0 : options.product_id);
    const MatchingCard$1 = isActive || userKnowsWhichProductIsNeeded ? MatchingCardChoosed : MatchingCard;
    return jsxs(MatchingCard$1, Object.assign({
      "data-test-id": id,
      onClick: () => {
        setLitresPerTonne === null || setLitresPerTonne === void 0 ? void 0 : setLitresPerTonne(+litresPerTonne);
        handleResult(finalQty, id, productName);
      }
    }, {
      children: [recommended && !userKnowsWhichProductIsNeeded && jsx(MatchingRecommendedTitle, Object.assign({
        isActive: isActive
      }, {
        children: recommended
      }), void 0), jsx(WizardTooltip, Object.assign({
        visibleOnHover: true,
        tooltipContent: jsx("div", {
          children: description
        }, void 0),
        temporary: true,
        position: 'top'
      }, {
        children: jsx(DescriptionIcon, {
          children: jsx(StyledIconQuestion, {}, void 0)
        }, void 0)
      }), void 0), jsxs(MatchingBody, {
        children: [jsx("div", {
          children: productName
        }, void 0), jsx(UnitsOfMeasurement, Object.assign({
          isActive: isActive || userKnowsWhichProductIsNeeded
        }, {
          children: `${floatLitresPerTonne} ${PROCOTE_WIZARD_VALUE} / ${t('unitsOfMeasurement')}`
        }), void 0), jsx(MatchingBodyFinalQty, {
          children: `${finalQty} ${PROCOTE_WIZARD_VALUE} ${t('total')}`
        }, void 0)]
      }, void 0)]
    }), product.id);
  });
  const otherProducts = wizardProducts.slice(2);
  const renderOtherProducts = otherProducts.map(product => {
    const options = product === null || product === void 0 ? void 0 : product.options;
    const multiplier = Number(options === null || options === void 0 ? void 0 : options.multiplier);
    const productName = product.title_i18n.en;
    const litresPerTonne = getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier).toFixed(1);
    const finalQty = getFinalQtyWithCrop(fertilizer, expectedYield, acres, multiplier);
    const MatchingCard$1 = productId === options.product_id ? MatchingCardChoosed : MatchingCard;
    return jsx(MoreProductsContainter, {
      children: jsx(MatchingCard$1, Object.assign({
        "data-test-id": options.product_id,
        onClick: () => {
          setLitresPerTonne === null || setLitresPerTonne === void 0 ? void 0 : setLitresPerTonne(+litresPerTonne);
          handleResult(finalQty, options.product_id, productName);
        }
      }, {
        children: jsxs(MatchingBody, {
          children: [jsx("div", {
            children: productName
          }, void 0), jsx("div", {
            children: `${finalQty} ${PROCOTE_WIZARD_VALUE}`
          }, void 0)]
        }, void 0)
      }), product.id)
    }, product.id);
  });

  const handleShowProducts = () => setShowProducts(prevState => !prevState);

  const showProductsBtnJSX = !showProducts ? jsx(ShowMoreProducts, Object.assign({
    onClick: handleShowProducts
  }, {
    children: `${t('stepForm.showMore')} (${wizardProducts.length - 2})`
  }), void 0) : jsx(ShowMoreProducts, Object.assign({
    onClick: handleShowProducts
  }, {
    children: t('stepForm.showLess')
  }), void 0);
  const CardWrapper$1 = userKnowsWhichProductIsNeeded ? CardWrapper : MatchingCardWrapper;
  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: ProcoteStepNameForAnalytics.Result
    });
    let litresPerTonne;

    if (userKnowsWhichProductIsNeeded) {
      litresPerTonne = getLitresPerTonneWithoutCrop(fertilizer).toFixed(1);
      const finalQty = getFinalQtyWithoutCrop(fertilizer, acres);
      const name = selected === null || selected === void 0 ? void 0 : selected.title;
      handleResult(finalQty, productId, name);
    } else {
      const options = wizardProducts[0].options;
      const multiplier = Number(options === null || options === void 0 ? void 0 : options.multiplier);
      const finalQty = getFinalQtyWithCrop(fertilizer, expectedYield, acres, multiplier);
      litresPerTonne = getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier).toFixed(1);
      handleResult(finalQty, options === null || options === void 0 ? void 0 : options.product_id, wizardProducts[0].title_i18n.en);
    }

    setLitresPerTonne === null || setLitresPerTonne === void 0 ? void 0 : setLitresPerTonne(+litresPerTonne);
  });
  return jsxs(StepResultContainer, {
    children: [jsxs(ResultTable, {
      children: [!userKnowsWhichProductIsNeeded && jsxs(Fragment, {
        children: [jsxs("div", {
          children: [jsx(TableHeader, {
            children: t('stepResult.tableHeader.crops')
          }, void 0), jsx(TableCell, {
            children: categoryName
          }, void 0)]
        }, void 0), jsxs("div", {
          children: [jsx(TableHeader, {
            children: t('stepResult.tableHeader.expectedYield')
          }, void 0), jsx(TableCell, {
            children: `${expectedYield} ${unit}`
          }, void 0)]
        }, void 0)]
      }, void 0), jsxs("div", {
        children: [jsx(TableHeader, {
          children: t('stepResult.tableHeader.dryFertilizer')
        }, void 0), jsx(TableCell, {
          children: fertilizer
        }, void 0)]
      }, void 0), jsxs("div", {
        children: [jsx(TableHeader, {
          children: t('stepResult.tableHeader.totalTreated')
        }, void 0), jsx(TableCell, {
          children: acres
        }, void 0)]
      }, void 0)]
    }, void 0), jsx(MatchingProductsHeader, {
      children: userKnowsWhichProductIsNeeded ? t('stepResult.product') : t('stepResult.matchingProducts')
    }, void 0), jsx(CardWrapper$1, {
      children: renderMatchingProducts
    }, void 0), !userKnowsWhichProductIsNeeded && jsx(MoreProductsCardWrapper, {
      children: showProducts && renderOtherProducts
    }, void 0), !userKnowsWhichProductIsNeeded && wizardProducts.length > 2 && jsx(ShowMoreProductsContainer, {
      children: showProductsBtnJSX
    }, void 0)]
  }, void 0);
};

let _ = t => t,
    _t;
const StepSelectProcote = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepSelectProcote",
  componentId: "sc-1svifur-0"
})(["width:640px;height:495px;display:flex;justify-content:center;align-items:center;flex-direction:column;"]);
const StepSelectProcoteTitle = /*#__PURE__*/styled.div.withConfig({
  displayName: "StepSelectProcoteTitle",
  componentId: "sc-1svifur-1"
})(["font-weight:bold;margin-bottom:40px;"]);
const ButtonsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ButtonsContainer",
  componentId: "sc-1svifur-2"
})(["display:grid;grid-template-columns:repeat(2,186px);grid-gap:17px;", ""], props => props.theme.media.mobile(_t || (_t = _`
    grid-template-columns: repeat(2, 124px);
    grid-gap: 8px;
  `)));

const StepSelectProcoteWizard = ({
  opened,
  onClose,
  isMobile,
  setUserKnowsWhichProductIsNeeded,
  setStep,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const {
    t
  } = useTranslation('procote');

  const handleClose = () => {
    onClose();
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.EntryQuestion,
      action: 'close'
    });
  };

  const handleUserChoice = userKnows => {
    setUserKnowsWhichProductIsNeeded(userKnows);
    setStep(ProductWizardProcoteSteps.ChooseItem);
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.EntryQuestion,
      choice: userKnows ? 'yes' : 'no'
    });
  };

  useDidMount(() => {
    if (opened) {
      onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
        step: ProcoteStepNameForAnalytics.EntryQuestion
      });
    }
  });
  return jsx(Modal, Object.assign({
    isOpen: opened,
    onClose: handleClose,
    size: isMobile ? 'medium' : 'responsive'
  }, {
    children: jsxs(StepSelectProcote, {
      children: [jsx(StepSelectProcoteTitle, {
        children: t('stepForm.selectProcoteTitle')
      }, void 0), jsxs(ButtonsContainer, {
        children: [jsx(ProductWizardCardItem, Object.assign({
          "data-test-id": 'yes-btn',
          onClick: () => handleUserChoice(true)
        }, {
          children: t('labels.yes')
        }), void 0), jsx(ProductWizardCardItem, Object.assign({
          "data-test-id": 'no-btn',
          onClick: () => handleUserChoice(false)
        }, {
          children: t('labels.no')
        }), void 0)]
      }, void 0)]
    }, void 0)
  }), void 0);
};

const ProductWizardResultQty = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultQty",
  componentId: "mh1q3u-0"
})(["display:flex;flex-direction:column;align-items:center;&:not(:first-child){margin-top:24px;}&:first-child{margin-top:8px;}"]);
const ProductWizardResultQtyLabel = /*#__PURE__*/styled.div.withConfig({
  displayName: "ProductWizardResultQtyLabel",
  componentId: "mh1q3u-1"
})(["margin-bottom:8px;font-size:16px;font-weight:500;"]);
const NoteWrap = /*#__PURE__*/styled.p.withConfig({
  displayName: "NoteWrap",
  componentId: "mh1q3u-2"
})(["width:250px;margin-top:4px;margin-bottom:0;font-size:10px;font-weight:400;text-align:center;line-height:16px;font-style:italic;"]);

const Qty = ({
  value,
  onChange,
  max,
  min: _min = 1
}) => {
  const [qty, setQty] = useState(value);
  const handleChange = useCallback(value => {
    setQty(value);
    onChange(value);
  }, [onChange]);
  useEffect(() => {
    setQty(value);
  }, [value]);
  return jsx(NumberInput$1, {
    min: _min,
    max: max,
    selectTextOnFocus: true,
    value: qty || 0,
    onChange: val => handleChange(val),
    size: 'medium'
  }, void 0);
};

const StepYieldAndPounds = ({
  getSelectedNode,
  expectedYield,
  setExpectedYield,
  fertilizer,
  setFertilizer,
  acres,
  setAcres,
  userKnowsWhichProductIsNeeded,
  onSendAnalyticsPage
}) => {
  const resultedCrop = getSelectedNode === null || getSelectedNode === void 0 ? void 0 : getSelectedNode();
  const unit = getSelectedNode && resultedCrop && getUnit(resultedCrop);
  const {
    t
  } = useTranslation('procote');
  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: ProcoteStepNameForAnalytics.EnterPounds
    });
  });
  return jsx(Fragment, {
    children: jsxs("div", {
      children: [!userKnowsWhichProductIsNeeded && jsxs(ProductWizardResultQty, {
        children: [jsx(ProductWizardResultQtyLabel, {
          children: t('labels.expectedYield', {
            unit
          })
        }, void 0), unit && jsx(Qty, {
          value: expectedYield,
          onChange: ratio => setExpectedYield(ratio, unit),
          min: 0
        }, void 0)]
      }, void 0), jsxs(ProductWizardResultQty, {
        children: [jsx(ProductWizardResultQtyLabel, {
          children: t('labels.fertilizer')
        }, void 0), jsx(Qty, {
          value: fertilizer,
          onChange: ratio => setFertilizer(ratio),
          min: 0
        }, void 0), jsx(NoteWrap, {
          children: t('stepForm.fertilizerNote')
        }, void 0)]
      }, void 0), jsxs(ProductWizardResultQty, {
        children: [jsx(ProductWizardResultQtyLabel, {
          children: t('labels.acres')
        }, void 0), jsx(Qty, {
          value: acres,
          onChange: ratio => setAcres(ratio),
          min: 0
        }, void 0)]
      }, void 0)]
    }, void 0)
  }, void 0);
};

const getProcoteComment = ({
  categoryName,
  expectedYield,
  fertilizer,
  acres,
  litresPerTonne,
  haveFertilizer,
  yieldUnit,
  unitsOfMeasurementText,
  questionText
}) => {
  const question = haveFertilizer ? ` :: ${questionText}: ${haveFertilizer}` : '';
  const cropNameStr = categoryName && `${categoryName} :: `;
  const expectedYieldStr = expectedYield && `${expectedYield} ${yieldUnit} :: `;
  return `${cropNameStr}${expectedYieldStr}${fertilizer} :: ${acres}${question} :: ${litresPerTonne} ${PROCOTE_WIZARD_VALUE}/${unitsOfMeasurementText}`;
};

var ProductWizardType;

(function (ProductWizardType) {
  ProductWizardType["DuckFoot"] = "duck_foot";
  ProductWizardType["Procote"] = "yara";
  ProductWizardType["Other"] = "other";
})(ProductWizardType || (ProductWizardType = {}));

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const getProcoteWizardData = ({
  categoryName,
  expectedYield,
  fertilizer,
  acres,
  litresPerTonne,
  haveFertilizer,
  yieldUnit,
  productId,
  productName,
  cardId,
  skuId,
  cardTitle,
  steps
}) => ({
  type: ProductWizardType.Procote,
  steps,
  params: {},
  sku_params: _objectSpread$3({
    expected_yield_unit: yieldUnit,
    litres_per_tonne: litresPerTonne,
    category_name: categoryName,
    fertilizer: fertilizer,
    expected_yield: expectedYield,
    acres: acres,
    has_purchased_fertilizer: haveFertilizer
  }, cardId ? {
    card_id: cardId,
    sku_id: skuId,
    card_title: cardTitle
  } : {
    product_id: productId,
    product_name: productName
  })
});

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const getProcoteUpdateCartItems = props => {
  const {
    litresPerTonne,
    t
  } = props;
  return {
    wizard_comment: getProcoteComment(_objectSpread$2(_objectSpread$2({}, props), {}, {
      litresPerTonne: parseFloat(litresPerTonne.toFixed(1)),
      questionText: t('wizardComment.question'),
      unitsOfMeasurementText: t('unitsOfMeasurement')
    })),
    wizard_data: getProcoteWizardData(props)
  };
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const getProcoteSubmitOptions = (_ref) => {
  let {
    qty = 0
  } = _ref,
      props = _objectWithoutProperties(_ref, ["qty"]);

  return _objectSpread$1({
    qty,
    packaging: null
  }, getProcoteUpdateCartItems(props));
};

const data$1 = PersistentStorage.getLocalData(STORAGE_KEYS.ProcoteQuestionnaire);
const Procote = ({
  handleCrm,
  productWizard,
  opened,
  onClose,
  isMobile,
  handleGoToCart,
  handleMarketAddProduct,
  handleMarketUpdateProduct,
  handleProductId,
  products: _products = [],
  allowChooseExactProduct,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const {
    t
  } = useTranslation('procote');
  const [step, setStep] = useState('');
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [wizardNodeId, setWizardNodeId] = useState(null); // Actually it's wizard's node title, but it's kinda confusing to name it as
  // wizardNodeTitle because procote wizard nodes is named as categories

  const [categoryName, setCategoryName] = useState(null);
  const [expectedYield, setExpectedYield] = useState(0);
  const [yieldUnit, setYieldUnit] = useState('');
  const [fertilizer, setFertilizer] = useState(0);
  const [acres, setAcres] = useState(0);
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [qty, setQty] = useState(0);
  const [userKnowsWhichProductIsNeeded, setUserKnowsWhichProductIsNeeded] = useState(null);
  const [litresPerTonne, setLitresPerTonne] = useState(0);
  const nodesDict = useMemo(() => {
    const result = {};
    productWizard.children.forEach(node => {
      result[node.id] = node;
    });
    return result;
  }, [productWizard]);

  const getSelectedNode = () => wizardNodeId ? nodesDict[wizardNodeId] : null;

  const getItems = () => {
    return allowChooseExactProduct ? userKnowsWhichProductIsNeeded ? _products : productWizard.children : productWizard.children;
  };

  const getWizardProducts = () => {
    var _getSelectedNode$chil, _getSelectedNode;

    return (_getSelectedNode$chil = (_getSelectedNode = getSelectedNode()) === null || _getSelectedNode === void 0 ? void 0 : _getSelectedNode.children) !== null && _getSelectedNode$chil !== void 0 ? _getSelectedNode$chil : [];
  };

  const handleWizardClose = () => {
    setUserKnowsWhichProductIsNeeded(null);
    setWizardNodeId(null);
    setCategoryName(null);
    setExpectedYield(0);
    setFertilizer(0);
    setAcres(0);
    setProductId('');
    setProductName('');
    setQty(0);
    setStep('');
    onClose();
  };

  const handleStepChooseItem = item => {
    setStep(ProductWizardProcoteSteps.YieldAndPounds);

    if (!userKnowsWhichProductIsNeeded || !allowChooseExactProduct) {
      setWizardNodeId(item.id);
      setCategoryName(item.title);
    } else {
      setProductId(item.id);
      setProductName(item.title);
    }
  };

  const handleStepResult = (qty, id, name) => {
    id && setProductId(id);
    qty && setQty(qty);
    name && setProductName(name);
    id && handleProductId && handleProductId(id);
  };

  const haveFertilizer = !!(data$1 !== null && data$1 !== void 0 && data$1[productId]);

  const handleAddToCart = () => {
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.Result,
      choice: 'add_to_cart'
    });
    const params = getProcoteSubmitOptions({
      qty,
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      productId,
      productName,
      haveFertilizer,
      steps: wizardNodeId ? [wizardNodeId] : []
    });

    if (handleMarketAddProduct) {
      handleMarketAddProduct(productId, params);
      setShowQuestionnaire(true);
      onClose();
      setStep(ProductWizardProcoteSteps.ChooseCrop);
    } else if (handleCrm) {
      handleCrm(productId, qty, params.wizard_comment, params.wizard_data);
      handleWizardClose();
    }
  };

  const handleSubmitQuestionnaire = haveFertilizerArg => {
    const updatedParams = getProcoteUpdateCartItems({
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      productId,
      productName,
      haveFertilizer: haveFertilizerArg,
      steps: wizardNodeId ? [wizardNodeId] : []
    });
    PersistentStorage.changeItemFromLocalData(STORAGE_KEYS.ProcoteQuestionnaire, productId, haveFertilizerArg);
    handleMarketUpdateProduct && handleMarketUpdateProduct(updatedParams);
    handleWizardClose();
    handleGoToCart && handleGoToCart();
  };

  const handleExpectedYield = (expectedYield, unit) => {
    setExpectedYield(expectedYield);
    unit && setYieldUnit(unit);
  };

  const mapStepNames = () => {
    switch (true) {
      case step === ProductWizardProcoteSteps.ChooseItem && userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseProcote;

      case step === ProductWizardProcoteSteps.ChooseItem && !userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseCrop;

      case step === ProductWizardProcoteSteps.YieldAndPounds:
        return ProcoteStepNameForAnalytics.EnterPounds;

      case step === ProductWizardProcoteSteps.Result:
        return ProcoteStepNameForAnalytics.Result;

      default:
        return ProcoteStepNameForAnalytics.Unknown;
    }
  };

  return jsxs(Fragment, {
    children: [allowChooseExactProduct && jsx(StepSelectProcoteWizard, {
      opened: !userKnowsWhichProductIsNeeded ? opened : false,
      onClose: handleWizardClose,
      isMobile: isMobile,
      setUserKnowsWhichProductIsNeeded: setUserKnowsWhichProductIsNeeded,
      setStep: setStep,
      onSendAnalyticsPage: onSendAnalyticsPage,
      onSendAnalyticsTrack: onSendAnalyticsTrack
    }, void 0), jsx(Modal, Object.assign({
      isOpen: !!step || opened && !allowChooseExactProduct,
      onClose: () => {
        handleWizardClose();
        onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
          step: mapStepNames(),
          action: 'close'
        });
      },
      heading: t('title'),
      size: isMobile ? 'medium' : 'responsive',
      modalPadding: 'noPadding'
    }, {
      children: jsxs(StepForm, Object.assign({
        step: step ? step : !allowChooseExactProduct ? ProductWizardProcoteSteps.ChooseItem : '',
        completed: false,
        onTabClick: () => undefined,
        headerSize: 'small'
      }, {
        children: [jsx(StepFormStep, Object.assign({
          id: ProductWizardProcoteSteps.ChooseItem,
          title: userKnowsWhichProductIsNeeded ? t('stepForm.chooseProcoteTitle') : t('stepForm.chooseCropTitle')
        }, {
          children: jsxs(StepWrapper, {
            children: [jsx(StepChooseItem, {
              getItems: getItems,
              handleNextStep: handleStepChooseItem,
              onSendAnalyticsPage: onSendAnalyticsPage,
              onSendAnalyticsTrack: onSendAnalyticsTrack,
              userKnowsWhichProductIsNeeded: !!userKnowsWhichProductIsNeeded
            }, void 0), allowChooseExactProduct && jsx(ButtonsWrapper, {
              children: jsx(BackButton, {
                onClick: () => {
                  setStep('');
                  setUserKnowsWhichProductIsNeeded(null);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: userKnowsWhichProductIsNeeded && allowChooseExactProduct ? ProcoteStepNameForAnalytics.ChooseProcote : ProcoteStepNameForAnalytics.ChooseCrop,
                    action: 'back'
                  });
                }
              }, void 0)
            }, void 0)]
          }, void 0)
        }), void 0), jsx(StepFormStep, Object.assign({
          id: ProductWizardProcoteSteps.YieldAndPounds,
          title: userKnowsWhichProductIsNeeded ? t('stepForm.poundsTitle') : t('stepForm.yieldAndPoundsTitle')
        }, {
          children: jsxs(StepWrapper, {
            children: [jsx(StepYieldAndPounds, {
              getSelectedNode: getSelectedNode,
              expectedYield: expectedYield,
              setExpectedYield: handleExpectedYield,
              fertilizer: fertilizer,
              setFertilizer: setFertilizer,
              acres: acres,
              setAcres: setAcres,
              userKnowsWhichProductIsNeeded: !!userKnowsWhichProductIsNeeded,
              onSendAnalyticsPage: onSendAnalyticsPage
            }, void 0), jsxs(ButtonsWrapper, {
              children: [jsx(BackButton, {
                onClick: () => {
                  setStep(ProductWizardProcoteSteps.ChooseItem);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: ProcoteStepNameForAnalytics.EnterPounds,
                    action: 'back'
                  });
                }
              }, void 0), jsx(Button$5, Object.assign({
                "data-test-id": 'next-btn',
                intent: 'primary',
                filled: true,
                onClick: () => {
                  setStep(ProductWizardProcoteSteps.Result);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: ProcoteStepNameForAnalytics.EnterPounds,
                    choice: `expected_yield: ${expectedYield}, fertilizer: ${fertilizer}, acres: ${acres}`
                  });
                },
                disabled: !userKnowsWhichProductIsNeeded && !expectedYield || !fertilizer || !acres
              }, {
                children: t('stepForm.continue')
              }), void 0)]
            }, void 0)]
          }, void 0)
        }), void 0), jsx(StepFormStep, Object.assign({
          id: ProductWizardProcoteSteps.Result,
          title: t('stepForm.resultTitle')
        }, {
          children: jsxs(StepWrapper, {
            children: [jsx(StepResult, {
              getSelectedNode: getSelectedNode,
              getWizardProducts: getWizardProducts,
              expectedYield: expectedYield,
              fertilizer: fertilizer,
              acres: acres,
              categoryName: categoryName,
              handleResult: handleStepResult,
              productId: productId,
              products: _products,
              userKnowsWhichProductIsNeeded: !!userKnowsWhichProductIsNeeded,
              setLitresPerTonne: setLitresPerTonne,
              onSendAnalyticsPage: onSendAnalyticsPage
            }, void 0), jsxs(ButtonsWrapper, {
              children: [jsx(BackButton, {
                onClick: () => {
                  setStep(ProductWizardProcoteSteps.YieldAndPounds);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: ProcoteStepNameForAnalytics.Result,
                    action: 'back'
                  });
                }
              }, void 0), jsx(Button$5, Object.assign({
                "data-test-id": 'next-btn',
                intent: 'primary',
                filled: true,
                onClick: handleAddToCart
              }, {
                children: t('stepForm.addToCart')
              }), void 0)]
            }, void 0)]
          }, void 0)
        }), void 0)]
      }), void 0)
    }), void 0), showQuestionnaire && jsx(StepQuestionnaire, {
      opened: showQuestionnaire,
      onClose: () => {
        handleWizardClose();
        setShowQuestionnaire(false);
      },
      isMobile: isMobile,
      value: haveFertilizer,
      onSubmit: handleSubmitQuestionnaire,
      onGoToCart: () => {
        handleWizardClose();
        handleGoToCart && handleGoToCart();
      },
      productName: productName,
      qty: qty,
      onSendAnalyticsPage: onSendAnalyticsPage,
      onSendAnalyticsTrack: onSendAnalyticsTrack
    }, void 0)]
  }, void 0);
};

const isBasicCard = item => 'skus' in item;

const StepChooseItemSku = ({
  wizardNodes,
  cards,
  handleNextStep,
  onSendAnalyticsPage,
  onSendAnalyticsTrack,
  userKnowsWhichProductIsNeeded
}) => {
  const items = userKnowsWhichProductIsNeeded ? cards : wizardNodes;
  const renderItemListJSX = items === null || items === void 0 ? void 0 : items.map(item => {
    const title = isBasicCard(item) ? item.title_i18n.en : item.title;

    const handleChooseItem = () => {
      handleNextStep(item);
      onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
        step: userKnowsWhichProductIsNeeded ? ProcoteStepNameForAnalytics.ChooseProcote : ProcoteStepNameForAnalytics.ChooseCrop,
        choice: title
      });
    };

    return jsx(ProductWizardCardItem, Object.assign({
      "data-test-id": item.id,
      title: title,
      onClick: handleChooseItem
    }, {
      children: title
    }), item.id);
  });
  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: userKnowsWhichProductIsNeeded ? ProcoteStepNameForAnalytics.ChooseProcote : ProcoteStepNameForAnalytics.ChooseCrop
    });
  });
  return jsx(ProductWizardWrapper, {
    children: renderItemListJSX
  }, void 0);
};

const isWizard = obj => {
  return obj.hasOwnProperty('options');
};

const StepResultSku = ({
  getSelectedNode,
  getWizardProducts,
  expectedYield,
  fertilizer,
  acres,
  categoryName,
  handleResult,
  userKnowsWhichProductIsNeeded,
  setLitresPerTonne,
  onSendAnalyticsPage,
  cardId: cardIdFromProps,
  skuId: skuIdFromProps,
  cards
}) => {
  const {
    t
  } = useTranslation(['procote']);
  const [showProducts, setShowProducts] = useState(false);
  const wizardProducts = getWizardProducts();
  const resultedCrop = getSelectedNode();
  const unit = resultedCrop && getUnit(resultedCrop);
  const selected = cards === null || cards === void 0 ? void 0 : cards.find(c => c.id === cardIdFromProps);
  const matchingProducts = selected && userKnowsWhichProductIsNeeded ? [selected] : wizardProducts.slice(0, 2);
  const renderMatchingProducts = matchingProducts.map((wizardNodeOrCard, idx) => {
    var _wizardNode$title;

    const recommended = idx === 0 ? t('stepForm.recommended') : null;
    const wizardNode = isWizard(wizardNodeOrCard) ? wizardNodeOrCard : null;
    const card = !isWizard(wizardNodeOrCard) ? wizardNodeOrCard : null;
    const options = wizardNode ? wizardNode.sku_options : undefined;
    const multiplier = Number(options === null || options === void 0 ? void 0 : options.multiplier);
    const cardOrNodeTitle = card ? card.title_i18n.en : (_wizardNode$title = wizardNode === null || wizardNode === void 0 ? void 0 : wizardNode.title) !== null && _wizardNode$title !== void 0 ? _wizardNode$title : '';
    const description = card ? card.description_i18n.en : options === null || options === void 0 ? void 0 : options.description;
    const finalQty = userKnowsWhichProductIsNeeded ? getFinalQtyWithoutCrop(fertilizer, acres) : getFinalQtyWithCrop(fertilizer, expectedYield, acres, multiplier);
    const litresPerTonne = userKnowsWhichProductIsNeeded ? getLitresPerTonneWithoutCrop(fertilizer).toFixed(1) : getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier).toFixed(1);
    const floatLitresPerTonne = parseFloat(litresPerTonne);
    const cardId = card ? card.id : options === null || options === void 0 ? void 0 : options.card_id;
    const skuId = card ? card.skus[0] : options === null || options === void 0 ? void 0 : options.sku_id;
    const isActive = cardIdFromProps === cardId;
    const MatchingCard$1 = isActive || userKnowsWhichProductIsNeeded ? MatchingCardChoosed : MatchingCard;
    return jsxs(MatchingCard$1, Object.assign({
      "data-test-id": cardId,
      onClick: () => {
        if (cardId && skuId) {
          setLitresPerTonne === null || setLitresPerTonne === void 0 ? void 0 : setLitresPerTonne(+litresPerTonne);
          handleResult(cardId, skuId, finalQty, cardOrNodeTitle, card);
        }
      }
    }, {
      children: [recommended && !userKnowsWhichProductIsNeeded && jsx(MatchingRecommendedTitle, Object.assign({
        isActive: isActive
      }, {
        children: recommended
      }), void 0), description && jsx(WizardTooltip, Object.assign({
        visibleOnHover: true,
        tooltipContent: jsx("div", {
          children: description
        }, void 0),
        temporary: true,
        position: 'top'
      }, {
        children: jsx(DescriptionIcon, {
          children: jsx(StyledIconQuestion, {}, void 0)
        }, void 0)
      }), void 0), jsxs(MatchingBody, {
        children: [jsx("div", {
          children: cardOrNodeTitle
        }, void 0), jsx(UnitsOfMeasurement, Object.assign({
          isActive: isActive || userKnowsWhichProductIsNeeded
        }, {
          children: `${floatLitresPerTonne} ${PROCOTE_WIZARD_VALUE} / ${t('unitsOfMeasurement')}`
        }), void 0), jsx(MatchingBodyFinalQty, {
          children: `${finalQty} ${PROCOTE_WIZARD_VALUE} ${t('total')}`
        }, void 0)]
      }, void 0)]
    }), cardId);
  });
  const otherProducts = wizardProducts.slice(2);
  const renderOtherProducts = otherProducts.map(wizardNode => {
    const options = wizardNode.sku_options;
    const multiplier = Number(options === null || options === void 0 ? void 0 : options.multiplier);
    const nodeTitle = wizardNode.title_i18n.en;
    const id = wizardNode.id;
    const cardId = options === null || options === void 0 ? void 0 : options.card_id;
    const skuId = options === null || options === void 0 ? void 0 : options.sku_id;
    const litresPerTonne = getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier).toFixed(1);
    const finalQty = getFinalQtyWithCrop(fertilizer, expectedYield, acres, multiplier);
    const MatchingCard$1 = cardIdFromProps === cardId ? MatchingCardChoosed : MatchingCard;
    return jsx(MoreProductsContainter, {
      children: jsx(MatchingCard$1, Object.assign({
        "data-test-id": cardId,
        onClick: () => {
          if (cardId && skuId) {
            setLitresPerTonne === null || setLitresPerTonne === void 0 ? void 0 : setLitresPerTonne(+litresPerTonne);
            handleResult(cardId, skuId, finalQty, nodeTitle);
          }
        }
      }, {
        children: jsxs(MatchingBody, {
          children: [jsx("div", {
            children: nodeTitle
          }, void 0), jsx("div", {
            children: `${finalQty} ${PROCOTE_WIZARD_VALUE}`
          }, void 0)]
        }, void 0)
      }), id)
    }, id);
  });

  const handleShowProducts = () => setShowProducts(prevState => !prevState);

  const showProductsBtnJSX = !showProducts ? jsx(ShowMoreProducts, Object.assign({
    onClick: handleShowProducts
  }, {
    children: `${t('stepForm.showMore')} (${wizardProducts.length - 2})`
  }), void 0) : jsx(ShowMoreProducts, Object.assign({
    onClick: handleShowProducts
  }, {
    children: t('stepForm.showLess')
  }), void 0);
  const CardWrapper$1 = userKnowsWhichProductIsNeeded ? CardWrapper : MatchingCardWrapper;
  useDidMount(() => {
    onSendAnalyticsPage === null || onSendAnalyticsPage === void 0 ? void 0 : onSendAnalyticsPage({
      step: ProcoteStepNameForAnalytics.Result
    });
    let litresPerTonne;

    if (userKnowsWhichProductIsNeeded && cardIdFromProps && skuIdFromProps) {
      var _selected$title_i18n$;

      litresPerTonne = getLitresPerTonneWithoutCrop(fertilizer).toFixed(1);
      const finalQty = getFinalQtyWithoutCrop(fertilizer, acres);
      const name = (_selected$title_i18n$ = selected === null || selected === void 0 ? void 0 : selected.title_i18n.en) !== null && _selected$title_i18n$ !== void 0 ? _selected$title_i18n$ : '';
      handleResult(cardIdFromProps, skuIdFromProps, finalQty, name);
    } else {
      const options = wizardProducts[0].sku_options;

      if (options !== null && options !== void 0 && options.card_id && options !== null && options !== void 0 && options.sku_id) {
        const multiplier = Number(options === null || options === void 0 ? void 0 : options.multiplier);
        const finalQty = getFinalQtyWithCrop(fertilizer, expectedYield, acres, multiplier);
        litresPerTonne = getLitresPerTonneWithCrop(fertilizer, expectedYield, acres, multiplier).toFixed(1);
        handleResult(options.card_id, options.sku_id, finalQty, wizardProducts[0].title_i18n.en);
      }
    }

    setLitresPerTonne === null || setLitresPerTonne === void 0 ? void 0 : setLitresPerTonne(+litresPerTonne);
  });
  return jsxs(StepResultContainer, {
    children: [jsxs(ResultTable, {
      children: [!userKnowsWhichProductIsNeeded && jsxs(Fragment, {
        children: [jsxs("div", {
          children: [jsx(TableHeader, {
            children: t('stepResult.tableHeader.crops')
          }, void 0), jsx(TableCell, {
            children: categoryName
          }, void 0)]
        }, void 0), jsxs("div", {
          children: [jsx(TableHeader, {
            children: t('stepResult.tableHeader.expectedYield')
          }, void 0), jsx(TableCell, {
            children: `${expectedYield} ${unit}`
          }, void 0)]
        }, void 0)]
      }, void 0), jsxs("div", {
        children: [jsx(TableHeader, {
          children: t('stepResult.tableHeader.dryFertilizer')
        }, void 0), jsx(TableCell, {
          children: fertilizer
        }, void 0)]
      }, void 0), jsxs("div", {
        children: [jsx(TableHeader, {
          children: t('stepResult.tableHeader.totalTreated')
        }, void 0), jsx(TableCell, {
          children: acres
        }, void 0)]
      }, void 0)]
    }, void 0), jsx(MatchingProductsHeader, {
      children: userKnowsWhichProductIsNeeded ? t('stepResult.product') : t('stepResult.matchingProducts')
    }, void 0), jsx(CardWrapper$1, {
      children: renderMatchingProducts
    }, void 0), !userKnowsWhichProductIsNeeded && jsx(MoreProductsCardWrapper, {
      children: showProducts && renderOtherProducts
    }, void 0), !userKnowsWhichProductIsNeeded && wizardProducts.length > 2 && jsx(ShowMoreProductsContainer, {
      children: showProductsBtnJSX
    }, void 0)]
  }, void 0);
};

const data = PersistentStorage.getLocalData(STORAGE_KEYS.ProcoteQuestionnaire);
const ProcoteSku = ({
  handleCrm,
  productWizard,
  opened,
  onClose,
  isMobile,
  handleGoToCart,
  handleMarketAddProduct,
  handleMarketUpdateProduct,
  onResultCardChange,
  cards: _cards = [],
  allowChooseExactCard,
  onSendAnalyticsPage,
  onSendAnalyticsTrack
}) => {
  const {
    t
  } = useTranslation('procote');
  const [step, setStep] = useState('');
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [wizardNodeId, setWizardNodeId] = useState(null); // Actually it's wizard's node title, but it's kinda confusing to name it as
  // wizardNodeTitle because procote wizard nodes is named as categories

  const [categoryName, setCategoryName] = useState(null);
  const [expectedYield, setExpectedYield] = useState(0);
  const [yieldUnit, setYieldUnit] = useState('');
  const [fertilizer, setFertilizer] = useState(0);
  const [acres, setAcres] = useState(0);
  const [skuId, setSkuId] = useState('');
  const [cardId, setCardId] = useState('');
  const [cardTitle, setCardTitle] = useState('');
  const [qty, setQty] = useState(0);
  const [userKnowsWhichProductIsNeeded, setUserKnowsWhichProductIsNeeded] = useState(null);
  const [litresPerTonne, setLitresPerTonne] = useState(0);
  const nodesDict = useMemo(() => {
    const result = {};
    productWizard.children.forEach(node => {
      result[node.id] = node;
    });
    return result;
  }, [productWizard]);

  const getSelectedNode = () => wizardNodeId ? nodesDict[wizardNodeId] : null;

  const getWizardProducts = () => {
    var _getSelectedNode$chil, _getSelectedNode;

    return (_getSelectedNode$chil = (_getSelectedNode = getSelectedNode()) === null || _getSelectedNode === void 0 ? void 0 : _getSelectedNode.children) !== null && _getSelectedNode$chil !== void 0 ? _getSelectedNode$chil : [];
  };

  const handleWizardClose = () => {
    setUserKnowsWhichProductIsNeeded(null);
    setWizardNodeId(null);
    setCategoryName(null);
    setExpectedYield(0);
    setFertilizer(0);
    setAcres(0);
    setCardId('');
    setCardTitle('');
    setSkuId('');
    setQty(0);
    setStep('');
    onClose();
  };

  const handleStepChooseItem = item => {
    setStep(ProductWizardProcoteSteps.YieldAndPounds);

    if (isBasicCard(item)) {
      setCardId(item.id);
      setCardTitle(item.title_i18n.en || '');
      setSkuId(item.skus[0]);
    } else {
      setWizardNodeId(item.id);
      setCategoryName(item.title);
    }
  };

  const handleStepResult = (cardId, skuId, qty, name) => {
    setCardId(cardId);
    setQty(qty);
    name && setCardTitle(name);
    setSkuId(skuId);
    onResultCardChange === null || onResultCardChange === void 0 ? void 0 : onResultCardChange(cardId, skuId);
  };

  const haveFertilizer = !!(data !== null && data !== void 0 && data[cardId]);

  const handleAddToCart = () => {
    onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
      step: ProcoteStepNameForAnalytics.Result,
      choice: 'add_to_cart'
    });
    const params = getProcoteSubmitOptions({
      qty,
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      cardId,
      cardTitle,
      skuId,
      haveFertilizer,
      steps: wizardNodeId ? [wizardNodeId] : []
    });

    if (handleMarketAddProduct) {
      handleMarketAddProduct(cardId, skuId, params);
      setShowQuestionnaire(true);
      onClose();
      setStep(ProductWizardProcoteSteps.ChooseCrop);
    } else if (handleCrm) {
      handleCrm(cardId, skuId, qty, params.wizard_comment, params.wizard_data);
      handleWizardClose();
    }
  };

  const handleSubmitQuestionnaire = haveFertilizerArg => {
    const updatedParams = getProcoteUpdateCartItems({
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      cardId,
      cardTitle,
      skuId,
      haveFertilizer: haveFertilizerArg,
      steps: wizardNodeId ? [wizardNodeId] : []
    });
    PersistentStorage.changeItemFromLocalData(STORAGE_KEYS.ProcoteQuestionnaire, cardId, haveFertilizerArg);
    handleMarketUpdateProduct && handleMarketUpdateProduct(updatedParams);
    handleWizardClose();
    handleGoToCart && handleGoToCart();
  };

  const handleExpectedYield = (expectedYield, unit) => {
    setExpectedYield(expectedYield);
    unit && setYieldUnit(unit);
  };

  const mapStepNames = () => {
    switch (true) {
      case step === ProductWizardProcoteSteps.ChooseItem && userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseProcote;

      case step === ProductWizardProcoteSteps.ChooseItem && !userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseCrop;

      case step === ProductWizardProcoteSteps.YieldAndPounds:
        return ProcoteStepNameForAnalytics.EnterPounds;

      case step === ProductWizardProcoteSteps.Result:
        return ProcoteStepNameForAnalytics.Result;

      default:
        return ProcoteStepNameForAnalytics.Unknown;
    }
  };

  return jsxs(Fragment, {
    children: [allowChooseExactCard && jsx(StepSelectProcoteWizard, {
      opened: userKnowsWhichProductIsNeeded === null ? opened : false,
      onClose: handleWizardClose,
      isMobile: isMobile,
      setUserKnowsWhichProductIsNeeded: setUserKnowsWhichProductIsNeeded,
      setStep: setStep,
      onSendAnalyticsPage: onSendAnalyticsPage,
      onSendAnalyticsTrack: onSendAnalyticsTrack
    }, void 0), jsx(Modal, Object.assign({
      isOpen: !!step || opened && !allowChooseExactCard,
      onClose: () => {
        handleWizardClose();
        onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
          step: mapStepNames(),
          action: 'close'
        });
      },
      heading: t('title'),
      size: isMobile ? 'medium' : 'responsive',
      modalPadding: 'noPadding'
    }, {
      children: jsxs(StepForm, Object.assign({
        step: step ? step : !allowChooseExactCard ? ProductWizardProcoteSteps.ChooseItem : '',
        completed: false,
        onTabClick: () => undefined,
        headerSize: 'small'
      }, {
        children: [jsx(StepFormStep, Object.assign({
          id: ProductWizardProcoteSteps.ChooseItem,
          title: userKnowsWhichProductIsNeeded ? t('stepForm.chooseProcoteTitle') : t('stepForm.chooseCropTitle')
        }, {
          children: jsxs(StepWrapper, {
            children: [jsx(StepChooseItemSku, {
              wizardNodes: productWizard.children,
              cards: _cards,
              handleNextStep: handleStepChooseItem,
              onSendAnalyticsPage: onSendAnalyticsPage,
              onSendAnalyticsTrack: onSendAnalyticsTrack,
              userKnowsWhichProductIsNeeded: !!userKnowsWhichProductIsNeeded
            }, void 0), jsx(ButtonsWrapper, {
              children: jsx(BackButton, {
                onClick: () => {
                  setStep('');
                  setUserKnowsWhichProductIsNeeded(null);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: userKnowsWhichProductIsNeeded && allowChooseExactCard ? ProcoteStepNameForAnalytics.ChooseProcote : ProcoteStepNameForAnalytics.ChooseCrop,
                    action: 'back'
                  });
                }
              }, void 0)
            }, void 0)]
          }, void 0)
        }), void 0), jsx(StepFormStep, Object.assign({
          id: ProductWizardProcoteSteps.YieldAndPounds,
          title: userKnowsWhichProductIsNeeded ? t('stepForm.poundsTitle') : t('stepForm.yieldAndPoundsTitle')
        }, {
          children: jsxs(StepWrapper, {
            children: [jsx(StepYieldAndPounds, {
              getSelectedNode: getSelectedNode,
              expectedYield: expectedYield,
              setExpectedYield: handleExpectedYield,
              fertilizer: fertilizer,
              setFertilizer: setFertilizer,
              acres: acres,
              setAcres: setAcres,
              userKnowsWhichProductIsNeeded: !!userKnowsWhichProductIsNeeded,
              onSendAnalyticsPage: onSendAnalyticsPage
            }, void 0), jsxs(ButtonsWrapper, {
              children: [jsx(BackButton, {
                onClick: () => {
                  setStep(ProductWizardProcoteSteps.ChooseItem);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: ProcoteStepNameForAnalytics.EnterPounds,
                    action: 'back'
                  });
                }
              }, void 0), jsx(Button$5, Object.assign({
                "data-test-id": 'next-btn',
                intent: 'primary',
                filled: true,
                onClick: () => {
                  setStep(ProductWizardProcoteSteps.Result);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: ProcoteStepNameForAnalytics.EnterPounds,
                    choice: `expected_yield: ${expectedYield}, fertilizer: ${fertilizer}, acres: ${acres}`
                  });
                },
                disabled: !userKnowsWhichProductIsNeeded && !expectedYield || !fertilizer || !acres
              }, {
                children: t('stepForm.continue')
              }), void 0)]
            }, void 0)]
          }, void 0)
        }), void 0), jsx(StepFormStep, Object.assign({
          id: ProductWizardProcoteSteps.Result,
          title: t('stepForm.resultTitle')
        }, {
          children: jsxs(StepWrapper, {
            children: [jsx(StepResultSku, {
              getSelectedNode: getSelectedNode,
              getWizardProducts: getWizardProducts,
              expectedYield: expectedYield,
              fertilizer: fertilizer,
              acres: acres,
              categoryName: categoryName,
              handleResult: handleStepResult,
              cardId: cardId,
              skuId: skuId,
              cards: _cards,
              userKnowsWhichProductIsNeeded: !!userKnowsWhichProductIsNeeded,
              setLitresPerTonne: setLitresPerTonne,
              onSendAnalyticsPage: onSendAnalyticsPage
            }, void 0), jsxs(ButtonsWrapper, {
              children: [jsx(BackButton, {
                onClick: () => {
                  setStep(ProductWizardProcoteSteps.YieldAndPounds);
                  onSendAnalyticsTrack === null || onSendAnalyticsTrack === void 0 ? void 0 : onSendAnalyticsTrack({
                    step: ProcoteStepNameForAnalytics.Result,
                    action: 'back'
                  });
                }
              }, void 0), jsx(Button$5, Object.assign({
                "data-test-id": 'next-btn',
                intent: 'primary',
                filled: true,
                onClick: handleAddToCart
              }, {
                children: t('stepForm.addToCart')
              }), void 0)]
            }, void 0)]
          }, void 0)
        }), void 0)]
      }), void 0)
    }), void 0), showQuestionnaire && jsx(StepQuestionnaire, {
      opened: showQuestionnaire,
      onClose: () => {
        handleWizardClose();
        setShowQuestionnaire(false);
      },
      value: haveFertilizer,
      isMobile: isMobile,
      onSubmit: handleSubmitQuestionnaire,
      onGoToCart: () => {
        handleWizardClose();
        handleGoToCart && handleGoToCart();
      },
      productName: cardTitle,
      qty: qty,
      onSendAnalyticsPage: onSendAnalyticsPage,
      onSendAnalyticsTrack: onSendAnalyticsTrack
    }, void 0)]
  }, void 0);
};

const SmallText = /*#__PURE__*/styled.p.withConfig({
  displayName: "SmallText",
  componentId: "aoopr-0"
})(["margin:0;font-weight:500;font-size:12px;line-height:16px;color:", ";"], ({
  theme
}) => theme.color.secondary300);
const ProcoteWizardData = ({
  data: {
    sku_params
  }
}) => {
  const {
    t
  } = useTranslation('procote');
  if (!sku_params) return null;
  const {
    expected_yield_unit,
    litres_per_tonne,
    category_name,
    expected_yield,
    fertilizer,
    acres
  } = sku_params;
  return jsxs("div", {
    children: [!!litres_per_tonne && jsx(SmallText, {
      children: `${parseFloat(litres_per_tonne.toString())} ${t('measures.short.lTonne')}`
    }, void 0), !!category_name && jsx(SmallText, {
      children: `${t('stepResult.tableHeader.crops')}: ${category_name}`
    }, void 0), !!expected_yield && jsx(SmallText, {
      children: `${t('stepResult.tableHeader.expectedYield')}: ${expected_yield} ${expected_yield_unit || ''}`
    }, void 0), !!fertilizer && jsx(SmallText, {
      children: `${t('stepResult.tableHeader.dryFertilizer')}: ${fertilizer}`
    }, void 0), !!acres && jsx(SmallText, {
      children: `${t('stepResult.tableHeader.totalTreated')}: ${acres}`
    }, void 0)]
  }, void 0);
};

var ui$4={country:{full:{RU:"Russia",US:"USA",CA:"Canada",UA:"Ukraine"}},common:{back:"Back",add:"Add",download:"Download",requiredInputMark:"required",optional:"Optional"},RemovePopover:{cancel:"No, cancel",submit:"Yes, remove"},CheckboxSelect:{allSelected:"All {{items}}",multipleSelected:"Multiple {{items}}"},CheckboxTreeSelect:{noOptionsText:"No options",loadingText:"Loading options...",multipleText:"Multiple {{items}}",allItemsText:"All {{items}}",defaultTitle:"Items",nestedOptionsFilteredWarning:"Some of the nested options have been filtered",selectOnly:"Select only",deselectOnly:"Deselect only"},NumberInput:{add:"Add {{count}} item",remove:"Remove {{count}} item",label:"Count"},FileDropzone:{dropLink:"the link",dropText:"Drop files here or click ",dragStatus:{accept:"Drop files",reject:"Invalid file format. Accepted only: {{formats}}","invalid-count":"Invalid files count. Allowed: {{fileCount}}"}},Banner:{learnMore:"Learn more"},CountrySelect:{placeholder:"Country"},BasicSelect:{"one-item-selected":"1 item selected","nth-items-selected":"{{count}} items selected"},TagsSelect:{showSelected:"Show selected",hideSelected:"Hide selected"},TabsWithSelect:{showing:"SHOWING",tabsMore:"Tabs more",changeTabs:"Change tabs"}};var en = {ui:ui$4};

var ui$3={country:{full:{RU:"Russia",US:"USA",CA:"Canada",UA:"Ukraine"}},common:{back:"Back",add:"Add",download:"Download",requiredInputMark:"required",optional:"Optional"},RemovePopover:{cancel:"No, cancel",submit:"Yes, remove"},CheckboxSelect:{allSelected:"All {{items}}",multipleSelected:"Multiple {{items}}"},CheckboxTreeSelect:{noOptionsText:"No options",loadingText:"Loading options...",multipleText:"Multiple {{items}}",allItemsText:"All {{items}}",defaultTitle:"Items",nestedOptionsFilteredWarning:"Some of the nested options have been filtered.",selectOnly:"Select only",deselectOnly:"Deselect only"},NumberInput:{add:"Add {{count}} item",remove:"Remove {{count}} item",label:"Count"},FileDropzone:{dropLink:"the link",dropText:"Drop files here or click ",dragStatus:{accept:"Drop files",reject:"Invalid file format. Accepted only: {{formats}}","invalid-count":"Invalid files count. Allowed: {{fileCount}}"}},Banner:{learnMore:"Learn more"},CountrySelect:{placeholder:"Country"}};var fr = {ui:ui$3};

var ui$2={country:{full:{RU:"Россия",US:"США",CA:"Канада",UA:"Украина"}},common:{back:"Назад",add:"Добавить",download:"Скачать",requiredInputMark:"обязательное",optional:"не обязательное"},RemovePopover:{cancel:"Нет, отменить",submit:"Да, удалить"},CheckboxSelect:{allSelected:"Все {{items}}",multipleSelected:"Несколько {{items}}"},CheckboxTreeSelect:{noOptionsText:"Нет вариантов",loadingText:"Загрузка...",multipleText:"Кратный {{items}}",allItemsText:"Все {{items}}",defaultTitle:"Список",nestedOptionsFilteredWarning:"Некоторые вложенные элементы были исключены из списка",selectOnly:"Выбрать только",deselectOnly:"Отменить только этот"},NumberInput:{add:"Добавить {{count}} товар",remove:"Удалить {{count}} элемент",label:"Кол-во"},FileDropzone:{dropLink:"на ссылку",dropText:"Перетащите файлы сюда или кликните ",dragStatus:{accept:"Перетащите файлы",reject:"Недопустимый формат. Поддерживаются только: {{formats}}","invalid-count":"Недействительное количество файлов. Разрешено: {{fileCount}}"}},Banner:{learnMore:"Узнать подробнее"},CountrySelect:{placeholder:"Страна"}};var ru = {ui:ui$2};

var ui$1={country:{full:{RU:"Russia",US:"USA",CA:"Canada",UA:"Ukraine"}},common:{back:"Atrás",add:"Añadir",download:"Descargar",requiredInputMark:"solicitado",optional:"Opcional"},RemovePopover:{cancel:"No, cancelar",submit:"Sí, quitar"},CheckboxSelect:{allSelected:"Todo {{items}}",multipleSelected:"Múltiple {{items}}"},CheckboxTreeSelect:{noOptionsText:"Sin opciones",loadingText:"Cargando opciones...",multipleText:"Múltiple {{items}}",allItemsText:"Todos {{items}}",defaultTitle:"Elementos",nestedOptionsFilteredWarning:"Algunas de las opciones anidadas se han filtrado",selectOnly:"Seleccionar solo",deselectOnly:"Deseleccionar solo"},NumberInput:{add:"{{count}} artículo",remove:"Eliminar {{count}} artículo",label:"Contar"},FileDropzone:{dropLink:"el enlace",dropText:"Suelta los archivos aquí o haz clic en ",dragStatus:{accept:"Soltar archivos",reject:"Formato de archivo inválido. Aceptado solamente: {{formats}}","invalid-count":"Recuento de archivos no válidos. Permitido: {{fileCount}}"}},Banner:{learnMore:"¿Socio de Cooperativa?"},CountrySelect:{placeholder:"País"},productWizard:{title:"Find Your Header Solution",qtyLabel:"Add additional Machines",stepForm:{makeTitle:"Choose Make",modelTitle:"Choose Model",battAndHeaderTitle:"Batt & Header",resultTitle:"Find Your Header Solution",battReelsHeader:"Batt Reels",headerSizeHeader:"Header Size",addToCart:"Add to Truck",continueShoppingButton:"Continue shopping",selectMachine:"Find Your Header Solution"},note:{header:"Header",pcs:"pcs",packs:"machines"}}};var es = {ui:ui$1};

var ui={country:{full:{RU:"Russia",US:"USA",CA:"Canada",UA:"Ukraine"}},common:{back:"Atrás",add:"Añadir",download:"Descargar",requiredInputMark:"solicitado",optional:"Opcional"},RemovePopover:{cancel:"No, cancelar",submit:"Sí, quitar"},CheckboxSelect:{allSelected:"Todo {{items}}",multipleSelected:"Múltiple {{items}}"},CheckboxTreeSelect:{noOptionsText:"Sin opciones",loadingText:"Cargando opciones...",multipleText:"Múltiple {{items}}",allItemsText:"Todos {{items}}",defaultTitle:"Elementos",nestedOptionsFilteredWarning:"Algunas de las opciones anidadas se han filtrado",selectOnly:"Seleccionar solo",deselectOnly:"Deseleccionar solo"},NumberInput:{add:"{{count}} artículo",remove:"Eliminar {{count}} artículo",label:"Contar"},FileDropzone:{dropLink:"el enlace",dropText:"Suelta los archivos aquí o haz clic en ",dragStatus:{accept:"Soltar archivos",reject:"Formato de archivo inválido. Aceptado solamente: {{formats}}","invalid-count":"Recuento de archivos no válidos. Permitido: {{fileCount}}"}},Banner:{learnMore:"¿Socio de Cooperativa?"},CountrySelect:{placeholder:"País"},productWizard:{title:"Find Your Header Solution",qtyLabel:"Add additional Machines",stepForm:{makeTitle:"Choose Make",modelTitle:"Choose Model",battAndHeaderTitle:"Batt & Header",resultTitle:"Find Your Header Solution",battReelsHeader:"Batt Reels",headerSizeHeader:"Header Size",addToCart:"Add to Truck",continueShoppingButton:"Continue shopping",selectMachine:"Find Your Header Solution"},note:{header:"Header",pcs:"pcs",packs:"machines"}}};var es_SV = {ui:ui};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

const merge = (source, replacement) => {
  return unflat(_objectSpread(_objectSpread({}, flat(source)), flat(replacement)));
};

const uiResources = {
  en,
  ru,
  fr,
  es,
  es_SV
};
const i18nComposeWithUIComponents = appResources => merge(uiResources, appResources);

export { AVAILABLE_CURRENCY, AddButton, AdvancedHeadCell, AdvancedSearchField, BackBtn, BackButton$2 as BackButton, Banner, BarMenuFullWidthItem, BaseInput, BasicSelect, Breadcrumbs, Button$5 as Button, Checkbox, CheckboxSelect, CheckboxTree, CheckboxTreeSelect, Chip, ClearIndicatorStyled, CollapseButton, CountryPhoneInput, CountrySelect, Currency, CurrencyInput, CurrencySelect, DATE_FORMAT, DatePicker, DiscountBadge, DownloadButton, DropdownIndicatorStyled$1 as DropdownIndicatorStyled, DuckFoot, DuckFootStepNameForAnalytics, EMPTY_ARRAY, FileDropzone, FilePicker, Flag, FontSize, FontWeight, FormComponents, FormField, FormText, GridArea, HelmetContext, SvgAdd as IconAdd, SvgArrowBack as IconArrowBack, SvgArrowDown as IconArrowDown, SvgArrowLeftBold as IconArrowLeftBold, SvgArrowRight as IconArrowRight, SvgBin as IconBin, SvgBurgerMenu as IconBurgerMenu, IconButton, SvgCalendar as IconCalendar, SvgCalls as IconCalls, SvgCallsHistory as IconCallsHistory, SvgCart as IconCart, SvgCategory as IconCategory, SvgCheck as IconCheck, SvgCheckCircle as IconCheckCircle, SvgCheckmark as IconCheckmark, SvgClip as IconClip, SvgCollapse as IconCollapse, SvgCompany as IconCompany, SvgCrops as IconCrops, SvgCross as IconCross, SvgCrossCircle as IconCrossCircle, SvgDashboard as IconDashboard, SvgDeal as IconDeal, SvgDocument as IconDocument, SvgDownload as IconDownload, SvgExit as IconExit, SvgExpand as IconExpand, SvgEye as IconEye, SvgFarmerOrders as IconFarmerOrders, SvgFarmerPlans as IconFarmerPlans, SvgFcc as IconFcc, SvgFilter as IconFilter, SvgImage as IconImage, SvgInvoice as IconInvoice, SvgLeaf as IconLeaf, SvgLink as IconLink, SvgLinkOpener as IconLinkOpener, SvgLoader as IconLoader, SvgLocation as IconLocation, SvgMagicWand as IconMagicWand, SvgMailing as IconMailing, SvgMarketingCampaigns as IconMarketingCampaigns, SvgMinus as IconMinus, SvgMoreVert as IconMoreVert, SvgOrder as IconOrder, SvgOrders as IconOrders, SvgPencil as IconPencil, SvgPickup as IconPickup, SvgPlus as IconPlus, SvgPlusCircle as IconPlusCircle, SvgPrint as IconPrint, SvgProducts as IconProducts, SvgPromocodes as IconPromocodes, SvgQuestion as IconQuestion, SvgRefresh as IconRefresh, SvgSalesTeamPlans as IconSalesTeamPlans, SvgSearch as IconSearch, SvgSendEmail as IconSendEmail, SvgSettings as IconSettings, SvgSign as IconSign, SvgSort as IconSort, SvgSpa as IconSpa, SvgStore as IconStore, SvgSuccess as IconSuccess, SvgSupport as IconSupport, SvgTarget as IconTarget, SvgTerritory as IconTerritory, SvgTile as IconTile, SvgTrialSeed as IconTrialSeed, SvgTriangleDown as IconTriangleDown, SvgTruck as IconTruck, SvgUpload as IconUpload, SvgUser as IconUser, SvgUsers as IconUsers, SvgUsersTeam as IconUsersTeam, SvgVerifiedShield as IconVerifiedShield, SvgWarehouse as IconWarehouse, SvgWarning as IconWarning, InfiniteScrollTable, Input, Label$3 as Label, MenuContainer, MenuContainerStyled, MenuItem, MenuItemLink, Modal, ModalHeading, ModalRaw, ModalWizardHeading, MultiSelectWithPagination, NumberInput$1 as NumberInput, OptionalNumber, OptionalString, POUNDS_TO_KILOGRAMS, Pagination, PopoverWithBtns, PrefixInput, Procote, ProcoteSku, ProcoteStepNameForAnalytics, ProcoteWizardData, ProductWizardDuckFootSteps, ProductWizardProcoteSteps, ProductWizardType, Progress, ProgressCircle, Radio, RadioItem, RangeDatePicker, RemovePopover, STORAGE_KEYS, SearchInput, SectionBody, SectionContainer, SectionFooter, SectionLinkWrapper, SectionTable, SectionTableBody, SectionTableBodyCell, SectionTableBodyRow, SectionTableHead, SectionTableHeadCell, SectionTableHeadRow, SectionTitle, Segment, SelectWithPagination, SimpleSelect, SingleValueStyled, Skeleton, Snackbar, Spinner$1 as Spinner, Spinnerable, StatusIcon, StepForm, StepFormStep, StyledMenu, StyledMenuList, StyledMultiValueContainer, StyledMultiValueLabel, StyledMultiValueRemove, StyledPlaceholder, Switch, SwitchButton, SwitchSelect, TEST_ID_SELECTED, Table, TableActionPanel, TableBody, TableBodyCell, TableBodyRow, TableComponentsActionButton, TableComponentsCheckboxWrapper, TableComponentsClearButton, TableComponentsFilters, TableComponentsFlag, TableComponentsLinkButton, TableComponentsSpinner, TableComponentsSpinnerWrapper, TableComponentsSummaryLabel, TableComponentsSummaryTitle, TableComponentsSummaryWrapper, TableComponentsWrapper, TableHead, TableHeadCell, TableHeadRow, TableNoData, TableSkeletonRow, Tabs, TabsWithSelect, Tag, TagList, TagsSelect, TextArea, TextAreaWithTags, Tooltip, TreeView, Typography, ValidationError, WizardTooltip, apiDateTimeFormat, baseCustomStyles, controlStyle, countryList, creatMediaQueries, createMedia, customStyles$2 as customStyles, defaultTheme, flat, getCountryByCountryCode, getStepDecimals, helpersBrowser, helpersColor, helpersDownload, helpersImageFileValidator, helpersObject, helpersPassword, helpersSlug, helpersUrl, i18nComposeWithUIComponents, isPlainObject, makeMediaQuery, makeRefreshTokenAndRetry, matchCallingCode, matchCountryCode, mediaQuery, menuListStyle, menuStyle, numberToStep, optionStyle, pairs, smallSelectStyles, uiResources, unflat, updateOpts, useAction, useBoolean, useCountdownTimer, useDebounce, useDidMount, useDidMountWithProgress, useFileObjectUrl, useFormManager, useGTM, useHelmet, useHistoryPush, useId, useInView, useInfiniteScroll, useIntercom, useIsMounted, useKey, useLockedBodyScroll, useMatchMedia, useOnClickOutside, usePageQuery, usePageVisibilityChange, useParentOffset, usePersistentScroll, useQuery, useScrollToTop, useScrollTrigger, useSelectWithPaginationState, useTempBoolean, useThrottle, useWindowResize, useZendeskWidget };
